import { useState, useEffect, useRef, useCallback, useMemo } from "react";
// ⚠ gameManager's `endMatch` IS NO LONGER IMPORTED HERE, and its
// absence is the point. It used to arrive as `endMatchRaw` purely so
// the Second-Wind wrapper below could shadow the short name (see THE
// SECOND WIND); its only remaining caller was the R87 twenty-minute
// clock, which now ends a duel through turnEngine's `endOnClock` — the
// same function R93's bell rings, so both hands of the clock resolve
// R98 co-winners identically. With that gone there is NO raw ending
// door left in this file at all: every `endMatch(...)` below is the
// wrapper, and the one deliberate bypass is named and explained.
import { listenToMatch, updateMatch, updatePlayer as updatePlayerRaw, updateTeamAnima, ensureActive } from "./gameManager";
// ── THE N-SEAT LAYER (2v2 / 3-4p Free-For-All) ──────────────────────
// Pure helpers only — see matchSeats.js's own header for why this file
// still has exactly the same three "opponent" chokepoints it always
// had, just each now resolved through one of these instead of an
// inline `playerIdx===0?1:0`. 1v1 is untouched: every function here
// returns the historical answer on a classic 2-seat match.
import { getTargetIdx, getNextActiveSeat, withEffectiveAnima,
         sharesAnimaWithTeam, teamOf, enemySeatsOf,
         // getEffectiveAnima / livingEnemySeatsOf came off this list
         // when R97's toll became ONE function in prophecyEngine — the
         // manual door no longer walks the enemy seats itself, which
         // is exactly the drift the shared implementation removes.
         // R98 — a Solrend can end with more than one winner.
         winnersOfMatch, isCoWonMatch,
         // teammatesOf / matchIsDecided / survivingTeamsOrSeats came off
         // this list when the C16 concede stopped hand-copying
         // finishOrEliminateSeat's body. They belong to the resolver;
         // this file asks the resolver.
         livingSeats, isEliminated, decideTurnOrder, didSeatWin,
         seatCountOf, allSeatIdxs,
         // C17-N — the abandonment watchdog watches the whole table now,
         // so it needs the living-enemy list back on this manifest.
         livingEnemySeatsOf, answerersFor, collectPvPResponses } from "./matchSeats";
// ⏳ R87 only. The duel clock anchors to a SERVER millisecond, not a
// device one — see SEED ONCE FROM THE SHARED STAMP. This is the file's
// only direct firebase import and it is deliberately one symbol wide.
import { serverTimestamp, ref as dbRef, onDisconnect } from "firebase/database";
import { db } from "./firebase";   // C17 — onDisconnect on our own `present` row
import { pendingPhase, escalationUpdates, phaseLabel } from "./bossPhases"; // THE SECOND WIND
import { startRecording, seedRecording, commitReplay, stopRecording } from "./replay"; // H6
import { registerResponder, unregisterResponder, requestReaction, noteWindowActivity } from "./ResponseBridge";
import { CHAIN_DEPTH_CAP, trueCounterOptions, chainStands, ruinedLinks } from "./ChainLaw";
import { ESCENDANTS, TOMES, IMPERA, UHLTIS, AFFINITIES, PROPHECIES, CURIOS, ESCLINGS } from "./cards";
import { SD, shimmerBorder, TYPE_COLORS_SD, SCHOOL_COLORS_SD, STARFIELD_BG,
         GOLD_METAL, SILVER_METAL, OBSIDIAN_SLAB, OBSIDIAN_SLAB_LIT, OBSIDIAN_RECESS,
         facet, bloom, textGlow } from "./theme";
import { OrnatePanel, OrnateDivider, StarDust, GlassCracks, NoiseOverlay, TerrorHeader, RedFlower } from "./TerrorUI";
// getProphecyKhaoCost — THE ONE derivation of a Prophecy's Khao fare,
// read off the `khaoCost` printed on the card row in cards.js. It used
// to be hand-typed here TWICE (handleActivateProphecy charges it,
// ProphecyButton gates on it) and a third time inside prophecyEngine,
// against a fourth copy on the card face. Four declarations of one
// number that only happened to agree.
import { buildAnimaLossUpdate, buildSelfDimUpdate, buildAnimaGainUpdate,
         getProphecyKhaoCost,
         // prophecyConditionMet — THE ONE definition of "is this
         // Prophecy true?", now read by the manual door and the button
         // as well as the automatic one. It was re-implemented inline in
         // ProphecyButton for 40 lines; see the banner there.
         prophecyConditionMet,
         // R97 — the toll's ONE implementation, shared with the
         // automatic door in prophecyEngine. See the banner there.
         applyProphetsToll, boardAfterToll, collectEmptiedSides, tollStamp } from "./prophecyEngine";
// ── THE HALF-TURN HAND-OFF (August 2026) ─────────────────────────────
// checkProphecies and buildEndOfHalfTurnUpdate used to be imported here
// and called from this file — the first from the onValue listener (a
// win condition in the renderer), the second from handleEndTurn and
// nowhere else (a per-half-turn ledger that only one of the game's
// three transitions ever rotated). Both are now handOffHalfTurn's
// business, and this file calls that instead. See src/turnEngine.js.
import { handOffHalfTurn, endOnClock } from "./turnEngine";
// The engine-side half of finishOrEliminate below — hoisted out of this
// component so prophecyEngine can knock out ONE seat in an FFA/2v2
// instead of ending the whole table. See src/matchOutcome.js.
import { finishOrEliminateSeat } from "./matchOutcome";
import { useModalKeys, useKeyboardNav, KeyboardHint } from "./KeyboardNav";
// ⭐ THE FACADE, CLOSED (2026-08-25). ruleGate's banner claimed every
// rule-bearing action passed through it; this file dispatched 13 action
// types and DRAW, DISCARD, KHAO_SPEND, PLACE_TOME and ACTIVATE_TOME were
// not among them — so R07/R10/R11/R12/R13/R14/R15/R88 had no gate at all
// on the only driver a human uses. ruleGate grew wrappers for exactly
// those and nothing imported them. Now something does.
import { enforce, setEffectiveLyfResolver,
         // ── R108 · THE PHASE GATE (caP'n, 2026-08-26) ─────────────
         // gateDeclarePhase validates the one-way step into Attack.
         // The BARS themselves are asked through rules.phaseAllows at
         // each handler (see `phaseBars` below) — synchronous, because
         // a radial wedge's `legal` flag cannot await a gate.
         gateDeclarePhase,
         gateDraw, gateDiscard, gateKhaoSpend,
         // ── R08's THREE NEW DOORS (caP'n ruling, 2026-08-25) ──
         // The broad Attack, the Book search and the mulligan. See the
         // banner on gateAttackEffect in ruleGate.js.
         gateAttackEffect, gateSearch, gateEffectSearch, gateMulligan,
         gateTomePlacement, gateTomeActivation, gateSoulActivation,
         // ── R32'S SEAL, ON EVERY ANIMA GAIN (2026-08-25) ──────────
         // 23 sites in this file each wrote their own
         // `Math.min(MAX_ANIMA, x + n)`. That is arithmetic, not a
         // rule: RulesEnforcer.validateAnimaGain — documented as "the
         // one place that can be asked" — had ZERO call sites in the
         // whole project. Every one of those 23 now goes through the
         // gate, which returns the same number the clamp did and
         // COUNTS the ask so a batch can prove the seal is live.
         // R32 CAPS, it does not forbid: a clamped gain is a legal
         // gain and the spill is simply lost, exactly as before.
         sealAnimaGain } from "./ruleGate";
// ── THE NECRO SEAL (July 27, 2026) — per-copy card identity ──
// caP'n's ruling: Resurrection tallies are PER COPY, not per card name.
// Two ruined copies of the same Beb are two identical strings in the
// Necropolis array, so they get SEALED on the way in (taez_beb~0~7) and
// keep that identity for the rest of the Solrend. Full rationale, the
// separator choice, and the Graveshift generation live in necroSeal.js.
import { baseId, sealPayload, sealGraveshift,
         resTally as resTallySealed, canPassiveResurrect } from "./necroSeal";
// ── PRIMERAS — THE STAMPS (H22, July 2026) ──────────────────
// rules.js is the ONE source of truth for the eight stamps, their costs,
// and the resurrection tallies. This used to be imported `PRIMERAS as
// PRIMERA_STAMPS` because cards.js ALSO exported a `PRIMERAS` — the
// Luxmoire card array. That array is `IMPERA` now, per the decree
// (Impera = the cards; Primera = the innate stamps), so the collision
// is gone and the stamps go by their own name here.
// tomeWaitHalfTurns joins the imports 2026-09-13, replacing the bare
// TOME_WAIT map (2026-08-20) — see the R35 banner on the
// face-down Tome's Activate wedge. rules.js stays the only place the
// number lives; nothing here retypes it.
import { PRIMERAS, PRIMERA_COST, RESURRECT_LIMIT, DUEL_TIME_LIMIT, timeoutWinner,
         // MAX_ANIMA is gone from this list on purpose: the 23 sites
         // that used it were `Math.min(MAX_ANIMA, …)` clamps and they
         // now ask ruleGate.sealAnimaGain instead. The ceiling belongs
         // to R32's validator, not to 23 call sites holding a constant.
         HERETICAL_TRAINING_COST, canPayVoluntary, STARTING_ANIMA, HAND_LIMIT,
         cooldownHalfTurns, deviantFare,
         // ── THE 2026-08-26 CANON PASS ────────────────────────
         // activationFare      R105 — the printed fare, EVERY activation.
         // durationExpiryTurn  R103 — "2 turns" is 4 half-turns.
         // farePoolsFor        R107 — one pool per fare, never a split.
         // isLuxSearchable     R106 — a Lux search finds Impera only.
         // PHASE/phaseOf/…     R108 — the five phases and the gate.
         // Every one of them is DECLARED in rules.js; this file asks.
         activationFare, canPayPrintedFare, printedFare, tomeWaitHalfTurns,
         // R128 — what a NATURAL Impera Training costs. caP'n, 2026-09-14.
         // `trainingFare` is the printed fare; `paysAgain` on its result is
         // `imperaPaysOnActivation`, so the toast can tell the player whether
         // this is the only payment the card will ever take.
         trainingFare,
         // R129 — the two fed Treasures. One table for the printed
         // currency, the per-Turn allowance and the threshold, and ONE
         // helper that folds all three limits into "how much more may
         // this Treasure take this Turn". The board kept a local copy
         // until rules.js could hold it; the copy is deleted.
         ATV_FEED, feedAllowanceLeft,
         soulCardOffCooldown, stampSoulUsed, lytAttacksAllowed, tomeIsIntangible,
         durationExpiryTurn, durationStillRunning, farePoolsFor, isLuxSearchable,
         PHASE, PHASES, phaseOf, phaseAllows, inAttackPhase,
         GRAVESHIFT_COST, LUX_SEARCH_COST, RESURRECT_COST, STARTING_HAND, MAX_KHAO_ABS, grimSearchFare,
         RESURRECT_PER_TURN,
         STARTING_KHAO,
         // MAX_KHAO_NAT and FIRST_KHAO_TURN are gone from this list on
         // purpose (R132). The only thing in this file that priced them
         // was the start-of-turn Max Khao climb, and the climb now lives
         // in turnEngine.handOffHalfTurn — ONE grant site for every seat
         // and every driver, inside the chair transaction. A renderer
         // must not hold a rule; do not bring either constant back here.
         // ── R08's LAST DOOR (caP'n ruling, 2026-08-25) ──
         // isOpeningCycle + adversaryDamageInPayload are how "no Attacks
         // of ANY kind" is enforced without a curated list of attacking
         // cards. See THE R08 BACKSTOP on updatePlayer below.
         isOpeningCycle, adversaryDamageInPayload,
         // R97 — THE PROPHET'S TOLL: only the MODE question is asked
         // here now. The arithmetic (prophetsToll) is reached through
         // prophecyEngine's applyProphetsToll, which both doors call.
         isOneVsOne,
         // ── THE EXILE WAVE (caP'n, 2026-09-06/07) ────────────
         // ESCLING_SLOTS  R110 — the battle line is FOUR rifts wide.
         //                Every literal 3 that meant "the battle line"
         //                is now this; the literal 3s that meant the
         //                TOME rifts are deliberately untouched.
         // The rift roster, its Arts, and the Exile rift's laws.
         ESCLING_SLOTS, RIFTS, RIFT_NAMES, ROLES, ROLE_ORDER, roleOfRift, riftNameOf,
         BODY_RIFT, SOL_RIFT, HEART_RIFT, MIND_RIFT,
         rolesAwake, riftArtIsAwake, roleFare, roleCooldown, ROLE_CADENCE,
         TERROR_ART_OVERKILL, TANK_LYT_REDUCTION, TANK_TARGETS_PER_TURN,
         wardenOpenToLytAttack, animaOpenToLytAttack, blocksAnimaLytAttack,
         WARDEN_OPTIONS, WARDEN_ARTS_PER_TURN, WARDEN_BOUNTY,
         mindIsSilenced, mindMayAct, trioOccupied, riftOccupied,
         EXILIATION_OPTIONS, exiliationOption, exileFare, exiliationCooldown,
         exileRetrievalFare, exileRetrievalCooldown, canReachExileRift,
         EXILE_RETRIEVAL_ANIMA_FLOOR, ruinDestination, exileBarsResurrection,
         isExileSpent, roundAgainstActor, routeExileOnWrite,
         EXILE_RIFT_FIELD, EXILE_RETURNED_FIELD, exileKey,
         // ── THE EXILE WAVE, PERFORMED (2026-09-13) ────────────────
         // The four rifts by name, the Warden's two options, and the
         // Exiliation menu. Every one of these is DATA read out of
         // rules.js: the wedges below draw themselves from these tables
         // rather than listing four Arts in JSX, so a fifth rift added
         // tomorrow appears on the board with no edit to this file.
         // (the rift indices, their names and the Warden's two options are
         // already imported above — only `wardenOption` is new here)
         wardenOption,
         PLAYER_TIMER, CURIO_GIFTS, deslashPayload } from "./rules";
// ── THE TOME BOOK'S LANE, READ RATHER THAN RETYPED ───────────────────
// `tomeIsStanding` answers "does this Tome's row sit on the STANDING lane"
// — i.e. is its whole effect being on the field, with nothing to fire. The
// book is the one table, asserted complete against cards.js at import, so
// the board asks it instead of keeping a twelfth hand-maintained list. It
// is pure data (it imports only cards, rules and necroSeal) and React-free.
import { tomeIsStanding } from "./npcTomeBook";
import { performRoleArt, performExiliation, performRitual, artsRailFor,
         // R116/R118 — the two marks the COMBAT path reads. They are
         // predicates, not effects: the Arts that set them are above.
         terrorOverkill, tankDullFor, payWardenBounty } from "./roleArts";
import { auth } from "./firebase";
import { cardUrl, cdnUrl } from "./CDN"; // one art-URL builder for the whole engine
import SourceSigil, { primaryOrigin } from "./SourceSigil";
import TheMark from "./TheMark";
import { getCardOrigins } from "./progression";
import TerrorFrame from "./TerrorFrame";
import Shatterfield3D from "./Shatterfield3D";
import { useEquippedDrawPulse, toneStyle, restStyle, pulseTone } from "./DrawPulse";
import CardFace from "./CardRenderer"; // ⬡ THE ONE FACE — shared card body (July 24 ruling) // ⬡ SHIP 1 — 3D field renderer (pure view; contract in file header)
import { CoinFaces, bannerFor, useCoinSkin } from "./coinSkins"; // T11 — the coin's wardrobe (a table; never touches the outcome)
// HOLOFOIL → SHATTERFIELD MIGRATION (July 3) — the duel is the LAST surface
// off the shared foil component. In-duel cards now render the same bespoke
// motion signatures as the Vault/Codex/shop (all 9 tiers, incl. the two the
// old private map never knew: holo_jynxi + holo_depths).
import HoloFoil from "./HoloFoil";
import { HOLO_TIER_INFO, TIER_ORDER, TIER_ORDER_ALL } from "./PackArt";
// StatGlyph twin MERGED (July 3) — Shatterfield's private StatGlyph/StatChip
// were extracted VERBATIM to engine/StatGlyph.js in Combo Run #2; this file
// now imports the canonical pair. Geometry is identical by construction.
import { StatGlyph, StatChip } from "./StatGlyph";
// ── H23 — THE HOLD (July 27) ────────────────────────────────
// caP'n: "gamer timer stops for animations." The 3D board's score
// runner buys the clock back for exactly as long as a chord runs;
// this file only ever READS the flag. strikeLanguage is pure math
// with zero renderer dependencies, which is precisely why the hold
// lives there — the turn clock must never import three.js to check
// a boolean. Cap, and the PvP-failsafe reasoning, are documented at
// the writer's end.
import { holdActive, releaseHold } from "./strikeLanguage";
import CompanionHUD from "./CompanionHUD";
import FieldPill from "./FieldPill";
import {
  fireShieldTome as resolver_fireShieldTome,
  tillRefusesSelfDim as resolver_tillRefusesSelfDim,
  consumeSGAutoWin as resolver_consumeSGAutoWin,
  recordShadowGamblingOutcome as resolver_recordShadowGamblingOutcome,
  triggerHopeIfTimeEntry as resolver_triggerHopeIfTimeEntry,
  fireAkulaListenerOnRuin as resolver_fireAkulaListenerOnRuin,
  searchAndAdd as resolver_searchAndAdd,
  descendBeing as resolver_descendBeing,
  applyTomeRuinClauses as resolver_applyTomeRuinClauses,
  attemptRuinOppTome as resolver_attemptRuinOppTome,
  applyTomeEffect as resolver_applyTomeEffect,
  // ONE canonical "ruin 1 being and its Soul" — R68's Heretical Tome,
  // the wielded-Treasure cascade, the Akula listener and the C13 soak,
  // in the funnel both drivers share. Never hand-roll a full ruin again.
  ruinBeingAndSoul,
  getRequiredTarget, normPick, cascadeWieldedTreasures,
  needsWielder,
} from "./tomeResolver";

// ── Grimoire Manuscript Palette — UI chrome only ─────────────
// Board stays dark void + blood red. Only chrome gets manuscript.
const GM = {
  page:          "#0f0c07",
  pageWarm:      "#1a1509",
  pageMid:       "#241c0c",
  pageLight:     "#2e2410",
  pageBorder:    "#3d2e14",
  inkGold:       "#c8963c",
  inkGoldBright: "#e8b84b",
  inkGoldDim:    "#8a6520",
  inkBrown:      "#8a6a30",
  inkRed:        "#8b2020",
  vellum:        "#e8d5a3",
  vellumDim:     "#b89a5a",
  vellumFaint:   "#7a6040",
  ruling:        "rgba(139,106,48,0.18)",
  font: "'Cinzel', 'Palatino Linotype', serif",
};

// MAGNUM PASS 4 — reading font. Cinzel is engraved capitals — superb
// for chrome, wrong for sentences. Effect text, flavor, and toasts
// read in Cormorant Garamond (runs small: use +1–2px vs Cinzel sizes).
const FONT_BODY = "'Cormorant Garamond', 'Palatino Linotype', serif";
const FONT_DECO = "'Cinzel Decorative', 'Cinzel', serif";


const ALL_CARDS_FLAT = [...ESCENDANTS, ...TOMES, ...IMPERA, ...UHLTIS, ...AFFINITIES, ...PROPHECIES, ...CURIOS, ...ESCLINGS];

// ── PRIMERA STAMP COLORS ─────────────────────────────────────
// SCHOOL words (`card.school` — Space/Phantom/Dragon/Time) use the
// established identity palette (SCHOOL_COLORS_SD — the same hues the
// strike slashes bleed). ILK words (`card.ilk` — Humanoid/Beast/
// Machine/Dragon) get their own seals here. Dragon is ONE word on both
// axes — one crimson, everywhere (R82's spirit).
// (An axis-inversion warning stood here until 2026-08-25; the fields
// were swapped in cards.js that day and now say what they hold.)
const STAMP_COLORS = {
  Dragon:   SCHOOL_COLORS_SD.Dragon,   // #f87171 — one word, one color
  Beast:    "#e8963c",                // feral amber
  Machine:  "#5eead4",                // teal (Rulebook's Machine hue)
  Humanoid: "#e8b84b",                // gilded
  Phantom:  SCHOOL_COLORS_SD.Phantom,  // #c084fc
  Time:     SCHOOL_COLORS_SD.Time,     // #fbbf24
  Space:    SCHOOL_COLORS_SD.Space,    // #22d3ee
};

// R44 tally — how many times THIS COPY has come back this Solrend.
// resurrectedCards is append-only and EVERY method appends, so counting
// occurrences IS the tally. Sealed ids (taez_beb~0~7) count per COPY;
// unsealed ids fall back to name matching so a match already in flight
// keeps working — no migration, no reset. (necroSeal.js)
const resTally = resTallySealed;
const EMPTY_PRIMERA_TURN = { words: {}, slots: {}, beastArmed: {} };

// PLAYER_TIMER (R126, 45 s a half-turn, every mode) is imported from
// ./rules — it was declared here as a local literal until the 2026-09-15
// unification pass, the one rule constant that lived outside the
// catalogue. caP'n's own history rides with it there (35 → 45).
// HAND_LIMIT and GRAVESHIFT_COST are imported from ./rules above.
// They were declared here as local literals — a second and third copy
// of two numbers the catalogue already owns. THE CATALOGUE LAW: one
// catalogue, and every consumer reads it.
const NPC_IDX = 1;

// ─────────────────────────────────────────────────────────────
// HOLO SYSTEM — foil effects that follow cards everywhere
// ─────────────────────────────────────────────────────────────
// HOLO_TIERS — DERIVED from PackArt's HOLO_TIER_INFO (the single source of
// truth for tier labels/glyphs/colors). The old hand-typed map here carried
// only 7 of 9 tiers (holo_jynxi + holo_depths were absent — J'ynxi foils
// rendered NOTHING in-duel, Depths had foil but no badge/border) and stale
// colors (shadow #6600cc vs canonical #9966ff). Adding a tier to PackArt now
// auto-lands it in the duel with zero changes to this file.
const HOLO_TIERS = Object.fromEntries(
  Object.entries(HOLO_TIER_INFO).map(([tier, t]) => [tier, {
    label: `${t.glyph} ${t.label.toUpperCase()}`,
    color: t.color,
    borderColor: t.color,
    glowColor: `${t.color}66`,
  }])
);

// Look up holo tier from the player's collection data.
// Legacy primary (holoTier + holoCount — what DeckBuilder's synthetic
// collection stamps) wins; else fall back to the per-tier holoTiers map
// (new schema) and show the first owned tier in canonical TIER_ORDER, so
// foils bought after the holoTiers schema shipped are never invisible
// in-duel. (Mirrors DeckBuilder's schema-union philosophy.)
function getHoloTier(cardId, collection) {
  if (!cardId || !collection) return null;
  // Base-aware: the collection is keyed by bare catalog ids, so a sealed
  // being would silently lose its foil without the strip.
  const data = collection[baseId(cardId)];
  if (!data) return null;
  if (data.holoTier && (data.holoCount || 0) >= 1) return data.holoTier;
  if (data.holoTiers && typeof data.holoTiers === "object") {
    // KHAO RENAME (July 25, 2026) — walk TIER_ORDER_ALL, not TIER_ORDER.
    // TIER_ORDER is what can be MINTED (8 live tiers); TIER_ORDER_ALL adds
    // the legacy keys still sitting in players' Firebase records. Walking
    // the short list here would make every foil bought before the rename
    // silently vanish from the duel.
    for (const t of TIER_ORDER_ALL) {
      if ((data.holoTiers[t] || 0) > 0) return t;
    }
  }
  return null;
}

// ══════════════════════════════════════════════════════════════
// THE DUST — board-wide ambient keyframes (D8, D14)
// ══════════════════════════════════════════════════════════════
// ⚠ ITS OWN INJECTOR, CALLED AT MODULE SCOPE, AND THAT IS THE
// WHOLE POINT. The first cut of these two hung them off
// ensureCompanionHudKeyframes, which is the exact trap PRIME §6.4
// already records as paid for: the CompanionHUD radiant ring never
// pulsed for weeks because it animated on a keyframe injected by a
// component the duel does not always mount. A fractured being
// exists in every duel; a companion does not. Board-wide motion
// cannot depend on an optional component being on screen.
//
// Injected once, id-guarded, straight into <head> — so it survives
// every unmount, which is the other half of the same lesson.
const SF_DUST_ID = "sf-dust-kf";
function ensureDustKeyframes() {
  if (typeof document === "undefined") return;          // SSR-safe
  if (document.getElementById(SF_DUST_ID)) return;
  const el = document.createElement("style");
  el.id = SF_DUST_ID;
  el.textContent = `
    /* ── D14 · THE FRACTURED SHIVER ────────────────────────────
       A being at effective Lyf 0 is standing and about to not be,
       and until now the only thing saying so was a hairline crack
       you had to already be looking at. This is sub-pixel on
       purpose — under half a pixel of travel, four seconds a
       cycle. You should never catch it moving; you should only
       notice that one card on the board is not still.

       Deliberately NOT a transform on the card itself: the card's
       transform is owned by arrival, strike and impact motion (ONE
       EVENT, ONE CHANNEL), and a second writer of that property
       would fight all three. This rides its own wrapper. */
    @keyframes sf-fractured-shiver {
      0%,100% { transform: translate3d(0, 0, 0) rotate(0deg); }
      25%     { transform: translate3d(0.4px, -0.3px, 0) rotate(0.18deg); }
      50%     { transform: translate3d(-0.3px, 0.4px, 0) rotate(-0.14deg); }
      75%     { transform: translate3d(0.3px, 0.3px, 0) rotate(0.1deg); }
    }

    /* ── D8 · THE TURN-TIMER HEARTBEAT ─────────────────────────
       The last ten seconds. Visual only — it changes no rule, ends
       no turn and cannot be interacted with. The pill already went
       red at 10; red is a STATE and it stops being news after two
       seconds of looking at it. A pulse is an EVENT, and it is
       still an event on the ninth one. */
    @keyframes sf-timer-heartbeat {
      0%,100% { transform: scale(1);     filter: brightness(1); }
      8%      { transform: scale(1.045); filter: brightness(1.35); }
      22%     { transform: scale(1);     filter: brightness(1); }
      30%     { transform: scale(1.025); filter: brightness(1.15); }
      44%     { transform: scale(1);     filter: brightness(1); }
    }

    /* THE DIAL STILL RULES (T17) and reduced motion is not a
       preference to be clever about. Both of these are pure
       decoration, so both simply stop. */
    @media (prefers-reduced-motion: reduce) {
      .sf-fractured-shiver, .sf-timer-heartbeat { animation: none !important; }
    }
  `;
  document.head.appendChild(el);
}
ensureDustKeyframes();

// Derive the ambient glow class (sf-holo-*) from the tier id — every tier in
// TIER_ORDER has a matching class in HoloStyle below. Unknown/absent → "".
const holoGlowClassFor = (holoTier) =>
  holoTier && HOLO_TIERS[holoTier] ? `sf-holo-${holoTier.slice(5)}` : "";

// CSS injected once into the game board — same class names as BoosterPack
function HoloStyle() {
  return (
    <style>{`
      /* HOLOFOIL MIGRATION (July 3) — the legacy sf-foil-* overlay classes
         and their keyframes (sf-holoShift / sf-silverSweep / sf-goldSweep /
         sf-voidPulse / sf-solrendPulse / sf-aeternus / sf-shadowP /
         sf-depthsPulse) are DELETED: in-duel foil now renders through the
         shared <HoloFoil> component (bespoke motion signatures, all 9 tiers).
         What remains here is the AMBIENT GLOW layer only — the per-tier
         box-shadow breathe on the card container, which HoloFoil does not
         own. sf-glowDepths + sf-glowJynxi added in the same pass (depths
         referenced a keyframe that never existed; jynxi was never mapped). */
      @keyframes sf-glowSilver {
        0%,100% { box-shadow:0 0 6px #aaaaaa55, 0 0 12px #cccccc33, 0 0 0 1px #aaaaaa88; }
        50%      { box-shadow:0 0 14px #ddddddaa, 0 0 28px #cccccc66, 0 0 0 1px #eeeeeecc; }
      }
      @keyframes sf-glowGold {
        0%,100% { box-shadow:0 0 10px #d4a84baa, 0 0 22px #d4a84b55, 0 0 0 1px #d4a84bcc; }
        50%      { box-shadow:0 0 20px #ffd700cc, 0 0 40px #d4a84b88, 0 0 0 2px #ffd700; }
      }
      @keyframes sf-glowMythic {
        0%   { box-shadow:0 0 16px #ff006688, 0 0 32px #7c3aed55, 0 0 0 2px #ff66cc; }
        33%  { box-shadow:0 0 16px #00ff8866, 0 0 32px #3b82f655, 0 0 0 2px #00ffcc; }
        66%  { box-shadow:0 0 16px #ffcc0066, 0 0 32px #ff660055, 0 0 0 2px #ffaa00; }
        100% { box-shadow:0 0 16px #ff006688, 0 0 32px #7c3aed55, 0 0 0 2px #ff66cc; }
      }
      @keyframes sf-glowVoid     { 0%,100%{box-shadow:0 0 14px #6600ccaa,0 0 28px #3b076466,0 0 0 2px #9333ea;} 50%{box-shadow:0 0 28px #9933ffcc,0 0 56px #6600cc88,0 0 0 2px #cc66ff;} }
      @keyframes sf-glowSolrend  { 0%,100%{box-shadow:0 0 14px #ff2266bb,0 0 28px #cc003355,0 0 0 2px #ff2266;} 50%{box-shadow:0 0 28px #ff4488dd,0 0 56px #ff226688,0 0 0 2px #ff88aa;} }
      @keyframes sf-glowAeternus { 0%,100%{box-shadow:0 0 12px #ffffff77,0 0 24px #ccccff44,0 0 0 2px #ffffffbb;} 50%{box-shadow:0 0 28px #ffffffcc,0 0 56px #ccccff88,0 0 0 2px #ffffff;} }
      @keyframes sf-glowShadow   { 0%,100%{box-shadow:0 0 14px #6600ccaa,0 0 28px #2d006655,0 0 0 2px #9966ff;} 50%{box-shadow:0 0 28px #9933cccc,0 0 56px #440099aa,0 0 0 2px #cc99ff;} }
      .sf-holo-silver  { animation:sf-glowSilver  3s ease-in-out infinite; }
      .sf-holo-gold    { animation:sf-glowGold    2.5s ease-in-out infinite; }
      .sf-holo-mythic  { animation:sf-glowMythic  2s linear infinite; }
      .sf-holo-void    { animation:sf-glowVoid    2.5s ease-in-out infinite; }
      .sf-holo-solrend { animation:sf-glowSolrend 2s ease-in-out infinite; }
      .sf-holo-aeternus{ animation:sf-glowAeternus 3s ease-in-out infinite; }
      .sf-holo-shadow  { animation:sf-glowShadow  2.5s ease-in-out infinite; }
      @keyframes sf-glowDepths   { 0%,100%{box-shadow:0 0 14px #00aaffaa,0 0 28px #00336655,0 0 0 2px #00aaff;} 50%{box-shadow:0 0 28px #33ccffcc,0 0 56px #00aaff88,0 0 0 2px #66ddff;} }
      @keyframes sf-glowJynxi    { 0%,100%{box-shadow:0 0 14px #2ee66baa,0 0 28px #16653455,0 0 0 2px #2ee66b;} 50%{box-shadow:0 0 28px #4ade80cc,0 0 56px #2ee66b88,0 0 0 2px #86efac;} }
      .sf-holo-depths  { animation:sf-glowDepths  2.5s ease-in-out infinite; }
      .sf-holo-jynxi   { animation:sf-glowJynxi   2.8s ease-in-out infinite; }
    `}</style>
  );
}


// Base-aware: a sealed being (taez_beb~0~7) resolves to its catalog card.
// This one line is why the seal is invisible to the other ~11,000 lines —
// every stat, art path, effect and render reads through here.
const getCard = (id) => {
  if (!id) return null;
  return ALL_CARDS_FLAT.find(c => c.id === baseId(id)) || null;
};

// Only Escendants carry a tally, so only Escendants are ever sealed.
const isEscendantId = (id) => getCard(id)?.type === "Escendant";

// ⭐ R70 ON THE WAY OUT — the player-side twin of NPC.js · stripSoulCard,
// deliberately the SAME shape so the two drivers cannot drift.
// "An Affinity is only attachable when the Natural L3 has its U'hlti."
// Attaching honours that; nothing honoured it when a card was TORN OFF.
// An Omnia counter (or any "ruin 1 soul card" effect) aimed at a U'hlti
// spliced exactly that one id out of the soul and left the Affinity
// worn by a being that no longer qualifies to wear it —
// GameStateValidator calls that state impossible (A06 / F08) and it is
// right. The Affinity rides out WITH its U'hlti, into the Necropolis
// where the rest of a torn soul goes.
//
// The reverse order is safe: tearing off an Affinity leaves a U'hlti
// that is perfectly legal on its own, so only the U'hlti case cascades.
// Returns { soul, gone } — `gone` is EVERY id that left, and every call
// site must send all of it to the Necropolis, not just the id it aimed
// at, or the Affinity vanishes from the game instead of being ruined.
// ⭐ THE TURN LEDGER — R11 · R18 · R67 · R89, one shape for all four.
//
// BLOCKER, found by an adversarial reviewer 2026-08-25: all four
// per-Turn allowances were gated by `useState(false)` locals ONLY. A
// refresh or a remount mid-Turn reset every one of them to false while
// `match.turn` never moved — a free second draw, a second Ascend, a
// second rite and a second paid Resurrection, available to anyone who
// pressed F5. React state is not a rule; it is a rendering convenience.
//
// The persisted shape is the one the R89 ledger already used:
//   <field>: { turn: <the match.turn it belongs to>, count: <n> }
// stamped, never cleared — a ledger whose `turn` does not match the
// match's is STALE and reads as 0 here, but it is still THERE, so
// "acted zero times this Turn" and "never wrote a ledger" stay
// distinguishable. That is the whole reason not to clear on turn change.
//
// ⚠ NAME COLLISION, DELIBERATELY ACCEPTED (twice). Debugger.js's
// simulator uses `ascendedThisTurn` and `resurrectedThisTurn` as plain
// INTEGERS on its own synthetic players (Legal.ascend `:262`,
// Legal.resurrect `:343`). Those objects are built by the simulator and
// never come from a match record, so nothing collides today — but if
// either is ever pointed at a REAL player, `{turn,count} >= 1` is
// `NaN >= 1`, which is false, and the gate silently passes forever.
// Read `.count` through turnLedgerCount, never the field directly.
// ⭐ R96 IN THE PLAYER'S OWN LANGUAGE. The Anima leg of a VOLUNTARY fare
// needs strictly MORE than the fare — a payment may not zero you — so a
// toast reading "Need 5 Anima" to a seat holding exactly 5 is a lie, and
// it is the lie a player acts on. One place words it; the Khao leg has no
// such floor and keeps saying exactly what it costs.
// (The Graveshift fare is R96's one exemption and does NOT use this.)
const animaFare = (n) => `${n + 1}+ Anima`;

const turnLedgerCount = (ledger, turn) =>
  (ledger && ledger.turn === turn) ? (Number(ledger.count) || 0) : 0;

const bumpTurnLedger = (ledger, turn, by = 1) =>
  ({ turn, count: turnLedgerCount(ledger, turn) + by });

const stripSoulCard = (soul, soulId) => {
  const card = getCard(soulId);
  const gone = [soulId];
  if (card?.type === "U'hlti") {
    (soul || []).forEach(id => { if (getCard(id)?.type === "Affinity") gone.push(id); });
  }
  return { soul: (soul || []).filter(id => !gone.includes(id)), gone };
};

// ── THE WRITE OVERSEER ────────────────────────────────────────
// Shadows the imported writer. Every ruin path in this file (58 sites)
// already funnels through updatePlayer, so sealing the payload HERE seals
// all of them without touching a single call site. Reads the live player
// off matchRef — no extra round trip.
let _sealMatchRef = null;

// ── THE LIVE READ (July 27, 2026) — C12's root cause, closed here ──
// `me` and `opponent` are RENDER-CLOSURE snapshots. Any handler that
// awaits a write and THEN clones `me.field` is cloning the board as it
// looked before that write — and writing the stale copy back on top of
// live state. Two bugs caP'n hit on July 27 were both this:
//   • an L2 ruined by a Lyt Attack read `soul: []` from the stale copy,
//     found no lower level, and sent the WHOLE Soul to the Necropolis
//     instead of descending;
//   • a Resurrected Lord landed on a slot the stale copy then nulled,
//     leaving a bare Escling where the being should stand.
// livePlayer() reads the subscription's newest snapshot and falls back
// to the closure only if the ref hasn't populated yet (first paint).
// Any handler that mutates `field` MUST source it from here.
const livePlayer = (idx, fallback = null) =>
  _sealMatchRef?.current?.players?.[idx] || fallback;

// The R08 backstop lives in `updatePlayer`, which is module scope, and the
// toaster is component state. One assignable slot bridges them: the
// component fills it on mount, headless drivers never do, and the refusal
// below is a no-op when nothing is listening. Deliberately a plain module
// binding rather than a React ref — nothing here re-renders on it.
let toastFromBackstop = null;
// ⚠ ONE SLOT, AND IT ASSUMES ONE LIVE BOARD. Two mounted Shatterfields
// would each hold a distinct `showToast`, and the second one's unmount
// would null a binding the first still wants — the backstop then goes
// quiet for the surviving board. Terror mounts exactly one duel screen at
// a time and always has; if that ever stops being true this becomes a Set
// rather than a slot. Said out loud rather than discovered later.

const updatePlayer = async (code, playerIdx, updates, opts = {}) => {
  const liveMatch = _sealMatchRef?.current || null;
  const live = liveMatch?.players?.[playerIdx] || null;

  // ══ THE R08 BACKSTOP — ONE CHOKEPOINT FOR "ATTACK IN ANY FORM" ══
  // caP'n, 2026-08-25: *"neither can Attack at all first turn"*, where
  // Attack is the broad word — *"a tome or Impera or anything to
  // 'attack' a being or your adversary directly"*.
  //
  // The gate closes every route that DECLARES an attack: the Lyt swing
  // (ATTACK), the Anima strike (ATTACK_ANIMA) and the Phantom/Beast
  // stamps (PRIMERA). But "anything" is not a list, and Terror has
  // roughly forty effect branches in this file that write damage
  // straight into an adversary's seat without ever calling themselves
  // an attack. Gating forty branches means forgetting the forty-first.
  //
  // So R08's last clause is enforced HERE, at the ONE function every
  // adversary write in this file already goes through — the same trick
  // the Second Wind uses on endMatch, and for the same reason: a rule
  // enforced at one chokepoint cannot be forgotten at the twentieth.
  // The test is on the PAYLOAD, not on the card: an adversary's Anima
  // going down, their Necropolis growing, or one of their beings losing
  // Lyf. Nothing else counts, and dimming your OWN Anima (a fare, the
  // Graveshift, Magma Carta) is untouched — R08 has never had an
  // opinion about what you do to yourself.
  //
  // ⚠ IT ONLY EVER LOOKS DURING THE OPENING CYCLE. Outside that window
  // the whole block is one boolean and returns immediately, so a normal
  // Turn pays nothing for it.
  //
  // ⚠ AND IT IS A BACKSTOP, NOT THE RULE. A refusal here means a route
  // reached damage without passing a gate, so it SAYS SO on the console
  // with the payload that tried it. A silent backstop is a bug farm.
  //
  // ⚠ ONE EDGE IT DELIBERATELY DOES NOT COVER, stated so nobody thinks
  // it was missed: a RESPONSE WINDOW (R92) in which the non-active seat
  // counters the active seat writes to the ACTIVE seat's node, so
  // `playerIdx === actor` and this block never looks. That is the right
  // answer — a Nihil/Omnia answers a DECLARATION, it does not Attack a
  // being or an adversary — but the reason is written down rather than
  // left to be rediscovered as a hole.
  if (liveMatch && isOpeningCycle(liveMatch) && live) {
    const actor = liveMatch.activePlayer;
    const isAdversary = actor != null && playerIdx !== actor
      && (teamOf(liveMatch, playerIdx) == null || teamOf(liveMatch, playerIdx) !== teamOf(liveMatch, actor));
    if (isAdversary) {
      // ⚠ THE EFFECTIVE ANIMA, NOT THE RAW SEAT FIELD. This compared
      // `updates.anima` against `live.anima` — the per-seat number —
      // while every attack site in this file computes its new total
      // from `opponent.anima`, which is withEffectiveAnima-wrapped and
      // therefore the POOLED team total in a shared-Anima 2v2. In that
      // mode the per-seat field is never written at all, so it sits at
      // its opening 30 forever: an attack that took a pooled team from
      // 50 to 45 wrote `anima: 45`, the backstop compared it against
      // 30, saw a RISE, and let an Attack through on the opening Cycle.
      const damage = adversaryDamageInPayload(withEffectiveAnima(liveMatch, live, playerIdx), updates);
      // ── THE DECLARED EXCEPTION ────────────────────────────────
      // `opts.notAnAttack` names a write whose PAYLOAD is
      // indistinguishable from an Attack but whose meaning is not one.
      // Exactly one shape uses it: a COMPLETED DEVOTION CONTRACT, where
      // the paying seat sends the finished Tome to ITS OWN OWNER'S
      // Necropolis (handleDevote below). Refusing that is worse than the
      // Attack it prevents — the payer's Anima has already left the
      // till. It is a DECLARATION, not a bypass: greppable, named, and
      // logged when it is used. Same word, same meaning, in
      // gameManager's seal — see the banner there.
      if (damage && opts.notAnAttack) {
        // eslint-disable-next-line no-console
        console.log(`[R08] allowed on a DECLARATION — seat ${actor} → seat ${playerIdx}: ${damage} (${opts.notAnAttack})`);
      } else if (damage) {
        // eslint-disable-next-line no-console
        console.error(`[R08] REFUSED an Attack on the opening Cycle — seat ${actor} → seat ${playerIdx}: ${damage}`);
        // ⚠ AND SAY SO TO THE PERSON HOLDING THE CARDS. This refusal used
        // to be a console line and nothing else, which meant the seat that
        // tried it had already paid the fare, watched its one-shot Tome
        // ride to the Necropolis, and been shown a SUCCESS toast for an
        // effect that never happened. The backstop is the right place for
        // the refusal — it is the one door every driver passes through —
        // but a silent correct refusal reads to a player as the game
        // eating their card.
        // ⚠ "THAT ATTACK", NOT "YOUR ATTACK". This refusal fires on a write
        // TO a seat caused by whoever is ACTING — which on the adversary's
        // half-turn is the adversary. Phrasing it as though the reader did
        // it would tell a player they broke a rule they never touched.
        toastFromBackstop?.(
          "⚖ No Attacks of any kind on the opening Turn — not a Lyt Attack, not a Tome, not a stamp. That attack did not resolve.",
          SD.red,
        );
        return null;
      }
    }
  }

  // THE SHARED POOL (2v2 — caP'n: "they share Anima and not Khao").
  // Every anima write in this file — curios, attacks, prophecy checks,
  // ~40 call sites — already calls this exact function the same exact
  // way. This is the ONE place that needed to learn teams exist at
  // all: when this seat pools its Anima with a teammate, the `anima`
  // key in `updates` gets redirected to the team's one shared total
  // instead of this seat's own players/{idx}/anima; everything else in
  // the same update still lands on this seat, unchanged.
  // ── THE SEAL'S VIEW NEVER LAGS ITS OWN WRITE (2026-09-15) ────────
  // `matchRef.current` is refreshed by React when the listener's snapshot
  // re-renders — asynchronously. A resolver that writes twice inside one
  // activation (Light Casting Shadow: an Impera, THEN a U'hlti) read the
  // seat between the writes and could see the pre-first-write board, so
  // the second tutor overwrote the first. The one-resolver differential
  // caught it on its first run. After every seat write the payload is
  // merged into the seal's own view of that seat, top-level keys only
  // (slash keys regrouped through rules.deslashPayload), on a fresh object
  // so nothing React holds is mutated. The listener's snapshot still wins
  // when it arrives; this only closes the gap before it does.
  const reflect = (idx, payload) => {
    try {
      const m = _sealMatchRef?.current;
      const seat = m?.players?.[idx];
      if (!m || !seat || !payload) return;
      // Deep-merge so `field/tomes/1: null` lands on ONE rift of a copied
      // array instead of replacing the whole tomes array with `{1:null}`.
      const deep = (base, patch) => {
        if (patch === null || typeof patch !== "object") return patch;
        const out = Array.isArray(base) ? [...base] : { ...(base && typeof base === "object" ? base : {}) };
        for (const [k, v] of Object.entries(patch)) out[k] = deep(out[k], v);
        return out;
      };
      const next = deep(seat, deslashPayload(payload));
      _sealMatchRef.current = { ...m, players: { ...m.players, [idx]: next } };
    } catch (_) { /* a view is a convenience; the write already landed */ }
  };
  if (updates && Object.prototype.hasOwnProperty.call(updates, "anima") && sharesAnimaWithTeam(liveMatch, playerIdx)) {
    const { anima, ...rest } = updates;
    const teamId = teamOf(liveMatch, playerIdx);
    const writes = [updateTeamAnima(code, teamId, anima)];
    if (Object.keys(rest).length) writes.push(updatePlayerRaw(code, playerIdx, routeExileOnWrite(sealPayload(rest, live, isEscendantId), live, isEscendantId), opts));
    const results = await Promise.all(writes);
    reflect(playerIdx, updates);
    return results[results.length - 1];
  }
  // ══ R113 · THE EXILE ROUTER — THE SECOND CHOKEPOINT ON THIS LINE ══
  // A card that has already been bought back out of the Exile rift once
  // does not return to the Necropolis when it is next ruined; it goes back
  // into the rift, forever. There are ~45 places in this file that write
  // an id into a Necropolis and three more that re-implement the descent
  // funnel by hand, and teaching all forty-eight would have been a
  // discipline rather than a rule. It is asked HERE instead, on the same
  // line as the seal and for exactly the same reason.
  //
  // ORDER MATTERS AND IT IS NOT ARBITRARY: seal first, route second. The
  // seal is what gives a being its per-copy identity, and the Mark is
  // keyed by that identity — route before sealing and a freshly-ruined
  // being would be compared as a bare name against a list of sealed ids,
  // and every copy of it would be diverted instead of the one that earned
  // the Mark.
  const sealed = routeExileOnWrite(sealPayload(updates, live, isEscendantId), live, isEscendantId);
  const result = await updatePlayerRaw(code, playerIdx, sealed, opts);
  if (result !== null) reflect(playerIdx, sealed);   // an R08 refusal returns null and reflects nothing
  return result;
};

// ── riftArray — Firebase array-hole normalizer (July 2026) ──
// RTDB deletes null entries and returns sparse integer-keyed OBJECTS
// for arrays like tomes [null, x, null] → {1:x}. Coerce any shape
// (array, sparse object, undefined) into a fixed-length array with
// explicit nulls in the holes. Used at the match-subscription
// chokepoint so every downstream `.map`/`.some`/`.findIndex` is safe.
const riftArray = (v, len) => {
  const out = new Array(len).fill(null);
  if (Array.isArray(v)) {
    for (let i = 0; i < len; i++) out[i] = v[i] ?? null;
  } else if (v && typeof v === "object") {
    Object.keys(v).forEach(k => {
      const n = parseInt(k, 10);
      if (n >= 0 && n < len) out[n] = v[k];
    });
  }
  return out;
};

// ── R101 · DELETED: the first of three copies of the Deviant fare ──
// This was `deviantCost(level, traits)`, the Training table. It is now
// rules.deviantFare(traits, level) — ONE table, read by this file's two
// payment sites, by NPC.js, by RulesEnforcer.validateAscend and by the
// Rulebook's printed page. It also could not express the schedule
// caP'n confirmed in Rulez v2 ch.15: the L3 / 0-trait cell is
// 5 KHAO OR 4 ANIMA and this returned a single number.

const formatTime = (secs) => {
  const v = Math.max(0, secs); // MP-FIX: grace window counts negative — display floor at 0:00
  const m = Math.floor(v / 60);
  const s = v % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

// ── Effective-stat helpers at module scope ─────────────────────────
// Pure functions that compute the runtime Lyt / Lyf of a being taking
// all active buffs into account: wielder-bonded Treasures (Gauntlets),
// soul Impera (Xulan Diamond Skin, H-09 Pulsar, Premier Dragon, etc.),
// and U'hltis (Z4Z). Used both by combat logic and by FieldCard for
// displaying the up-to-date numbers on the card itself.
//
// Both take (slot, myField). slot is the escling slot object; myField
// is the player's full field. Neither function touches the opponent
// or any React state — completely pure.
// Counts beings by SCHOOL (Space / Phantom / Dragon / Time). Named for
// the Ilk axis until 2026-08-25 — every caller below has always passed
// a Family-B word, and `card.school` is now where those words live.
const _countBeingsOfSchool = (field, cls) =>
  (field?.esclings || []).filter(s => { const c = getCard(s?.escendant); return c && c.school === cls; }).length;

const getEffectiveLytPure = (slot, myField, oppField = null) => {
  const card = getCard(slot?.escendant);
  if (!card) return 0;
  let lyt = card.lyt || 0;
  // Gauntlets of A'zka bonded to this slot
  const mySlotIdx = (myField?.esclings || []).findIndex(s => s === slot);
  if (mySlotIdx >= 0) {
    const hasGauntletsBonded = (myField?.tomes || []).some(t =>
      t?.id === "tome_gauntlets_azka" && t?.wieldedBy === mySlotIdx && !t?.faceDown
    );
    if (hasGauntletsBonded) lyt += 2;
  }
  // Legacy soul-slot Gauntlets (pre-Wave-1 fallback)
  const hasGauntletsLegacy = (slot?.soul || []).some(id => id === "tome_gauntlets_azka");
  if (hasGauntletsLegacy) lyt += 2;
  // Premier Dragon Beb — +1 Lyt per Dragon Being you have in combat (own field only)
  if (card.id === "premier_dragon_beb") {
    const hasGoodMorning = (slot?.soul || []).some(id => id === "p_premier_dragon_1");
    if (hasGoodMorning) lyt += _countBeingsOfSchool(myField, "Dragon");
  }
  // Premier Dragon Champion — +1 Lyt per Dragon Being in combat (BOTH fields)
  if (card.id === "premier_dragon_champion") {
    const hasSocialDragonfly = (slot?.soul || []).some(id => id === "p_premier_dragon_2");
    if (hasSocialDragonfly) {
      lyt += _countBeingsOfSchool(myField, "Dragon");
      lyt += _countBeingsOfSchool(oppField, "Dragon");
    }
  }
  // Z4Z Lord U'hlti Apocalypse Now — +1 Lyt per Time Being in combat (BOTH fields)
  const hasZ4zUhlti = (slot?.soul || []).some(id => id === "u_z4z_lord");
  if (hasZ4zUhlti) {
    lyt += _countBeingsOfSchool(myField, "Time");
    lyt += _countBeingsOfSchool(oppField, "Time");
  }
  // Dragon Primera stat-shift (H22): +1 from the Ilk stamp, −1 from the
  // School stamp — signed, lasts the Turn, cleared by both end-turn resets.
  lyt += slot?.primeraLytBuff || 0;
  return Math.max(0, lyt);
};

const getEffectiveLyfPure = (slot, myField) => {
  const card = getCard(slot?.escendant);
  if (!card) return slot?.currentLyf ?? 0;
  let lyf = slot?.currentLyf !== null && slot?.currentLyf !== undefined ? slot.currentLyf : (card.lyf ?? 0);
  // Xulan Wanderer Diamond Skin — +2 Lyf passive
  const hasDiamondSkin = (slot?.soul || []).some(id => id === "p_xulan_1");
  if (hasDiamondSkin) lyf += 2;
  // Cosmic Casbah (Impera on H-09 Beb) — TEAM AURA: "Your beings gain +1 Lyf,
  // for every Astro Being you have in combat." If ANY of my beings has this
  // Impera attached, every one of my beings gains +1 Lyf per Astro Being.
  const anyCosmicCasbah = (myField?.esclings || []).some(s =>
    (s?.soul || []).some(id => id === "p_h09_1")
  );
  if (anyCosmicCasbah) lyf += _countBeingsOfSchool(myField, "Space");
  // Premier Dragon Beb — +1 Lyf per Dragon Being (when impera attached)
  if (card.id === "premier_dragon_beb") {
    const hasPremierImpera = (slot?.soul || []).some(id => id === "p_premier_dragon_1");
    if (hasPremierImpera) lyf += _countBeingsOfSchool(myField, "Dragon");
  }
  // Dragon Primera stat-shift (H22): +1 Lyf from the School stamp for the Turn
  lyf += slot?.primeraLyfBuff || 0;
  return lyf;
};

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// Draw N random cards from grimoire — removes them from grimoire, returns {drawn, newGrimoire}
// ── Shared trait helpers for Deviant/Natural checks ──────────
const sharesSchoolAndIlk = (card, escling) => {
  if (!card || !escling) return false;
  return card.school === escling.school && card.ilk === escling.ilk;
};

const sharedTraits = (cardA, cardB) => {
  if (!cardA || !cardB) return 0;
  let traits = 0;
  if (cardA.school === cardB.school) traits++;
  if (cardA.ilk === cardB.ilk) traits++;
  return traits;
};

// ── R101 · DELETED: the second copy — `deviantAscendCost(traits,
// level)`. Same table, transposed arguments, same missing asymmetry.
// rules.deviantFare is the only one left.

const drawFromGrimoire = (grimoire, count = 1) => {
  const pool = Object.entries(grimoire || {}).flatMap(([id, qty]) => Array(qty).fill(id));
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  const drawn = shuffled.slice(0, count);
  const newGrimoire = { ...grimoire };
  drawn.forEach(id => {
    if (newGrimoire[id] > 1) newGrimoire[id]--; else delete newGrimoire[id];
  });
  return { drawn, newGrimoire };
};

const drawCards = (grimoire, hand, count) => {
  const pool = [];
  Object.entries(grimoire || {}).forEach(([id, qty]) => {
    for (let i = 0; i < qty; i++) pool.push(id);
  });
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

// ── C17 pacing. Both DIALS. The heartbeat is deliberately far
// shorter than the silence window so a single dropped write can
// never look like an abandonment, and the window is deliberately
// longer than the 10s PvP disconnect grace the Strike Language's
// HOLD is capped under — an animation chord must never be able to
// spend a player into looking absent.
const HEARTBEAT_MS = 8000;
const ABANDON_MS   = 45000;

const buildInitialEsclings = (grimoire) => {
  const deckCards = Object.keys(grimoire || {});
  const firstEscendant = ESCENDANTS.find(e => deckCards.includes(e.id));
  const defaultEscling = firstEscendant
    ? (ESCLINGS.find(e => e.ilk === firstEscendant.ilk) || ESCLINGS[0])
    : ESCLINGS[0];
  // R110 — the battle line is ESCLING_SLOTS wide, not three.
  return Array.from({ length: ESCLING_SLOTS }, (_, i) => ({
    id: `slot${i}`, escling: defaultEscling.id, escendant: null,
    soul: [], soulUsed: {}, currentLyf: null, attackedThisTurn: false, deviant: false, natural: true, intangible: false,
  }));
};

const smartPos = (x, y, margin = 110) => ({
  x: Math.min(Math.max(x, margin), window.innerWidth - margin),
  y: Math.min(Math.max(y, margin), window.innerHeight - margin),
});

// Responsive sizes — shrink on narrow screens
const isMobile = typeof window !== "undefined" && window.innerWidth < 480;
const SIZES = isMobile ? {
  normal: { width: 56,  height: 78  },
  small:  { width: 42,  height: 58  },
  tiny:   { width: 32,  height: 45  },
  hand:   { width: 48,  height: 67  },
} : {
  normal: { width: 88,  height: 122 },
  small:  { width: 66,  height: 92  },
  tiny:   { width: 50,  height: 70  },
  hand:   { width: 76,  height: 106 },
};

// Check if a card image exists in public/cards/
// ── ART URL (July 28, 2026) ───────────────────────────────────
// Was a second hardcoded copy of the CDN domain. It is now the one
// shared builder, which also strips the Necro Seal: `topNecroImg`
// below is fed the TOP OF THE NECROPOLIS, and every being sitting
// there is sealed by definition — so the ruin pile has been showing
// a broken picture for every ruined being since the seal shipped.
const cardImageUrl = (cardId) => cardId ? cardUrl(cardId) : null;

function CardBack({ size = "normal", flipped = false }) {
  const dim = SIZES[size] || SIZES.normal;
  return (
    <div style={{ ...dim, borderRadius: 10, overflow: "hidden", border: `1px solid ${GM.inkBrown}66`, transform: flipped ? "rotate(180deg)" : "none", position: "relative" }}>
      <img
        src={cdnUrl("card_back.webp")}
        alt="card back"
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        onError={e => {
          // Fallback to generated design if image missing
          e.target.style.display = "none";
          e.target.parentNode.style.background = `linear-gradient(145deg, ${GM.page}, ${GM.pageWarm})`;
          e.target.parentNode.style.display = "flex";
          e.target.parentNode.style.alignItems = "center";
          e.target.parentNode.style.justifyContent = "center";
          e.target.parentNode.innerHTML = `<div style="width:70%;height:70%;border:1px solid ${SD.void4};border-radius:6px;display:flex;align-items:center;justify-content:center"><span style="color:${SD.purpleDim};font-size:${dim.width * 0.3}px">◆</span></div>`;
        }}
      />
      {/* MAGNUM PASS 2 — gilded inner hairline so backs share the frame language */}
      <div style={{ position: "absolute", inset: 0, borderRadius: 10, boxShadow: "inset 0 0 0 1px rgba(200,168,75,0.18)", pointerEvents: "none" }} />
    </div>
  );
}

// ── HoverCardPreview — the centered "peek." Client-only chrome: the
// opponent never sees this, it never touches Firebase, and it never
// shows anything the small card on the table didn't already show —
// faceDown/hidden cards render the shared back, never the real face.
// Deliberately NOT the CardDetailModal: no dim shell, no close button,
// no text panel — just the art, big, gone the instant the cursor leaves.
function HoverCardPreview({ info }) {
  const [imgFailed, setImgFailed] = useState(false);
  const revealedId = info && !info.faceDown ? info.cardId : null;
  useEffect(() => { setImgFailed(false); }, [revealedId]);
  if (!info) return null;
  const card = revealedId ? getCard(revealedId) : null;
  const showBack = !revealedId || !card;
  const typeColor = card ? (TYPE_COLORS_SD[card.type] || GM.vellumFaint) : GM.inkGold;
  return (
    <div style={{
      position: "fixed", left: "50%", top: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 350, pointerEvents: "none",
      width: "min(30vw, 300px)", minWidth: 190, aspectRatio: "2 / 3",
      borderRadius: 14, overflow: "hidden",
      border: `1px solid ${showBack ? GM.inkGold + "88" : typeColor}`,
      boxShadow: `0 20px 55px rgba(0,0,0,0.6), 0 0 40px ${(showBack ? GM.inkGold : typeColor)}33`,
      animation: "sf-hoverPeekIn 0.14s ease-out both",
    }}>
      {showBack || imgFailed ? (
        <img src={cdnUrl("card_back.webp")} alt="card back"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          onError={e => { e.target.style.display = "none"; }} />
      ) : (
        <img src={cardImageUrl(revealedId)} alt={card.name}
          onError={() => setImgFailed(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      )}
      {/* gilded inner hairline, matching CardBack's frame language */}
      <div style={{ position: "absolute", inset: 0, borderRadius: 14, boxShadow: "inset 0 0 0 1px rgba(200,168,75,0.2)", pointerEvents: "none" }} />
    </div>
  );
}

// ── Field3DWithHoverPreview — thin host around <Shatterfield3D>. Owns
// the hover state LOCALLY so a hover in/out never re-renders the giant
// Shatterfield component above it (ie-realtime-perf: state changes stay
// as close to their own DOM as possible). handleCardHover is stable via
// useCallback + a functional setState updater, so the Context value
// Shatterfield3D hands to every Card3D on the field never changes
// identity — hovering never re-renders the 3D scene itself, only this
// small wrapper and the peek it draws next to it.
//
// ── THE PEEK STANDS DOWN WHILE YOU ACT (July 26) ──────────────────
// caP'n: "when I click my card's radial to use Lyt Attack, Primera,
// doesn't matter, I am attempting an action now, the hover thing
// should be disabled now until I either cancel or finish my action.
// Clicking on grimoire to search counts, all that."
//
// He's right, and the reason is sharper than "it's in the way": the
// peek is a READING tool and every one of those moments is a CHOOSING
// moment. Mid-action the cursor is travelling to a target, so it drags
// a 200px card portrait across the exact half of the board being aimed
// at — the preview covers the thing the tap is for. Worse, it fights
// the radial and the modals for the same screen space while the board
// is trying to tell you what's legal.
//
// `busy` is derived ONCE in the parent from every state that means
// "an action is underway" (see actionInProgress) rather than being
// re-derived per surface — one predicate, one place, no drift. When it
// is set the peek is both hidden AND cleared, so releasing the action
// never pops a stale portrait for a card the cursor left long ago.
// ── THE PEEK WAITS A BEAT AFTER YOU ACT (H23, July 27) ────────────
// caP'n: "let's make sure that mouse hover card preview is delayed by
// 1 second after any action takes place so the card preview doesn't
// immediately spam the screen ;3"
//
// The suppression above was necessary and not sufficient. Releasing an
// action puts the cursor wherever the last click LANDED — on a card,
// almost always, because that is what you were aiming at — so the peek
// used to snap back on the instant the radial closed, over the exact
// board you were trying to read the result on. The fix is a cooldown,
// not a longer suppression: one second of quiet after the action, then
// the peek behaves normally again.
//
// It is a DWELL, not a mute: the gate re-arms itself if the cursor is
// still resting on a card when the second elapses, so a player who
// parks on a card still gets their portrait — just a beat later, on
// purpose. (1000ms is a dial. Settle it with a mouse in hand.)
const PEEK_COOLDOWN_MS = 1000;

function Field3DWithHoverPreview({ busy = false, ...props }) {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [gated, setGated] = useState(false);
  const lastHoverRef = useRef(null);
  const handleCardHover = useCallback((payload) => {
    lastHoverRef.current = (payload && !payload.clear) ? payload : null;
    setHoveredCard(prev => {
      if (!payload || payload.clear) {
        return (!payload || !prev || prev.token === payload.token) ? null : prev;
      }
      return payload;
    });
  }, []);
  // Drop whatever was being peeked the instant an action begins. The 3D
  // board keeps REPORTING hovers (its pointer handlers are unchanged and
  // cost nothing); this host simply stops listening to them, which keeps
  // the suppression entirely on the DOM side where it belongs.
  useEffect(() => {
    if (busy) { setHoveredCard(null); setGated(true); return undefined; }
    // Action released — start the quiet second, then hand the peek
    // back to whatever the cursor is ACTUALLY on now (which may well
    // be nothing, or a different card than when the action started).
    const t = setTimeout(() => {
      setGated(false);
      setHoveredCard(lastHoverRef.current);
    }, PEEK_COOLDOWN_MS);
    return () => clearTimeout(t);
  }, [busy]);
  const hidden = busy || gated;
  return (
    <>
      <Shatterfield3D {...props} onCardHover={handleCardHover} />
      <HoverCardPreview info={hidden ? null : hoveredCard} />
    </>
  );
}


// ── StatGlyph / StatChip — MERGED onto engine/StatGlyph.js (July 3) ──
// (Magnum Pass 2's bespoke stat gems — the crisp SVG replacements for the
// OS-dependent 🛡⚔♥✦ emoji; `color` drives the fill so buffed-gold /
// fractured-red tinting still works.)
// The private twins that lived here (extracted VERBATIM in Combo Run #2)
// are deleted; the canonical pair is imported at the top of this file.
// Identical SVG geometry by construction — the house stat gems have ONE home.

function FieldCard({ cardId, prevCardId = null, faceDown = false, size = "normal", onClick, highlight = false, fallen = false, currentLyf = null, attacked = false, soulCount = 0, flipped = false, intangible = false, holoTier = null, revived = false, effectiveLyt = null, effectiveLyf = null, deviant = false, heretical = false, stamps = null }) {
  // (art load/fallback state lives inside CardFace now — ONE FACE law)

  // ── Pass 2 — Animation state ────────────────────────────────
  // Refs start at null so the FIRST real value triggers the diff effect.
  // If we init with cardId/currentLyf, the first render's prev == curr and
  // no animation ever fires (was the bug in the first Pass 2 ship).
  const prevCardIdRef = useRef(null);
  const prevLyfRef    = useRef(null);
  const prevFallenRef = useRef(false);
  const prevAttackedRef = useRef(false);
  const mountedRef    = useRef(false); // skip the very first render for damage/shatter only
  const [settlePlaying,  setSettlePlaying]  = useState(false);
  const [shatterPlaying, setShatterPlaying] = useState(false);
  const [lungePlaying,   setLungePlaying]   = useState(false);
  const [dmgFloats,      setDmgFloats]      = useState([]); // [{ id, amount }]
  // ── LAST RITES PASS — reaction FX ──────────────────────────
  // hitPlaying: the struck card flinches + an ilk-tinted slash streaks
  // across it the instant its Lyf drops. The victim REACTS — the attack
  // finally lands somewhere, not just in a number.
  // healFloats: soft blue +N when Lyf rises — makes R28's end-of-turn
  // "Lyf returns home" visible as a gentle synchronized breath.
  const [hitPlaying,     setHitPlaying]     = useState(false);
  const [healFloats,     setHealFloats]     = useState([]); // [{ id, amount }]

  // Pass 2.5 — detect attackedThisTurn flipping false→true.
  // That's the exact tick when the card just swung; fire a short lunge.
  useEffect(() => {
    const prev = prevAttackedRef.current;
    prevAttackedRef.current = attacked;
    if (mountedRef.current && !prev && attacked && cardId) {
      setLungePlaying(true);
      const t = setTimeout(() => setLungePlaying(false), 360);
      return () => clearTimeout(t);
    }
  }, [attacked, cardId]);

  // Detect card entering slot OR card transforming (ascension, transcendence).
  // Every cardId change triggers settle — this way ascensions, transcendences,
  // and mid-turn replacements all get their moment. Skips the very first render
  // only if cardId was already present (resuming an existing match mid-state).
  const [transitionType, setTransitionType] = useState(null); // null | "transcend" | "ascend" | "revive"
  useEffect(() => {
    // Parent-supplied prevCardId wins (survives our own remount on key change).
    // Internal ref is only used as fallback for cases where the parent doesn't
    // provide prevCardId (e.g. tome/curio/prophecy slots we haven't wired yet).
    const prev = prevCardId || prevCardIdRef.current;
    const changed = prev !== cardId;
    if (changed && cardId) {
      // Determine transition type by comparing the prev and curr card objects.
      //
      // Priority order (Pass 4):
      //   1. Holo tier present → use holo-specific glow (overrides everything)
      //   2. Revival flag → purple-black-green
      //   3. Escling → Escendant → silver (transcend)
      //   4. Escendant level 1 → level 2 (Beb → Champion) → gold (ascend)
      //   5. Escendant level 2 → level 3 (Champion → Lord) → rainbow (lord)
      const prevCard = prev ? getCard(prev) : null;
      const currCard = getCard(cardId);
      let tType = null;

      // Detect structural transition first (needed to decide if glow fires at all)
      let structural = null;
      if (prevCard && currCard) {
        if (prevCard.type === "Escling" && currCard.type === "Escendant") {
          structural = "transcend";
        } else if (prevCard.type === "Escendant" && currCard.type === "Escendant"
                 && typeof prevCard.level === "number" && typeof currCard.level === "number"
                 && currCard.level > prevCard.level) {
          // Distinguish Beb→Champion (level 1→2) from Champion→Lord (level 2→3)
          structural = currCard.level >= 3 ? "lord" : "ascend";
        }
      }

      if (revived) {
        // Necropolis revival takes priority over structural transitions
        tType = "revive";
      } else if (holoTier && structural) {
        // Holo card with a real transition → use holo-specific glow
        tType = `holo_${holoTier.replace(/^holo_/, "")}`;
      } else if (structural) {
        tType = structural;
      } else if (currCard?.type === "Tome") {
        // LAST RITES — a Tome entering the rift is a CASTING, not a mere
        // placement. Amethyst rune-burst (J'ynxi's color — the narrator's
        // ink) so spellwork reads distinct from beings taking the field.
        tType = "tome";
      }

      setTransitionType(tType);
      setSettlePlaying(true);
      const t = setTimeout(() => { setSettlePlaying(false); setTransitionType(null); }, 1350);
      prevCardIdRef.current = cardId;
      return () => clearTimeout(t);
    }
    prevCardIdRef.current = cardId;
  }, [cardId, prevCardId, revived, holoTier]);

  // Detect Lyf drops → queue a floating damage number.
  // Skip the first render (no "drop" if we just got here).
  useEffect(() => {
    const prev = prevLyfRef.current;
    const next = currentLyf;
    if (mountedRef.current && cardId && typeof prev === "number" && typeof next === "number" && next < prev) {
      const delta = prev - next;
      const id = Date.now() + Math.random();
      setDmgFloats(df => [...df, { id, amount: delta }]);
      setTimeout(() => {
        setDmgFloats(df => df.filter(f => f.id !== id));
      }, 1500);
      // LAST RITES — the victim flinches and an ilk slash streaks across.
      // Fires on BOTH clients (pure Firebase state diff, no extra writes).
      setHitPlaying(true);
      setTimeout(() => setHitPlaying(false), 460);
    }
    // LAST RITES — Lyf rose (heal, buff, or R28 end-of-turn regeneration).
    // Softer than damage on purpose: small, blue, brief — a breath, not a hit.
    if (mountedRef.current && cardId && typeof prev === "number" && typeof next === "number" && next > prev) {
      const gain = next - prev;
      const id = Date.now() + Math.random();
      setHealFloats(hf => [...hf, { id, amount: gain }]);
      setTimeout(() => {
        setHealFloats(hf => hf.filter(f => f.id !== id));
      }, 1300);
    }
    prevLyfRef.current = next;
  }, [currentLyf, cardId]);

  // Detect fractured / fallen → shatter. Skip first render.
  useEffect(() => {
    const wasAlive = !prevFallenRef.current && (prevLyfRef.current == null || prevLyfRef.current > 0);
    const nowGone  = fallen || currentLyf === 0;
    if (mountedRef.current && cardId && wasAlive && nowGone) {
      setShatterPlaying(true);
      const t = setTimeout(() => setShatterPlaying(false), 650);
      prevFallenRef.current = fallen;
      return () => clearTimeout(t);
    }
    prevFallenRef.current = fallen;
  }, [fallen, currentLyf, cardId]);

  // Mark mounted AFTER the first render's effects have run, so subsequent
  // effects can distinguish "just mounted" from "genuine change".
  useEffect(() => {
    mountedRef.current = true;
  }, []);

  const card = getCard(cardId);
  const dim = SIZES[size] || SIZES.normal;
  const lyf = currentLyf !== null ? currentLyf : (card?.lyf ?? null);
  const fractured = lyf === 0;
  // ── Display stats — use effective values when provided so Gauntlets
  // (+2 Lyt), Diamond Skin (+2 Lyf), etc. show on the card face itself.
  // When effective > base, we tint the number gold so the buff is obvious.
  const baseLyt = card?.lyt ?? null;
  const displayLyt = effectiveLyt !== null && effectiveLyt !== undefined ? effectiveLyt : baseLyt;
  const lytBuffed = baseLyt !== null && displayLyt !== null && displayLyt > baseLyt;
  const baseLyfForBuff = card?.lyf ?? null;
  const displayLyf = effectiveLyf !== null && effectiveLyf !== undefined ? effectiveLyf : lyf;
  const lyfBuffed = baseLyfForBuff !== null && displayLyf !== null && displayLyf > baseLyfForBuff && !fractured;
  const holo = HOLO_TIERS[holoTier] || null;
  const holoGlowClass = holoGlowClassFor(holoTier); // all 9 tiers, registry-driven

  if (faceDown || !card) return (
    <div onClick={onClick} style={{ cursor: onClick ? "pointer" : "default" }}>
      <CardBack size={size} flipped={flipped} />
    </div>
  );
  // (border / gild / boxShadow / art URL all live in CardFace now —
  //  ONE FACE law. This function only composes duel-side FX around it.)
  const wrapperTransform = flipped ? "rotate(180deg)" : "none";

  // Compose animation classes onto the outer wrapper
  // All glow classes are mutually exclusive — only one applies per transition.
  const transitionGlowClass =
      transitionType === "transcend"          ? "sf-glow-transcend"
    : transitionType === "ascend"             ? "sf-glow-ascend"
    : transitionType === "lord"               ? "sf-glow-lord"
    : transitionType === "revive"             ? "sf-glow-revive"
    : transitionType === "tome"               ? "sf-glow-tome"
    // holo_* placement bursts derive from the tier id — all 9 tiers covered
    // (sf-glow-holo-jynxi added with the HoloFoil migration).
    : transitionType && transitionType.startsWith("holo_") ? `sf-glow-holo-${transitionType.slice(5)}`
    : "";
  const fxClass = [
    settlePlaying  ? "sf-card-settle"  : "",
    shatterPlaying ? "sf-card-shatter" : "",
    lungePlaying   ? "sf-card-lunge"   : "",
    // Recoil never fights the shatter — a ruined card's death animation
    // owns the frame; the flinch is for survivors.
    hitPlaying && !shatterPlaying ? "sf-card-recoil" : "",
    transitionGlowClass,
  ].filter(Boolean).join(" ");

  return (
    <div style={{ position: "relative", display: "inline-block", transform: wrapperTransform, transition: "all 0.2s" }}>
      {/* D14 · THE FRACTURED SHIVER rides THIS div, and it YIELDS.
          The outer wrapper's transform belongs to arrival/strike/
          impact motion; this inner one is free — but only while
          nothing else is playing on it. `fxClass` running means an
          event is happening to this card, and ONE EVENT ONE CHANNEL
          says an idle tremble does not get to argue with it. The
          shiver is what the card does when nothing is happening,
          which is exactly when "this being is about to die" is the
          thing worth saying. */}
      <div className={fxClass} style={{
        position: "relative", display: "inline-block",
        animation: (fractured && !fallen && !shatterPlaying && !fxClass && card?.type === "Escendant")
          ? "sf-fractured-shiver 4s ease-in-out infinite" : undefined,
      }}>
      {/* ⬡ THE ONE FACE (July 24 ruling) — the card body is CardRenderer's
          <CardFace>, the exact component the shop / Codex / Vault / Deck
          Builder and the 3D Shatterfield mount. This file keeps only what
          a duel ADDS to a card: transition FX, floats, veils, pips. */}
      <CardFace
        card={card} cardId={cardId} holoTier={holoTier}
        width={dim.width} height={dim.height}
        size={size} mobile={isMobile} onClick={onClick}
        className={holoGlowClass}
        highlight={highlight} fractured={fractured} attacked={attacked}
        intangible={intangible} flipped={flipped}
        displayLyf={displayLyf} displayLyt={displayLyt}
        lyfBuffed={lyfBuffed} lytBuffed={lytBuffed}
        stamps={stamps}
      />
      {/* Shatter cracks — sweep across the card as it ruins */}
      {shatterPlaying && (
        <svg className="sf-card-shatter-overlay" viewBox="0 0 100 140" style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          pointerEvents: "none", zIndex: 6, borderRadius: 10, overflow: "visible",
        }}>
          <path d="M 12 8 L 48 52 L 30 78 L 62 100 L 50 132"
                stroke={SD.red} strokeWidth="1.6" fill="none" opacity="0.95"
                strokeLinecap="round" />
          <path d="M 78 18 L 56 46 L 72 74 L 44 96 L 68 128"
                stroke="#ff6655" strokeWidth="1.2" fill="none" opacity="0.85"
                strokeLinecap="round" />
          <path d="M 30 4  L 58 30 L 42 60"
                stroke="#ffaa88" strokeWidth="0.8" fill="none" opacity="0.7"
                strokeLinecap="round" />
        </svg>
      )}
      {/* LAST RITES — School-tinted strike slash. Two crossing streaks in the
          STRUCK being's School color: every hit carries the victim's blood-hue.
          Space cyan, Phantom violet, Dragon crimson, Time amber. (Read off
          `card.school` since the 2026-08-25 field-name fix; same four hues,
          same beings — the words never moved, only their label did.) */}
      {hitPlaying && !shatterPlaying && (() => {
        const ilkC = SCHOOL_COLORS_SD[card.school] || SD.red;
        return (
          <svg className="sf-ilk-slash" viewBox="0 0 100 140" style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            pointerEvents: "none", zIndex: 7, overflow: "visible",
          }}>
            <path d="M 4 118 L 96 22" stroke={ilkC} strokeWidth="2.4" fill="none"
                  strokeLinecap="round" opacity="0.95"
                  style={{ filter: `drop-shadow(0 0 6px ${ilkC})` }} />
            <path d="M 18 132 L 88 58" stroke={ilkC} strokeWidth="1.2" fill="none"
                  strokeLinecap="round" opacity="0.7"
                  style={{ filter: `drop-shadow(0 0 4px ${ilkC})` }} />
          </svg>
        );
      })()}
      {/* LAST RITES — Fallen shade veil. R25: Fallen beings are impervious
          to Lyt Attacks for one turn. Make the rule VISIBLE — the being
          goes gray, phantom mist drifts across it, and the word hangs
          quietly. When it looks like a ghost, players stop wasting attacks
          on it without reading a toast. */}
      {fallen && !shatterPlaying && (
        <div style={{
          position: "absolute", inset: 0, borderRadius: 10, zIndex: 5,
          pointerEvents: "none", overflow: "hidden",
          backdropFilter: "grayscale(0.85) brightness(0.75)",
          WebkitBackdropFilter: "grayscale(0.85) brightness(0.75)",
          boxShadow: `inset 0 0 22px rgba(180,180,200,0.22)`,
        }}>
          <div className="sf-fallen-mist" style={{
            position: "absolute", inset: "-20%",
            background: "radial-gradient(ellipse at 30% 40%, rgba(200,200,220,0.16), transparent 55%), radial-gradient(ellipse at 70% 65%, rgba(150,150,180,0.12), transparent 50%)",
          }} />
          <div style={{
            position: "absolute", left: 0, right: 0, top: "44%", textAlign: "center",
            fontFamily: GM.font, fontSize: 9, fontWeight: 900, fontStyle: "italic",
            letterSpacing: 4, color: "rgba(220,220,235,0.85)",
            textShadow: "0 0 10px rgba(200,200,255,0.5), 0 1px 3px rgba(0,0,0,0.9)",
          }}>FALLEN</div>
        </div>
      )}
      {/* LAST RITES — fracture shimmer. While Lyf sits at 0 (Vulnerable),
          hairline cracks glow and breathe in red — the being is standing,
          but the glass is already broken. Persistent, faint, cheap. */}
      {fractured && !fallen && !shatterPlaying && card.type === "Escendant" && (
        <svg className="sf-fracture-shimmer" viewBox="0 0 100 140" style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          pointerEvents: "none", zIndex: 5, borderRadius: 10,
        }}>
          <path d="M 12 8 L 48 52 L 30 78 L 62 100 L 50 132"
                stroke={SD.red} strokeWidth="1" fill="none" strokeLinecap="round" />
          <path d="M 78 18 L 56 46 L 72 74 L 44 96"
                stroke="#ff6655" strokeWidth="0.7" fill="none" strokeLinecap="round" />
        </svg>
      )}
      {/* LAST RITES — floating heal numbers. Lyf rising (heals, buffs,
          R28's end-of-turn regeneration) breathes upward in Lyf-blue. */}
      {healFloats.map(f => (
        <div key={f.id} className="sf-heal-float" style={{
          position: "absolute",
          left: "50%", top: "48%",
          color: "#4a9fd4",
          fontFamily: GM.font, fontSize: 15, fontWeight: 900,
          textShadow: `0 0 10px #4a9fd4aa, 0 1px 3px rgba(0,0,0,0.9)`,
          pointerEvents: "none", zIndex: 12,
          whiteSpace: "nowrap",
        }}>+{f.amount}</div>
      ))}
      {/* Floating damage numbers — rise from the card when Lyf drops */}
      {dmgFloats.map(f => (
        <div key={f.id} className="sf-dmg-float" style={{
          position: "absolute",
          left: "50%", top: "40%",
          color: SD.red,
          fontFamily: GM.font, fontSize: 22, fontWeight: 900,
          textShadow: `0 0 12px ${SD.red}, 0 0 24px ${SD.red}66, 0 2px 4px rgba(0,0,0,0.95)`,
          pointerEvents: "none", zIndex: 12,
          whiteSpace: "nowrap",
        }}>−{f.amount}</div>
      ))}
      {soulCount > 0 && (
        <div style={{
          position: "absolute", top: -7, right: -7,
          width: 22, height: 22, borderRadius: "50%",
          background: SD.purpleDim, border: `1px solid ${SD.purpleLight}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 10, fontWeight: 900, color: SD.purpleLight,
          boxShadow: shimmerBorder(SD.purple),
        }}>{soulCount}</div>
      )}
      {/* DEVIANT SIGIL — the 3D board has worn ⛧ since Ship 2; the 2D board
          never did, so an unnatural ascension was invisible here. Bottom-left
          so it never collides with the soul pip (top-right). Red, because a
          Deviant is a debt, not an ornament. */}
      {/* 🔥 THE HERESY SIGIL. A Heretical Tome does NOT live in `soul`
          (R68 — it must not return on Resurrect), so it does not raise
          the soul pip and without this it would be an attachment the
          player paid twelve resources for and could not see. Top-left,
          the one free corner: the soul pip owns top-right, the Deviant
          sigil bottom-left, the stat gems the bottom edge. */}
      {heretical && (
        <div title="Heretical Tome — bound where an U'hlti would have been" style={{
          position: "absolute", top: -6, left: -6,
          width: 18, height: 18, borderRadius: "50%",
          background: "#1a0a02", border: "1px solid #f97316",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 9, fontWeight: 900, color: "#fdba74", lineHeight: 1,
          textShadow: "0 0 6px #f97316",
          boxShadow: "0 0 10px #f9731688",
          pointerEvents: "none", zIndex: 11,
        }}>🔥</div>
      )}

      {deviant && (
        <div title="Deviant — unnatural ascension" style={{
          position: "absolute", bottom: -6, left: -6,
          width: 18, height: 18, borderRadius: "50%",
          background: "#1a0505", border: `1px solid ${SD.red}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 10, fontWeight: 900, color: SD.red, lineHeight: 1,
          textShadow: `0 0 6px ${SD.red}`,
          boxShadow: `0 0 10px ${SD.red}66`,
          pointerEvents: "none", zIndex: 11,
        }}>⛧</div>
      )}
      </div>
    </div>
  );
}

function EmptyRift({ size = "normal", onClick, highlight = false, flipped = false }) {
  const dim = SIZES[size] || SIZES.normal;
  // Three visual states:
  //   highlight=true  — valid drop target, breathes with gold glow
  //   onClick+false   — clickable but not inviting (still subtle)
  //   else            — pure void, nearly invisible
  const isInviting = !!highlight;
  const isInteractive = !!onClick;

  return (
    <div onClick={onClick} className={isInviting ? "sf-rift-breathe" : ""} style={{
      ...dim, borderRadius: 10,
      border: isInviting
        ? `1px dashed ${SD.gold}aa`
        : `1px dashed ${GM.pageBorder}44`,
      background: isInviting
        ? `radial-gradient(ellipse at center, rgba(212,168,75,0.08), rgba(0,0,5,0.2) 70%)`
        : `radial-gradient(ellipse at center, rgba(45,27,105,0.04), rgba(0,0,5,0.12) 80%)`,
      cursor: isInteractive ? "pointer" : "default",
      display: "flex", alignItems: "center", justifyContent: "center",
      transition: "all 0.3s ease",
      boxShadow: isInviting
        ? `0 0 18px ${SD.gold}22, inset 0 0 16px ${SD.gold}0a`
        : "none",
      opacity: isInviting ? 1 : 0.42,
      transform: flipped ? "rotate(180deg)" : "none",
    }}>
      {isInviting && <span style={{ fontSize: 20, color: SD.gold + "88", fontWeight: 100, letterSpacing: 0 }}>✦</span>}
    </div>
  );
}

// ═══ MAGNUM PASS 5 — THE SHATTERED VEIL ═══════════════════════
// Original Shatterfield sky, built from the lore: a Solrend is a
// sun-rend — so the field hangs beneath a torn sun bleeding gold
// through its fracture. Obsidian shards of the shattered plane drift
// in slow parallax. The sky itself takes sides: cold crimson breathes
// down from the opposition's half, warm ember gold rises from yours.
// 4xS: sexy (the Rent Sun), smart (the sky tells you whose half is
// whose), simple (three layers, no clutter), special (it IS the lore).
// All animation is transform/opacity only — GPU-cheap, mobile-safe.
function ShatterfieldVeilBG({ myAnima = 30, oppAnima = 30 }) {
  // ── MAGNUM PASS 8 — THE SOLREND MIRROR ─────────────────────
  // The Rent Sun reflects the duel. Every ~5 Anima YOU lose tears a
  // wound-crack from below, bleeding void light (crimson/violet).
  // Every ~5 Anima the ADVERSARY loses splits radiance from above
  // (white/gold/blue). Healing mends cracks — the stage simply drops
  // and segments vanish. A bloody endgame leaves the eclipse a war
  // of both lights. Stages clamp 0–6 per side; null-safe defaults.
  const stageOf = (a) => Math.max(0, Math.min(6, Math.ceil((30 - (typeof a === "number" ? a : 30)) / 5)));
  const stageMine = stageOf(myAnima);
  const stageOpp  = stageOf(oppAnima);
  // Wound cracks — spread from the lower-left rim (your side of the sky)
  const WOUND_CRACKS = [
    { d: "M 58 156 L 84 142",                 c: "#e05555" },
    { d: "M 84 142 L 96 152 L 110 140",        c: "#a855f7" },
    { d: "M 84 142 L 77 121",                  c: "#e05555" },
    { d: "M 110 140 L 128 148 L 142 136",      c: "#a855f7" },
    { d: "M 77 121 L 90 106 L 84 91",          c: "#c084fc" },
    { d: "M 142 136 L 158 144 M 90 106 L 106 99", c: "#e05555" },
  ];
  // Radiance cracks — split from the upper-right rim (theirs)
  const LIGHT_CRACKS = [
    { d: "M 180 86 L 154 102",                 c: "#fff3c4" },
    { d: "M 154 102 L 144 91 L 130 101",       c: "#7ec8ff" },
    { d: "M 154 102 L 163 123",                c: "#e8b84b" },
    { d: "M 130 101 L 117 94",                 c: "#fff3c4" },
    { d: "M 163 123 L 175 133 L 171 148",      c: "#7ec8ff" },
    { d: "M 117 94 L 108 82 M 175 133 L 187 124", c: "#e8b84b" },
  ];
  // Deterministic star + shard fields (starfield pattern — stable
  // across re-renders, zero state, zero churn).
  const stars = Array.from({ length: isMobile ? 26 : 46 }, (_, i) => ({
    left: (i * 37.3 + 13) % 100,
    top: (i * 53.7 + 7) % 100,
    size: i % 7 === 0 ? 2 : 1,
    op: 0.14 + ((i * 17) % 10) / 34,
    twinkle: i % 4 === 0,
    dur: 3 + (i % 5),
    key: i,
  }));
  const shards = Array.from({ length: isMobile ? 4 : 8 }, (_, i) => ({
    left: (i * 41 + 9) % 92,
    top: 8 + ((i * 31) % 78),
    sc: 0.55 + ((i * 29) % 10) / 13,
    dur: 26 + (i % 5) * 9,
    rot: (i * 47) % 360,
    op: 0.10 + ((i * 13) % 8) / 55,
    key: i,
  }));
  return (
    <div style={{
      position: "absolute", inset: 0, zIndex: 0, overflow: "hidden", pointerEvents: "none",
      background: `
        radial-gradient(ellipse 130% 65% at 50% 118%, rgba(200,150,60,0.11), transparent 55%),
        radial-gradient(ellipse 130% 65% at 50% -18%, rgba(150,28,50,0.14), transparent 55%),
        radial-gradient(ellipse 95% 95% at 50% 46%, #0b0712 0%, #060409 55%, #030204 100%)`,
    }}>
      {/* ── Stars ── */}
      {stars.map(s => (
        <div key={`st${s.key}`} style={{
          position: "absolute", left: `${s.left}%`, top: `${s.top}%`,
          width: s.size, height: s.size, borderRadius: "50%",
          background: "#e8d9b0", opacity: s.op,
          animation: s.twinkle ? `sf-veilTwinkle ${s.dur}s ease-in-out infinite` : "none",
        }} />
      ))}

      {/* ── Aenergy ribbons — one cold breath above, one warm below ── */}
      <div className="sf-veil-ribbon-cold" style={{
        position: "absolute", top: "6%", left: "-30%", width: "160%", height: "24%",
        background: "radial-gradient(ellipse 50% 50% at 50% 50%, rgba(130,34,70,0.16), transparent 70%)",
      }} />
      <div className="sf-veil-ribbon-warm" style={{
        position: "absolute", bottom: "4%", left: "-30%", width: "160%", height: "22%",
        background: "radial-gradient(ellipse 50% 50% at 50% 50%, rgba(200,140,50,0.11), transparent 70%)",
      }} />

      {/* ── The Rent Sun ── */}
      <div style={{ position: "absolute", left: isMobile ? "-14%" : "5%", top: isMobile ? "6%" : "10%", width: isMobile ? 170 : 240, height: isMobile ? 170 : 240 }}>
        <svg viewBox="0 0 240 240" style={{ width: "100%", height: "100%", overflow: "visible" }}>
          <defs>
            <radialGradient id="sfVeilCorona" cx="50%" cy="50%" r="50%">
              <stop offset="52%" stopColor="transparent" />
              <stop offset="66%" stopColor="#c8963c" stopOpacity="0.30" />
              <stop offset="78%" stopColor="#8b2038" stopOpacity="0.16" />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>
            <linearGradient id="sfVeilCrack" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#c8963c" stopOpacity="0" />
              <stop offset="35%" stopColor="#e8b84b" stopOpacity="0.85" />
              <stop offset="55%" stopColor="#fff3c4" stopOpacity="0.95" />
              <stop offset="75%" stopColor="#e8b84b" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#c8963c" stopOpacity="0" />
            </linearGradient>
            <clipPath id="sfVeilDisc"><circle cx="120" cy="120" r="72" /></clipPath>
            <radialGradient id="sfVeilWoundGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#9333ea" stopOpacity="0.34" />
              <stop offset="55%" stopColor="#e05555" stopOpacity="0.16" />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>
            <radialGradient id="sfVeilLightGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.36" />
              <stop offset="55%" stopColor="#7ec8ff" stopOpacity="0.18" />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>
            <radialGradient id="sfVeilCoronaWound" cx="50%" cy="50%" r="50%">
              <stop offset="56%" stopColor="transparent" />
              <stop offset="72%" stopColor="#9333ea" stopOpacity="0.28" />
              <stop offset="86%" stopColor="#e05555" stopOpacity="0.14" />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>
            <radialGradient id="sfVeilCoronaLight" cx="50%" cy="50%" r="50%">
              <stop offset="56%" stopColor="transparent" />
              <stop offset="72%" stopColor="#fff3c4" stopOpacity="0.30" />
              <stop offset="86%" stopColor="#7ec8ff" stopOpacity="0.16" />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>
          </defs>
          {/* breathing corona */}
          <circle cx="120" cy="120" r="118" fill="url(#sfVeilCorona)" style={{ transformOrigin: "120px 120px", animation: "sf-veilCorona 9s ease-in-out infinite" }} />
          {/* MIRROR — clash rims: the corona takes on the duel's colors */}
          <circle cx="120" cy="120" r="118" fill="url(#sfVeilCoronaWound)" opacity={(stageMine / 6) * 0.7} style={{ transition: "opacity 1.4s ease", mixBlendMode: "screen" }} />
          <circle cx="120" cy="120" r="118" fill="url(#sfVeilCoronaLight)" opacity={(stageOpp / 6) * 0.7} style={{ transition: "opacity 1.4s ease", mixBlendMode: "screen" }} />
          {/* the dark sun */}
          <circle cx="120" cy="120" r="72" fill="#060309" />
          <circle cx="120" cy="120" r="72" fill="none" stroke="#c8963c" strokeOpacity="0.35" strokeWidth="1" />
          <circle cx="120" cy="120" r="76" fill="none" stroke="#8b2038" strokeOpacity="0.22" strokeWidth="1" />
          {/* MIRROR — light blooming through the broken places */}
          <g clipPath="url(#sfVeilDisc)">
            <circle cx="94" cy="130" r="50" fill="url(#sfVeilWoundGlow)" opacity={(stageMine / 6) * 0.85} style={{ transition: "opacity 1.4s ease" }} />
            <circle cx="150" cy="104" r="50" fill="url(#sfVeilLightGlow)" opacity={(stageOpp / 6) * 0.9} style={{ transition: "opacity 1.4s ease" }} />
            {/* wound cracks — void light, one per ~5 Anima you've lost */}
            {WOUND_CRACKS.slice(0, stageMine).map((k, i) => (
              <path key={`wc${i}`} d={k.d} fill="none" stroke={k.c} strokeWidth="1.7" strokeLinejoin="round" strokeLinecap="round"
                style={{ filter: `drop-shadow(0 0 5px ${k.c})`, animation: "sf-crackPop 0.7s ease-out both" }} />
            ))}
            {/* radiance cracks — one per ~5 Anima the adversary has lost */}
            {LIGHT_CRACKS.slice(0, stageOpp).map((k, i) => (
              <path key={`lc${i}`} d={k.d} fill="none" stroke={k.c} strokeWidth="1.7" strokeLinejoin="round" strokeLinecap="round"
                style={{ filter: `drop-shadow(0 0 5px ${k.c})`, animation: "sf-crackPop 0.7s ease-out both" }} />
            ))}
          </g>
          {/* the rend — a fracture of light across the disc, turning once
              every four minutes so it is never quite where you left it */}
          <g clipPath="url(#sfVeilDisc)" style={{ transformOrigin: "120px 120px", animation: "sf-veilSpin 240s linear infinite" }}>
            <path d="M 40 138 L 82 112 L 104 126 L 138 92 L 164 108 L 202 84"
              fill="none" stroke="url(#sfVeilCrack)" strokeWidth="2.4" strokeLinejoin="round"
              style={{ filter: "drop-shadow(0 0 6px #e8b84b)" }} />
            <path d="M 104 126 L 112 148 M 138 92 L 132 70"
              fill="none" stroke="#e8b84b" strokeOpacity="0.5" strokeWidth="1.2" />
            <circle cx="104" cy="126" r="2.2" fill="#fff3c4" opacity="0.9" />
            <circle cx="138" cy="92"  r="1.8" fill="#fff3c4" opacity="0.8" />
          </g>
        </svg>
      </div>

      {/* ── The Rite Circle — vast, faint summoning geometry beneath the
             whole field. Twin hexes echo the nameplate sigil; the ring of
             rune-ticks turns once every seven minutes, the hexes counter-
             turn. Fills the void with lore instead of clutter. ── */}
      <div style={{ position: "absolute", left: "50%", top: "50%", width: "92vmin", height: "92vmin", transform: "translate(-50%,-50%)", opacity: 0.08 }}>
        <svg viewBox="0 0 400 400" style={{ width: "100%", height: "100%" }}>
          <g style={{ transformOrigin: "200px 200px", animation: "sf-veilSpin 420s linear infinite" }}>
            <circle cx="200" cy="200" r="197" fill="none" stroke="#c8963c" strokeWidth="1" />
            <circle cx="200" cy="200" r="178" fill="none" stroke="#c8963c" strokeWidth="0.6" />
            {Array.from({ length: 48 }, (_, i) => {
              const a = (i / 48) * 2 * Math.PI;
              const long = i % 4 === 0;
              const r1 = long ? 172 : 180;
              return <line key={`tk${i}`}
                x1={200 + r1 * Math.cos(a)} y1={200 + r1 * Math.sin(a)}
                x2={200 + 195 * Math.cos(a)} y2={200 + 195 * Math.sin(a)}
                stroke="#c8963c" strokeWidth={long ? 1.1 : 0.6} />;
            })}
          </g>
          <g style={{ transformOrigin: "200px 200px", animation: "sf-veilSpinRev 300s linear infinite" }}>
            <circle cx="200" cy="200" r="140" fill="none" stroke="#8b2038" strokeWidth="0.9" />
            <polygon points="200,86 298.7,143 298.7,257 200,314 101.3,257 101.3,143" fill="none" stroke="#c8963c" strokeWidth="0.9" />
            <polygon points="314,200 257,298.7 143,298.7 86,200 143,101.3 257,101.3" fill="none" stroke="#8b2038" strokeWidth="0.7" />
            <circle cx="200" cy="200" r="60" fill="none" stroke="#c8963c" strokeWidth="0.5" />
          </g>
        </svg>
      </div>

      {/* ── Drifting shards of the shattered plane ── */}
      {shards.map(sh => (
        <div key={`sh${sh.key}`} style={{
          position: "absolute", left: `${sh.left}%`, top: `${sh.top}%`,
          width: 46 * sh.sc, height: 58 * sh.sc,
          opacity: sh.op,
          transform: `rotate(${sh.rot}deg)`,
        }}>
          <svg viewBox="0 0 46 58" style={{ width: "100%", height: "100%", animation: `sf-veilShard ${sh.dur}s ease-in-out infinite` }}>
            <polygon points="23,1 44,20 30,57 6,44 2,14" fill="#0d0813" stroke="#c8963c" strokeOpacity="0.34" strokeWidth="1" />
            <polygon points="23,1 44,20 30,57" fill="#c8963c" fillOpacity="0.05" />
            <line x1="23" y1="1" x2="30" y2="57" stroke="#8b2038" strokeOpacity="0.28" strokeWidth="0.8" />
          </svg>
        </div>
      ))}
    </div>
  );
}

// ═══ MAGNUM PASS 7 — THE TERROR FRAME ═════════════════════════
// The house frame. An overlay that dresses any position:relative panel
// in the Terror language: double gold hairline, thorned gothic corners,
// and a blood-gem finial at the crown. Pure SVG, pointer-events none —
// drop it as the first child of any panel and the box becomes ours.
// ── THE HOUSE FRAME — extracted to engine/TerrorFrame (July 3) ─
// Same component, same defaults (the literal GM golds), now one
// drop-in line for ANY IE surface. Imported at the top of this file;
// do not re-declare it here.

// ── MAGNUM PASS 1 — Ember motes ──────────────────────────────
// Ambient gold/purple sparks drifting up through the field. Pure CSS,
// deterministic positions (starfield pattern — no re-render churn).
// Negative animation delays mean every mote is already mid-flight on
// mount, so the field never looks like it just "turned on".
function EmberMotes() {
  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none", overflow: "hidden" }}>
      {Array.from({ length: 14 }, (_, i) => {
        const left = (i * 31.7 + i * i * 3.3) % 100;
        const size = i % 5 === 0 ? 3 : 2;
        const dur = 14 + (i % 6) * 3;
        const delay = (i * 1.9) % dur;
        const color = i % 4 === 0 ? "#c8a84b" : i % 3 === 0 ? "#a78bfa" : "#e8d9a8";
        return (
          <div key={`mote${i}`} style={{
            position: "absolute", left: `${left}%`, bottom: "-3%",
            width: size, height: size, borderRadius: "50%",
            background: color, opacity: 0,
            boxShadow: `0 0 ${size * 3}px ${color}`,
            animation: `sf-moteRise ${dur}s linear ${-delay}s infinite`,
          }} />
        );
      })}
    </div>
  );
}

function HandCard({ cardId, selected, onClick, small = false, holoTier = null,
                    entering = false, exiting = false, dragging = false,
                    draggable = false, onDragStart, onDragEnd, spine = false }) {
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [cardId]);

  // ── MAGNUM PASS 2 — hover tilt + light-follow sheen ────────
  // Desktop only. Imperative style mutation (no re-render per mousemove);
  // React's own transform (selected lift / drag shrink) is the base pose,
  // and any re-render simply resets the tilt — which is the correct reset.
  const tiltRef  = useRef(null);
  const sheenRef = useRef(null);
  const baseTransform = dragging ? "scale(0.88)" : selected ? "translateY(-18px)" : "none";
  const handleTiltMove = (e) => {
    if (isMobile || dragging || exiting) return;
    const el = tiltRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width  - 0.5;
    const py = (e.clientY - r.top)  / r.height - 0.5;
    const base = baseTransform === "none" ? "" : ` ${baseTransform}`;
    el.style.transform = `perspective(640px) rotateY(${(px * 9).toFixed(2)}deg) rotateX(${(-py * 9).toFixed(2)}deg)${base} scale(1.045)`;
    el.style.zIndex = "40";
    const sh = sheenRef.current;
    if (sh) {
      sh.style.opacity = "1";
      sh.style.background = `radial-gradient(circle at ${((px + 0.5) * 100).toFixed(1)}% ${((py + 0.5) * 100).toFixed(1)}%, rgba(255,244,214,0.16), transparent 55%)`;
    }
  };
  const handleTiltLeave = () => {
    const el = tiltRef.current;
    if (el) { el.style.transform = baseTransform; el.style.zIndex = ""; }
    const sh = sheenRef.current;
    if (sh) sh.style.opacity = "0";
  };

  const card = getCard(cardId);
  if (!card) return null;
  const typeColor = TYPE_COLORS_SD[card.type] || GM.vellumFaint;
  const dim = small ? SIZES.small : SIZES.hand;
  const imgUrl = cardImageUrl(cardId);
  const holo = HOLO_TIERS[holoTier] || null;
  const holoGlowClass = holoGlowClassFor(holoTier); // all 9 tiers, registry-driven

  // Compose enter/exit animation INLINE so it doesn't replace the holo glow class's animation.
  // CSS `animation` is single-valued per rule set — but setting it inline only when we need the
  // one-shot enter/exit preserves the holoGlowClass animation (via class) the rest of the time.
  const enterExitAnim = entering ? "sf-handCardEnter 0.42s cubic-bezier(.2,.85,.3,1.05) both"
                      : exiting  ? "sf-handCardExit 0.28s ease-in both"
                      : null;

  return (
    <div
      ref={tiltRef}
      onClick={onClick}
      onMouseMove={handleTiltMove}
      onMouseLeave={handleTiltLeave}
      className={holoGlowClass}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      style={{
      ...dim, flexShrink: 0, position: "relative", overflow: "hidden",
      background: imgUrl ? "transparent" : `linear-gradient(145deg, ${SD.void2}, ${SD.void1})`,
      border: `1px solid ${selected ? SD.gold : holo ? holo.borderColor : typeColor + "77"}`,
      borderRadius: 10, padding: "9px 10px",
      cursor: draggable ? "grab" : "pointer", transition: "all 0.2s",
      transform: dragging ? "scale(0.88)" : selected ? "translateY(-18px)" : "none",
      opacity: dragging ? 0.35 : 1,
      boxShadow: selected ? shimmerBorder(SD.gold) : "none",
      ...(enterExitAnim ? { animation: enterExitAnim } : {}),
      pointerEvents: exiting ? "none" : undefined,
      display: "flex", flexDirection: "column", gap: 4,
      fontFamily: GM.font,
      WebkitUserSelect: "none", userSelect: "none",
      // THE FOIL BLEND FIX — same law as FieldCard: this box is the blend
      // group, so HoloFoil's dodge/soft-light passes see the art beneath.
      isolation: "isolate",
    }}>
    {imgUrl && (
      <>
        {!imgFailed && <img src={imgUrl} alt={card.name} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", borderRadius: 9, opacity: 0.9 }}
          onError={() => setImgFailed(true)} />}
        <div style={{ position: "absolute", inset: 0, borderRadius: 9, background: "linear-gradient(to bottom, rgba(0,0,5,0.72) 0%, rgba(0,0,5,0.0) 30%, rgba(0,0,5,0.0) 50%, rgba(0,0,5,0.92) 100%)" }} />
      </>
    )}
    {/* THE SPINE (July 26) — when the fan has to close ranks, the visible
        strip of an overlapped card is its LEFT edge, so that edge is made to
        speak: a type-coloured rib (holo border wins when the card has one).
        Off entirely when the hand fits, and off on the peeked card. */}
    {spine && (
      <div style={{ position: "absolute", left: 0, top: 4, bottom: 4, width: 3, zIndex: 4, borderRadius: "9px 0 0 9px", pointerEvents: "none",
        background: `linear-gradient(180deg, transparent, ${holo ? holo.borderColor : typeColor}, transparent)`,
        boxShadow: `0 0 7px ${holo ? holo.glowColor || holo.borderColor : typeColor}bb` }} />
    )}
    {/* HOLOFOIL MIGRATION — shared bespoke foil (was legacy sf-foil-* div) */}
    {holoTier && holo && <HoloFoil tier={holoTier} radius={9} corners={false} />}
    {/* MAGNUM PASS 2 — light-follow sheen (driven imperatively by hover tilt) */}
    <div ref={sheenRef} style={{ position: "absolute", inset: 0, borderRadius: 9, zIndex: 3, pointerEvents: "none", opacity: 0, transition: "opacity 0.3s ease", mixBlendMode: "screen" }} />
    {/* Holo badge */}
    {holo && (
      <div style={{ position: "absolute", top: 2, right: 2, zIndex: 3, fontSize: 7, color: holo.color, fontWeight: 900, letterSpacing: 0.5, textShadow: `0 0 4px ${holo.color}` }}>
        {holo.label}
      </div>
    )}
      <div style={{ position: "relative", zIndex: 1, fontSize: 10, color: imgUrl ? "#fff" : typeColor, fontWeight: 900, letterSpacing: 1, lineHeight: 1, textShadow: imgUrl ? "0 1px 3px rgba(0,0,0,0.9)" : "none" }}>
        {card.type?.substring(0, 4).toUpperCase()}{card.level ? ` L${card.level}` : ""}
      </div>
      <div style={{ position: "relative", zIndex: 1, fontSize: 12, color: imgUrl ? "#fff" : GM.vellumDim, fontStyle: "italic", lineHeight: 1.3, flex: 1, overflow: "hidden", textShadow: imgUrl ? "0 1px 4px rgba(0,0,0,0.95)" : "none" }}>{card.name}</div>
      {card.type === "Escendant" && card.lyt !== undefined && (
        <div style={{ position: "relative", zIndex: 1, display: "flex", justifyContent: "space-between", fontWeight: 900 }}>
          <StatChip kind="lyf" value={card.lyf} color="#88ccff" size={isMobile ? 10 : 12} fontSize={isMobile ? 11 : 13} bare={isMobile} />
          <StatChip kind="lyt" value={card.lyt} color="#ff8888" size={isMobile ? 10 : 12} fontSize={isMobile ? 11 : 13} bare={isMobile} />
        </div>
      )}
      {card.type !== "Escendant" && card.type !== "Escling" && (() => {
        const showLyf = ["Ritual", "Construction", "Treasure"].includes(card.ilk) && card.lyf != null;
        return (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 900, marginTop: 2, position: "relative", zIndex: 2 }}>
            {card.type === "U'hlti"
              ? <StatChip kind="anima" value={card.animaCost != null ? card.animaCost : "?"} color="#f87171" size={isMobile ? 9 : 11} fontSize={isMobile ? 10 : 12} bare={isMobile} />
              : showLyf
                ? <StatChip kind="lyf" value={card.lyf} color="#88ccff" size={isMobile ? 9 : 11} fontSize={isMobile ? 10 : 12} bare={isMobile} />
                : <span />
            }
            {card.khaoCost != null
              ? <StatChip kind="khao" value={card.khaoCost} color={GM.inkGoldBright} size={isMobile ? 9 : 11} fontSize={isMobile ? 10 : 12} bare={isMobile} />
              : <span />
            }
          </div>
        );
      })()}
      {card.effectTag && <div style={{ position: "relative", zIndex: 2, fontSize: 9, color: SD.gold, textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}>{card.effectTag}</div>}
      {card.ssg && <div style={{ position: "relative", zIndex: 2, fontSize: 9, color: SD.gold, fontWeight: 900, letterSpacing: 1 }}>◆ SSG</div>}
    </div>
  );
}

function DrawButton({ label, icon, color, enabled, onClick, used }) {
  return (
    <button onClick={onClick} disabled={!enabled} style={{
      padding: "6px 10px",
      background: used ? "transparent" : enabled ? `linear-gradient(135deg, ${GM.inkGold}15, ${GM.pageMid})` : "transparent",
      border: `1px solid ${used ? SD.void4 : enabled ? color : GM.pageBorder}`,
      borderRadius: 8,
      display: "flex", alignItems: "center", gap: 5,
      cursor: enabled ? "pointer" : "not-allowed",
      opacity: used ? 0.25 : enabled ? 1 : 0.3,
      boxShadow: enabled && !used ? shimmerBorder(color) : "none",
      transition: "all 0.2s", fontFamily: GM.font,
    }}>
      <span style={{ fontSize: 14 }}>{icon}</span>
      <span style={{ fontSize: 11, color: used ? SD.void4 : enabled ? color : GM.pageBorder, fontStyle: "italic", whiteSpace: "nowrap" }}>{label}{used ? " ✓" : ""}</span>
    </button>
  );
}

// ═════════════════════════════════════════════════════════════
// THE FORGED RING — RadialMenu v2 (July 26, 2026)
//
// The last piece of duel chrome still wearing v3's flat brown
// rounded rectangles. Rebuilt against the GILDED OBSIDIAN material
// laws (theme.js): no flat fills, no 1px hairlines (petals wear a
// real GOLD_METAL rim), faceted geometry (clip-path, so outer depth
// rides `filter: drop-shadow` — clip-path eats box-shadow), depth on
// everything pressable, and ACTIVE = LIGHT (bloom, not a colour swap).
//
// ⚠ THE ACTION CONTRACT IS UNCHANGED — every existing call site
// ({ icon, label, color, legal, reason, cost, onSelect }) renders as
// before. Three OPTIONAL fields joined it, all defaulting to today's
// behaviour: `costKind` ("khao" | "anima" | "mix"), `dim` (legal but
// spent — pressed-in wax, still tappable), and the menu-level
// `title` (a caption engraved under the ring).
//
// THE HUB SPEAKS. The old radial explained an illegal petal through
// `title=` — a tooltip, i.e. desktop-only, i.e. invisible on the
// board caP'n actually plays on. Now the hub medallion is a readout:
// hover (or keyboard-focus) a petal and it names it; TAP a locked
// petal and it says WHY, in red, while the petal recoils. Same law
// the wax seals already live under — an illegal press explains
// itself — finally honored by touch.
// ═════════════════════════════════════════════════════════════
function RadialMenu({ actions, onClose, x, y, title = null }) {
  const count = actions.length;
  const [deny, setDeny] = useState(null); // { i, reason } — a locked petal's protest

  // Petal metrics + a radius that BREATHES with the count. The old
  // fixed 92 crowded eight petals into six petals' worth of arc; the
  // circumference is now solved for, then clamped to the screen.
  const petalW = isMobile ? 58 : 76;
  const petalH = isMobile ? 56 : 66;
  const need   = (count * (petalW + (isMobile ? 6 : 10))) / (2 * Math.PI);
  const radius = Math.round(Math.max(isMobile ? 66 : 88, Math.min(isMobile ? 98 : 134, need + 22)));

  // Our own clamp — smartPos()'s single 110px margin knows nothing
  // about this ring's real footprint (and the caption hangs below it).
  const vw = typeof window !== "undefined" ? window.innerWidth  : 800;
  const vh = typeof window !== "undefined" ? window.innerHeight : 600;
  const mx = radius + petalW / 2 + 10;
  const my = radius + petalH / 2 + (title ? 46 : 34);
  const pos = { x: Math.min(Math.max(x, mx), vw - mx), y: Math.min(Math.max(y, my), vh - my) };

  const legalActions = actions.filter(a => a.legal !== false);
  // ── NESTED RADIAL FIX (July 2026): close FIRST, then onSelect. The old
  // order (onSelect → onClose) meant any action that opened a follow-up
  // radial (setRadial) was instantly clobbered by onClose's setRadial(null)
  // — the Necropolis Resurrect petal did literally nothing. With close-first,
  // React batches setRadial(null) then setRadial(step2): last write wins.
  const nav = useKeyboardNav(legalActions, (action) => { onClose(); action.onSelect(); });
  useModalKeys(onClose);

  const active = legalActions[nav.activeIdx] || null;
  const caption = deny ? deny.reason
                : active ? String(active.label || "").replace(/\n/g, " ")
                : title || null;
  const captionColor = deny ? SD.red : active ? (active.color || GM.inkGoldBright) : GM.inkGoldDim;
  const ringSize = (radius + petalH / 2 + 8) * 2;

  return (
    <>
      {/* Dismiss catcher + a vignette that sinks the board behind the ring.
          Deliberately NO backdrop-filter: this floats over a live WebGL
          canvas on the 3D board and a full-screen blur there is a per-frame
          tax for a menu that lives two seconds. A gradient is free. */}
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 300, animation: "sf-radialScrim 0.22s ease-out both",
        background: `radial-gradient(circle ${radius + 130}px at ${pos.x}px ${pos.y}px, rgba(3,2,10,0.82) 0%, rgba(3,2,10,0.62) 45%, rgba(3,2,10,0.28) 78%, rgba(3,2,10,0.12) 100%)` }} />

      <div style={{ position: "fixed", left: pos.x, top: pos.y, zIndex: 301, transform: "translate(-50%, -50%)", pointerEvents: "none" }}>

        {/* ── THE ORBIT — a graven ring the petals are set into ── */}
        <svg width={ringSize} height={ringSize} viewBox={`0 0 ${ringSize} ${ringSize}`}
          style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", overflow: "visible", animation: "sf-radialRing 0.34s ease-out both" }}>
          <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke={GM.inkGold} strokeWidth="1" opacity="0.22" />
          <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke={GM.inkGoldBright} strokeWidth="1.5"
            strokeDasharray="2 13" strokeLinecap="round" opacity="0.5" className="sf-radial-orbit" />
          <circle cx={ringSize / 2} cy={ringSize / 2} r={radius - 7} fill="none" stroke={SD.purple} strokeWidth="1" opacity="0.16" />
        </svg>

        {/* ── THE HUB — forged medallion, and the menu's voice. Also the
             quickest way out: the middle is a close button. ── */}
        <div onClick={(e) => { e.stopPropagation(); onClose(); }}
          style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)",
            width: 46, height: 46, borderRadius: "50%", padding: 2, background: GOLD_METAL,
            filter: `drop-shadow(0 0 12px ${GM.inkGold}55) drop-shadow(0 3px 8px rgba(0,0,0,0.8))`,
            zIndex: 303, pointerEvents: "all", cursor: "pointer",
            animation: "sf-radialCore 0.26s cubic-bezier(0.2,0.9,0.3,1.2) both" }}>
          <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: OBSIDIAN_RECESS,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "inset 0 2px 8px rgba(0,0,0,0.85), inset 0 -1px 0 rgba(226,232,244,0.06)" }}>
            <span className="sf-radial-sigil" style={{ fontSize: 17, color: GM.inkGoldBright, textShadow: textGlow(GM.inkGoldBright), lineHeight: 1 }}>◆</span>
          </div>
        </div>

        {/* ── THE ENGRAVING — what the hub is saying right now ── */}
        {caption && (
          <div key={caption} style={{ position: "absolute", left: "50%", top: `calc(50% + ${radius + petalH / 2 + 12}px)`,
            transform: "translateX(-50%)", maxWidth: Math.min(vw - 24, 260), whiteSpace: "nowrap",
            overflow: "hidden", textOverflow: "ellipsis", pointerEvents: "none", zIndex: 303,
            background: OBSIDIAN_SLAB, clipPath: facet(7), padding: "5px 14px",
            filter: `drop-shadow(0 0 8px ${(deny ? SD.red : GM.inkGold)}44) drop-shadow(0 2px 6px rgba(0,0,0,0.8))`,
            fontFamily: GM.font, fontSize: 10, letterSpacing: 1.1, fontStyle: "italic",
            color: captionColor, textShadow: textGlow(captionColor),
            animation: "sf-radialSay 0.2s ease-out both" }}>
            {caption}
          </div>
        )}

        {/* ── THE PETALS ── */}
        {actions.map((action, i) => {
          const isLegal = action.legal !== false;
          const legalIdx = legalActions.indexOf(action);
          const isHot = isLegal && nav.activeIdx === legalIdx;
          const isDim = isLegal && action.dim === true;   // legal, but already spent
          const hue = action.color || GM.inkGold;
          const angle = (i / count) * 2 * Math.PI - Math.PI / 2;
          const ax = Math.cos(angle) * radius;
          const ay = Math.sin(angle) * radius;
          const rimMetal = !isLegal ? SILVER_METAL : isHot ? GOLD_METAL : `linear-gradient(160deg, ${hue}dd 0%, ${hue}44 26%, ${hue}ff 46%, ${hue}33 70%, ${hue}cc 100%)`;
          return (
            <div key={i}
              onClick={(e) => {
                e.stopPropagation();
                if (isLegal) { onClose(); action.onSelect(); return; }
                // Locked: don't close, don't act — say why, and recoil.
                setDeny(d => ({ i, reason: action.reason || "Not available", n: ((d && d.n) || 0) + 1 }));
              }}
              onMouseEnter={() => { if (isLegal) { nav.setActiveIdx(legalIdx); setDeny(null); } else setDeny(d => ({ i, reason: action.reason || "Not available", n: ((d && d.n) || 0) + 1 })); }}
              onMouseLeave={() => { if (!isLegal) setDeny(null); }}
              style={{
                position: "absolute",
                left: `calc(50% + ${ax}px)`, top: `calc(50% + ${ay}px)`,
                width: petalW, minHeight: petalH,
                transform: "translate(-50%, -50%)",
                "--petal-op": isLegal ? (isDim ? 0.62 : 1) : 0.42,
                opacity: isLegal ? (isDim ? 0.62 : 1) : 0.42,
                animation: `sf-radialBloom 0.3s cubic-bezier(0.2, 0.9, 0.3, 1.15) both${deny && deny.i === i ? ", sf-radialDeny 0.34s ease-out" : ""}`,
                animationDelay: `${i * 0.032}s${deny && deny.i === i ? (deny.n % 2 ? ", 0.001s" : ", 0s") : ""}`,
                cursor: isLegal ? "pointer" : "not-allowed",
                zIndex: isHot ? 305 : 302, pointerEvents: "all",
              }}>
              {/* metal rim — Material Law 2: borders are forged, not hairlines */}
              <div style={{
                width: "100%", height: "100%", minHeight: petalH, padding: 1.5,
                background: rimMetal, clipPath: facet(11),
                filter: isHot ? `drop-shadow(0 0 16px ${GM.inkGoldBright}aa) drop-shadow(0 5px 12px rgba(0,0,0,0.85))`
                      : isLegal ? `drop-shadow(0 0 9px ${hue}55) drop-shadow(0 4px 9px rgba(0,0,0,0.75))`
                                : "drop-shadow(0 2px 6px rgba(0,0,0,0.7))",
                transition: "filter 0.16s ease",
              }}>
                <div style={{
                  width: "100%", height: "100%", minHeight: petalH - 3, clipPath: facet(10),
                  background: isHot ? OBSIDIAN_SLAB_LIT : isLegal ? OBSIDIAN_SLAB : OBSIDIAN_RECESS,
                  boxShadow: isHot ? `${bloom(hue)}, inset 0 1px 0 rgba(245,216,120,0.34)`
                           : isDim ? "inset 0 2px 8px rgba(0,0,0,0.75)"
                                   : "inset 0 1px 0 rgba(226,232,244,0.12)",
                  position: "relative",
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  gap: 2, padding: "6px 3px", fontFamily: GM.font,
                  transition: "background 0.16s ease, box-shadow 0.16s ease",
                }}>
                  <span style={{ fontSize: isMobile ? 16 : 19, lineHeight: 1,
                    color: isLegal ? hue : GM.vellumFaint,
                    filter: isLegal ? `drop-shadow(0 0 6px ${hue}88)` : "grayscale(1)",
                    textShadow: isLegal ? textGlow(hue) : "none" }}>{action.icon}</span>
                  <span style={{ fontSize: isMobile ? 8.5 : 10, letterSpacing: 0.6, lineHeight: 1.18,
                    textAlign: "center", whiteSpace: "pre-line",
                    color: isHot ? GM.inkGoldBright : isLegal ? GM.vellum : GM.vellumFaint,
                    textShadow: "0 1px 2px rgba(0,0,0,0.9)" }}>{action.label}</span>
                  {action.cost != null && isLegal && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: 9, fontWeight: 900, color: SD.goldBright, textShadow: textGlow(SD.gold) }}>
                      <StatGlyph kind={action.costKind === "anima" ? "anima" : "khao"} size={8} color={action.costKind === "anima" ? "#f87171" : SD.goldBright} />
                      {action.costKind === "mix" && <StatGlyph kind="anima" size={8} color="#f87171" />}
                      {action.cost}
                    </span>
                  )}
                  {!isLegal && <span style={{ position: "absolute", top: 3, right: 5, fontSize: 8, color: GM.vellumFaint, opacity: 0.8 }}>⛨</span>}
                  {isDim && <span style={{ position: "absolute", top: 3, right: 5, fontSize: 8, color: GM.inkGoldDim }}>✓</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function CardDetailModal({ cardId, soul = [], onClose, holoTier = null, rec = null }) {
  const card = getCard(cardId);
  const [imgFailed, setImgFailed] = useState(false);
  // THE MARK (H18) — folded behind a toggle; default read stays a card.
  const [showMark, setShowMark] = useState(false);
  useEffect(() => { setImgFailed(false); }, [cardId]);
  useModalKeys(onClose);
  if (!card) return null;
  const typeColor = TYPE_COLORS_SD[card.type] || GM.vellumFaint;
  const soulCards = (soul || []).map(id => getCard(id)).filter(Boolean);

  const holo = HOLO_TIERS[holoTier] || null;
  const holoGlowClass = holoGlowClassFor(holoTier); // all 9 tiers, registry-driven
  const borderColor = holo ? holo.borderColor : typeColor;

  return (
    <div className="sf-modal-shell" style={{ position: "fixed", inset: 0, background: "rgba(5,3,0,0.96)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 400, backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div style={{ width: "min(88vw, 340px)", display: "flex", flexDirection: "column", maxHeight: "92vh", fontFamily: GM.font }} onClick={e => e.stopPropagation()}>

        {/* ── Full card art with holo ── */}
        <div className={holoGlowClass} style={{ width: "100%", aspectRatio: "2/3", borderRadius: "14px 14px 0 0", overflow: "hidden", flexShrink: 0, position: "relative",
          // THE FOIL BLEND FIX — the inspect frame is the blend group too.
          isolation: "isolate",
          boxShadow: holo ? `0 0 0 2px ${holo.borderColor}, 0 0 40px ${holo.glowColor}` : `0 0 0 1px ${typeColor}33` }}>
          {!imgFailed ? (
            <img src={cardImageUrl(cardId)} alt={card.name}
              onError={() => setImgFailed(true)}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", position: "relative", zIndex: 0 }} />
          ) : (
            <div style={{ width: "100%", height: "100%", background: GM.page, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10 }}>
              <div style={{ fontSize: 40, color: typeColor }}>◆</div>
              <div style={{ fontSize: 15, color: typeColor, fontStyle: "italic" }}>{card.name}</div>
            </div>
          )}
          {/* HOLOFOIL MIGRATION — shared bespoke foil on the card inspect;
              the container clips (overflow:hidden, 14px top corners) so the
              overlay itself runs square. */}
          {holoTier && holo && <HoloFoil tier={holoTier} radius={0} />}
          {/* ORIGIN sigil — the one-origin law, worn BY the card: no text
              strip, just the mark crowning the art (top-center). Your
              copies' top provenance wins; otherwise the canonical primary —
              so every card on EITHER side of the board is marked. */}
          {(() => {
            const src = primaryOrigin(rec, getCardOrigins(cardId));
            if (!src) return null;
            return (
              <div style={{ position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)", zIndex: 2 }}>
                <SourceSigil source={src} size={32} />
              </div>
            );
          })()}
          {/* Holo tier badge — mirrors the sigil at bottom-center */}
          {holo && (
            <div style={{ position: "absolute", bottom: 10, left: "50%", transform: "translateX(-50%)", zIndex: 2,
              fontSize: 10, color: holo.color, fontWeight: 900, letterSpacing: 1, whiteSpace: "nowrap",
              background: "rgba(0,0,0,0.65)", padding: "3px 8px", borderRadius: 6,
              border: `1px solid ${holo.color}66`, textShadow: `0 0 8px ${holo.color}`,
              fontFamily: GM.font }}>
              {holo.label}
            </div>
          )}
        </div>

        {/* ── Info panel ── */}
        <div style={{ background: `linear-gradient(145deg, ${GM.pageMid}, ${GM.pageWarm})`, border: `1px solid ${borderColor || GM.inkBrown}`, borderTop: "none", borderRadius: "0 0 6px 6px", padding: "14px 18px", overflowY: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 9, color: holo?.color || typeColor, letterSpacing: 3, fontWeight: 900 }}>{card.type?.toUpperCase()}{card.level ? ` · L${card.level}` : ""}</div>
              <div style={{ fontSize: 18, fontWeight: 900, fontStyle: "italic", color: GM.vellum, lineHeight: 1.2, fontFamily: GM.font }}>{card.name}</div>
            </div>
            {card.lyt !== undefined && (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div style={{ textAlign: "center" }}><div style={{ fontSize: 7, color: GM.vellumFaint, fontFamily: GM.font }}>LYF</div><div style={{ fontSize: 18, fontWeight: 900, color: "#88ccff" }}>{card.lyf}</div></div>
                <div style={{ textAlign: "center" }}><div style={{ fontSize: 7, color: GM.vellumFaint, fontFamily: GM.font }}>LYT</div><div style={{ fontSize: 18, fontWeight: 900, color: "#f87171" }}>{card.lyt}</div></div>
              </div>
            )}
          </div>
          {(card.khaoCost > 0 || card.animaCost > 0) && (
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              {card.khaoCost > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: SD.gold, background: `${SD.gold}18`, border: `1px solid ${SD.gold}44`, borderRadius: 4, padding: "1px 8px" }}><StatGlyph kind="khao" size={10} color={SD.gold} />{card.khaoCost} Khao</span>}
              {card.animaCost > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "#f87171", background: "#f8717118", border: "1px solid #f8717144", borderRadius: 4, padding: "1px 8px" }}><StatGlyph kind="anima" size={10} color="#f87171" />{card.animaCost} Anima</span>}
            </div>
          )}
          {card.effect && (
            <div style={{ background: GM.pageLight, border: `1px solid ${GM.inkBrown}66`, borderRadius: 2, padding: "10px 13px", fontSize: 14, color: GM.vellumDim, lineHeight: 1.7, marginBottom: 10, fontFamily: FONT_BODY }}>
              {card.effectTag && <span style={{ color: GM.inkGold, fontWeight: 900 }}>{card.effectTag}: </span>}
              {card.effect}
            </div>
          )}
          {card.flavorText && <div style={{ fontSize: 13, color: GM.vellumFaint, fontStyle: "italic", marginBottom: 10, lineHeight: 1.6, fontFamily: FONT_BODY }}>{card.flavorText}</div>}
          {soulCards.length > 0 && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "14px 0 10px" }}>
                <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${GM.inkBrown}55)` }} />
                <span style={{ fontSize: 11, color: GM.inkGoldDim, letterSpacing: 3, fontFamily: GM.font }}>✦ SOUL ✦</span>
                <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${GM.inkBrown}55, transparent)` }} />
              </div>
              {soulCards.map((sc, i) => {
                const scColor = TYPE_COLORS_SD[sc.type] || GM.vellumFaint;
                return (
                  <div key={i} style={{ background: GM.pageMid, border: `1px solid ${scColor}44`, borderRadius: 2, padding: "10px 13px", marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <div style={{ fontSize: 9, color: scColor, fontWeight: 900 }}>{sc.type?.toUpperCase()}{sc.level ? ` L${sc.level}` : ""}</div>
                      {sc.khaoCost != null && <div style={{ fontSize: 10, color: GM.inkGold }}>✦{sc.khaoCost}</div>}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 900, fontStyle: "italic", color: GM.vellumDim, marginBottom: 4, fontFamily: GM.font }}>{sc.name}</div>
                    {sc.effect && <div style={{ fontSize: 13, color: GM.vellumFaint, lineHeight: 1.6, fontFamily: FONT_BODY }}>{sc.effectTag && <span style={{ color: GM.inkGold, fontWeight: 900 }}>{sc.effectTag}: </span>}{sc.effect}</div>}
                  </div>
                );
              })}
            </>
          )}
          {/* ── THE MARK (H18) — summon the Empire's record on this card,
              board-side. `rec` IS the collection record (yours or, for the
              opponent's cards, absent — data-tolerant by law); unowned
              falls back to canonical origins. Read-only, always. */}
          <button onClick={() => setShowMark(m => !m)} style={{
            width: "100%", marginTop: 10, padding: "8px",
            background: showMark ? `${GM.inkGold}18` : "transparent",
            border: `1px solid ${GM.inkGold}66`,
            borderRadius: 2, cursor: "pointer", color: GM.inkGold,
            fontFamily: GM.font, fontSize: 10, fontWeight: 900, letterSpacing: 2.5,
          }}>▦ THE MARK</button>
          {showMark && (
            <TheMark
              being={{ key: cardId, name: card.name, type: card.type, game: "Terror" }}
              record={rec}
              origins={getCardOrigins(cardId)}
              width="100%"
              style={{ marginTop: 10 }}
            />
          )}
          <button onClick={onClose} style={{ width: "100%", background: "transparent", border: `1px solid ${GM.inkBrown}66`, color: GM.vellumFaint, padding: "11px", borderRadius: 2, cursor: "pointer", fontFamily: GM.font, fontSize: 13, marginTop: 10 }}>✕ Close</button>
        </div>
      </div>
    </div>
  );
}
function SoulModal({ slot, slotIdx, onClose, onActivate, myTurn, myKhao, myAnima, collection, turn = 0, seatCount = 2 }) {
  const escendant = getCard(slot?.escendant);
  const soulCards = (slot?.soul || []).map(id => getCard(id)).filter(Boolean);
  useModalKeys(onClose);
  // GRAND RITE — the 8th and final modal dressed in the house frame.
  // This panel used to scroll ON ITSELF (maxHeight + overflowY on the
  // same div), so an absolute TerrorFrame would have scrolled away with
  // the content — that's why it was skipped in Pass 7. Restructured:
  // the OUTER shell holds the frame, border, and glow (never scrolls);
  // an INNER region carries the padding and the overflow.
  return (
    <div className="sf-modal-shell" style={{ position: "fixed", inset: 0, background: "rgba(5,3,0,0.93)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 400, backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div style={{ background: `linear-gradient(145deg, ${GM.pageMid}, ${GM.pageWarm})`, border: `1px solid ${GM.inkGold}`, borderRadius: 4, minWidth: 340, maxWidth: 440, maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: `0 0 24px rgba(0,0,0,0.8), inset 0 0 20px ${GM.inkGold}06`, position: "relative", overflow: "hidden" }} onClick={e => e.stopPropagation()}>
        <NoiseOverlay opacity={0.05} />
        <TerrorFrame />
        <div style={{ overflowY: "auto", padding: "26px 30px" }}>
        <div style={{ fontSize: 11, color: GM.inkGoldDim, letterSpacing: 4, marginBottom: 6, fontWeight: 900, fontFamily: GM.font }}>✦ SOUL</div>
        <div style={{ fontSize: 22, fontWeight: 900, fontStyle: "italic", color: GM.vellum, marginBottom: 18, fontFamily: GM.font }}>{escendant?.name || "Being"}</div>
        {soulCards.length === 0 && <div style={{ color: GM.vellumFaint, fontStyle: "italic", fontSize: 14, textAlign: "center", padding: "20px 0", fontFamily: GM.font }}>No cards attached yet.</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {soulCards.map((sc, i) => {
            const scColor = TYPE_COLORS_SD[sc.type] || GM.vellumFaint;
            const isPassive = !sc.effectTag || sc.effectTag === "";
            // R105 / R122 (caP'n, 2026-09-13) — the printed fare, BOTH
            // numbers, every activation, and the cadence tag is a real
            // limit on a soul card exactly as it is on a Tome.
            //
            // ⚠ `sc.type === "U'hlti"` USED TO GATE THE ANIMA HALF, so
            // an Impera or Affinity that printed an Anima number was
            // activated for free. It prints it; it pays it.
            const { khao: khaoCost, anima: animaCost } = printedFare(sc);
            const affordable = canPayPrintedFare(sc, { khao: myKhao, anima: myAnima });
            const offCooldown = soulCardOffCooldown(slot, sc.id, sc.effectTag, turn, seatCount);
            const canActivate = myTurn && !isPassive && affordable && offCooldown;
            const activateReason = !myTurn ? "Not your turn"
              : isPassive ? "Passive — always active"
              : !offCooldown ? `${sc.effectTag} — on cooldown`
              : myKhao < khaoCost ? `Need ${khaoCost} Khao`
              : !affordable ? `Need ${animaFare(animaCost)}`
              : null;
            const costLabel = animaCost > 0 ? `♥${animaCost} Anima + ✦${khaoCost} Khao` : khaoCost > 0 ? `✦${khaoCost} Khao` : "Free";
            return (
              <div key={i} style={{ background: GM.pageMid, border: `1px solid ${scColor}55`, borderRadius: 2, padding: "14px 16px" }}>
                {/* GILDED PICKER SWEEP — the attached card itself, foil included */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                  <div style={{ flexShrink: 0 }}>
                    <FieldCard cardId={sc.id} size="small" holoTier={getHoloTier(sc.id, collection)} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 10, color: scColor, fontWeight: 900, letterSpacing: 1 }}>{sc.type?.toUpperCase()}{sc.level ? ` • L${sc.level}` : ""}</div>
                    <div style={{ fontSize: 16, fontWeight: 900, fontStyle: "italic", color: GM.vellumDim, fontFamily: GM.font }}>{sc.name}</div>
                  </div>
                </div>
                {sc.effect && <div style={{ fontSize: 15, color: GM.vellumFaint, lineHeight: 1.7, marginBottom: 10, fontFamily: FONT_BODY }}>{sc.effectTag && <span style={{ color: SD.gold, fontWeight: 900 }}>{sc.effectTag}: </span>}{sc.effect}</div>}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, borderTop: `1px solid ${scColor}22`, paddingTop: 8 }}>
                  {animaCost > 0
                    ? <StatChip kind="anima" value={animaCost} color={SD.red} size={11} fontSize={13} bare />
                    : sc.lyf != null
                      ? <StatChip kind="lyf" value={sc.lyf} color={SD.blue} size={11} fontSize={13} bare />
                      : <span />}
                  {khaoCost > 0
                    ? <StatChip kind="khao" value={khaoCost} color={SD.gold} size={11} fontSize={13} bare />
                    : <span />}
                </div>
                <button
                  onClick={canActivate ? () => { onActivate(slot?.escendant, sc.id, khaoCost); onClose(); } : undefined}
                  disabled={!canActivate}
                  style={{ width: "100%", padding: "10px", background: canActivate ? `linear-gradient(135deg, ${GM.inkGold}15, ${GM.pageMid})` : "transparent", border: `1px solid ${canActivate ? scColor : GM.pageBorder}`, borderRadius: 2, cursor: canActivate ? "pointer" : "not-allowed", color: canActivate ? scColor : GM.vellumFaint, fontSize: 13, fontStyle: "italic", fontFamily: GM.font, opacity: canActivate ? 1 : 0.35, transition: "all 0.2s" }}>
                  {activateReason || `✦ Activate — ${costLabel}`}
                </button>
              </div>
            );
          })}
        </div>
        <button onClick={onClose} style={{ width: "100%", background: "transparent", border: `1px solid ${GM.inkBrown}66`, color: GM.vellumFaint, padding: "13px", borderRadius: 2, cursor: "pointer", fontFamily: GM.font, fontSize: 14, marginTop: 18 }}>Close</button>
        </div>
      </div>
    </div>
  );
}

function TargetOverlay({ message, onCancel }) {
  useModalKeys(onCancel);
  return (
    <div style={{ position: "fixed", bottom: 230, left: "50%", transform: "translateX(-50%)", background: `linear-gradient(135deg, ${GM.pageMid}, ${GM.page})`, border: `1px solid ${SD.red}88`, borderRadius: 2, padding: "12px 24px", display: "flex", gap: 16, alignItems: "center", zIndex: 200, fontFamily: GM.font }}>
      <span style={{ fontSize: 14, color: SD.red, fontStyle: "italic", fontFamily: GM.font }}>{message}</span>
      <button onClick={onCancel} style={{ background: "transparent", border: `1px solid ${GM.inkBrown}66`, color: GM.vellumFaint, padding: "5px 14px", borderRadius: 2, cursor: "pointer", fontSize: 12, fontFamily: GM.font }}>Cancel</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TARGET PICKER MODAL — for card effects needing a choice
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// SHADOW GAMBLING MODAL
// ─────────────────────────────────────────────────────────────
function ShadowGamblingModal({ sg, onClose }) {
  const [phase, setPhase] = useState("setup"); // setup | rolling | result
  const [diceValues, setDiceValues] = useState([]);
  const [coinResult, setCoinResult] = useState(null);
  const [oppChoice, setOppChoice] = useState(null);
  const [rolling, setRolling] = useState(false);
  const [result, setResult] = useState(null); // "win" | "lose"
  const [guess, setGuess] = useState(null);
  useModalKeys(onClose, { enabled: phase === "result" }); // only closeable after result

  const rollDice = (count) => Array.from({ length: count }, () => Math.floor(Math.random() * 6) + 1);

  const handleACB = async (chosen) => {
    setOppChoice(chosen);
    setRolling(true);
    setPhase("rolling");
    // Animate dice rolling
    const frames = 8;
    for (let i = 0; i < frames; i++) {
      await new Promise(r => setTimeout(r, 80));
      setDiceValues(rollDice(sg.diceCount));
    }
    const final = rollDice(sg.diceCount);
    setDiceValues(final);
    setRolling(false);
    // Casino Royale's fortune — a consumed autoWin forces the win.
    // (Dice display stays honest; the banner and effect are what's forced.)
    const won = sg.autoWin ? true : (sg.diceCount === 1 ? final[0] === chosen : final.every(d => d === chosen));
    setResult(won ? "win" : "lose");
    setPhase("result");
    sg.onResolve(won, final, chosen);
  };

  const handleCoinFlip = async (side) => {
    setRolling(true);
    setPhase("rolling");
    for (let i = 0; i < 6; i++) {
      await new Promise(r => setTimeout(r, 100));
      setCoinResult(Math.random() > 0.5 ? "heads" : "tails");
    }
    const final = Math.random() > 0.5 ? "heads" : "tails";
    setCoinResult(final);
    setRolling(false);
    const won = sg.autoWin ? true : final === side;
    setResult(won ? "win" : "lose");
    setPhase("result");
    sg.onResolve(won, final);
  };

  const handleGuess = (guessVal) => {
    setGuess(guessVal);
    const won = sg.onResolve(guessVal, !!sg.autoWin);
    setResult(won ? "win" : "lose");
    setPhase("result");
  };

  const DICE_FACES = ["", "⚀", "⚁", "⚂", "⚃", "⚄", "⚅"];

  return (
    <div className="sf-modal-shell" style={{ position: "fixed", inset: 0, background: "rgba(5,3,0,0.96)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500, backdropFilter: "blur(6px)" }}>
      <div style={{ position: "relative", background: `linear-gradient(145deg, ${GM.pageMid}, ${GM.pageWarm})`, border: `1px solid ${GM.inkGold}`, borderRadius: 4, padding: "28px 26px", width: "min(94vw, 420px)", fontFamily: GM.font, boxShadow: `0 0 32px rgba(0,0,0,0.9), inset 0 0 24px ${GM.inkGold}06` }}>
        <TerrorFrame />
        {/* Header */}
        <div style={{ fontSize: 10, color: GM.inkGold, letterSpacing: 4, marginBottom: 4, fontFamily: GM.font }}>◆ SHADOW GAMBLING</div>
        <div style={{ fontSize: 22, fontWeight: 900, fontStyle: "italic", color: GM.vellum, marginBottom: 6, fontFamily: GM.font }}>{sg.cardName}</div>
        <div style={{ fontSize: 11, color: GM.vellumFaint, lineHeight: 1.7, marginBottom: 20, fontStyle: "italic", fontFamily: GM.font }}>{sg.description}</div>

        {/* SETUP PHASE */}
        {phase === "setup" && sg.type === "acb" && (
          <div>
            <div style={{ fontSize: 12, color: SD.gold, marginBottom: 12, fontStyle: "italic" }}>
              ◆ ACB — Hand your device to your Adversary and have them choose a number (1–6):
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
              {[1,2,3,4,5,6].map(n => (
                <button key={n} onClick={() => handleACB(n)} style={{ width: 52, height: 52, fontSize: 28, background: `linear-gradient(135deg, ${GM.inkGold}18, ${GM.pageMid})`, border: `1px solid ${GM.inkGold}55`, borderRadius: 2, cursor: "pointer", color: GM.inkGoldBright, fontFamily: GM.font }}>
                  {DICE_FACES[n]}
                </button>
              ))}
            </div>
          </div>
        )}

        {phase === "setup" && sg.type === "coin" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 12, color: GM.inkGold, marginBottom: 16, fontStyle: "italic", fontFamily: GM.font }}>◆ You call your side — Heads or Tails:</div>
            <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
              <button onClick={() => handleCoinFlip("heads")} style={{ padding: "14px 28px", background: `linear-gradient(135deg, ${GM.inkGold}18, ${GM.pageMid})`, border: `1px solid ${GM.inkGold}`, borderRadius: 2, cursor: "pointer", color: GM.inkGoldBright, fontSize: 16, fontWeight: 900, fontStyle: "italic", fontFamily: GM.font }}>
                ◎ Heads
              </button>
              <button onClick={() => handleCoinFlip("tails")} style={{ padding: "14px 28px", background: `linear-gradient(135deg, ${GM.inkGold}18, ${GM.pageMid})`, border: `1px solid ${GM.inkGold}`, borderRadius: 2, cursor: "pointer", color: GM.inkGoldBright, fontSize: 16, fontWeight: 900, fontStyle: "italic", fontFamily: GM.font }}>
                ◉ Tails
              </button>
            </div>
          </div>
        )}

        {phase === "setup" && sg.type === "guess_level" && (
          <div>
            <div style={{ fontSize: 12, color: GM.inkGold, marginBottom: 12, fontStyle: "italic", fontFamily: GM.font }}>◆ ACB — Have your Adversary guess the card level (1, 2, or 3):</div>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              {[1,2,3].map(n => (
                <button key={n} onClick={() => handleGuess(n)} style={{ padding: "16px 28px", background: `linear-gradient(135deg, ${GM.inkGold}18, ${GM.pageMid})`, border: `1px solid ${GM.inkGold}55`, borderRadius: 2, cursor: "pointer", color: GM.inkGoldBright, fontSize: 20, fontWeight: 900, fontFamily: GM.font }}>
                  L{n}
                </button>
              ))}
            </div>
          </div>
        )}

        {phase === "setup" && sg.type === "guess_ilk" && (
          <div>
            <div style={{ fontSize: 12, color: GM.inkGold, marginBottom: 12, fontStyle: "italic", fontFamily: GM.font }}>◆ Adversary — guess the Ilk of each Tome:</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(sg.ilkOptions || []).map((opt, i) => (
                <div key={i} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 11, color: GM.vellumFaint, width: "100%", marginBottom: 4, fontFamily: GM.font }}>Tome {i+1}:</div>
                  {["Construction","Treasure","Nihil","Omnia","Ancient Sorcery","Ritual","Shadow Gambling","Contract"].map(ilk => (
                    <button key={ilk} onClick={() => handleGuess({ tomeIdx: i, ilk })} style={{ padding: "6px 10px", background: (guess?.tomeIdx === i && guess?.ilk === ilk) ? `linear-gradient(135deg, ${GM.pageMid}, ${GM.pageMid})` : "transparent", border: `1px solid ${GM.inkBrown}`, borderRadius: 2, cursor: "pointer", color: GM.vellumDim, fontSize: 10, fontFamily: GM.font }}>
                      {ilk}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ROLLING PHASE */}
        {phase === "rolling" && sg.type === "acb" && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 60, letterSpacing: 20, animation: "pulse 0.2s infinite" }}>
              {diceValues.map(d => DICE_FACES[d] || "⚀").join(" ")}
            </div>
            <div style={{ fontSize: 12, color: GM.vellumFaint, marginTop: 12, fontStyle: "italic", fontFamily: GM.font }}>Rolling...</div>
          </div>
        )}

        {phase === "rolling" && sg.type === "coin" && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 60 }}>{coinResult === "heads" ? "◎" : "◉"}</div>
            <div style={{ fontSize: 12, color: GM.vellumFaint, marginTop: 12, fontStyle: "italic", fontFamily: GM.font }}>Flipping...</div>
          </div>
        )}

        {/* RESULT PHASE */}
        {phase === "result" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>
              {result === "win" ? "✦" : "◇"}
            </div>
            {sg.type === "acb" && (
              <div style={{ fontSize: 32, letterSpacing: 16, marginBottom: 12 }}>
                {diceValues.map(d => DICE_FACES[d] || "").join(" ")}
              </div>
            )}
            {sg.type === "coin" && (
              <div style={{ fontSize: 40, marginBottom: 12 }}>{coinResult === "heads" ? "◎" : "◉"} {coinResult}</div>
            )}
            <div style={{ fontSize: 20, fontWeight: 900, fontStyle: "italic", color: result === "win" ? GM.inkGoldBright : SD.red, marginBottom: 8, fontFamily: GM.font }}>
              {result === "win" ? "✦ Shadow Gambling Won!" : "◇ Shadow Gambling Lost"}
            </div>
            <div style={{ fontSize: 12, color: GM.vellumFaint, fontStyle: "italic", marginBottom: 20, fontFamily: GM.font }}>{sg.resultText?.(result)}</div>
            <button onClick={onClose} style={{ padding: "12px 32px", background: `linear-gradient(135deg, ${GM.inkGold}18, ${GM.pageMid})`, border: `1px solid ${GM.inkGold}`, borderRadius: 2, cursor: "pointer", color: GM.inkGoldBright, fontFamily: GM.font, fontSize: 14, fontWeight: 900, fontStyle: "italic" }}>
              ◆ Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// GRIMOIRE SEARCH MODAL — choose level then pay Khao or Anima
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// ATV FEED MODAL — feed Anima/Khao into Necklace tomes
// ─────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════
// ◆ THE TWO ATV TREASURES — R129, AND THE TABLE IS NOT HERE
// ══════════════════════════════════════════════════════════════
// The printed currency, the per-Turn allowance and the threshold were a
// local table in this file (2026-09-14) because rules.js was not mine to
// touch. They are now R129 — `ATV_FEED`, `isFedTreasure` and
// `feedAllowanceLeft` — and the local copy is DELETED rather than left
// to agree by luck. Two copies of a printed number is how the first one
// drifts, and `npcTomeBook` hand-types the same two numbers again, which
// is the third.
//
// ⚠ `feedAllowanceLeft(card, tome, turn)` IS NOT RE-DERIVED HERE. It
// already folds all three limits — the per-Turn allowance, how much of
// it this Turn has spent (`fedAmountThisTurn`), and the store's own
// ceiling. Asking it is the whole calculation.
function ATVFeedModal({ tome, tomeSlotIdx, onFeed, onClose, myAnima, myKhao, currentTurn }) {
  const card = getCard(tome?.id);
  useModalKeys(onClose);
  if (!card) return null;
  const spec = ATV_FEED[card.id];
  if (!spec) return null;
  const isAnima = spec.currency === "anima";
  const threshold = spec.threshold;
  const current = tome.fed || 0;
  const currency = spec.currency;
  const available = isAnima ? myAnima : myKhao;
  // ── "UP TO n PER TURN" IS AN ALLOWANCE, NOT ONE FEED PER TURN ──
  // ⚠ THIS LOCKED THE WHOLE TURN AFTER A SINGLE FEED. Both cards print
  // "Feed this Treasure UP TO n per turn", and a seat that fed 1 could
  // not feed the other 2 — so Joyous Courage took four Turns to fill
  // instead of two and Congealed Death took eight instead of two.
  //
  // ⚠ AND THE ALLOWANCE IS NOW THE ONLY BRAKE ON THE CARD. caP'n,
  // 2026-09-14: *"yes, it can be fed after use, it needs to be ruined
  // for it to be taken off the shatterfield."* The Treasure recharges
  // indefinitely, so `perTurn` went from cosmetic to load-bearing — it
  // is the entire reason a Necklace cannot be cycled twice in one Turn.
  const spentThisTurn = (tome?.fedThisTurn === currentTurn) ? (tome?.fedAmountThisTurn || 0) : 0;
  const maxPerTurn = feedAllowanceLeft(card, tome, currentTurn);
  const alreadyFedThisTurn = maxPerTurn === 0;

  return (
    <div className="sf-modal-shell" style={{ position: "fixed", inset: 0, background: "rgba(5,3,0,0.93)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 420, backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div style={{ position: "relative", background: `linear-gradient(145deg, ${GM.pageMid}, ${GM.pageWarm})`, border: `1px solid ${GM.inkGold}`, borderRadius: 4, padding: "24px 22px", width: "min(92vw, 360px)", fontFamily: GM.font, boxShadow: `0 0 24px rgba(0,0,0,0.8)` }} onClick={e => e.stopPropagation()}>
        <TerrorFrame />
        <div style={{ fontSize: 10, color: GM.inkGold, letterSpacing: 4, marginBottom: 4, fontFamily: GM.font }}>◆ ATV — FEED</div>
        <div style={{ fontSize: 18, fontWeight: 900, fontStyle: "italic", color: GM.vellum, marginBottom: 6, fontFamily: GM.font }}>{card.name}</div>
        <div style={{ fontSize: 11, color: GM.vellumFaint, marginBottom: 16, fontStyle: "italic", fontFamily: GM.font }}>{card.effect}</div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, padding: "10px 14px", background: GM.pageLight, borderRadius: 2 }}>
          <span style={{ fontSize: 12, color: GM.vellumFaint, fontFamily: GM.font }}>Fed so far</span>
          <span style={{ fontSize: 14, fontWeight: 900, color: GM.inkGoldBright, fontFamily: GM.font }}>{current} / {threshold}</span>
        </div>
        {alreadyFedThisTurn ? (
          <div style={{ padding: "16px 14px", textAlign: "center", background: GM.pageLight, borderRadius: 2, border: `1px dashed ${GM.inkBrown}88` }}>
            <div style={{ fontSize: 12, color: GM.vellumFaint, fontStyle: "italic", fontFamily: GM.font }}>
              {current >= threshold ? "Full." : `Fed ${spentThisTurn} of ${spec.perTurn} this turn.`}
            </div>
            <div style={{ fontSize: 10, color: GM.vellumFaint, marginTop: 6, fontFamily: GM.font }}>
              {current >= threshold ? "The store is full." : "Feeding resumes next turn."}
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 11, color: GM.vellumFaint, marginBottom: 10, fontFamily: GM.font }}>Feed up to {maxPerTurn} more {currency === "anima" ? "♥ Anima" : "✦ Khao"} this turn{spentThisTurn > 0 ? ` (${spentThisTurn}/${spec.perTurn} already fed)` : ""}:</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[1,2,3,4].filter(n => n <= maxPerTurn).map(n => (
                <button key={n} onClick={available >= n ? () => onFeed(tomeSlotIdx, n, currency) : undefined}
                  style={{ flex: 1, padding: "12px 0", background: available >= n ? `linear-gradient(135deg, ${GM.inkGold}18, ${GM.pageMid})` : "transparent", border: `1px solid ${available >= n ? GM.inkGold : GM.pageBorder}`, borderRadius: 2, cursor: available >= n ? "pointer" : "not-allowed", color: available >= n ? GM.inkGoldBright : GM.vellumFaint, fontFamily: GM.font, fontSize: 13, fontWeight: 900, opacity: available >= n ? 1 : 0.4 }}>
                  {currency === "anima" ? "♥" : "✦"}{n}
                </button>
              ))}
            </div>
          </>
        )}
        <button onClick={onClose} style={{ width: "100%", marginTop: 14, padding: "10px", background: "transparent", border: `1px solid ${GM.inkBrown}66`, borderRadius: 2, color: GM.vellumFaint, cursor: "pointer", fontFamily: GM.font, fontSize: 12 }}>{alreadyFedThisTurn ? "Close" : "Cancel"}</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DEVOTION MODAL — opponent devotes Anima to contract tomes
// ─────────────────────────────────────────────────────────────
function DevotionModal({ contracts, onDevote, onClose, myAnima }) {
  useModalKeys(onClose);
  return (
    <div className="sf-modal-shell" style={{ position: "fixed", inset: 0, background: "rgba(5,3,0,0.93)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 420, backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div style={{ position: "relative", background: `linear-gradient(145deg, ${GM.pageMid}, ${GM.pageWarm})`, border: `1px solid ${SD.red}88`, borderRadius: 4, padding: "24px 22px", width: "min(92vw, 360px)", fontFamily: GM.font, boxShadow: `0 0 24px rgba(0,0,0,0.8)` }} onClick={e => e.stopPropagation()}>
        <TerrorFrame />
        <div style={{ fontSize: 10, color: SD.red, letterSpacing: 4, marginBottom: 4, fontFamily: GM.font }}>◆ DEVOTION REQUIRED</div>
        <div style={{ fontSize: 16, fontWeight: 900, fontStyle: "italic", color: GM.vellumDim, marginBottom: 16, fontFamily: GM.font }}>Your opponent holds a contract. Devote Anima.</div>
        {contracts.map((c, i) => (
          <div key={i} style={{ marginBottom: 12, padding: "10px 14px", background: GM.pageLight, borderRadius: 2 }}>
            {/* GILDED PICKER SWEEP — the contract itself, staring back at you */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ flexShrink: 0 }}>
                <FieldCard cardId={c.id} size="small" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: GM.inkGoldBright, fontFamily: GM.font }}>{getCard(c.id)?.name}</div>
                <div style={{ fontSize: 10, color: GM.vellumFaint, marginTop: 2, fontFamily: GM.font }}>Devoted: {c.devoted || 0} / {c.id === "tome_king_of_the_castle" ? 8 : 4}</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              {[1,2].map(n => (
                <button aria-label={`Devote ${n} Anima`} key={n} onClick={myAnima >= n ? () => onDevote(n) : undefined}
                  style={{ flex: 1, padding: "10px", background: myAnima >= n ? "rgba(139,32,32,0.2)" : "transparent", border: `1px solid ${myAnima >= n ? SD.red : GM.pageBorder}`, borderRadius: 2, cursor: myAnima >= n ? "pointer" : "not-allowed", color: myAnima >= n ? SD.red : GM.vellumFaint, fontFamily: GM.font, fontSize: 12, fontWeight: 900, opacity: myAnima >= n ? 1 : 0.4 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                    <StatGlyph kind="anima" size={11} color={myAnima >= n ? SD.red : GM.vellumFaint} />{n}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
        <button onClick={onClose} style={{ width: "100%", marginTop: 4, padding: "10px", background: "transparent", border: `1px solid ${GM.inkBrown}66`, borderRadius: 2, color: GM.vellumFaint, cursor: "pointer", fontFamily: GM.font, fontSize: 12 }}>Close (forfeit)</button>
      </div>
    </div>
  );
}

function GrimoireSearchModal({ onSearch, onDraw, onClose, myKhao, myAnima, drawnThisTurn }) {
  const [level, setLevel] = useState(null);
  const [payWith, setPayWith] = useState(null);
  const cost = level ? grimSearchFare(level) : null; // R88 — one fare table
  // R96 on the ANIMA half only: `spendAnima` already refuses a payment that
  // would zero the payer, so a plain `myAnima >= cost` lit a button that the handler
  // then rejected with "Not enough Anima" at exactly `cost` Anima. Ask the
  // same rule the spender asks. Khao is not a life total and has no floor.
  const canPay = cost != null && (payWith === "anima" ? canPayVoluntary(myAnima, cost) : myKhao >= cost);
  useModalKeys(onClose);

  return (
    <div className="sf-modal-shell" style={{ position: "fixed", inset: 0, background: "rgba(5,3,0,0.93)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 420, backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div style={{ background: `linear-gradient(145deg, ${GM.pageWarm}, ${GM.page})`, border: `1px solid ${SD.gold}44`, borderRadius: 4, padding: "24px 22px", width: "min(92vw, 380px)", fontFamily: GM.font, boxShadow: `0 0 0 1px ${SD.gold}22, 0 0 24px ${SD.gold}11`, position: "relative" }} onClick={e => e.stopPropagation()}>
        <TerrorFrame />
        <NoiseOverlay opacity={0.05} />
        <div style={{ fontSize: 10, color: GM.inkGold, letterSpacing: 4, marginBottom: 4 }}>◆ GRIMOIRE</div>
        <div style={{ fontSize: 18, fontWeight: 900, fontStyle: "italic", color: GM.vellumDim, marginBottom: 18 }}>Draw or Search?</div>

        {/* Draw option */}
        <div onClick={drawnThisTurn ? undefined : onDraw} className="sf-plate" style={{ background: drawnThisTurn ? "transparent" : `linear-gradient(135deg, ${GM.pageMid}66, ${GM.page})`, border: `1px solid ${drawnThisTurn ? GM.pageBorder : GM.inkGold}66`, boxShadow: drawnThisTurn ? "none" : `inset 0 0 0 1px ${GM.inkGold}18, inset 0 0 24px rgba(0,0,0,0.5)`, padding: "12px 16px", cursor: drawnThisTurn ? "not-allowed" : "pointer", marginBottom: 12, opacity: drawnThisTurn ? 0.35 : 1 }}>
          <div style={{ fontSize: 13, fontWeight: 900, color: GM.inkGold, marginBottom: 2 }}>◆ Draw 1 Card</div>
          <div style={{ fontSize: 10, color: GM.vellumFaint }}>{drawnThisTurn ? "Already drew this turn" : "Random card from your Grimoire — free"}</div>
        </div>

        {/* Search option */}
        <div className="sf-plate" style={{ border: `1px solid ${SD.gold}55`, background: `linear-gradient(135deg, ${GM.pageMid}33, transparent)`, boxShadow: `inset 0 0 0 1px ${SD.gold}12, inset 0 0 24px rgba(0,0,0,0.45)`, padding: "12px 16px" }}>
          <div style={{ fontSize: 13, fontWeight: 900, color: SD.gold, marginBottom: 8 }}>◈ Search for a Being</div>
          <div style={{ fontSize: 10, color: GM.vellumFaint, marginBottom: 12 }}>Costs your draw. Pick level, then pay Khao or Anima.</div>

          {/* Level picker */}
          <div style={{ fontSize: 10, color: GM.vellumFaint, marginBottom: 6 }}>Choose Level:</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            {[1, 2, 3].map(l => {
              const c = grimSearchFare(l); // R88 — same fare the handler charges
              return (
                <button key={l} onClick={() => setLevel(l)} className="sf-plate" style={{ flex: 1, padding: "10px 0", background: level === l ? `linear-gradient(135deg, ${GM.inkGold}1a, ${GM.pageMid})` : "transparent", border: `1px solid ${level === l ? SD.gold : GM.pageBorder}`, boxShadow: level === l ? `inset 0 0 0 1px ${SD.gold}22, inset 0 0 16px ${SD.gold}0d` : "none", cursor: "pointer", color: level === l ? SD.gold : GM.vellumFaint, fontFamily: GM.font, fontSize: 12, fontWeight: 900 }}>
                  L{l}
                  <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3, fontSize: 9, color: GM.vellumFaint, marginTop: 3 }}>{c} <StatGlyph kind="khao" size={8} color={SD.gold} />/<StatGlyph kind="anima" size={8} color="#c05555" /></span>
                </button>
              );
            })}
          </div>

          {/* Payment picker */}
          {level && (
            <>
              <div style={{ fontSize: 10, color: GM.vellumFaint, marginBottom: 6 }}>Pay with:</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <button onClick={() => setPayWith("khao")} className="sf-plate" style={{ flex: 1, padding: "8px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5, background: payWith === "khao" ? `linear-gradient(135deg, ${GM.inkGold}1a, ${GM.pageMid})` : "transparent", border: `1px solid ${payWith === "khao" ? SD.gold : GM.pageBorder}`, cursor: myKhao >= cost ? "pointer" : "not-allowed", color: myKhao >= cost ? (payWith === "khao" ? SD.gold : GM.vellumFaint) : GM.pageBorder, fontFamily: GM.font, fontSize: 11, opacity: myKhao >= cost ? 1 : 0.4 }}>
                  <StatGlyph kind="khao" size={10} color={myKhao >= cost ? SD.gold : GM.pageBorder} /> {cost} Khao
                </button>
                <button onClick={() => setPayWith("anima")} className="sf-plate" style={{ flex: 1, padding: "8px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5, background: payWith === "anima" ? `linear-gradient(135deg, ${GM.inkGold}1a, ${GM.pageMid})` : "transparent", border: `1px solid ${payWith === "anima" ? SD.gold : GM.pageBorder}`, cursor: canPayVoluntary(myAnima, cost) ? "pointer" : "not-allowed", color: canPayVoluntary(myAnima, cost) ? (payWith === "anima" ? SD.gold : GM.vellumFaint) : GM.pageBorder, fontFamily: GM.font, fontSize: 11, opacity: canPayVoluntary(myAnima, cost) ? 1 : 0.4 }}>
                  <StatGlyph kind="anima" size={10} color={canPayVoluntary(myAnima, cost) ? "#e05555" : GM.pageBorder} /> {cost} Anima
                </button>
              </div>
              <button onClick={canPay && !drawnThisTurn ? () => { onSearch(level, payWith === "anima"); onClose(); } : undefined} disabled={!canPay || drawnThisTurn} className="sf-plate sf-chrome-btn" style={{ width: "100%", padding: "12px", background: canPay && !drawnThisTurn ? `linear-gradient(135deg, ${GM.inkGold}22, ${GM.pageMid})` : "transparent", border: `1px solid ${canPay && !drawnThisTurn ? SD.gold : GM.pageBorder}`, cursor: canPay && !drawnThisTurn ? "pointer" : "not-allowed", color: canPay && !drawnThisTurn ? SD.gold : GM.pageBorder, fontFamily: GM.font, fontSize: 13, fontWeight: 900, fontStyle: "italic", opacity: canPay && !drawnThisTurn ? 1 : 0.4 }}>
                ◆ Search Grimoire
              </button>
            </>
          )}
        </div>

        <button onClick={onClose} className="sf-plate" style={{ width: "100%", marginTop: 12, padding: "10px", background: "transparent", border: `1px solid ${GM.pageBorder}`, color: GM.vellumFaint, cursor: "pointer", fontFamily: GM.font, fontSize: 12 }}>Cancel</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PROPHECY BUTTON — glows when conditions are met
// ─────────────────────────────────────────────────────────────
function ProphecyButton({ prophecyId, myKhao, match, playerIdx, onActivate }) {
  const players = match?.players || {};
  const oppIdx = getTargetIdx(match, playerIdx);
  const me = withEffectiveAnima(match, players[playerIdx], playerIdx);
  const opp = withEffectiveAnima(match, players[oppIdx], oppIdx);
  // (`myField` / `oppField` were unpicked here purely to feed the inline
  // condition below; the engine's function takes the SEATS, so both are
  // gone with it rather than left as two unread locals.)

  // ── THE CONDITION HAS ONE DEFINITION, AND THIS WAS THE SECOND ──
  // ⚠ 40 LINES OF RE-IMPLEMENTED RULE STOOD HERE — its own `isSupreme`,
  // its own `getDivineTitle`, its own School comparison, its own
  // Bloody Hell and Ace of Shades thresholds — under the comment
  // "mirrors prophecyEngine without async". BOTH HALVES OF THAT
  // COMMENT WERE WRONG: `prophecyConditionMet` is a pure synchronous
  // function (it was lifted out of the engine's switch precisely so it
  // could be asked from anywhere), so there was no async barrier to
  // work around; and "mirrors" is a claim about two bodies of code that
  // nothing checked. A copy that happens to agree is a copy that will
  // stop agreeing on the day one card changes.
  //
  // ⭐ IT WAS MEASURED BEFORE IT WAS DELETED, because the swap would
  // have been a BALANCE change if the two had drifted: the lifted body
  // and the engine's function were run against each other over 32,000
  // (board x Prophecy) cases — 8 Prophecies x 4,000 generated boards
  // spanning natural/unnatural, fallen/standing, partial Souls and
  // every Affinity — with the engine answering TRUE in 19.5% of them.
  // Zero disagreements. So this is a deletion of a duplicate, not a
  // change to the rule, and the game now has ONE answer to "is this
  // Prophecy true?" for the automatic door, the manual door and the
  // button that tells the player which.
  const conditionMet = useMemo(
    () => prophecyConditionMet(prophecyId, me, opp),
    [prophecyId, me, opp]
  );

  // ⚠ ONE SOURCE FOR THE FARE — the fourth of the four places this
  // number used to be hand-typed. The button that TELLS the player what
  // a Prophecy costs must read the same row that charges them, or the
  // game lies in the only place the player can see it.
  const cost = getProphecyKhaoCost(prophecyId);
  const canAfford = myKhao >= cost;
  const ready = conditionMet && canAfford;

  const card = [...PROPHECIES].find(c => c.id === prophecyId);

  if (!conditionMet) return (
    <div style={{ fontSize: 9, color: GM.vellumFaint, fontStyle: "italic", fontFamily: GM.font, padding: "4px 8px", border: `1px solid ${GM.inkGoldDim}77`, borderRadius: 6, opacity: 0.85, textShadow: `0 0 5px ${GM.inkGold}33` }}>
      ◇ {card?.name || "Prophecy"}
    </div>
  );

  return (
    <button onClick={ready ? onActivate : undefined} style={{
      padding: "8px 14px", fontFamily: GM.font, fontSize: 11, fontWeight: 900, fontStyle: "italic",
      background: ready ? `linear-gradient(135deg, ${GM.pageMid}, ${GM.pageMid})` : "transparent",
      border: `2px solid ${ready ? SD.gold : GM.vellumFaint}`,
      borderRadius: 8, cursor: ready ? "pointer" : "not-allowed",
      color: ready ? SD.gold : GM.vellumFaint,
      boxShadow: ready ? shimmerBorder(SD.gold) : "none",
      animation: ready ? "mulliganPulse 1.5s ease-in-out infinite" : "none",
      whiteSpace: "nowrap",
    }}>
      {ready ? `◆ Activate — ${card?.name}` : `◇ ${card?.name} (need ${cost} ✦)`}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// COUNTER WINDOW — 3 second window to play a Nihil/Omnia card
// ─────────────────────────────────────────────────────────────
// ── RESPONSE WINDOW (RULEZ_V2.md, canon; false 'July 2026' cite scrapped 2026-08-27) ──────────────
// The "wait!" of physical play, made digital. Materializes ONLY when
// the defender holds a legal, affordable reaction to the declared
// action (the 4xS anti-prompt-fest law — no answer, no pause). Five
// seconds; Escape or "Let it resolve" passes; "Hold this Turn" snoozes
// every window until the Turn changes. Counter options (Nihil/Omnia vs
// an activation) are styled apart from generic interrupts because their
// verdicts travel back through the ResponseBridge to the NPC engine.
function ResponseWindow({ rw, onFire, onPass, onSnooze }) {
  const [timeLeft, setTimeLeft] = useState(5);
  useModalKeys(onPass); // Escape = let it resolve
  useEffect(() => {
    const t = setInterval(() => setTimeLeft(v => v <= 1 ? (clearInterval(t), onPass(), 0) : v - 1), 1000);
    return () => clearInterval(t);
  }, []);

  const a = rw.action || {};
  const headline =
    a.kind === "REBUTTAL"        ? <>Adversary counters with <span style={{ color: SD.red }}>{rw.actingName || "a counter"}</span> — answer it?</> :
    a.kind === "ACTIVATION"      ? <>Adversary activating: <span style={{ color: SD.gold }}>{rw.actingName || "a card"}</span></> :
    a.kind === "ANIMA_ATTACK"    ? <>Adversary strikes your <span style={{ color: SD.red }}>Anima</span> — {a.lyt} incoming</> :
    a.kind === "LYT_ATTACK_TOME" ? <>Adversary attacks your <span style={{ color: SD.gold }}>Tome rift</span> — {a.lyt} Lyt</> :
    <>Lyt Attack declared — <span style={{ color: SD.red }}>{a.lyt} Lyt</span> → <span style={{ color: SD.gold }}>{rw.targetName || "your being"}</span></>;

  return (
    <div style={{ position: "fixed", bottom: 200, left: "50%", transform: "translateX(-50%)", zIndex: 450, fontFamily: GM.font }}>
      <div style={{ background: `linear-gradient(145deg, ${GM.pageWarm}, ${GM.page})`, border: `1px solid ${GM.inkGold}`, borderRadius: 14, padding: "16px 20px", width: "min(94vw, 440px)", boxShadow: shimmerBorder(GM.inkGold) }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 9, color: GM.inkGold, letterSpacing: 3 }}>
              ⚡ RESPONSE WINDOW{(rw.chainDepth || 0) >= 2 ? ` · CHAIN ×${rw.chainDepth}` : ""}
            </div>
            <div style={{ fontSize: 13, fontWeight: 900, fontStyle: "italic", color: GM.vellumDim }}>{headline}</div>
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: timeLeft <= 1 ? SD.red : SD.gold, minWidth: 28, textAlign: "center" }}>{timeLeft}</div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {(rw.options || []).map((opt, i) => {
            const card = getCard(opt.cardId);
            if (!card) return null;
            const isCounter = !!opt.counter;
            const isNihil = opt.counter === "Nihil";
            const color = isCounter ? (isNihil ? GM.inkGold : SD.red) : SD.gold;
            const verb = isCounter ? (isNihil ? "◈ Nullify" : "✕ Counter") : "⚡";
            return (
              <button key={i} onClick={() => onFire(opt)} style={{ padding: "8px 12px", background: `linear-gradient(135deg, ${GM.pageMid}, ${GM.pageMid})`, border: `1px solid ${color}`, borderRadius: 8, cursor: "pointer", color, fontFamily: GM.font, fontSize: 11, fontWeight: 900, fontStyle: "italic" }}>
                {verb} {card.name}
                <span style={{ fontSize: 9, color: GM.vellumFaint }}> ({opt.source === "hand" ? "hand" : "flip"}{(card.khaoCost || 0) > 0 ? ` · ${card.khaoCost}◆` : ""}{opt.late ? " · late — effect resolves first" : ""})</span>
              </button>
            );
          })}
          <button onClick={onPass} style={{ padding: "8px 12px", background: "transparent", border: `1px solid ${GM.pageBorder}`, borderRadius: 8, cursor: "pointer", color: GM.vellumFaint, fontFamily: GM.font, fontSize: 11 }}>
            Let it resolve
          </button>
          <button onClick={onSnooze} style={{ padding: "8px 12px", background: "transparent", border: `1px solid ${GM.pageBorder}`, borderRadius: 8, cursor: "pointer", color: GM.vellumFaint, fontFamily: GM.font, fontSize: 11 }}>
            ⏸ Hold this Turn
          </button>
        </div>
      </div>
    </div>
  );
}

// ── GILDED PICKER — the choice modal shows the ACTUAL card you own:
// real art, real foil, breathing right there in the list. If you own
// multiple foil tiers of a card, tier chips appear under the name so you
// can flip between them and admire each before committing. (The pick
// payload is untouched — same string/object the handlers always got —
// so all 30+ onPick call sites stay byte-identical in behavior.)
function PickerOptionCard({ opt, collection, onPick }) {
  // Resolve the REAL card id. Many pickers use synthetic option ids
  // ("hand_h01_blue_giant", "grim_...", slot numbers, ilk guesses) and
  // stash the true card id under another key — check them all before
  // falling back to `id`. Non-strings (slot indexes) resolve to no card.
  const rawId = typeof opt === "string" ? opt
    : (opt?.cardId || opt?.soulId || opt?.escendantId || opt?.sid || opt?.id);
  const cardId = typeof rawId === "string" ? rawId : null;
  const card = getCard(cardId);
  const label = opt?.label || card?.name || cardId;
  const colRec = (cardId && collection) ? collection[cardId] : null;
  // Every foil tier this player actually OWNS of this card (both schemas).
  const ownedTiers = useMemo(() => {
    if (!colRec) return [];
    const set = [];
    for (const t of TIER_ORDER) {
      if ((colRec.holoTiers?.[t] || 0) > 0) set.push(t);
    }
    if (colRec.holoTier && (colRec.holoCount || 0) >= 1 && !set.includes(colRec.holoTier)) {
      set.unshift(colRec.holoTier);
    }
    return set;
  }, [colRec]);
  const [previewTier, setPreviewTier] = useState(() => ownedTiers[0] || null);
  const hasArt = !!card;
  return (
    <div onClick={() => onPick(opt)} style={{ background: `linear-gradient(135deg, ${GM.pageMid}33, ${GM.page})`, border: `1px solid ${GM.inkGold}44`, borderRadius: 10, padding: "10px 14px", cursor: "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 12 }}>
      {hasArt && (
        <div style={{ flexShrink: 0 }}>
          <FieldCard cardId={cardId} size="small" holoTier={previewTier} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 900, fontStyle: "italic", color: GM.inkGold }}>{label}</div>
        {card?.effect && <div style={{ fontSize: 10, color: GM.vellumFaint, marginTop: 3 }}>{card.effect.substring(0, 80)}</div>}
        {ownedTiers.length > 0 && (
          <div style={{ display: "flex", gap: 5, marginTop: 7, flexWrap: "wrap" }} onClick={e => e.stopPropagation()}>
            {/* Base (non-foil) chip so you can compare against the standard print */}
            <div onClick={() => setPreviewTier(null)} style={{
              fontSize: 8, fontWeight: 900, letterSpacing: 0.5, padding: "3px 7px", borderRadius: 8,
              cursor: "pointer", fontFamily: GM.font,
              color: previewTier === null ? GM.page : GM.vellumFaint,
              background: previewTier === null ? GM.vellumFaint : "transparent",
              border: `1px solid ${GM.pageBorder}`,
              transition: "all 0.15s",
            }}>BASE</div>
            {ownedTiers.map(t => {
              const info = HOLO_TIERS[t];
              if (!info) return null;
              const active = previewTier === t;
              return (
                <div key={t} onClick={() => setPreviewTier(t)} style={{
                  fontSize: 8, fontWeight: 900, letterSpacing: 0.5, padding: "3px 7px", borderRadius: 8,
                  cursor: "pointer", fontFamily: GM.font,
                  color: active ? GM.page : info.color,
                  background: active ? info.color : "transparent",
                  border: `1px solid ${info.color}${active ? "" : "88"}`,
                  boxShadow: active ? `0 0 10px ${info.glowColor}` : "none",
                  transition: "all 0.15s",
                }}>{info.label}</div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function TargetPickerModal({ picker, onClose, collection }) {
  useModalKeys(onClose);
  if (!picker) return null;
  return (
    <div className="sf-modal-shell" style={{ position: "fixed", inset: 0, background: "rgba(5,3,0,0.93)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 420, backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div style={{ position: "relative", background: `linear-gradient(145deg, ${GM.pageWarm}, ${GM.page})`, border: `1px solid ${GM.inkGold}`, borderRadius: 16, padding: "24px 22px", width: "min(92vw, 460px)", maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: shimmerBorder(GM.inkGold), fontFamily: GM.font }} onClick={e => e.stopPropagation()}>
        <TerrorFrame />
        <div style={{ fontSize: 13, fontWeight: 900, fontStyle: "italic", color: GM.inkGold, marginBottom: 16, letterSpacing: 1 }}>{picker.title}</div>
        <div style={{ overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
          {(picker.options || picker.cards || []).map((opt, i) => (
            <PickerOptionCard key={i} opt={opt} collection={collection} onPick={picker.onPick} />
          ))}
        </div>
        <button onClick={onClose} style={{ marginTop: 14, padding: "10px", background: "transparent", border: `1px solid ${GM.pageBorder}`, borderRadius: 8, color: GM.vellumFaint, cursor: "pointer", fontFamily: GM.font, fontSize: 12 }}>Cancel</button>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// PASS 3 — SOLREND END CINEMATIC
// Phased orchestration between match-finished and VictoryScreen:
//   Phase 1 (0–800ms)   — board dims, cards tremble
//   Phase 2 (800–1400)  — singularity bloom + inward rings
//   Phase 3 (1400–1950) — white flash at peak
//   Phase 4 (1950+)     — VictoryScreen rises from light
// ═════════════════════════════════════════════════════════════
function SolrendEndCinematic({ winnerName, coWon = false, onComplete, onExit }) {
  const [phase, setPhase] = useState(1); // 1 → 2 → 3 → 4
  useEffect(() => {
    const t2 = setTimeout(() => setPhase(2), 800);
    const t3 = setTimeout(() => setPhase(3), 1400);
    const t4 = setTimeout(() => { setPhase(4); onComplete && onComplete(); }, 1950);
    return () => { clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, []);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 490,
      pointerEvents: phase >= 4 ? "auto" : "none",
      fontFamily: GM.font,
    }}>

      {/* ── PHASE 2 — Inward-pulling void rings ── */}
      {phase >= 2 && phase < 4 && (
        <>
          {[0, 180, 360].map((delay, i) => (
            <div key={i} className="sf-solrend-ring" style={{
              position: "absolute", top: "50%", left: "50%",
              width: 600, height: 600, borderRadius: "50%",
              border: `2px solid ${i === 0 ? SD.purpleLight : i === 1 ? SD.red : GM.inkGoldBright}`,
              boxShadow: `0 0 40px ${i === 0 ? SD.purple : i === 1 ? SD.red : GM.inkGold}88`,
              animationDelay: `${delay}ms`,
              pointerEvents: "none",
            }} />
          ))}
        </>
      )}

      {/* ── PHASE 2 — Singularity bloom at center ── */}
      {phase >= 2 && (
        <div className="sf-solrend-bloom" style={{
          position: "absolute", top: "50%", left: "50%",
          width: 320, height: 320, borderRadius: "50%",
          background: `radial-gradient(circle,
            #fffbe8 0%,
            ${GM.inkGoldBright} 20%,
            ${SD.gold} 40%,
            ${SD.purple} 70%,
            transparent 95%)`,
          mixBlendMode: "screen",
          pointerEvents: "none",
        }} />
      )}

      {/* ── PHASE 3 — White flash, peak of singularity ── */}
      {phase >= 3 && phase < 4 && (
        <div className="sf-solrend-flash" style={{
          position: "absolute", inset: 0,
          background: `radial-gradient(circle at 50% 50%,
            #ffffff 0%,
            #fff8d8 25%,
            ${GM.inkGoldBright}dd 50%,
            #1a0820aa 80%,
            transparent 100%)`,
          pointerEvents: "none",
        }} />
      )}

      {/* ── PHASE 4 — VictoryScreen rises from the light ── */}
      {phase >= 4 && (
        <VictoryScreen winnerName={winnerName} coWon={coWon} onExit={onExit} animateIn={true} />
      )}
    </div>
  );
}

// R98 — `coWon` is the only new thing on this screen: the name line
// already carries both names (see winnerName), and this changes the
// verb under it from "has won the duel" to "BOTH win the Solrend", so
// a player never has to guess whether the second name is an ally.
function VictoryScreen({ winnerName, coWon = false, onExit, animateIn = false }) {
  useModalKeys(onExit, { delay: 800 }); // delay prevents accidental skip
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(5,3,0,0.97)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500, backdropFilter: "blur(8px)", fontFamily: GM.font }}>
      <div className={animateIn ? "sf-victory-rise" : ""} style={{ background: `linear-gradient(145deg, ${GM.pageMid}, ${GM.pageWarm})`, border: `1px solid ${GM.inkGold}`, borderRadius: 4, padding: "48px 56px", textAlign: "center", boxShadow: `0 0 40px rgba(0,0,0,0.9), 0 0 20px ${GM.inkGold}22`, maxWidth: 420 }}>
        <div style={{ fontSize: 14, letterSpacing: 6, color: GM.inkGoldDim, fontStyle: "italic", marginBottom: 24, fontFamily: GM.font }}>◆ The Solrend is Over ◆</div>
        <div style={{ background: GM.page, border: `1px solid ${GM.inkBrown}`, borderRadius: 2, padding: "28px", marginBottom: 28 }}>
          <div style={{ fontSize: 30, fontWeight: 900, fontStyle: "italic", color: GM.inkGoldBright, marginBottom: 12, filter: "drop-shadow(0 0 16px rgba(200,150,60,0.5))", fontFamily: GM.font }}>{winnerName}</div>
          <div style={{ fontSize: 15, color: GM.vellumDim, fontFamily: GM.font }}>{coWon ? "BOTH win the Solrend — a Prophecy grants a win, it does not prevent one" : "has won the duel"}</div>
        </div>
        <button onClick={onExit} style={{ background: `linear-gradient(135deg, ${GM.pageMid}, ${GM.pageMid})`, border: `1px solid ${GM.inkGold}`, color: GM.inkGoldBright, padding: "16px 44px", borderRadius: 2, cursor: "pointer", fontFamily: GM.font, fontSize: 15, fontStyle: "italic", letterSpacing: 2 }}>← Return to Menu</button>
      </div>
    </div>
  );
}

// Pass 1 — fires a short CSS animation whenever `value` changes.
// Returns the className to apply for the duration of the animation,
// then clears. Direction-aware (up/down) for HP so drops can glow red,
// gains can glow green — here we just detect "change" and leave styling
// to the caller's flash class.
function useFlashOnChange(value, { duration = 600, direction = false } = {}) {
  const [flash, setFlash] = useState(null); // null | "up" | "down" | "change"
  const prevRef = useRef(value);
  useEffect(() => {
    if (prevRef.current === value) return;
    if (direction) {
      const prev = Number(prevRef.current);
      const next = Number(value);
      if (!Number.isNaN(prev) && !Number.isNaN(next)) {
        setFlash(next > prev ? "up" : "down");
      } else {
        setFlash("change");
      }
    } else {
      setFlash("change");
    }
    prevRef.current = value;
    const t = setTimeout(() => setFlash(null), duration);
    return () => clearTimeout(t);
  }, [value, duration, direction]);
  return flash;
}

// ═══════════════════════════════════════
// COMPANION HUD — the duel familiar → `./CompanionHUD.js`
// ───────────────────────────────────────
// SHIP 10 (2026-09-16): the frozen private copy that lived here since
// the July 25 extraction (ensureCompanionHudKeyframes / CompFloatHeart /
// CompanionHUD / SF_RADIANT_AURA) is DELETED, exactly as its own
// migration note asked — the 2D board now mounts the same component the
// 3D board mounts, so the companion's body (companionBody3D.jsx) shows
// on both. One face, one file.
// ═══════════════════════════════════════

// Single stat cell — uses useFlashOnChange to animate when its value changes.
// ═════════════════════════════════════════════════════════════
// ABSOLUTE SHATTERFIELD CHROME — redesign components
// Gold-chrome ornate frames, crystalline Anima heart, Khao vortex.
// All upright for both players; positioned to mirror the mockup.
// ═════════════════════════════════════════════════════════════

// Ornate gold corner brackets — drawn as 4 little L-frames around a box.
function OrnateCorners({ color = GM.inkGold, inset = -2, len = 9, thickness = 1.5 }) {
  const arm = (pos) => {
    const base = { position: "absolute", pointerEvents: "none" };
    const h = { width: len, height: thickness, background: color, boxShadow: `0 0 4px ${color}88` };
    const v = { width: thickness, height: len, background: color, boxShadow: `0 0 4px ${color}88` };
    switch (pos) {
      case "tl": return [<div key="a" style={{ ...base, ...h, top: inset, left: inset }} />, <div key="b" style={{ ...base, ...v, top: inset, left: inset }} />];
      case "tr": return [<div key="a" style={{ ...base, ...h, top: inset, right: inset }} />, <div key="b" style={{ ...base, ...v, top: inset, right: inset }} />];
      case "bl": return [<div key="a" style={{ ...base, ...h, bottom: inset, left: inset }} />, <div key="b" style={{ ...base, ...v, bottom: inset, left: inset }} />];
      case "br": return [<div key="a" style={{ ...base, ...h, bottom: inset, right: inset }} />, <div key="b" style={{ ...base, ...v, bottom: inset, right: inset }} />];
      default: return null;
    }
  };
  return <>{arm("tl")}{arm("tr")}{arm("bl")}{arm("br")}</>;
}

// Ornate nameplate — hexagonal sigil + player name in a gold-chrome frame.
function NamePlate({ name, isMe, isActive }) {
  // MAGNUM PASS 4 — the opposition reads crimson. Border, corners,
  // hex sigil, and glow all follow the accent; vellum name stays shared.
  const A = isMe
    ? { edge: GM.inkGold,  bright: GM.inkGoldBright, glow: GM.inkGold }
    : { edge: "#a83232",   bright: "#e05555",        glow: "#c03a3a" };
  return (
    <div style={{
      position: "relative",
      display: "inline-flex", alignItems: "center", gap: isMobile ? 5 : 9,
      padding: isMobile ? "4px 9px 4px 7px" : "7px 16px 7px 11px",
      background: `linear-gradient(135deg, ${GM.pageWarm}ee, ${GM.page}f2)`,
      border: `1.5px solid ${A.edge}`,
      borderRadius: 4,
      boxShadow: isActive
        ? `0 0 18px ${A.glow}55, inset 0 0 16px ${A.edge}14, 0 2px 8px rgba(0,0,0,0.6)`
        : `inset 0 0 12px ${A.edge}0a, 0 2px 8px rgba(0,0,0,0.6)`,
      transition: "box-shadow 0.4s",
      minWidth: 0,
    }}>
      <OrnateCorners color={A.bright} inset={2} len={isMobile ? 6 : 8} />
      {/* Hex sigil */}
      <svg width={isMobile ? 18 : 26} height={isMobile ? 19 : 28} viewBox="0 0 26 28" style={{ flexShrink: 0, filter: `drop-shadow(0 0 5px ${A.glow}66)` }}>
        <polygon points="13,2 23,8 23,20 13,26 3,20 3,8" fill="none" stroke={A.edge} strokeWidth="1.3" />
        <polygon points="13,7 18.5,10.5 18.5,17.5 13,21 7.5,17.5 7.5,10.5" fill={`${A.edge}1f`} stroke={A.bright} strokeWidth="0.9" />
        <path d="M 13 10.5 L 16 13.5 L 13 17.5 L 10 13.5 Z" fill={A.bright} opacity="0.92" />
      </svg>
      <span style={{
        fontSize: isMobile ? 10 : 14, fontWeight: 900, fontStyle: "italic",
        letterSpacing: isMobile ? 1.5 : 3, fontFamily: GM.font,
        color: GM.vellum,
        textShadow: `0 0 10px ${A.glow}66`,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        overflow: "hidden", textOverflow: "ellipsis",
      }}>{name || "???"}</span>
    </div>
  );
}

// Framed timer — gold-chrome box, only lit while it's that player's turn.
function FramedTimer({ secs, isActive, urgent, isMe = true }) {
  // MAGNUM PASS 4 — opponent timer wears crimson chrome; urgency
  // overrides both sides with hot red.
  const edge = urgent ? "#dc2626" : isMe ? GM.inkGold : "#a83232";
  const col  = urgent ? "#dc2626" : isMe ? GM.inkGoldBright : "#e05555";
  return (
    <div style={{
      position: "relative",
      padding: isMobile ? "3px 10px" : "5px 20px",
      background: `linear-gradient(135deg, ${GM.pageWarm}ee, ${GM.page}f2)`,
      border: `1.5px solid ${edge}`,
      borderRadius: 4,
      boxShadow: isActive
        ? `0 0 16px ${col}44, inset 0 0 12px ${col}14, 0 2px 8px rgba(0,0,0,0.6)`
        : `inset 0 0 10px ${edge}0a, 0 2px 8px rgba(0,0,0,0.6)`,
      opacity: isActive ? 1 : 0.5,
      transition: "all 0.4s",
      flexShrink: 0,
      // D8. Only on the pill whose clock is actually running — a
      // heartbeat on the idle seat's timer would be a lie about
      // whose problem the time is.
      animation: (urgent && isActive) ? "sf-timer-heartbeat 1s ease-in-out infinite" : "none",
      willChange: (urgent && isActive) ? "transform" : "auto",
    }} className={(urgent && isActive) ? "sf-timer-heartbeat" : undefined}>
      <OrnateCorners color={urgent ? "#ff6b6b" : isMe ? GM.inkGoldBright : "#e05555"} inset={2} len={isMobile ? 6 : 7} />
      <span style={{
        fontSize: isMobile ? 14 : 24, fontWeight: 900, fontStyle: "italic",
        letterSpacing: isMobile ? 1 : 2, fontFamily: GM.font, color: col,
        textShadow: `0 0 12px ${col}66`,
      }}>{formatTime(secs)}</span>
    </div>
  );
}

// Crystalline Anima heart — faceted red gem + ANIMA label + value.
function AnimaHeart({ value, isMe, isActive }) {
  const flash = useFlashOnChange(value, { duration: 600 });
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: isMobile ? 5 : 10,
      opacity: isActive ? 1 : 0.82, transition: "opacity 0.3s",
    }}>
      <svg width={isMobile ? 42 : 78} height={isMobile ? 42 : 78} viewBox="0 0 80 80"
        style={{ flexShrink: 0, filter: "drop-shadow(0 0 14px #c0202088) drop-shadow(0 0 6px #ff334466)" }}>
        <defs>
          <linearGradient id={`animaG-${isMe ? "me" : "op"}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ff5a6e" />
            <stop offset="45%" stopColor="#d31030" />
            <stop offset="100%" stopColor="#6e0014" />
          </linearGradient>
        </defs>
        {/* heart silhouette built from faceted triangles */}
        <path d="M40 72 L10 40 Q4 30 12 22 Q22 12 32 22 L40 30 L48 22 Q58 12 68 22 Q76 30 70 40 Z"
          fill={`url(#animaG-${isMe ? "me" : "op"})`} stroke="#ff8294" strokeWidth="1" />
        {/* facet lines */}
        <g stroke="#ffb3c0" strokeWidth="0.7" opacity="0.55" fill="none">
          <path d="M40 30 L22 40 L40 64 Z" />
          <path d="M40 30 L58 40 L40 64 Z" />
          <path d="M22 40 L14 34 M58 40 L66 34 M40 30 L40 22" />
          <path d="M22 40 L32 50 M58 40 L48 50" />
        </g>
        <path d="M30 26 Q26 24 24 28" stroke="#ffd0d8" strokeWidth="1.4" opacity="0.7" fill="none" strokeLinecap="round" />
      </svg>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 0.95 }}>
        <span style={{ fontSize: isMobile ? 10 : 17, fontWeight: 900, fontStyle: "italic", letterSpacing: isMobile ? 1.5 : 3, fontFamily: GM.font, color: SD.red, textShadow: `0 0 10px ${SD.red}66`, textTransform: "uppercase" }}>Anima</span>
        <span className={flash ? "sf-hp-flash" : ""} style={{ fontSize: isMobile ? 22 : 40, fontWeight: 900, fontFamily: GM.font, color: SD.red, textShadow: `0 0 16px ${SD.red}88`, display: "inline-block", transformOrigin: "left center" }}>{value}</span>
      </div>
    </div>
  );
}

// Khao vortex — purple swirl + KHAO label + value/max.
function KhaoVortex({ khao, maxKhao, isMe, isActive }) {
  const flash = useFlashOnChange(`${khao}/${maxKhao}`, { duration: 600 });
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: isMobile ? 5 : 10, flexDirection: "row-reverse",
      opacity: isActive ? 1 : 0.82, transition: "opacity 0.3s",
    }}>
      <div data-khao-well={isMe ? "me" : "opp"} style={{
        width: isMobile ? 42 : 84, height: isMobile ? 42 : 84, borderRadius: "50%",
        flexShrink: 0, position: "relative", overflow: "hidden",
        background: "radial-gradient(circle at 50% 50%, #1a0533 0%, #3b0764 28%, #6d28d9 58%, #2a0a4a 100%)",
        boxShadow: `0 0 22px #6d28d988, inset 0 0 20px #1a0533, 0 0 0 1.5px #9333ea66`,
      }}>
        <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "conic-gradient(from 0deg, transparent, #a855f7aa, transparent 40%, #7c3aed99, transparent 75%)", animation: "sf-khaoSpin 7s linear infinite" }} />
        <div style={{ position: "absolute", inset: "30%", borderRadius: "50%", background: "radial-gradient(circle, #0a0014 0%, #2a0a4a 100%)", boxShadow: "inset 0 0 12px #000" }} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 0.95, alignItems: "flex-end" }}>
        <span style={{ fontSize: isMobile ? 10 : 17, fontWeight: 900, fontStyle: "italic", letterSpacing: isMobile ? 1.5 : 3, fontFamily: GM.font, color: SD.gold, textShadow: `0 0 10px ${SD.gold}66`, textTransform: "uppercase" }}>Khao</span>
        <span className={flash ? "sf-khao-flash" : ""} style={{ fontSize: isMobile ? 19 : 34, fontWeight: 900, fontFamily: GM.font, color: SD.gold, textShadow: `0 0 16px ${SD.gold}88`, display: "inline-block", transformOrigin: "right center" }}>{khao}/{maxKhao}</span>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// DUST (2026-08-18) — D9 the Necropolis breath · D15 the Khao
// ember trail. Imperative WAAPI motes on the crumble's discipline:
// spawn into document.body, animate, remove. No state, no keyframe
// injection, nothing to clean up but the nodes themselves.
// ═════════════════════════════════════════════════════════════

// D9 — the discard slot exhales when a card enters. Faint by
// decree: two or three spectral motes, a slow rise, gone.
function exhaleWisp(el) {
  if (!el || typeof document === "undefined") return;
  const r = el.getBoundingClientRect();
  if (!r.width) return;
  const n = 2 + Math.floor(Math.random() * 2);
  for (let i = 0; i < n; i++) {
    const m = document.createElement("div");
    const size = 5 + Math.random() * 4;
    const x = r.left + r.width * (0.30 + Math.random() * 0.40);
    const y = r.top + 4 + Math.random() * 6;
    m.style.cssText = `position:fixed;left:0;top:0;width:${size}px;height:${size}px;border-radius:50%;pointer-events:none;z-index:460;background:radial-gradient(circle, #c4b5fdcc 0%, #a78bfa88 45%, transparent 72%);filter:blur(1.6px);will-change:transform,opacity;`;
    document.body.appendChild(m);
    const drift = (Math.random() - 0.5) * 18;
    const rise = 26 + Math.random() * 20;
    const anim = m.animate([
      { transform: `translate(${x}px, ${y}px) scale(0.7)`, opacity: 0 },
      { transform: `translate(${x + drift * 0.4}px, ${y - rise * 0.35}px) scale(1)`, opacity: 0.55, offset: 0.3 },
      { transform: `translate(${x + drift}px, ${y - rise}px) scale(1.6)`, opacity: 0 },
    ], { duration: 1250 + Math.random() * 350, delay: i * 150, easing: "cubic-bezier(0.25,0.6,0.5,1)", fill: "forwards" });
    anim.onfinish = () => m.remove();
  }
}

// D15 — spending Khao is visible: embers leave the vortex and
// drift to where the action happened. Same hold-bright/sink-late
// arc the coin crumble taught us — a trail that dims mid-flight
// reads as nothing.
function khaoEmberTrail(target, amount = 1) {
  if (!target || typeof document === "undefined") return;
  const well = document.querySelector('[data-khao-well="me"]');
  if (!well) return;
  const r = well.getBoundingClientRect();
  if (!r.width) return;
  const sx = r.left + r.width / 2, sy = r.top + r.height / 2;
  const tx = target.x, ty = target.y;
  if (typeof tx !== "number" || typeof ty !== "number") return;
  const n = Math.min(3 + (amount | 0), 7);
  for (let i = 0; i < n; i++) {
    const m = document.createElement("div");
    const size = 3 + Math.random() * 2.5;
    m.style.cssText = `position:fixed;left:0;top:0;width:${size}px;height:${size}px;border-radius:50%;pointer-events:none;z-index:460;background:radial-gradient(circle, #ffd54aee 0%, #f8717199 55%, transparent 78%);filter:blur(0.8px);will-change:transform,opacity;`;
    document.body.appendChild(m);
    const j0x = (Math.random() - 0.5) * 16, j0y = (Math.random() - 0.5) * 16;
    // Mid-arc waypoint: pulled toward the target with lift and jitter.
    const jx = sx + (tx - sx) * (0.45 + Math.random() * 0.15) + (Math.random() - 0.5) * 46;
    const jy = sy + (ty - sy) * (0.45 + Math.random() * 0.15) - (14 + Math.random() * 26);
    const anim = m.animate([
      { transform: `translate(${sx + j0x}px, ${sy + j0y}px) scale(1)`, opacity: 0.95, offset: 0 },
      { transform: `translate(${jx}px, ${jy}px) scale(0.92)`, opacity: 0.9, offset: 0.55, easing: "cubic-bezier(0.3,0.7,0.5,1)" },
      { transform: `translate(${sx + (tx - sx) * 0.9}px, ${sy + (ty - sy) * 0.9}px) scale(0.7)`, opacity: 0.85, offset: 0.86, easing: "cubic-bezier(0.5,0,0.75,0.45)" },
      { transform: `translate(${tx}px, ${ty}px) scale(0.3)`, opacity: 0, offset: 1, easing: "ease-in" },
    ], { duration: 520 + Math.random() * 260, delay: i * 60, fill: "forwards" });
    anim.onfinish = () => m.remove();
  }
}

// ── THE GRIM'S OWN COLOUR (caP'n, July 28) ───────────────────
// "After turn one the light stays on as normal for the Lux,
//  however, now there is a Red version for the Grimoire."
// The tile was borrowing SD.purple, which said nothing; it now
// carries a red of its own at rest.
//
// ⚠ COLLISION, HANDLED: the tiles ALREADY flip to SD.red as the
// hand-full warning at 6+. Two reds would mean the warning stops
// warning — so the resting red is deliberately DEEPER and duller
// than SD.red, and the warning keeps the brighter one. Rest is a
// colour; the warning is still a change.
const GRIM_LIT = "#a32020";

// Framed draw tile — gold-chrome Lux / Grim tile matching the mockup.
//
// ── THE DRAW PULSE (caP'n, July 28) ──────────────────────────
// `pulse` is a { base, hot } tone or null. When present the tile
// BREATHES on that colour so a first-turn player cannot walk past
// their draw. It is set by the board, never decided here — this
// component is a renderer and owns no turn logic.
//
// ── `sealed` — R07 MADE VISIBLE ──────────────────────────────
// The first player's GRIMOIRE is sealed on Turn 1, and until now
// the tile did not say so: it rendered fully enabled, took the
// click, and only THEN answered with a toast. A tile that looks
// live and isn't is the exact confusion the pulse exists to
// prevent, so a sealed tile now reads dead and reads SEALED.
// ── TONE vs BEAT (2026-08-18) ────────────────────────────────
// `tone` is the equipped cosmetic's colour and it is worn for the
// WHOLE Solrend. `pulse` is the first-turn teaching beat and it is
// null the rest of the time. They used to be the same prop, so
// switching the teacher off also stripped the paint — a bought aura
// that lasted exactly one turn. See the TONE vs BEAT note in
// DrawPulse.js.
//
// `warn` is the overdraw signal (hand at 6+). GAMEPLAY BEATS
// COSMETIC: when it is on, the tile goes red and the tone stands
// down. Passed explicitly rather than inferred from `color`,
// because inferring it means comparing colour strings and that
// breaks silently the first time a palette moves.
function FramedDrawTile({ icon, label, color, enabled, used, onClick, isMe, flipped, pulse = null, tone = null, warn = false, sealed = false }) {
  const live = enabled && !sealed;
  const beat = !!pulse && live && !used;
  // The resting aura shows on a live, unused, un-warned tile. A used
  // tile keeps its 0.32 opacity and says nothing — you already drew.
  const rest = !beat && !!tone && live && !used && !warn;
  const edge = warn ? color : beat ? pulse.base : rest ? tone.base : color;
  const corner = warn ? color : beat ? pulse.hot : rest ? tone.hot : color;
  return (
    <div onClick={live ? onClick : undefined} className={live ? "sf-tile-hover" : ""} style={{
      position: "relative",
      width: isMobile ? 44 : 54, height: isMobile ? 50 : 62,
      borderRadius: 5,
      background: live
        ? `linear-gradient(145deg, ${GM.pageMid}, ${GM.pageWarm})`
        : `linear-gradient(145deg, ${GM.pageWarm}, ${GM.pageMid})`,
      border: `1.5px solid ${sealed ? GM.pageBorder : live ? edge : GM.inkGold + "55"}`,
      boxShadow: live ? `0 0 12px ${color}33, inset 0 0 10px ${GM.inkGold}0a, 0 2px 6px rgba(0,0,0,0.5)` : `0 2px 6px rgba(0,0,0,0.5)`,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2,
      cursor: live ? "pointer" : "default",
      opacity: sealed ? 0.4 : used ? 0.32 : 1,
      transition: "all 0.2s",
      transform: flipped ? "rotate(180deg)" : "none",
      // The pulse comes last so its animation + box-shadow win the
      // cascade over the resting glow above.
      // The beat wins the cascade over the resting glow; the resting
      // glow wins over the flat one above it. Exactly one of the three
      // is ever applied.
      ...(beat ? toneStyle(pulse, pulse.ms) : rest ? restStyle(tone) : null),
    }}>
      {/* the ring — a second, slower ripple so the cue reads even in
          peripheral vision on a busy board. Pointer-events off: it
          must never eat the tap it exists to attract. */}
      {beat && (
        <div style={{
          position: "absolute", inset: -3, borderRadius: 7, pointerEvents: "none",
          border: `1.5px solid ${pulse.hot}`,
          animation: `dpRing ${(pulse.ms || 1150) * 1.5}ms ease-out infinite`,
        }} />
      )}
      <OrnateCorners color={sealed ? GM.pageBorder : live ? corner : GM.inkGold + "66"} inset={2} len={6} />
      <span style={{
        fontSize: 18,
        filter: beat ? `drop-shadow(0 0 6px ${pulse.hot})` : rest ? `drop-shadow(0 0 4px ${tone.base})` : "none",
        animation: beat ? `dpGlyph ${(pulse.ms || 1150)}ms ease-in-out infinite` : "none",
      }}>{icon}</span>
      {isMe && <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: 2, color: sealed ? GM.vellumFaint : beat ? pulse.hot : live ? color : GM.vellumFaint, fontFamily: GM.font }}>{sealed ? "SEALED" : used ? "USED" : label}</span>}
    </div>
  );
}

// Framed action button — gold-chrome pill for Interrupt / Mulligan / End Turn.
function SFActionButton({ icon, label, onClick, disabled, accent = GM.inkGold, strong = false, big = false }) {
  const pad = big ? "14px 32px" : strong ? "10px 22px" : "7px 16px";
  const fs  = big ? 15 : strong ? 12 : 11;
  return (
    <button onClick={onClick} disabled={disabled} className="sf-chrome-btn sf-plate" style={{
      position: "relative", overflow: "hidden",
      padding: pad,
      background: disabled ? "transparent" : `linear-gradient(135deg, ${GM.pageWarm}ee, ${GM.page}f2)`,
      border: `1.5px solid ${disabled ? GM.inkGold + "44" : accent}`,
      cursor: disabled ? "not-allowed" : "pointer",
      fontFamily: GM.font, fontSize: fs, fontWeight: 900,
      fontStyle: "italic", letterSpacing: 2,
      color: disabled ? GM.vellumFaint : accent === GM.inkGold ? GM.inkGoldBright : accent,
      whiteSpace: "nowrap",
      boxShadow: disabled ? "none" : `0 0 12px ${accent}33, inset 0 0 10px ${accent}10, 0 2px 6px rgba(0,0,0,0.5)`,
      opacity: disabled ? 0.45 : 1,
      transition: "all 0.25s",
    }}>
      {icon ? `${icon} ` : ""}{label}
    </button>
  );
}

function PlayerSide({ player, isMe, myTurn, onSlotAction, onTomeAction, onNecropolisAction, attackingSlot, targetMode, sharedTimer, onDrawGrimoire, onDrawLuxmoire, canDraw, drawnThisTurn, selectedHandIdx, onHandCardClick, handHint, collection, oppField, oppAnima, stampsFor = null,
                      // ── THE DRAW PULSE — all DECIDED by the board and merely
                      // worn here. `*Pulse` is the first-turn BEAT (null the
                      // rest of the duel); `*Tone` is the bought colour and is
                      // present every turn. Splitting them is what stopped a
                      // purchased aura from vanishing after turn one — see the
                      // TONE vs BEAT note in DrawPulse.js.
                      luxPulse = null, grimPulse = null,
                      luxTone = null, grimTone = null, grimSealed = false,
                      // NEW — drag-to-play + hand animations (all optional)
                      draggingHandIdx, enteringHandIdxs, exitingHandCards, onHandDragStart, onHandDragEnd, onRiftDrop, prophecyBtnProps,
                      // R111/R113 — the two Arts with no rift of their own.
                      // Passed only for `isMe`; the adversary's Exile tile
                      // stays exactly the read-only pile it has always been.
                      onExileAction = null }) {
  // ── Derive everything from `player` FIRST with null-safe defaults.
  // Hooks below depend on these, and Rules of Hooks requires every
  // hook to run unconditionally on every render — so we cannot
  // early-return before them. Instead, we derive safely (empty arrays
  // when player is missing) and let the early-return gate the RENDER
  // further down after all hooks have fired.
  const field = player?.field || {};
  const esclings = riftArray(field.esclings, ESCLING_SLOTS);   // R110
  const tomes = [field.tomes?.[0] ?? null, field.tomes?.[1] ?? null, field.tomes?.[2] ?? null];
  const curios = [field.curios?.[0] ?? null, field.curios?.[1] ?? null];
  const prophecy = field.prophecy || player?.prophecy || null;
  const isActive = isMe ? myTurn : !myTurn;
  const necropolis = player?.necropolis || [];

  // ── Revival detector — cards removed from necropolis in the last tick.
  // When the necropolis loses a card and that same card appears on the
  // field, we flag it as revived so FieldCard can play the green glow.
  const prevNecropolisRef = useRef(necropolis);
  const [recentlyRevived, setRecentlyRevived] = useState({}); // { cardId: expiresAt }
  useEffect(() => {
    const prev = prevNecropolisRef.current || [];
    const curr = necropolis;
    // Find cards that were in prev but not curr (removed from necropolis)
    const currCounts = curr.reduce((acc, id) => { acc[id] = (acc[id]||0)+1; return acc; }, {});
    const removed = [];
    for (const id of prev) {
      const c = currCounts[id] || 0;
      if (c > 0) { currCounts[id] = c - 1; } else { removed.push(id); }
    }
    if (removed.length) {
      const now = Date.now();
      setRecentlyRevived(r => {
        const next = { ...r };
        removed.forEach(id => { next[id] = now + 1200; });
        return next;
      });
      // Sweep expired entries after a short delay
      setTimeout(() => {
        setRecentlyRevived(r => {
          const n = Date.now();
          return Object.fromEntries(Object.entries(r).filter(([, exp]) => exp > n));
        });
      }, 1300);
    }
    prevNecropolisRef.current = curr;
  }, [necropolis]);

  // ── D9 — THE NECROPOLIS BREATH (2026-08-18) ─────────────────
  // When the pile GROWS, the slot exhales. Own count ref (the
  // revival detector above owns prevNecropolisRef and watches the
  // shrink direction); seeded on first run so a mid-duel mount
  // never greets you with a sigh over cards long dead.
  const necroTileRef = useRef(null);
  const prevNecroCountRef = useRef(null);
  useEffect(() => {
    const count = necropolis.length;
    if (prevNecroCountRef.current === null) { prevNecroCountRef.current = count; return; }
    if (count > prevNecroCountRef.current) exhaleWisp(necroTileRef.current);
    prevNecroCountRef.current = count;
  }, [necropolis.length]);

  // ── THE HAND THAT FITS (July 26, 2026 — caP'n: "there's always a card
  //    or 2 I can't see"). The fan overlapped by a HARD-CODED −22px no
  //    matter how many cards were held, so a 3-card hand wasted half the
  //    rail while an 8-card hand hid six cards behind five slivers. The
  //    rail now MEASURES itself and solves for the step: spread to full
  //    card width + gap whenever the hand fits (which, at HAND_LIMIT 8,
  //    it now almost always does), and only close ranks — evenly, down
  //    to a floor that still shows a readable strip — when it can't.
  const handRailRef = useRef(null);
  const [handRailW, setHandRailW] = useState(0);
  useEffect(() => {
    const el = handRailRef.current;
    if (!el) return;
    // clientWidth, never innerWidth — the scrollbar lies (room-chrome law).
    const measure = () => setHandRailW(el.clientWidth || 0);
    measure();
    let ro = null;
    if (typeof ResizeObserver !== "undefined") { ro = new ResizeObserver(measure); ro.observe(el); }
    window.addEventListener("resize", measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [isMe]);

  // The fan geometry, solved once per render. `handStep` is the distance
  // between successive card LEFT EDGES — equal to a full card + gap when
  // there's room (no overlap at all), shrinking evenly when there isn't,
  // and floored so no card is ever reduced to a sliver.
  const HAND_CARD_W = SIZES.hand.width;
  const HAND_GAP    = isMobile ? 4 : 6;
  const handCount   = (player?.hand || []).length;
  const handAvail   = handRailW || (isMobile ? 340 : 640);
  const idealStep   = HAND_CARD_W + HAND_GAP;
  const fitStep     = handCount > 1 ? (handAvail - HAND_CARD_W) / (handCount - 1) : idealStep;
  const handStep    = Math.max(Math.round(HAND_CARD_W * 0.46), Math.min(idealStep, Math.floor(fitStep)));
  const handTight   = handStep < idealStep;  // ranks closed → arc + spines earn their keep
  const handFanArc  = handCount > 1 ? Math.min(3, 20 / handCount) : 0;

  // ── Slot history — track the previous escendant cardId per slot index.
  // FieldCard remounts on cardId change (key=cardId), losing its internal
  // prevRef. So the PARENT must remember what was there before, and pass
  // it down as a prop so the fresh FieldCard knows what it replaced.
  const prevSlotsRef = useRef({});
  const prevSlotCards = prevSlotsRef.current;
  // Separate refs for each slot family so different card types don't
  // collide (e.g. a tome and an escendant with the same array index).
  const prevTomesRef    = useRef({});
  const prevCuriosRef   = useRef({});
  const prevProphecyRef = useRef(null);
  const prevTomeCards    = prevTomesRef.current;
  const prevCurioCards   = prevCuriosRef.current;
  const prevProphecyCard = prevProphecyRef.current;
  useEffect(() => {
    const next = {};
    esclings.forEach((slot, i) => {
      next[i] = slot?.escendant || slot?.escling || null;
    });
    prevSlotsRef.current = next;
    const nextTomes = {};
    tomes.forEach((tome, i) => {
      nextTomes[i] = typeof tome === "object" ? (tome?.id || null) : (tome || null);
    });
    prevTomesRef.current = nextTomes;
    const nextCurios = {};
    curios.forEach((c, i) => { nextCurios[i] = c || null; });
    prevCuriosRef.current = nextCurios;
    prevProphecyRef.current = prophecy || null;
  }); // runs every render — no deps — captures each frame

  // ── Lord spotlight — detects when a Level-3 Escendant (Lord) enters
  // any escling slot, and briefly lights up that slot while subtly
  // darkening the surrounding board. The glow on the card itself is
  // handled by the rainbow transition glow (Pass 3.5). The spotlight
  // adds board-level recognition: "the void notices a Lord."
  const [lordSpotlight, setLordSpotlight] = useState(null); // null | { slotIdx }
  const lordSlotFingerprint = esclings.map(s => s?.escendant || "").join("|");
  const prevLordFingerprintRef = useRef(lordSlotFingerprint);
  useEffect(() => {
    const prev = prevLordFingerprintRef.current;
    prevLordFingerprintRef.current = lordSlotFingerprint;
    if (prev === lordSlotFingerprint) return;
    const prevIds = prev.split("|");
    const currIds = lordSlotFingerprint.split("|");
    for (let i = 0; i < currIds.length; i++) {
      const currId = currIds[i];
      const prevId = prevIds[i] || "";
      if (!currId || currId === prevId) continue;
      const currCard = getCard(currId);
      if (currCard?.type === "Escendant" && currCard?.level === 3) {
        const prevCard = prevId ? getCard(prevId) : null;
        if (!prevCard || prevCard.level !== 3) {
          setLordSpotlight({ slotIdx: i });
          const t = setTimeout(() => setLordSpotlight(null), 900);
          return () => clearTimeout(t);
        }
      }
    }
  }, [lordSlotFingerprint]);

  // ── Post-hooks early return — safe now that all hooks have fired ──
  if (!player) return (
    <div style={{ minHeight: "46vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: GM.vellumFaint, fontStyle: "italic", fontSize: 16, fontFamily: GM.font }}>Waiting for opponent...</div>
    </div>
  );

  return (
    <div style={{
      display: "flex", flexDirection: isMe ? "column" : "column-reverse", gap: 2, padding: "2px 10px",
      position: "relative",
      // Reserve space on left/right so center card rows never collide with
      // the corner-anchored nameplate (top) and heart/vortex (bottom).
      paddingLeft: isMobile ? 10 : 150,
      paddingRight: isMobile ? 10 : 150,
      background: isMe ? `linear-gradient(180deg, transparent, rgba(109,40,217,0.08))` : `linear-gradient(180deg, rgba(153,27,27,0.07), transparent)`,
    }}>
      {/* ── MOBILE STAT BAR ──
          On mobile there are no side gutters, so the absolute corner anchors
          would land on top of the card rows. Instead we render a single,
          compact in-flow strip per side: Heart (left) · NamePlate+Timer
          (center) · Khao vortex (right). It hugs the outer edge of each side
          (top for opponent via column-reverse, bottom for me), mirroring the
          PC corner placement without ever overlapping cards. */}
      {isMobile && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 4, padding: "0 2px", width: "100%",
          position: "relative", zIndex: 6,
          // Opponent side uses column-reverse, so order math is inverted.
          // For me (column): highest order → visual bottom (outer edge).
          // For opponent (column-reverse): lowest order → visual top (outer edge).
          order: isMe ? 99 : -1,
        }}>
          <div style={{ pointerEvents: "none", flexShrink: 0, display: "flex", alignItems: "flex-end", gap: 2 }}>
            <AnimaHeart value={player.anima ?? STARTING_ANIMA} isMe={isMe} isActive={isActive} />
            {isMe && <CompanionHUD myAnima={player.anima ?? STARTING_ANIMA} oppAnima={oppAnima ?? STARTING_ANIMA} size={44} />}
          </div>
          <div style={{
            display: "flex", alignItems: "center", gap: 4,
            flexShrink: 1, minWidth: 0, overflow: "hidden",
          }}>
            <NamePlate name={player.name} isMe={isMe} isActive={isActive} />
            <FramedTimer secs={sharedTimer} isActive={isActive} urgent={isActive && sharedTimer <= 10} isMe={isMe} />
          </div>
          <div style={{ pointerEvents: "none", flexShrink: 0 }}>
            <KhaoVortex khao={player.khao ?? STARTING_KHAO} maxKhao={player.maxKhao ?? STARTING_KHAO} isMe={isMe} isActive={isActive} />
          </div>
        </div>
      )}

      {/* ── CORNER ANCHOR: nameplate + timer (DESKTOP ONLY) ──
          ME    → bottom-outer-LEFT  (sits above the Anima heart, hard left edge)
          OPP   → top-outer-RIGHT    (sits below the global match-timer header)
          Both clusters are pushed clear of the global top-left / top-right
          header strips so nothing ever stacks on top of them. */}
      {!isMobile && (
        <div style={{
          position: "absolute", zIndex: 6,
          ...(isMe
            ? { left: 12, bottom: 150 }
            : { right: 12, top: 52 }),
          display: "flex", flexDirection: "column", alignItems: isMe ? "flex-start" : "flex-end", gap: 8,
          pointerEvents: "none",
        }}>
          <NamePlate name={player.name} isMe={isMe} isActive={isActive} />
          <FramedTimer secs={sharedTimer} isActive={isActive} urgent={isActive && sharedTimer <= 10} isMe={isMe} />
        </div>
      )}

      {/* ── CORNER ANCHOR: Anima heart (left) + Khao vortex (right) — DESKTOP ONLY ──
          ME  → hard bottom corners (heart bottom-left, vortex bottom-right).
          OPP → pushed down from the very top so they clear both the global
                header strip and the opponent nameplate that now lives there. */}
      {!isMobile && (
        <>
          <div style={{
            position: "absolute", zIndex: 5,
            left: 10,
            ...(isMe ? { bottom: 6 } : { top: 56 }),
            pointerEvents: "none",
          }}>
            <AnimaHeart value={player.anima ?? STARTING_ANIMA} isMe={isMe} isActive={isActive} />
          </div>
          {isMe && (
            <div style={{
              position: "absolute", zIndex: 6,
              left: 150, bottom: 8,
              pointerEvents: "none",
            }}>
              <CompanionHUD myAnima={player.anima ?? STARTING_ANIMA} oppAnima={oppAnima ?? STARTING_ANIMA} size={88} />
            </div>
          )}
          <div style={{
            position: "absolute", zIndex: 5,
            right: 10,
            ...(isMe ? { bottom: 6 } : { top: 160 }),
            pointerEvents: "none",
          }}>
            <KhaoVortex khao={player.khao ?? STARTING_KHAO} maxKhao={player.maxKhao ?? STARTING_KHAO} isMe={isMe} isActive={isActive} />
          </div>
        </>
      )}

      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        background: "transparent",
        border: "none",
        borderRadius: 0,
        height: 0, padding: 0,
        transition: "box-shadow 0.5s ease",
        position: "relative",
      }}>
        {/* Pass 1 — active-side breathing aura, fades in/out while your turn */}
        {isActive && (
          <div className="sf-side-active-aura" style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            background: isMe
              ? `radial-gradient(ellipse 100% 200% at 50% 120%, ${GM.inkGold}14, transparent 65%)`
              : `radial-gradient(ellipse 100% 200% at 50% -20%,  ${SD.red}14,   transparent 65%)`,
            zIndex: 0,
          }} />
        )}
        {/* Pass 1 — per-stat change flashes. Only animate when value changes;
            otherwise the class is empty and no animation runs. */}
        {(() => { return null; })()}
      </div>

      <div style={{ display: "flex", gap: isMobile ? 3 : 6, justifyContent: "center", alignItems: "flex-end", flexWrap: "nowrap", flexDirection: isMe ? "row" : "row-reverse", position: "relative" }}>
        {/* Lord spotlight — dims surrounding cards when a Lord enters.
            The spotlit slot stays bright via its own sibling ::after layer. */}
        {lordSpotlight && (
          <div className="sf-lord-spotlight-dim" style={{
            position: "absolute", inset: "-20px -20px -20px -20px",
            background: "radial-gradient(ellipse 70% 100% at 50% 50%, transparent 0%, rgba(0,0,5,0.55) 80%)",
            pointerEvents: "none", zIndex: 5, borderRadius: 20,
          }} />
        )}
        <div style={{ display: "flex", flexDirection: "row", gap: 6, alignItems: "center" }}>
          {isMe && prophecy && myTurn && prophecyBtnProps && (
            <ProphecyButton prophecyId={prophecy} myKhao={prophecyBtnProps.myKhao} match={prophecyBtnProps.match} playerIdx={prophecyBtnProps.playerIdx} onActivate={prophecyBtnProps.onActivate} />
          )}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            {prophecy
              ? <FieldCard key={`proph-${prophecy}`} cardId={prophecy} prevCardId={prevProphecyCard} size="tiny" faceDown={false} flipped={!isMe} highlight={false} holoTier={isMe ? getHoloTier(prophecy, collection) : null} onClick={onTomeAction ? (e) => onTomeAction("prophecy", 0, e) : undefined} />
              : <EmptyRift size="tiny" flipped={!isMe} highlight={false} />
            }
            {isMe && <div style={{ fontSize: 6, color: GM.inkGold, letterSpacing: 1, opacity: 0.7 }}>PROP</div>}
          </div>

        </div>
        {esclings.map((slot, i) => {
          const isAttacking = isMe && attackingSlot === i;
          const isTarget = !isMe && (targetMode === "select" || targetMode === "escling");
          // Key derived from the active cardId — when the slot transforms
          // (escling → escendant, or ascension to new tier), this key changes,
          // forcing a remount so the settle animation fires every time.
          const slotCardId = slot?.escendant || slot?.escling || "empty";
          const isSpotlit = lordSpotlight && lordSpotlight.slotIdx === i;
          // NEW — drop target detection for this slot based on currently dragged card
          const draggingCard = (isMe && draggingHandIdx != null) ? getCard((player.hand || [])[draggingHandIdx]) : null;
          const isDropOk = (() => {
            if (!draggingCard || !isMe) return false;
            const t = draggingCard.type;
            if (t === "Escendant" && draggingCard.level === 1 && !slot?.escendant && !slot?.escling) return true;
            if (t === "Escendant" && draggingCard.level === 1 && slot?.escling && !slot?.escendant) return true;
            if (t === "Escendant" && draggingCard.level > 1 && slot?.escendant) return true;
            if (t === "Impera" && slot?.escendant) return true;
            if ((t === "U'hlti" || t === "Affinity") && slot?.escendant) return true;
            return false;
          })();
          const dropHandlers = isMe && draggingHandIdx != null ? {
            onDragOver: (e) => { if (isDropOk) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } },
            onDrop:     (e) => { if (isDropOk && onRiftDrop) { e.preventDefault(); onRiftDrop("slot", i, e.clientX, e.clientY); } },
          } : {};
          return (
            <div key={i} {...dropHandlers} className={`${isSpotlit ? "sf-lord-slot-rise" : ""} ${isDropOk ? "sf-drop-ok" : ""}`.trim()} style={{
              display: "flex", flexDirection: "column", gap: 2, alignItems: "center",
              position: isSpotlit ? "relative" : "static",
              zIndex: isSpotlit ? 10 : 1,
              borderRadius: 10,
            }}>
              {slot?.escendant ? (
                <FieldCard key={`esc-${slotCardId}`} cardId={slot.escendant} prevCardId={prevSlotCards[i]} size="normal" highlight={isAttacking || isTarget} fallen={slot.fallen} currentLyf={slot.currentLyf} attacked={slot.attackedThisTurn} soulCount={(slot.soul || []).length} flipped={!isMe} intangible={slot.intangible} onClick={(e) => onSlotAction(i, e)} holoTier={isMe ? getHoloTier(slot.escendant, collection) : null} revived={!!recentlyRevived[slot.escendant]} effectiveLyt={getEffectiveLytPure(slot, field, oppField)} effectiveLyf={getEffectiveLyfPure(slot, field)} deviant={!!slot.deviant} heretical={!!slot.heretical} stamps={isMe && stampsFor ? stampsFor(slot, i) : null} />
              ) : slot?.escling ? (
                <FieldCard key={`esl-${slotCardId}`} cardId={slot.escling} prevCardId={prevSlotCards[i]} size="normal" highlight={isMe && myTurn} flipped={!isMe} holoTier={isMe ? getHoloTier(slot.escling, collection) : null} onClick={(e) => onSlotAction(i, e)} />
              ) : (
                <EmptyRift size="normal" highlight={isMe && myTurn} flipped={!isMe} onClick={(e) => onSlotAction(i, e)} />
              )}

            </div>
          );
        })}
        <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
          {(() => {
            const necroCount = (player.necropolis || []).length;
            const necroAnim = necroCount === 0 ? "none"
              : necroCount <= 3  ? "sf-necroPulseFaint 3.2s ease-in-out infinite"
              : necroCount <= 7  ? "sf-necroPulseMid 2.4s ease-in-out infinite"
              :                    "sf-necroPulseStrong 1.8s ease-in-out infinite";
            const glowColor = necroCount === 0 ? GM.pageBorder
              : necroCount <= 3  ? "#7a8a5a"
              : necroCount <= 7  ? "#9ec47d"
              :                    "#c6e29a";
            const skullColor = necroCount === 0 ? GM.pageBorder : glowColor;
            // ── PUBLIC RECORD (caP'n ruling, July 10 2026): both
            // Necropolises are viewable at ALL times — yours off-turn,
            // the adversary's always. The handler decides what opens
            // (full radial on your turn, view-only picker otherwise).
            // ── THE PILE REMEMBERS (July 24) — the 3D board has shown the
            // top interred card face-up since Ship 3; the 2D board showed a
            // bare skull. Both Necropolises are public record by ruling, so
            // there is nothing to hide: the last one to fall lies face-up
            // under the count, drained of colour, and the skull sits on top
            // of it. Empty pile → unchanged (bare tile, no ghost).
            const topNecro = (player.necropolis || [])[necroCount - 1] || null;
            const topNecroImg = cardImageUrl(topNecro);
            return (
              <div ref={necroTileRef} onClick={onNecropolisAction} title="Necropolis — public record" style={{ ...SIZES.tiny, background: GM.page, border: `1px solid ${necroCount > 0 ? GM.pageMid : GM.pageBorder}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 4, cursor: necroCount > 0 ? "pointer" : "default", transition: "all 0.2s", animation: necroAnim, position: "relative", overflow: "hidden" }}>
                {topNecroImg && (
                  <div style={{ position: "absolute", inset: 0, borderRadius: 9, overflow: "hidden", zIndex: 0, pointerEvents: "none" }}>
                    <img src={topNecroImg} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", filter: "grayscale(0.9) brightness(0.55)", opacity: 0.72 }} />
                    <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse at center, rgba(0,0,5,0.35), rgba(0,0,5,0.88) 90%)` }} />
                  </div>
                )}
                <span style={{ fontSize: 14, color: skullColor, transition: "color 0.4s", position: "relative", zIndex: 1, textShadow: "0 1px 4px rgba(0,0,0,0.95)" }}>💀</span>
                <span style={{ fontSize: 13, color: skullColor, fontWeight: 900, transition: "color 0.4s", position: "relative", zIndex: 1, textShadow: "0 1px 4px rgba(0,0,0,0.95)" }}>{necroCount}</span>
              </div>
            );
          })()}

        </div>

        {/* ── R112 · THE EXILE RIFT ────────────────────────────────
            caP'n, 2026-09-07: the rift sits BESIDE the Necropolis, not
            above it — *"we could probably just sit it to the right of the
            Necroplis and everything should fit perfectly without having to
            resize too much if it at all"*, and he is right. It is one more
            `tiny` tile on a row that is `justifyContent: center` with slack
            to spare: the desktop battle row goes from 394px to 544px and
            mobile from 247px to 341px against a <480px viewport. NOT ONE
            CARD-SIZE CONSTANT MOVES, which was the whole point of putting
            it here rather than stacking it.

            It sits after the Necropolis column, so `flexDirection` puts it
            to the RIGHT of the pile on your own side and mirrors it on the
            adversary's, exactly like every other tile on this row.

            THE PILE AND THE VOID READ DIFFERENTLY ON PURPOSE. The
            Necropolis breathes green and brightens as it fills, because
            everything in it might come back. The Exile rift is violet and
            gets DARKER as it fills, because nothing in it will.

            ⭐ AND THE DOOR IS HERE NOW (2026-09-13). This banner used to
            say "view only, for now ... the door is somewhere else", and
            there was no somewhere else: Exiliation and the Ritual had no
            call site anywhere in the app. caP'n ruled the wave playable
            at release ("1) yes"), so the tile is the door — tap it on
            your own side and it offers the two Arts that have no rift of
            their own. The four Role Arts stayed on their rifts, because
            the rift owns the Art. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
          {(() => {
            const exiled = player.exile || [];
            const n = exiled.length;
            const ink = n === 0 ? GM.pageBorder
              : n <= 3 ? "#8b6bd6"
              : n <= 7 ? "#7c3aed"
              :          "#5b21b6";
            const topExile = exiled[n - 1] || null;
            const topExileImg = cardImageUrl(topExile);
            const names = exiled.map(id => getCard(id)?.name || id).join(" · ");
            return (
              <div
                title={(isMe ? "Exiliation & the Ritual — " : "") + (n ? `Exile — ${n} card${n === 1 ? "" : "s"} gone from the Solrend: ${names}` : "Exile — nothing has been given up yet")}
                onClick={isMe && onExileAction ? (e) => { e.stopPropagation(); onExileAction(e); } : undefined}
                style={{ ...SIZES.tiny, background: GM.page, border: `1px solid ${n > 0 ? "#4c1d95" : GM.pageBorder}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 4, cursor: isMe && onExileAction ? "pointer" : "default", transition: "all 0.2s", position: "relative", overflow: "hidden" }}
              >
                {topExileImg && (
                  <div style={{ position: "absolute", inset: 0, borderRadius: 9, overflow: "hidden", zIndex: 0, pointerEvents: "none" }}>
                    <img src={topExileImg} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", filter: "grayscale(1) brightness(0.32) contrast(1.15)", opacity: 0.6 }} />
                    <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at center, rgba(30,10,60,0.45), rgba(0,0,8,0.94) 88%)" }} />
                  </div>
                )}
                <span style={{ fontSize: 14, color: ink, transition: "color 0.4s", position: "relative", zIndex: 1, textShadow: "0 1px 4px rgba(0,0,0,0.95)" }}>🕳</span>
                <span style={{ fontSize: 13, color: ink, fontWeight: 900, transition: "color 0.4s", position: "relative", zIndex: 1, textShadow: "0 1px 4px rgba(0,0,0,0.95)" }}>{n}</span>
              </div>
            );
          })()}
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, justifyContent: "center", alignItems: "center", flexWrap: "nowrap", flexDirection: isMe ? "row" : "row-reverse" }}>
        {(() => {
          // NEW — drop target helpers for curio + tome rifts
          const draggingCard = (isMe && draggingHandIdx != null) ? getCard((player.hand || [])[draggingHandIdx]) : null;
          // R05 (caP'n, 2026-08-26): Curios are PRE-PLACED at duel setup
          // and belong to NO deck — there is no play-from-hand path.
          // Drop target permanently closed. (The citation here used to
          // read "Rulez v2 (July 2026)" and was false: Rulez v2 ch.3 puts
          // Curios IN the Grimoire. The ruling is what closes this door.)
          const curioDropOk = () => false;
          const tomeDropOk  = (tIdx) => !!draggingCard && isMe && draggingCard.type === "Tome"  && !tomes[tIdx];
          const mkHandlers = (ok, kind, idx) => (isMe && draggingHandIdx != null ? {
            onDragOver: (e) => { if (ok) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } },
            onDrop:     (e) => { if (ok && onRiftDrop) { e.preventDefault(); onRiftDrop(kind, idx, e.clientX, e.clientY); } },
          } : {});
          return (
            <>
              {/* MOBILE: Lux docked at the far-left of the curio/tome row,
                  beside curio[0]. On desktop it stays flanking the hand. */}
              {isMobile && isMe && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end" }}>
                  <FramedDrawTile icon="✦" label="LUX" color={(myTurn && canDraw && !drawnThisTurn && (player.hand||[]).length>=6) ? SD.red : GM.inkGold} warn={myTurn && canDraw && !drawnThisTurn && (player.hand||[]).length>=6} enabled={myTurn && canDraw && !drawnThisTurn} used={drawnThisTurn} onClick={myTurn ? onDrawLuxmoire : undefined} isMe={true} flipped={false} pulse={luxPulse} tone={luxTone} />
                </div>
              )}
              <div {...mkHandlers(curioDropOk(0), "curio", 0)} className={curioDropOk(0) ? "sf-drop-ok" : ""} style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center", borderRadius: 10 }}>
                {curios[0] ? <FieldCard key={`curio0-${curios[0]}`} cardId={curios[0]} prevCardId={prevCurioCards[0]} size="tiny" flipped={!isMe} holoTier={isMe ? getHoloTier(curios[0], collection) : null} onClick={(e) => onTomeAction("curio", 0, e)} /> : (isMe ? <EmptyRift size="tiny" highlight={false} flipped={false} /> : <EmptyRift size="tiny" flipped={true} />)}

              </div>
              {[0, 1, 2].map(tomeIdx => {
                const tome = tomes[tomeIdx];
                const tomeId = typeof tome === "object" ? tome?.id : tome;
                const faceDown = typeof tome === "object" ? (tome?.faceDown || false) : false;
                const ok = tomeDropOk(tomeIdx);
                return (
                  <div key={tomeIdx} {...mkHandlers(ok, "tome", tomeIdx)} className={ok ? "sf-drop-ok" : ""} style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center", borderRadius: 10 }}>
                    {tomeId ? <FieldCard key={`tome-${tomeId}-${faceDown ? "fd" : "fu"}`} cardId={tomeId} prevCardId={prevTomeCards[tomeIdx]} faceDown={faceDown} size="small" flipped={!isMe} currentLyf={typeof tome === "object" ? (tome?.currentLyf ?? null) : null} holoTier={(isMe && !faceDown) ? getHoloTier(tomeId, collection) : null} onClick={(e) => onTomeAction("tome", tomeIdx, e)} /> : <EmptyRift size="small" highlight={isMe && myTurn} flipped={!isMe} onClick={(e) => onTomeAction("tome", tomeIdx, e)} />}

                  </div>
                );
              })}
              <div {...mkHandlers(curioDropOk(1), "curio", 1)} className={curioDropOk(1) ? "sf-drop-ok" : ""} style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center", borderRadius: 10 }}>
                {curios[1] ? <FieldCard key={`curio1-${curios[1]}`} cardId={curios[1]} prevCardId={prevCurioCards[1]} size="tiny" flipped={!isMe} holoTier={isMe ? getHoloTier(curios[1], collection) : null} onClick={(e) => onTomeAction("curio", 1, e)} /> : (isMe ? <EmptyRift size="tiny" highlight={false} flipped={false} /> : <EmptyRift size="tiny" flipped={true} />)}

              </div>
              {/* MOBILE: Grim docked at the far-right of the curio/tome row,
                  beside curio[1]. On desktop it stays flanking the hand. */}
              {isMobile && isMe && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end" }}>
                  <FramedDrawTile icon="📖" label="GRIM" color={(myTurn && canDraw && !drawnThisTurn && (player.hand||[]).length>=6) ? SD.red : GRIM_LIT} warn={myTurn && canDraw && !drawnThisTurn && (player.hand||[]).length>=6} enabled={canDraw && !drawnThisTurn} used={drawnThisTurn} onClick={myTurn ? onDrawGrimoire : undefined} isMe={true} flipped={false} pulse={grimPulse} tone={grimTone} sealed={grimSealed} />
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* ── HAND — floating below tomes, flanked by Lux (left) + Grim (right) ── */}
      {isMe && (
        <div style={{ display: "flex", flexDirection: "row", gap: isMobile ? 6 : 14, alignItems: "flex-end", justifyContent: "center", position: "relative", zIndex: 3, width: "100%" }}>
          {!isMobile && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", paddingBottom: 4 }}>
              <FramedDrawTile icon="✦" label="LUX" color={(isMe && myTurn && canDraw && !drawnThisTurn && (player.hand||[]).length>=6) ? SD.red : GM.inkGold} warn={isMe && myTurn && canDraw && !drawnThisTurn && (player.hand||[]).length>=6} enabled={isMe && myTurn && canDraw && !drawnThisTurn} used={isMe && drawnThisTurn} onClick={isMe && myTurn ? onDrawLuxmoire : undefined} isMe={isMe} flipped={false} pulse={isMe ? luxPulse : null} tone={isMe ? luxTone : null} />
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center", position: "relative", zIndex: 3, flex: "1 1 auto", minWidth: 0 }}>
            {handHint && <div style={{ fontSize: 9, color: SD.gold, fontStyle: "italic", letterSpacing: 1, fontFamily: GM.font }}>{handHint}</div>}
            {/* THE RAIL — width:100% so clientWidth reports the space the hand
                HAS, not the space it currently takes. Overflow is visible now
                (never scroll): handStep guarantees the fan fits, so the old
                overflowX:auto only ever clipped the selected card's lift —
                CSS coerces overflow-y:visible to auto when the other axis
                scrolls, which is exactly why peeked cards were getting cut. */}
            <div ref={handRailRef} className="sf-hand-fan" style={{ display: "flex", gap: 0, overflow: "visible", alignItems: "flex-end", justifyContent: "center", WebkitOverflowScrolling: "touch", position: "relative", paddingTop: isMobile ? 22 : 14, width: "100%" }}>
            {(player.hand || []).length === 0 && (!exitingHandCards || exitingHandCards.length === 0)
              ? <div style={{ color: GM.pageBorder, fontStyle: "italic", fontSize: 10, padding: "4px 8px", fontFamily: GM.font }}>— empty —</div>
              : (player.hand || []).map((cardId, i) => {
                // ── The held hand. Cards spread to full width whenever the rail
                //    can take them and only close ranks when it can't; the
                //    selected card "peeks" — it pushes its neighbors apart and
                //    lifts above them, the touch equivalent of hover. ──
                const isSel = selectedHandIdx === i;
                const tilt  = handTight ? (i - (handCount - 1) / 2) * handFanArc : 0;
                const fanWrap = {
                  marginLeft: i === 0 ? 0 : handStep - HAND_CARD_W,
                  zIndex: isSel ? 50 : i,
                  // Give a breathing gap on either side of the peeked card so
                  // it reads as fully separated from its neighbors.
                  paddingLeft: isSel && handTight ? 9 : 0,
                  paddingRight: isSel && handTight ? 9 : 0,
                  transform: tilt ? `rotate(${tilt.toFixed(2)}deg)` : "none",
                  transformOrigin: "50% 150%",
                  transition: "margin 0.18s ease, padding 0.18s ease, transform 0.18s ease",
                };
                const handCard = (
                  <HandCard
                    cardId={cardId}
                    selected={isSel}
                    spine={handTight && !isSel}
                    onClick={(e) => onHandCardClick && onHandCardClick(i, e)}
                    holoTier={isMe ? getHoloTier(cardId, collection) : null}
                    entering={enteringHandIdxs && enteringHandIdxs.has(i)}
                    dragging={draggingHandIdx === i}
                    draggable={!isMobile && myTurn}
                    onDragStart={(e) => {
                      if (isMobile || !myTurn) { e.preventDefault(); return; }
                      try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(i)); } catch {}
                      onHandDragStart && onHandDragStart(i);
                    }}
                    onDragEnd={() => onHandDragEnd && onHandDragEnd()}
                  />
                );
                return <div key={`${cardId}-${i}`} style={{ position: "relative", flexShrink: 0, ...fanWrap }}>{handCard}</div>;
              })
            }
            {/* Exiting ghosts — animate out after a card leaves the hand */}
            {(exitingHandCards || []).map(ex => (
              <HandCard
                key={ex.key}
                cardId={ex.cardId}
                selected={false}
                onClick={() => {}}
                holoTier={getHoloTier(ex.cardId, collection)}
                exiting={true}
              />
            ))}
            </div>
          </div>
          {!isMobile && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", paddingBottom: 4 }}>
              <FramedDrawTile icon="📖" label="GRIM" color={(isMe && myTurn && canDraw && !drawnThisTurn && (player.hand||[]).length>=6) ? SD.red : GRIM_LIT} warn={isMe && myTurn && canDraw && !drawnThisTurn && (player.hand||[]).length>=6} enabled={isMe && canDraw && !drawnThisTurn} used={isMe && drawnThisTurn} onClick={isMe && myTurn ? onDrawGrimoire : undefined} isMe={isMe} flipped={false} pulse={isMe ? grimPulse : null} tone={isMe ? grimTone : null} sealed={isMe && grimSealed} />
            </div>
          )}
        </div>
      )}

      {/* ── OPPONENT HAND — card BACKS only, fronts hidden. ──
          Universal across NPC + human (Shatterfield rules are symmetric).
          Rendered smaller than your own hand so it reads as "theirs", and
          gives an at-a-glance count without revealing any card identity. */}
      {!isMe && (
        <div style={{ display: "flex", gap: isMobile ? 1 : 2, justifyContent: "flex-start", alignItems: "flex-start", flexWrap: "nowrap", position: "relative", zIndex: 3, transform: isMobile ? "scale(0.6)" : "scale(0.78)", transformOrigin: "left top", paddingLeft: isMobile ? 6 : 40, marginTop: -2, marginBottom: isMobile ? -14 : -40 }}>
          {(player.hand || []).length === 0
            ? <div style={{ color: GM.pageBorder, fontStyle: "italic", fontSize: 10, padding: "4px 8px", fontFamily: GM.font }}>— empty —</div>
            : (player.hand || []).map((_, i) => (
                <div key={`oppback-${i}`} style={{ marginLeft: i === 0 ? 0 : (isMobile ? -18 : -16) }}>
                  <CardBack size="hand" flipped={true} />
                </div>
              ))
          }
        </div>
      )}
    </div>
  );
}

export default function Shatterfield({ code, playerIdx, onExit, isNPC = false, tutorialMode = false, collection = {}, replayFeed = null }) {
  // ── SEAL: register Shatterfield's effective-Lyf math with the rule gate ──
  // so legality checks compute Lyf identically to the board (buffs included).
  useEffect(() => { setEffectiveLyfResolver(getEffectiveLyfPure); }, []);
  const [match, setMatch] = useState(null);
  const [radial, setRadialState] = useState(null);
  // ── SHIP 9.1 · THE RADIAL SINK ──────────────────────────────
  // caP'n, 2026-09-16: *"make the options playable from the pill."* The
  // Tome wedges' onSelect bodies are the Tome doors (the flip, the
  // "Activate Again"), inlined inside handleTomeAction's radial builder.
  // Rather than lift 200 lines of duel logic to reach them, the pill
  // asks that builder to build its radial and CATCHES it here instead
  // of showing it: the sink is armed for exactly one setRadial, takes
  // the actions, and presses the wedge it was looking for. Same wedge,
  // same legal flag, same onSelect, same gate — one door. A sink that
  // is never fed (the builder returned early) is disarmed by the
  // caller right after the call, so it can never swallow the NEXT
  // radial a real click opens.
  const radialSinkRef = useRef(null);
  const setRadial = (r) => {
    const sink = radialSinkRef.current;
    if (sink && r && Array.isArray(r.actions)) { radialSinkRef.current = null; sink(r); return; }
    setRadialState(r);
  };
  // ── D15 — where did the action happen? Radials open ON the thing
  // being acted on, so the last radial's coords are the best honest
  // answer to "where should the Khao embers land". Freshness-gated
  // in spendKhao; actions that never opened a radial simply don't
  // draw a trail — no fake targets.
  const lastActionPosRef = useRef(null);
  useEffect(() => {
    if (radial) lastActionPosRef.current = { x: radial.x, y: radial.y, at: Date.now() };
  }, [radial]);
  const [detailModal, setDetailModal] = useState(null);
  // Helper — open card detail modal with holo tier from collection
  const viewCard = (cardId, soul = []) => setDetailModal({ cardId, soul, holoTier: getHoloTier(cardId, collection), rec: collection?.[baseId(cardId)] || null });
  // ⬡ SHIP 5 — the 3D field's foil resolver. Same getHoloTier the 2D
  // board uses (one source of truth for "which foil does this copy
  // wear"); Shatterfield3D stays a pure renderer and owns no
  // collection data. Memoized so the renderer's props stay stable.
  const holoOf = useCallback((cardId) => getHoloTier(cardId, collection), [collection]);
  const [soulModal, setSoulModal] = useState(null);
  const [selectedHandIdx, setSelectedHandIdx] = useState(null);
  // ⬡ SHIP 1 — 3D field toggle. true = Shatterfield3D renders the field;
  // false = the original 2D PlayerSides. Instant rollback, A/B in one tap.
  const [field3D, setField3D] = useState(true);
  const [attackingSlot, setAttackingSlot] = useState(null);
  const [targetMode, setTargetMode] = useState(null);
  const [ascendedThisTurnLocal, setAscendedThisTurn] = useState(false);
  // ⛧ DEVIANT TRAINING's own latch. R66 caps Deviant ASCENDS at one per
  // turn and Rulebook ch.17 caps Deviant TRAININGS at one per turn — two
  // separate allowances, because they are two separate actions and a
  // player who Deviant-Ascended should still be able to bolt an
  // off-School Impera on somebody else. A shared flag would have made
  // one silently eat the other.
  const [trainedThisTurnLocal, setTrainedThisTurn] = useState(false);
  const [drawnThisTurnLocal, setDrawnThisTurn] = useState(false);
  const [forcedDiscardModal, setForcedDiscardModal] = useState(false); // R10 draw-then-discard
  // ── CANON (caP'n ruling, July 2026): one Resurrection per turn — the
  // NPC is structurally once-per-turn (one resurrectOne call per turn
  // runner); this flag holds the human to the identical rule.
  const [resurrectedThisTurnLocal, setResurrectedThisTurn] = useState(false);
  // ── PRIMERAS — THE STAMPS (H22a engine state) ───────────────
  // words: R82 — one use per WORD per Turn across the whole field
  //        (Dragon is ONE word on both axes — one key covers both).
  // slots: R81 — one stamp per BEING per Turn (keyed by slot index,
  //        the same convention attackedThisTurn lives by).
  // beastArmed: the Beast rider waiting for its dim (slot-keyed).
  // All reset at the start of MY next Turn (R83). This one is still
  // local-plus-persisted in the other direction from the four allowances
  // above: the LEDGER is written to the record (see markPrimeraSpent) so
  // the validator can watch R81/R82, while this object stays the fast
  // path my own client reads mid-Turn. Unlike the old note here,
  // `ascendedThisTurn` is NO LONGER local-only — see THE PER-TURN
  // ALLOWANCES block; an F5 used to refund every one of them.
  const [primeraTurn, setPrimeraTurn] = useState(EMPTY_PRIMERA_TURN);
  const primeraTurnRef = useRef(EMPTY_PRIMERA_TURN);
  primeraTurnRef.current = primeraTurn;
  // Which of my slots have dimmed the adversary's Anima this Turn —
  // consulted by the Beast rider ("if the user dims... dim 1 more").
  const animaDimmedSlotsRef = useRef(new Set());
  const [coinFlipResult, setCoinFlipResult] = useState(null);
  const [coinFlipDone, setCoinFlipDone] = useState(false);
  const [solrendPressed, setSolrendPressed] = useState(false);
  const [concedeArmed, setConcedeArmed] = useState(false); // C16 two-tap concede
  const [solrendCountdown, setSolrendCountdown] = useState(15);
  const [terrorCountdown, setTerrorCountdown] = useState(10); // TERROR gate auto-begin
  const [coinFlipPhase, setCoinFlipPhase] = useState("idle"); // idle | spinning | result
  // T11 — which coin this player is holding. Live, falls back to the
  // shipped Solrend at every step, and cannot reach the flip: the side
  // is already decided from match.createdAt parity by the time this is
  // read. Purely what the metal looks like.
  const coinSkinId = useCoinSkin(auth?.currentUser?.uid);
  // THE SECOND WIND's announcement. A boss standing back up is the
  // loudest thing that can happen in a duel and it must not be a toast
  // you can miss while reading your hand.
  const [secondWind, setSecondWind] = useState(null);
  const [mulliganAvailable, setMulliganAvailable] = useState(true);
  const [luxmoireSearchModal, setLuxmoireSearchModal] = useState(null);
  const [myFirstTurnStarted, setMyFirstTurnStarted] = useState(false);
  const [myFirstTurnOver, setMyFirstTurnOver] = useState(false);
  const [mulliganUsed, setMulliganUsed] = useState(false);
  // NEW — hand entry/exit tracking
  const [enteringHandIdxs, setEnteringHandIdxs] = useState(() => new Set());
  const [exitingHandCards, setExitingHandCards] = useState([]); // [{ key, cardId }]
  const prevHandRef = useRef([]);
  const handBaselineSetRef = useRef(false);
  // NEW — drag-to-play (desktop)
  const [draggingHandIdx, setDraggingHandIdx] = useState(null);
  // Mobile landscape detection — only affects phones rotated sideways, not desktop
  const isMobileLandscape = isMobile && typeof window !== "undefined" && window.innerWidth > window.innerHeight;
  const [sharedTimer, setSharedTimer] = useState(PLAYER_TIMER);
  const [globalTimer, setGlobalTimer] = useState(0);
  const [toast, setToast] = useState(null);
  const [targetPicker, setTargetPicker] = useState(null); // { mode, onPick, title, filter }
  const [sgModal, setSgModal] = useState(null); // Shadow Gambling modal state
  // ── RESPONSE WINDOW state (RULEZ_V2.md, canon; false 'July 2026' cite scrapped 2026-08-27) ──
  // { action, options, resolve, turnKey, actingName?, targetName? }
  const [responseWindow, setResponseWindow] = useState(null);
  const responseWindowRef = useRef(null);
  responseWindowRef.current = responseWindow;
  const snoozeTurnRef = useRef(null);      // "Hold this Turn" — stores the snoozed turnKey
  const responderImplRef = useRef(null);   // fresh impl every render; stable wrapper registered once
  // ── WAVE 2/3 (July 2026): the PLAYER's actions are declared too ──
  // awaitingResponse renders the "adversary may answer" pill while a
  // declaration is in flight (either lane). The pvp* refs are the
  // Wave 3 Firebase handshake plumbing (see declarePvP below).
  const [awaitingResponse, setAwaitingResponse] = useState(null); // { kind } | null
  const pvpSeqRef = useRef(0);           // declarer-side sequence stamp
  const pvpAwaitRef = useRef(null);      // { seq, resolve, timer } — declarer waits here
  const declarePvPRef = useRef(null);            // declarePvP is defined further down; the co-op fan-out reaches it through this
  const resolveResponseWindowRef = useRef(null); // resolveResponseWindow likewise — so a withdrawn window can be closed from an effect
  const pvpAnsweredSeqRef = useRef(0);   // defender-side: last pendingAction seq answered
  const pvpAnsweringRef = useRef(false); // defender-side: answer in flight
  const chainRuinSeqRef = useRef(0);     // C12: last chainResult I acted on
  const chainActiveRef = useRef(false);  // C12: a chain is running (holds the clock)
  const COUNTER_WINDOW_MS = 3000; // 3 seconds to counter
  // ── ACTION IN PROGRESS (July 26) ─────────────────────────────
  // ONE predicate for "the player is mid-action", derived here so no
  // surface has to re-derive it and drift. Today its only consumer is
  // the 3D hover peek (which must stand down while you aim), but it is
  // deliberately written as a general truth about the board rather
  // than a hover flag — anything else that should hold its tongue
  // during an action reads this, it does not grow its own list.
  //
  // Three families, all of them "I have committed to something":
  //   CHOOSING  radial open · a picker up · an attacker declared ·
  //             target mode armed · the concede armed
  //   SEARCHING Grimoire / Luxmoire / ATV feed / Shadow Gambling
  //   ANSWERING a Response Window, either side of it
  // Plus the read-only modals, because a card portrait floating over an
  // open modal is nothing but clutter.
  // ⚠ NOT included, on purpose: selectedHandIdx. Picking a card up is
  // when you MOST want to read the board — the peek's whole job — and
  // the action doesn't begin until you choose where it goes.
  const [grimoireSearchModal, setGrimoireSearchModal] = useState(false); // Search being from Grimoire
  const [graveshiftModal, setGraveshiftModal] = useState(false); // R40-R43 Graveshift confirm
  const [atvModal, setAtvModal] = useState(null); // ATV feeding modal { tomeId, tomeSlotIdx }
  const [devotionModal, setDevotionModal] = useState(null); // Devotion prompt for opponent

  // The predicate itself (documented above, beside grimoireSearchModal).
  // Every term is already-existing state — this adds no new source of
  // truth, it only names one that was implicit.
  const actionInProgress = !!(
    radial                      // a radial is open — you are choosing
    || targetPicker             // ...or a picker is
    || attackingSlot !== null   // an attacker is declared
    || targetMode               // and the board is armed for a target
    || concedeArmed             // C16's two-tap surrender is halfway through
    || grimoireSearchModal      // searching the Grimoire (caP'n named this one)
    || luxmoireSearchModal      // ...or the Luxmoire
    || atvModal                 // feeding an ATV tome
    || sgModal                  // a Shadow Gambling is resolving
    || graveshiftModal          // R40–R43 Graveshift confirm
    || forcedDiscardModal       // R10 draw-then-discard
    || devotionModal            // a Devotion prompt is up
    || responseWindow           // you are being asked to answer
    || awaitingResponse         // ...or you are waiting on their answer
    || detailModal              // read-only, but a peek over a modal is clutter
    || soulModal
  );
  // Passive flags checked on attack: animaDimmedThisTurn tracks if attacker dimmed Anima this attack

  const matchRef = useRef(null);
  const timerRef = useRef(null);
  const globalRef = useRef(null);
  // ⏳ THE CLOCK's latch (R87). One duel, one verdict, ever — see THE CLOCK
  // RUNS OUT below. A ref and not state because the effect that reads
  // it also writes it, and it must be true for the very next render.
  const timeUpRef = useRef(false);
  const endingTurn = useRef(false);
  // ── C7 (July 17 2026): faction in-match instrumentation ──────
  // Counts the LOCAL player's resolved tome activations by ilk.
  // Bumped once at applyTomeEffect's head (the single funnel every
  // player-side tome resolution flows through — nullified
  // activations never reach it, so only effects that actually
  // resolved count). Written ONCE at match end (guard ref below)
  // via recordJesterMatchStats — a membership-gated no-op for
  // non-Jesters, so this costs nothing for everyone else.
  const factionStatsRef = useRef({ nihil: 0, omnia: 0, ancient: 0, otherTomes: 0 });
  const factionStatsWrittenRef = useRef(false);

  // ── THE MAIN N-SEAT CHOKEPOINT ──────────────────────────────────
  // oppIdx used to be a hardcoded flip; it's now "my currently-targeted
  // enemy seat" (getTargetIdx), which is the SAME seat as before on any
  // classic 2-seat match — so `me`/`opponent`/`oppIdx` still mean
  // exactly what they always meant here for 1v1, and everything below
  // that closes over them (attack resolution, curios, response windows)
  // needed no further changes. `withEffectiveAnima` overlays a team's
  // shared Anima total when this match pools it (2v2); it's a no-op
  // overlay for FFA/1v1 seats, which keep their own individual anima.
  const oppIdx = getTargetIdx(match, playerIdx);
  const me = withEffectiveAnima(match, match?.players?.[playerIdx], playerIdx);
  const opponent = withEffectiveAnima(match, match?.players?.[oppIdx], oppIdx);
  const myTurn = match?.activePlayer === playerIdx;
  const isFinished = match?.status === "finished";
  // ── R08's WINDOW IS AS LONG AS THE TABLE ────────────────────
  // ⚠ THIS WAS THE LITERAL `< 2`, i.e. a two-seat opening Cycle typed
  // into the interface. R08's window is `openingCycleLength(seatCount)`
  // half-turns — FOUR at a four-seat table — so on a 3-4p board the
  // wedges declared the Cycle over at half-turn 2 and offered a Lyt
  // Attack the gate then refused. The rule was never broken (gateCtx
  // passes the real seatCount to the enforcer, and gameManager's R08
  // backstop is seat-count aware); the INTERFACE lied, which is how a
  // player learns a rule wrong. `isOpeningCycle` is the same predicate
  // the backstop at the top of this file asks — one function, one answer.
  const isFirstTurn = isOpeningCycle(match);

  // ── THE PER-TURN ALLOWANCES, READ FROM THE RECORD ────────────
  // R11 (one draw) · R18 (one Ascend) · R67 (one rite) · R89 (one paid
  // Resurrection). Each is `local || persisted`: the local half is kept
  // ONLY so a wedge greys out on the same frame it is pressed, but the
  // persisted half is what survives an F5 and therefore what actually
  // gates. `turnLedgerCount` returns 0 for a ledger stamped with any
  // other turn, so these expire on their own without a reset write.
  //
  // These four names are the ones the whole file already reads — the
  // useState locals were renamed to `*Local` rather than the consumers
  // being rewritten, so every legality check and every lit/unlit wedge
  // in ~40 places picked up the persisted answer with no call-site churn.
  // R07's discriminator. The coin writes `turnOrder` at the flip
  // (:4685); seat 0 is the legacy fallback for matches written before it.
  const iAmFirstPlayer = (match?.turnOrder?.[0] ?? 0) === playerIdx;
  const turnNowLedger = match?.turn || 0;
  const meRecord = match?.players?.[playerIdx];
  const drawsThisTurn        = turnLedgerCount(meRecord?.drawnThisTurn, turnNowLedger);
  const ascensionsThisTurn   = turnLedgerCount(meRecord?.ascendedThisTurn, turnNowLedger);
  const ritesThisTurn        = turnLedgerCount(meRecord?.trainedThisTurn, turnNowLedger);
  const resurrectionsThisTurn = turnLedgerCount(meRecord?.resurrectedThisTurn, turnNowLedger);
  const drawnThisTurn        = drawnThisTurnLocal        || drawsThisTurn >= 1;
  const ascendedThisTurn     = ascendedThisTurnLocal     || ascensionsThisTurn >= 1;
  const trainedThisTurn      = trainedThisTurnLocal      || ritesThisTurn >= 1;
  const resurrectedThisTurn  = resurrectedThisTurnLocal  || resurrectionsThisTurn >= RESURRECT_PER_TURN;

  // THE WRITER. One call marks an allowance spent both ways: the local
  // latch for this frame, the record for every frame after a remount.
  // Fire-and-forget — a failed ledger write must never wedge the action
  // it is counting, and the local latch still holds for this session.
  const noteTurnAction = (fieldName, setLocal) => {
    if (setLocal) setLocal(true);
    const live = matchRef.current?.players?.[playerIdx] ?? meRecord;
    const turnNow = matchRef.current?.turn ?? turnNowLedger;
    return updatePlayer(code, playerIdx, {
      [fieldName]: bumpTurnLedger(live?.[fieldName], turnNow),
    }).catch(e => console.warn(`[turn ledger] ${fieldName} write failed:`, e?.message));
  };
  // ══ THE DRAW PULSE — caP'n's first-turn cue ══════════════════
  // "only player who goes first, the player who goes second (even
  //  on the second turn) gets both pulses"
  //
  //   turn 0 → first player. R07 SEALS their Grimoire, so the Lux
  //            pulses alone and the Grim tile reads SEALED.
  //   turn 1 → second player's first turn. Nothing is sealed for
  //            them (Rulez_v2: the seal is first-player only), so
  //            BOTH pulse.
  //   turn 2+ → nothing pulses. Both tiles sit lit as normal.
  //
  // Everything else is a live-truth gate: the cue requires it to be
  // your turn, the draw to still be available, and you not to have
  // taken it — so it can never pulse at someone who already drew,
  // and never on the adversary's seat.
  const equippedPulse = useEquippedDrawPulse();
  const grimSealed = (match?.turn || 0) === 0;                  // R07, made visible
  const firstTurnCue = myTurn && isFirstTurn && !drawnThisTurn;
  //
  // TONE — the bought colour. Unconditional, every turn, whole duel.
  // caP'n, 2026-08-18: the aura "should remain." It used to vanish with
  // the beat, which made a 600◕ cosmetic a one-turn cosmetic.
  const luxTone = useMemo(() => {
    const t = pulseTone(equippedPulse, "lux");
    return { base: t.base, hot: t.hot };
  }, [equippedPulse]);
  const grimTone = useMemo(() => {
    const t = pulseTone(equippedPulse, "grim");
    return { base: t.base, hot: t.hot };
  }, [equippedPulse]);
  //
  // BEAT — the first-turn teacher. Same colours, plus the animation,
  // and only while the cue is genuinely live.
  const luxPulse = useMemo(() => {
    if (!firstTurnCue) return null;
    return { ...luxTone, ms: equippedPulse.ms };
  }, [firstTurnCue, luxTone, equippedPulse]);
  const grimPulse = useMemo(() => {
    if (!firstTurnCue || grimSealed) return null;
    return { ...grimTone, ms: equippedPulse.ms };
  }, [firstTurnCue, grimSealed, grimTone, equippedPulse]);
  const drawCue = useMemo(
    () => ({ lux: luxPulse, grim: grimPulse, luxTone, grimTone, sealed: grimSealed }),
    [luxPulse, grimPulse, luxTone, grimTone, grimSealed]
  );

  // ── SEAL: rule-gate context builder ──────────────────────────
  // Single source for the state the gate validates against. `attacker`
  // param lets attack handlers report which slots already attacked.
  const gateCtx = (overrides = {}) => ({
    me, opponent,
    turn: match?.turn || 0,
    // R08's opening Cycle is one Turn PER SEAT, so every predicate that
    // asks "is this the opening Turn" needs the table size. 2 in a
    // classic duel, which is what the enforcer has always assumed.
    seatCount: seatCountOf(match),
    // R18 — the REAL count off the persisted ledger, not a boolean cast.
    // A local-only latch reset itself on every remount; this does not.
    ascensionsThisTurn: Math.max(ascensionsThisTurn, ascendedThisTurnLocal ? 1 : 0),
    // ── R30 + R124 — A LIST WITH REPEATS, NOT A LIST OF FLAGS ────
    // The enforcer counts how many times a rift index appears, which is
    // exactly the right shape for an allowance greater than one — and
    // this builder was handing it each rift AT MOST ONCE, off the
    // boolean. A being with three swings would have read as a being
    // with one used and two never taken.
    //
    // `attacksThisTurn` is the count; `attackedThisTurn` is still the
    // flag, still written, and still what the transition referee,
    // GameStateValidator and the brain read. A slot written before this
    // shipped has no count and its flag stands in for one — which is
    // the old behaviour exactly.
    defendersAttackedThisTurn: (me?.field?.esclings || []).flatMap((s, i) => {
      const n = Number(s?.attacksThisTurn);
      const taken = Number.isFinite(n) && n > 0 ? n : (s?.attackedThisTurn ? 1 : 0);
      return Array(taken).fill(i);
    }),
    ...overrides,
  });

  // ══════════════════════════════════════════════════════════
  // 🚦 R108 · THE PHASE GATE — REPLENISH · DRAW · MAIN · ATTACK · END
  // ══════════════════════════════════════════════════════════
  // caP'n, 2026-08-26, verbatim:
  //
  //   *"let's canonize the 5 phases. After main phase is over — playing
  //   all your cards, ascending, resurrection, everything — now you
  //   declare attack phase, you cannot play any more cards. After your
  //   Lyt Attacks and/or Imperas, U'hltis etc (so yes you can use cards
  //   attached to a being's Soul here …) — then turn over."*
  //
  // ⚠ THIS BOARD HAS NEVER HAD A PHASE. Every restriction in the file
  // was an independent latch — `myTurn`, `ascendedThisTurn`,
  // `drawnThisTurn` — so swinging with three beings and THEN Transcending,
  // Ascending, resurrecting and setting a Tome was perfectly legal.
  //
  // THE PHASE LIVES ON THE SEAT (`me.phase`), not in React state, and
  // that is load-bearing: the enforcer reads it through ruleAdapter for
  // the NPC and the player alike (R-UNIVERSAL), a remount must not
  // silently reopen the Main phase, and an adversary's client has to be
  // able to see that you have committed to attacking.
  //
  // ABSENT READS AS MAIN — see rules.phaseOf. Every match written
  // before today, every replay and every stored duel therefore behaves
  // exactly as it did.
  const myPhase = phaseOf(match?.players?.[playerIdx]);
  const attackPhaseDeclared = myPhase === PHASE.ATTACK;

  /**
   * The one refusal, used at the top of every card-playing handler.
   * Reads the LIVE seat rather than the render closure for the same
   * reason spendKhao does: mid-chain, `me` can be a write behind, and a
   * phase is exactly the sort of thing that changes between renders.
   * Returns true when the action is BARRED (so: `if (phaseBars(…)) return;`).
   */
  const phaseBars = (actionType) => {
    const liveMe = matchRef.current?.players?.[playerIdx] || match?.players?.[playerIdx] || me;
    const v = phaseAllows(actionType, phaseOf(liveMe));
    if (!v.allowed) { showToast(v.reason, SD.red); return true; }
    return false;
  };

  /**
   * DECLARE THE ATTACK PHASE. One-way, inside your own Turn.
   * The write is the declaration; there is no local mirror, because a
   * local mirror is a second opinion about what phase you are in.
   */
  const handleDeclareAttackPhase = async () => {
    if (!myTurn || !me) return;
    if (attackPhaseDeclared) return;
    const v = gateDeclarePhase({ me: matchRef.current?.players?.[playerIdx] || me }, PHASE.ATTACK);
    if (!v.valid) { showToast(v.reason, SD.red); return; }
    await updatePlayer(code, playerIdx, { phase: PHASE.ATTACK });
    showToast("⚔ Attack phase declared — no more cards may be played for the rest of your half-turn.", SD.red);
  };

  useEffect(() => { matchRef.current = match; }, [match]);
  // ── R10 (Rulez v2) — DRAW-THEN-DISCARD, FOR EVERY SOURCE ──────
  // The turn draw always raised the forced-discard modal itself, but
  // five EFFECT-driven draws instead hard-BLOCKED at the cap, which is
  // the pre-Rulez-v2 rule and silently ate cards the effect said were
  // drawn. Those blocks are gone; the draws now resolve. This watches
  // MY hand and raises the discard modal no matter which source pushed
  // it over, including a draw an ADVERSARY's card forced on me — that
  // seat's client cannot raise a modal on mine, so the state has to.
  useEffect(() => {
    if ((match?.players?.[playerIdx]?.hand || []).length > HAND_LIMIT) setForcedDiscardModal(true);
  }, [match, playerIdx]);
  // THE NECRO SEAL needs the live player to compute the next seal and to
  // read the Graveshift generation. Point the module-level handle at the
  // same ref the rest of the file already trusts.
  _sealMatchRef = matchRef;

  // ══ THE SECOND WIND — the one chokepoint ═════════════════════
  // Nineteen places in this file can end a duel. Rather than teach
  // nineteen call sites about phases, gameManager's `endMatch` used to
  // be imported under an alias so this local one could shadow the short
  // name — every existing call site routed through the check untouched,
  // including the ones nobody had written yet. The alias is gone now
  // (the R87 clock, its last user, ends through turnEngine's
  // `endOnClock`), so the raw door is not even in scope in this file
  // and `endMatch` here is the ONLY `endMatch` there is. A rule
  // enforced at one chokepoint cannot be forgotten at the twentieth.
  //
  // "The same board as when you beat him" costs nothing: the board is
  // already persistent, and ENDING is the event. So a phase change is a
  // refusal to end plus two numbers — every card standing, every Tome
  // set, every Necropolis pile stays exactly where phase one left it.
  // N-SEAT: someone's anima hitting 0 KNOCKS THEM OUT — it only ENDS
  // THE MATCH when that leaves one seat or team standing. In 1v1 those
  // are the same instant (eliminating the only other seat always
  // decides the match), so this changes nothing there; in a 3-4p FFA
  // or a 2v2, the first knockout is usually the match continuing with
  // one fewer chair. `winnerIdx` here means the same thing it always
  // did — "the party that just won THIS exchange" — and the loser is
  // whoever `winnerIdx` was fighting (getTargetIdx), exactly the seat
  // whose anima this call is a reaction to.
  // AUDIT FIX (2026-08-22): `explicitLoserIdx` — a self-cost, a self-dim
  // passive, or a reflected attack all know EXACTLY who just hit 0 (it's
  // whoever paid/dimmed/reflected, i.e. the caller's own seat), but the
  // default `getTargetIdx(match, winnerIdx)` answers a different
  // question — "who is winnerIdx CURRENTLY targeting" — which only
  // happens to be the same seat in 1v1 (the only other seat) and is
  // arbitrary in a 3-4p FFA (winnerIdx's own activeTarget can be a
  // third seat that never lost anything). Pass the real loser through
  // explicitly whenever the call site already knows it; every existing
  // call that doesn't pass it keeps the old getTargetIdx behavior.
  // ── THE KNOCKOUT / THE END, WITH A TOAST ON TOP ────────────────
  // The DECISION — does this win end the match or merely knock a side
  // out — moved to src/matchOutcome.js in August 2026 and is unchanged
  // line for line; what stays here is the only part that was ever the
  // renderer's: telling the table about it.
  //
  // WHY IT MOVED: prophecyEngine's automatic Prophecy fire is a win
  // condition owned by the ENGINE, and it called gameManager's raw
  // endMatch — ending the WHOLE TABLE in a 3-4p FFA or a 2v2 where this
  // function would correctly have knocked out one side. An engine file
  // cannot import a useCallback, so the answer had to leave the
  // component. Both doors now go through the same resolver.
  //
  // ⚠ `matchRef.current`, NOT the `match` render closure — AND THIS IS
  // A CORRECTION. This callback used to read `match` "ON PURPOSE",
  // with a comment saying so, at the same time as its own sibling
  // module (src/turnEngine.js) stated as a rule that "A REACT CALLER
  // MUST PASS matchRef.current, NEVER the match state variable", and
  // matchOutcome's own docblock said "a React caller hands it
  // matchRef.current". Three files, two contracts, and the one being
  // fed the stale board was the one deciding WHO IS ELIMINATED and
  // WHETHER THE MATCH ENDS.
  //
  // "Every caller was written against it" was true and was not a
  // justification: no caller reads the board this function is handed —
  // they pass a code and seat indices and read the returned result. The
  // only thing the choice ever decided was whether the elimination
  // projection, matchIsDecided and the reseat ran against Firebase or
  // against last render. There is no reading of this game in which the
  // answer is "last render".
  //
  // `match` stays in the dependency array: the callback identity must
  // still change when the board does, so consumers that memoise off it
  // keep re-deriving. Only the VALUE READ moved.
  const finishOrEliminate = useCallback(async (matchCode, winnerIdx, explicitLoserIdx = null) => {
    const res = await finishOrEliminateSeat(matchCode, matchRef.current || match, winnerIdx, explicitLoserIdx);
    if (!res.ended) {
      showToast(
        res.eliminatedSeats.length > 1 ? "◆ A team has fallen — the match continues." : "◆ A player has fallen — the match continues.",
        SD.red
      );
    }
    return res;
  }, [match]);

  const endMatch = useCallback(async (matchCode, winnerIdx, explicitLoserIdx = null) => {
    const loserIdx = explicitLoserIdx != null ? explicitLoserIdx : getTargetIdx(match, winnerIdx);
    const next = pendingPhase(match, loserIdx);
    if (!next) return finishOrEliminate(matchCode, winnerIdx, explicitLoserIdx);

    const updates = escalationUpdates(match, loserIdx, next);
    if (!updates) return finishOrEliminate(matchCode, winnerIdx, explicitLoserIdx);
    try {
      // ONE update() — a boss that gains its Anima and its new name in
      // two writes can be seen half-escalated.
      await updateMatch(matchCode, updates);
      if (next.say) showToast(next.say, next.accentColor || SD.red);
      setSecondWind({ name: next.name, title: next.title, say: next.say,
                      accent: next.accentColor || SD.red });
      setTimeout(() => setSecondWind(null), 4200);
    } catch (e) {
      // A failed escalation must not leave a duel that cannot be won.
      // Fall through and end it honestly.
      console.warn("[secondWind] escalation failed — ending the duel:", e?.message);
      return finishOrEliminate(matchCode, winnerIdx, explicitLoserIdx);
    }
  }, [match, finishOrEliminate]);

  // ── C7: match-end faction stats write (fires once) ───────────
  // Any Solrend counts — NPC or human adversary alike; a Solrend is
  // a Solrend. Fire-and-forget: a failed write never blocks the
  // victory cinematic or the exit path.
  useEffect(() => {
    if (!isFinished || factionStatsWrittenRef.current) return;
    // H6 — a replay reaches "finished" too, and this is the ONE write
    // path in this file that does not go through gameManager, so THE
    // PLAYBACK LOCK cannot see it. Watching a won duel back must not
    // re-write its faction stats. Found in the critique pass, not in QA.
    if (replayFeed) return;
    factionStatsWrittenRef.current = true;
    const uid = auth?.currentUser?.uid;
    if (!uid) return;
    const st = factionStatsRef.current;
    const localWon = didSeatWin(match, playerIdx);
    const stats = {
      nihilTomes:      st.nihil,
      playedBothSides: st.nihil > 0 && st.omnia > 0,
      wonAncientOnly:  localWon && st.ancient > 0 &&
                       st.nihil === 0 && st.omnia === 0 && st.otherTomes === 0,
    };
    if (stats.nihilTomes > 0 || stats.playedBothSides || stats.wonAncientOnly) {
      import("./progression").then(({ recordJesterMatchStats }) =>
        recordJesterMatchStats(uid, stats)
      ).catch(e => console.warn("[C7 factionStats] failed:", e));
    }

    // ── H6 · SAVE THE TAPE ────────────────────────────────────
    // Rides C7's once-only latch rather than minting a second one:
    // endMatch is called from a dozen places and "the board became
    // finished" is the only description of the end that all of them
    // share. Fire-and-forget by contract — a replay that fails to
    // save is a replay you do not have; a throw here would be a duel
    // you cannot leave.
    if (!replayFeed) {
      commitReplay(uid, {
        won: localWon,
        // AUDIT FIX (2026-08-22): the old seat 0/1 ternary named the
        // wrong opponent in 3-4p FFA/2v2 — getTargetIdx is the same
        // "who am I actually facing" resolution the rest of the file
        // uses, and it degrades to the identical ternary in 1v1.
        oppName: match?.players?.[getTargetIdx(match, playerIdx)]?.name || "",
        turns: match?.turn || 0,
      }).catch((e) => console.warn("[replay] commit failed:", e?.message));
    }
  }, [isFinished]);

  // ── C16 (July 17 2026): THE CONCEDE LAW ──────────────────────
  // The floating ✕ used to be a trapdoor: onExit(null) always, which
  // in a LIVE PvP duel froze the adversary in a verdict-less match and
  // (post-C8) let the quitter dodge the ladder. Now:
  //   · Finished duel → the ✕ carries the verdict out (rewards record
  //     even when the player skips the victory screen's own button).
  //   · Live PvP duel → two-tap concede: first tap ARMS (✕ becomes
  //     CONCEDE?, disarms after 3s), second tap writes endMatch for
  //     the adversary — they get the victory screen and their C8
  //     rewards; the quitter records a crestless loss (−12 score).
  //   · NPC / waiting-room → walk away freely, unchanged.
  const concedeTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(concedeTimerRef.current), []);
  const handleExitClick = async () => {
    if (isFinished) {
      // ⚠ A NO-CONTEST MUST CARRY `null`, NOT `false`. didSeatWin on a
      // null winner returns false, and App.js reads `typeof iWon ===
      // "boolean"` as "there is a verdict" — so a plain false would file a
      // ladder LOSS against every player of a duel that never started.
      const iWon = (match && !match.noContest) ? didSeatWin(match, playerIdx) : null;
      onExit(match?.noContest ? null : (match?.winner ?? null), { iWon });
      return;
    }
    if (!isNPC && match?.status === "active") {
      if (!concedeArmed) {
        setConcedeArmed(true);
        clearTimeout(concedeTimerRef.current);
        concedeTimerRef.current = setTimeout(() => setConcedeArmed(false), 3000);
        return;
      }
      clearTimeout(concedeTimerRef.current);
      // ⭐ N-SEAT CONCEDE FIX. The old one-liner — endMatch(code, oppIdx)
      // then onExit(oppIdx, ...) — reused finishOrEliminate's "exchange
      // winner → their target is the loser" logic (getTargetIdx(match,
      // oppIdx)). In 1v1 that's invisible: my opponent's only possible
      // target IS me, so it happened to eliminate the right seat. In a
      // 3-4p FFA it is not — oppIdx's own activeTarget can be a THIRD
      // seat, so conceding could silently knock out someone who never
      // touched the exit button. A concede always means exactly one
      // thing: MY seat (and my whole team, under shared Anima) is out.
      // Never routed through getTargetIdx.
      // ⚠ THE HAND-COPY IS GONE (2026-08-25). Everything below used to
      // be an inline re-implementation of finishOrEliminateSeat's two
      // branches — the decided branch and the knock-out-and-play-on
      // branch — right down to a verbatim copy of its four reseat
      // lines. And it inherited that copy's defect: it moved the chair
      // to a seat that then played A FULL HALF-TURN with no `turn++`,
      // no R93 clock call, and no Anima-ledger rotation. Every
      // concession in an FFA under-counted R93 by one, and a table
      // where people concede is exactly the table that runs long.
      //
      // The resolver already does the concede's semantics exactly, and
      // does them ONCE: `explicitLoserIdx` is the whole reason that
      // parameter exists. A concede always means "MY seat (and my whole
      // team, under shared Anima) is out" and must never be routed
      // through getTargetIdx — passing playerIdx as the explicit loser
      // says precisely that. `winnerIdx` is only consulted by the
      // resolver's empty-survivors floor, so the next living seat is
      // handed in as the sanest thing to name there.
      //
      // matchRef.current, not `match`: see finishOrEliminate's banner.
      const liveMatch = matchRef.current || match;
      let concededWinner = null;
      try {
        const res = await finishOrEliminateSeat(
          code, liveMatch, getNextActiveSeat(liveMatch, playerIdx), playerIdx
        );
        // null whenever conceding didn't decide the whole match — my
        // exit doesn't get to declare a winner among players still
        // fighting. Unchanged from the hand-copy's contract.
        concededWinner = res.ended ? res.winner : null;
      } catch (e) { console.warn("[C16 concede] failed:", e); }
      // `concededWinner` is null whenever conceding didn't decide the
      // whole match (my exit doesn't get to declare a winner among
      // players still fighting). A concede is always MY loss regardless
      // — `iWon: false` tells App.js's PvP result flow to record the
      // crestless loss even when there's no match-wide winner yet to
      // compare `winnerId` against.
      onExit(concededWinner, { conceded: true, iWon: false });
      return;
    }
    onExit(null, {});
  };

  // ── NEW — Hand enter/exit animation tracker ──
  // First non-empty hand seen → establish baseline, skip animations that tick.
  // Prevents the opening 5-card hand from all sliding in at match start.
  useEffect(() => {
    const currHand = me?.hand || [];
    const prevHand = prevHandRef.current || [];
    if (JSON.stringify(prevHand) === JSON.stringify(currHand)) return;

    if (!handBaselineSetRef.current && currHand.length > 0) {
      handBaselineSetRef.current = true;
      prevHandRef.current = currHand;
      return;
    }

    const prevCount = {};
    prevHand.forEach(id => { prevCount[id] = (prevCount[id] || 0) + 1; });
    const currCount = {};
    currHand.forEach(id => { currCount[id] = (currCount[id] || 0) + 1; });

    const newEntering = new Set();
    const seenInPrev = { ...prevCount };
    currHand.forEach((id, i) => {
      if ((seenInPrev[id] || 0) > 0) {
        seenInPrev[id]--;
      } else {
        newEntering.add(i);
      }
    });

    const newExiting = [];
    Object.keys(prevCount).forEach(id => {
      const diff = prevCount[id] - (currCount[id] || 0);
      for (let k = 0; k < diff; k++) {
        newExiting.push({ key: `${id}-${Date.now()}-${k}-${Math.random().toString(36).slice(2,6)}`, cardId: id });
      }
    });

    if (newEntering.size > 0) {
      setEnteringHandIdxs(newEntering);
      setTimeout(() => setEnteringHandIdxs(new Set()), 500);
    }
    if (newExiting.length > 0) {
      setExitingHandCards(prev => [...prev, ...newExiting]);
      newExiting.forEach(ex => {
        setTimeout(() => setExitingHandCards(prev => prev.filter(c => c.key !== ex.key)), 320);
      });
    }

    prevHandRef.current = currHand;
  }, [me?.hand]);

  // ── Pass 2 — Turn breath animation
  // When the active player changes, the whole board dims briefly.
  // Ignores the initial mount (when prev is null).
  const [turnBreath, setTurnBreath] = useState(false);
  const prevActivePlayerRef = useRef(match?.activePlayer);
  useEffect(() => {
    const prev = prevActivePlayerRef.current;
    const curr = match?.activePlayer;
    if (curr != null && prev != null && curr !== prev) {
      setTurnBreath(true);
      const t = setTimeout(() => setTurnBreath(false), 460);
      prevActivePlayerRef.current = curr;
      return () => clearTimeout(t);
    }
    prevActivePlayerRef.current = curr;
  }, [match?.activePlayer]);

  // ── MAGNUM PASS 1 — Turn Herald ─────────────────────────────
  // A brief cinematic banner sweeps the center when the active player
  // changes. Fires only mid-duel — never on mount, during the coin flip,
  // or after the Solrend ends. Pointer-events none; play is never blocked.
  const [turnHerald, setTurnHerald] = useState(null); // null | "mine" | "theirs"
  const prevHeraldTurnRef = useRef(null);
  useEffect(() => {
    const prev = prevHeraldTurnRef.current;
    const curr = match?.activePlayer;
    prevHeraldTurnRef.current = curr;
    if (prev == null || curr == null || prev === curr) return;
    if (!coinFlipDone || isFinished) return;
    setTurnHerald(curr === playerIdx ? "mine" : "theirs");
    const t = setTimeout(() => setTurnHerald(null), 1600);
    return () => clearTimeout(t);
  }, [match?.activePlayer, coinFlipDone, isFinished, playerIdx]);

  // ── MAGNUM PASS 1 — Anima impact: screen shake + edge flash ──
  // When MY Anima drops, the whole field flinches and the screen edges
  // flash red for a beat. Skips the first reading (mount / mid-match
  // resume) so joining an existing match never shakes. Healing = no shake.
  const [animaImpact, setAnimaImpact] = useState(false);
  const prevMyAnimaRef = useRef(null);
  useEffect(() => {
    const prev = prevMyAnimaRef.current;
    const next = me?.anima;
    prevMyAnimaRef.current = typeof next === "number" ? next : prev;
    if (typeof prev === "number" && typeof next === "number" && next < prev) {
      setAnimaImpact(true);
      const t = setTimeout(() => setAnimaImpact(false), 480);
      return () => clearTimeout(t);
    }
  }, [me?.anima]);



  // ── Solrend ready-up + coin flip ─────────────────────────────
  // MP-FIX: each client announces itself on mount. "Both present" now means
  // both CLIENTS are actually in the Shatterfield — not merely that both
  // player records exist in the DB (the creator's record exists before
  // they've even clicked Enter). NPC matches treat the NPC as present.
  // ── C17 (August 17 2026): THE ABANDONED-TAB LAW ──────────────
  // C16 closed the deliberate quit. This closes the other one: a
  // player who CLOSES THE BROWSER mid-PvP writes nothing at all, and
  // their adversary sat in a live match waiting for a turn that was
  // never coming, with no verdict, no rewards and no way out but the
  // ✕ — which post-C16 asks them to concede a duel they were winning.
  //
  // THE HEARTBEAT RIDES `present`. It does not need a new field: the
  // presence flag was already written by both clients and already
  // read as truthy, and a TIMESTAMP is truthy. So `present/{idx}`
  // stops being "I arrived" and becomes "I am still here as of",
  // `bothPresent` keeps working untouched, and no rules change is
  // needed because match children were never $extra-locked.
  useEffect(() => {
    updateMatch(code, { [`present/${playerIdx}`]: Date.now() });
    // The NPC is stamped `true`, not a time, and that is the tell the
    // watchdog below keys off: a boolean means "cannot abandon."
    if (isNPC) updateMatch(code, { [`present/${getTargetIdx(match, playerIdx)}`]: true });
    if (isNPC) return;
    // ⭐ THE DISCONNECT LAW REACHES THE MATCH (2026-09-05). Before this,
    // `matches/{code}` had no onDisconnect anywhere — the ONLY mechanism
    // in the Empire that survives a closed browser lived in ieSocial and
    // had never been pointed at a duel. So a shut tab left a frozen
    // timestamp and the adversary had to sit out the full 45-second
    // ABANDON_MS before the board would even admit something was wrong.
    // Now the server drops our row the instant the socket dies, the
    // watchdog sees `undefined`, and the empty chair is named at once.
    // No rules change: match children were never $extra-locked.
    let od = null;
    try {
      od = onDisconnect(dbRef(db, `matches/${code}/present/${playerIdx}`));
      od.remove().catch(() => {});
    } catch (e) { /* offline / rules — the 45s watchdog still covers it */ }
    const iv = setInterval(() => {
      updateMatch(code, { [`present/${playerIdx}`]: Date.now() });
    }, HEARTBEAT_MS);
    return () => {
      clearInterval(iv);
      try { od?.cancel?.().catch(() => {}); } catch (e) {}
    };
  }, [code, playerIdx, isNPC]);

  // ⭐ AI SEATS ARE STAMPED IN THEIR OWN EFFECT, AND THIS IS WHY (2026-09-05).
  // This loop used to live inside the heartbeat effect above, whose deps are
  // `[code, playerIdx, isNPC]` — all stable at mount. So its body ran exactly
  // ONCE, on the first render, when `match` is still the `useState(null)`
  // initial value. `allSeatIdxs(null)` falls back to seatCount 2 and iterates
  // [0,1]; `match?.players?.[i]?.controller` is undefined for both; and
  // `present/2` was NEVER WRITTEN. `bothPresent` demands a present row for
  // every seat of a 3-seat match, so **The Gauntlet — Duo and Story Co-op
  // could not start at all** — both sat on "waiting for the table" forever.
  // It never surfaced in 1v1-vs-NPC because that path has no third seat.
  // Depending on the seat list is the whole fix.
  const aiSeatKey = allSeatIdxs(match)
    .filter((i) => match?.players?.[i]?.controller === "ai").join(",");
  useEffect(() => {
    if (!aiSeatKey) return;
    const updates = {};
    aiSeatKey.split(",").forEach((k) => {
      const i = Number(k);
      if (!match?.present?.[i]) updates[`present/${i}`] = true;
    });
    if (Object.keys(updates).length) updateMatch(code, updates);
  }, [code, aiSeatKey, match?.present]);

  // A seated client repairs a match left full-but-"waiting" by a join whose
  // second write never landed. No-op for anything already active/finished.
  useEffect(() => { if (!isNPC && code) ensureActive(code); }, [code, isNPC]);

  // N-SEAT: "both" generalizes to "every seat" — a 3p/4p FFA or 2v2
  // match doesn't start the ceremony until the WHOLE table is present/
  // ready/struck, not just me and whichever seat I happen to be
  // targeting right now. `bothPresent`/`bothReady`/`bothTerror` keep
  // their names (everything below already reads them) but now mean
  // "the whole match," which is exactly what they meant on a classic
  // 2-seat match anyway.
  const seatIdxsAll = allSeatIdxs(match);
  const bothPresent = seatIdxsAll.every((i) => !!match?.players?.[i]) &&
    (isNPC || seatIdxsAll.every((i) => !!match?.present?.[i]));

  const myReady = match?.solrendReady?.[playerIdx] ?? false;
  const oppReady = match?.solrendReady?.[oppIdx] ?? false;
  const bothReady = seatIdxsAll.every((i) => match?.solrendReady?.[i]);

  // ── TERROR GATE ───────────────────────────────────────────────
  // After the coin flip resolves and the board is dispersed — but BEFORE
  // the first turn — a TERROR strike must land from both seats. Until then
  // NOTHING ticks: not the match clock, not the turn clock. This kills the
  // late-joiner desync: the joiner used to arrive ~15s into the countdown
  // with their turn timer already draining (half their first turn gone
  // before they saw the board). Now no clock moves until both are ready.
  // (NPC auto-strikes its own seat — only the human must press.)
  const myTerror  = match?.terrorReady?.[playerIdx] ?? false;
  const oppTerror = match?.terrorReady?.[oppIdx] ?? false;
  const bothTerror = isNPC
    ? myTerror // classic 1v1-vs-NPC — unchanged, the NPC effect below auto-strikes its own seat
    : seatIdxsAll.every((i) => match?.terrorReady?.[i]);
  // duelStarted is a SHARED Firebase latch: the instant EITHER client sees
  // both strikes land, it writes this flag — and from then on BOTH clients
  // read the identical "duel is live" truth straight from the DB. This makes
  // it structurally impossible for one seat's clock to run while the other
  // is still gated (the exact host-bypass split we saw). Local coinFlipDone
  // timing can differ between clients; a shared latch cannot.
  const duelStarted = !!match?.duelStarted;
  // Master clock gate — every timer in the duel reads this.
  const duelLive = coinFlipDone && (duelStarted || bothTerror);

  // ⚠ PLACEMENT IS LOAD-BEARING. This block reads `duelLive`, which
  // is a const declared just above — the first cut of C17 sat it up
  // beside `bothPresent` where it reads better, and `oppAbandoned`
  // is computed during render, so it threw a temporal-dead-zone
  // ReferenceError on the opening frame of every duel. Keep it
  // below the gate it depends on.
  // ── C17's watchdog ───────────────────────────────────────────
  // ⚠ IT NEVER COMPARES TWO CLOCKS. The obvious version — Date.now()
  // minus their timestamp — is wrong, because that timestamp came off
  // THEIR machine and a phone five minutes fast would declare its
  // opponent abandoned on the opening frame. So the only clock read
  // here is this one, and the only question asked of the DB is "has
  // the number CHANGED." Silence is measured as how long I have been
  // watching the same value.
  // ⭐ C17-N — THE WATCHDOG WATCHES THE WHOLE TABLE (2026-09-05).
  //
  // It used to watch exactly one seat: `oppIdx`, i.e. `getTargetIdx`, i.e.
  // WHOEVER I AM CURRENTLY AIMED AT. Two things were wrong with that, and
  // both are invisible in 1v1 where the target never moves:
  //
  //   1. In a 3-4p FFA, a seat I am not targeting could close their browser
  //      and I would never be told. Only the player who happened to be
  //      pointing at them saw the empty chair.
  //   2. The watched value CHANGED when I re-targeted, which reset
  //      `seenAt` to now and restarted the 45-second silence count from
  //      zero — so re-aiming at an abandoned player postponed the
  //      accusation indefinitely. The watchdog could be walked away from.
  //
  // Now every other seat is watched at once, each with its own silence
  // clock, and re-targeting changes nothing. Same measurement discipline as
  // before: never compare two clocks — ask only "has the number CHANGED",
  // and measure how long I have been watching the same value.
  //
  // AND IT NOW COVERS THE CEREMONY, which is where the real freeze always
  // was. `duelLive` gated the whole thing off, so the exact C17 symptom
  // survived in the one window C17 did not cover: B joins, the match flips
  // active, B's client stamps present — and A never clicks "◆ Enter
  // Shatterfield" or closes the tab before clicking. `present/0` is never
  // written, `bothPresent` is false forever, and B sat on "Waiting for
  // opponent…" with no watchdog, no timer, no claim, and no exit that was
  // not a concede. A seat that never arrives is watched from mount.
  const CEREMONY_ABANDON_MS = 75000;   // ⟵ DIAL — pre-duel is more patient
  const seatBeatsRef = useRef({});
  const [silenceTick, setSilenceTick] = useState(0);

  // Every seat that is not me, not AI-controlled, and not already out.
  const watchedSeats = useMemo(() => {
    if (isNPC || !match) return [];
    return allSeatIdxs(match).filter((i) =>
      i !== playerIdx &&
      match?.players?.[i]?.controller !== "ai" &&
      !isEliminated(match, i)
    );
  }, [match, isNPC, playerIdx]);

  const watchKey = watchedSeats.map((i) => `${i}:${match?.present?.[i] ?? "-"}`).join("|");
  useEffect(() => {
    const now = Date.now();
    const book = seatBeatsRef.current;
    watchedSeats.forEach((i) => {
      const v = match?.present?.[i];
      // `undefined` is a real, watchable value — it is what onDisconnect
      // leaves behind, and what a seat that never arrived has always had.
      if (!book[i] || book[i].value !== v) book[i] = { value: v, seenAt: now };
    });
    Object.keys(book).forEach((k) => { if (!watchedSeats.includes(Number(k))) delete book[k]; });
    setSilenceTick((t) => t + 1);
  }, [watchKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isNPC || isFinished || !watchedSeats.length) return;
    const iv = setInterval(() => setSilenceTick((t) => t + 1), 2000);
    return () => clearInterval(iv);
  }, [isNPC, isFinished, watchedSeats.length]);

  // Seats that owe us a heartbeat and have stopped giving one. A BOOLEAN
  // presence is the NPC or an AI seat and can never abandon a tab —
  // accusing one would be the worst possible false positive — so only a
  // number, or a row that has gone missing entirely, is ever accused.
  const absentSeats = useMemo(() => {
    if (isNPC || isFinished) return [];
    const limit = duelLive ? ABANDON_MS : CEREMONY_ABANDON_MS;
    const now = Date.now();
    return watchedSeats.filter((i) => {
      const v = match?.present?.[i];
      if (typeof v === "boolean") return false;
      const seat = match?.players?.[i];
      if (!seat) return false;                       // chair not claimed — that is the lobby's job
      const seen = seatBeatsRef.current[i]?.seenAt;
      if (!seen) return false;
      return (now - seen) >= limit;
    });
    // silenceTick is the heartbeat of this memo — it is why it re-runs.
  }, [watchKey, silenceTick, duelLive, isFinished, isNPC, watchedSeats, match]);   // eslint-disable-line react-hooks/exhaustive-deps

  // The name every existing consumer already reads. Post-start only, so the
  // in-duel "claim the Solrend" panel behaves exactly as it did.
  const oppAbandoned = !isNPC && !isFinished && duelLive && absentSeats.length > 0;
  // Pre-start. A different verdict entirely — see abandonBeforeStart.
  const ceremonyStalled = !isNPC && !isFinished && !duelLive && absentSeats.length > 0;

  // ⭐ THE ELECTED SEAT (2026-09-05). Three separate jobs in this file were
  // hardcoded to "seat 0 does it, seat 1 stays quiet" — a sentence written
  // for two chairs, in a file that now seats four. Each one is replaced by
  // an ELECTION over the living seats, which returns seat 0 in every 1v1
  // (so nothing about a classic duel changes) and returns a real answer
  // when seat 0 is an AI, is eliminated, or has closed its tab.
  const hostSeat = useMemo(() => {
    if (!match) return 0;
    const humans = allSeatIdxs(match).filter((i) =>
      match?.players?.[i] && match.players[i].controller !== "ai" && !isEliminated(match, i));
    const arrived = humans.filter((i) => match?.present?.[i] != null);
    return (arrived.length ? arrived : humans)[0] ?? 0;
  }, [match]);
  const clockWriterSeat = useMemo(() => (match ? (livingSeats(match)[0] ?? 0) : 0), [match]);
  const clockRank = useMemo(() => {
    if (!match) return 0;
    const ls = livingSeats(match);
    const i = ls.indexOf(playerIdx);
    return i < 0 ? ls.length : i;
  }, [match, playerIdx]);

  // Naming for the panel. At 3-4 seats "your adversary" is not an answer —
  // the player needs to know WHICH chair emptied.
  const absentNames = absentSeats
    .map((i) => match?.players?.[i]?.name || `Seat ${i + 1}`)
    .join(", ");
  const absentSilenceSec = absentSeats.length
    ? Math.floor(Math.max(...absentSeats.map((i) =>
        Date.now() - (seatBeatsRef.current[i]?.seenAt || Date.now()))) / 1000)
    : 0;

  // THE CLAIM IS A TAP, NOT AN AUTOMATIC VERDICT. caP'n's C17 wording
  // is "adversary CLAIMS victory," and the wording is the right
  // design: a phone that backgrounded the tab for fifty seconds looks
  // identical from here to a browser that closed, and auto-ending a
  // duel somebody was winning because their opponent took a phone
  // call is a worse bug than the one this fixes. So the board says
  // what it sees and lets the player decide.
  // ⭐ AND IT KNOCKS OUT THE SEAT THAT ACTUALLY LEFT (2026-09-05).
  //
  // This was `endMatch(code, playerIdx)` — no explicit loser — which fell
  // through to `getTargetIdx(match, playerIdx)`, i.e. WHOEVER I AM AIMED
  // AT. Three separate wrongs, all invisible in 1v1:
  //   · it could eliminate a fully present, innocent third player;
  //   · at 3+ seats `matchIsDecided` is false, so it wrote an elimination
  //     flag and the match carried on — a button labelled "Claim the
  //     Solrend" that did not claim the Solrend;
  //   · it went through the escalation-aware wrapper, so an abandoning
  //     boss could be handed a Second Wind on its way out the door.
  // Now every absent seat is named explicitly and knocked out through the
  // resolver, which is the one thing in the codebase that already knows
  // how to take a whole pooled team out with one seat and how to decide
  // whether that ends the table.
  const claimAbandonedVictory = async () => {
    try {
      for (const loser of absentSeats) {
        const res = await finishOrEliminate(code, playerIdx, loser);
        if (res?.ended) return;
      }
    } catch (e) { console.warn("[C17 claim] finishOrEliminate failed:", e); }
  };

  // ⭐ A DUEL THAT NEVER STARTED HAS NO WINNER. Pre-TERROR there is no
  // board, no exchange and nothing to adjudicate, so this ends the match as
  // a NO CONTEST rather than handing anyone a win. That matters twice: it
  // is honest, and it stops two friends farming the ladder by joining each
  // other and closing a tab — which a "claim" verdict here would have made
  // the cheapest +20 in the game. `winner` stays null and `noContest` is
  // what handleExitClick reads to pass `iWon: null` instead of `false`,
  // because `didSeatWin` on a null winner returns false, and false would
  // have recorded a LOSS for everybody who walked away from a duel that
  // never happened.
  const abandonBeforeStart = async () => {
    try { await updateMatch(code, { status: "finished", winner: null, noContest: true, finishedAt: Date.now() }); }
    catch (e) { console.warn("[C17 no-contest] failed:", e); }
  };

  // NPC auto-presses immediately once player presses
  useEffect(() => {
    if (!isNPC || !match || coinFlipDone) return;
    if (bothPresent && myReady && !oppReady) {
      updateMatch(code, { [`solrendReady/${oppIdx}`]: true });
    }
  }, [isNPC, myReady, bothPresent]);

  // NPC always strikes TERROR the instant the gate opens.
  useEffect(() => {
    if (!isNPC || !coinFlipDone) return;
    if (!oppTerror) updateMatch(code, { [`terrorReady/${oppIdx}`]: true });
  }, [isNPC, coinFlipDone, oppTerror]);

  // N-SEAT AI SEATS (Gauntlet-2v2, co-op story) — parallel to the two
  // isNPC effects just above, for the DIFFERENT case of an AI-controlled
  // seat living inside a real multiplayer Firebase match. Nobody's
  // client is mounted on that seat, so it never presses its own button
  // — the host's client (whichever human is seat 0) presses it on the
  // AI seat's behalf, same "auto-press only what nobody else will"
  // reasoning as the isNPC effects.
  useEffect(() => {
    // ⚠ WAS `playerIdx !== 0`. If seat 0 refreshed or closed during the
    // ceremony of a Gauntlet-Duo / co-op match, nobody pressed the AI
    // seat's buttons and the table never started — with, before today, no
    // watchdog to say why. The elected host is the lowest living human
    // seat that has actually arrived, so the job moves when seat 0 does not.
    if (isNPC || !match || playerIdx !== hostSeat) return;
    const aiSeats = seatIdxsAll.filter((i) => match?.players?.[i]?.controller === "ai");
    if (!aiSeats.length) return;
    if (!coinFlipDone && bothPresent && myReady) {
      const updates = {};
      aiSeats.forEach((i) => { if (!match?.solrendReady?.[i]) updates[`solrendReady/${i}`] = true; });
      if (Object.keys(updates).length) updateMatch(code, updates);
    }
    if (coinFlipDone) {
      const updates = {};
      aiSeats.forEach((i) => { if (!match?.terrorReady?.[i]) updates[`terrorReady/${i}`] = true; });
      if (Object.keys(updates).length) updateMatch(code, updates);
    }
  }, [isNPC, playerIdx, hostSeat, match, coinFlipDone, bothPresent, myReady, seatIdxsAll]);

  // Latch the duel live the moment both strikes are in — one shared write,
  // idempotent, either client may fire it. Once written, every client's
  // duelLive reads true from the DB in lockstep.
  useEffect(() => {
    if (!coinFlipDone || duelStarted) return;
    // ⏳ `duelStartedAt` RIDES THE SAME LATCH, and R87 depends on it.
    // The clock used to be pure client state (`globalTimer`, zeroed on
    // mount), which meant a page refresh at 19:59 handed the player a
    // fresh twenty minutes — the Anima-freeze escape hatch defeated by
    // F5 — and the two PvP seats' clocks drifted apart by however long
    // the joiner took to arrive. This file had ALREADY solved exactly
    // that problem for the START of the duel, one line above, with a
    // shared Firebase latch whose own comment says a local value
    // "makes it structurally impossible for one seat's clock to run
    // while the other is still gated." The END of the duel did not get
    // the same treatment until now. One extra field, written once,
    // beside the flag it belongs to.
    if (bothTerror) updateMatch(code, { duelStarted: true, duelStartedAt: serverTimestamp() });
  }, [coinFlipDone, bothTerror, duelStarted]);

  // Guarantee a fresh, visible 10s window the instant the gate opens (guards
  // against any leftover countdown state from a prior match on the same mount).
  useEffect(() => {
    if (coinFlipDone) setTerrorCountdown(10);
  }, [coinFlipDone]);

  // 10-second TERROR auto-begin. Each client presses only ITS OWN seat
  // (same authority rule as the Solrend countdown), so no client can ever
  // force-start the duel for a player whose board isn't mounted.
  useEffect(() => {
    if (!coinFlipDone || bothTerror || duelStarted) return;
    const interval = setInterval(() => {
      setTerrorCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          const updates = {};
          if (!myTerror) updates[`terrorReady/${playerIdx}`] = true;
          if (isNPC && !oppTerror) updates[`terrorReady/${oppIdx}`] = true;
          if (Object.keys(updates).length > 0) updateMatch(code, updates);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [coinFlipDone, bothTerror, duelStarted]);

  // 15-second countdown auto-start
  useEffect(() => {
    if (coinFlipDone || !bothPresent) return;
    const interval = setInterval(() => {
      setSolrendCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          // MP-FIX: each client auto-presses only ITS OWN seat. Force-
          // readying the opponent let one client start the duel for a
          // player whose Shatterfield wasn't even mounted. NPC seats are
          // still pressed by the human's client (no one else will).
          const updates = {};
          if (!myReady) updates[`solrendReady/${playerIdx}`] = true;
          if (isNPC && !oppReady) updates[`solrendReady/${oppIdx}`] = true;
          if (Object.keys(updates).length > 0) updateMatch(code, updates);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [bothPresent, coinFlipDone]);

  // Trigger coin flip when both are ready
  // Player 0 does the flip, writes BOTH the result AND the activePlayer to Firebase
  // atomically. Both clients then read match.coinFlipResult for the display so
  // there's no flicker from async Firebase round-trips vs local state.
  useEffect(() => {
    if (coinFlipDone || !bothReady) return;
    // MP-FIX: if the duel is already underway (we mounted late — e.g. the
    // creator entering after the joiner), do NOT re-run the ceremony.
    // The old code let a late client write a fresh flip and stomp
    // activePlayer mid-game.
    if (match?.coinFlipResult && (match?.turn || 0) > 0) {
      setCoinFlipDone(true);
      setCoinFlipPhase("idle");
      return;
    }
    setCoinFlipPhase("spinning");
    setTimeout(() => {
      // MP-FIX: the flip is now DETERMINISTIC from createdAt parity, so
      // both clients compute the identical result and either may write it
      // (idempotent — same payload). The old design made player 0 the sole
      // authority; if their client wasn't mounted, player 1 hung forever
      // on a result that never arrived.
      const seed = match?.createdAt || 0;
      const flip = (seed % 2 === 0) ? "heads" : "tails";
      const winner = flip === "heads" ? 0 : 1;
      // N-SEAT: "classic coin toss, keep tossing till there's winners
      // and losers" (caP'n) — decideTurnOrder is that same coin, drawn
      // once per remaining seat instead of once total. For a 2-seat
      // match this is EXACTLY `winner`/the old flip (decideTurnOrder on
      // two seats degenerates to "heads → [0,1], tails → [1,0]", i.e.
      // the same coin deciding the same thing), so 1v1 writes the
      // identical payload it always did.
      const order = decideTurnOrder(seatIdxsAll, seed);
      const firstUp = seatIdxsAll.length <= 2 ? winner : order[0];
      if (!match?.coinFlipResult && (match?.turn || 0) === 0) {
        updateMatch(code, { coinFlipResult: flip, activePlayer: firstUp, turnOrder: order });
      }
      setCoinFlipPhase("result");
      setTimeout(() => {
        setCoinFlipDone(true);
        setCoinFlipPhase("idle");
      }, 3500);
    }, 1600); // D12 — the spin itself is 1.5s (by decree); result fires just after touchdown
  }, [bothReady, coinFlipDone]);

  // Both clients read the authoritative coin flip result from Firebase.
  // Falls back to local state for player 0 during the brief pre-write window.
  const effectiveCoinFlipResult = match?.coinFlipResult || coinFlipResult;
  // Mirror Firebase value into local state so the result screen renders consistently.
  useEffect(() => {
    if (match?.coinFlipResult && !coinFlipResult) {
      setCoinFlipResult(match.coinFlipResult);
    }
  }, [match?.coinFlipResult, coinFlipResult]);

  // ── INTERRUPT SYSTEM (per rulebook) ──
  // Proactive interrupts available during adversary's turn:
  //   • L3 Tomes from your hand — play-and-activate in one motion, pay Khao
  //   • L2 or L3 Tomes face-down on your field — flip face-up, pay Khao
  // L1 Tomes cannot interrupt. L2 Tomes from hand cannot interrupt — per
  // rules, L2 can only interrupt if already placed face-down (ambush style).
  const handInterrupts = (me?.hand || [])
    .map((id, i) => ({ card: getCard(id), idx: i, source: "hand" }))
    .filter(({ card }) => card?.type === "Tome" && card?.level === 3);
  const fieldInterrupts = ((me?.field?.tomes) || [])
    .map((tome, i) => {
      if (!tome || typeof tome !== "object" || !tome.faceDown) return null;
      const card = getCard(tome.id);
      if (!card || card.type !== "Tome" || card.level < 2) return null;
      return { card, idx: i, source: "field" };
    })
    .filter(Boolean);
  const canInterrupt = !myTurn && (handInterrupts.length + fieldInterrupts.length) > 0;

  // ── H6 · THE RECORDER, AND THE TAPE THAT CAN STAND IN FOR THE SOCKET ──
  // The recorder starts with the listener and stops with it. The SEED is
  // taken from the first full board the renderer ever sees rather than
  // from createMatch, because a joiner never calls createMatch and a
  // reconnect starts mid-duel — whatever the renderer started from is by
  // definition what the replay must start from.
  //
  // `replayFeed` is the whole of playback. It exposes subscribe() with
  // listenToMatch's exact contract, so the ONLY difference between a live
  // duel and a replayed one is which of these two lines runs. Read-only
  // is enforced at the three gameManager writes (THE PLAYBACK LOCK), not
  // by auditing this file for buttons.
  useEffect(() => {
    if (replayFeed || tutorialMode) return;   // a lesson is not a duel
    startRecording(code, { mode: isNPC ? "npc" : "pvp" });
    return () => stopRecording();
  }, [code, isNPC, replayFeed, tutorialMode]);

  useEffect(() => {
    if (replayFeed) return replayFeed.subscribe(setMatch);
    const unsub = listenToMatch(code, (data) => {
      // ── FIREBASE ARRAY-HOLE NORMALIZATION (July 2026 crash fix) ──
      // RTDB strips null entries from arrays and hands back sparse
      // integer-keyed OBJECTS instead: tomes [null, D'uhmdumn, null]
      // round-trips as {1:{...}}, and every `(tomes || []).map/.some`
      // downstream crashes (an object is truthy, so `|| []` never
      // saves it). This bit when the NPC's nullify-consumption write
      // touched the player's tome rift for the first time. Coerce every
      // rift collection back into a fixed-length array HERE — the one
      // chokepoint every read flows from — so the whole app only ever
      // sees real arrays.
      if (data?.players) {
        Object.keys(data.players).forEach(k => {
          const f = data.players[k]?.field;
          if (!f) return;
          f.tomes = riftArray(f.tomes, 3);
          f.curios = riftArray(f.curios, 2);
          // ⚠ R110 — THIS LINE WAS A SILENT BEING-EATER AT THE OLD WIDTH.
          // Firebase drops null holes and hands back an object, so a board
          // with rifts 0 and 3 occupied and 1-2 empty arrives as {0:…,3:…}.
          // Coercing that to length THREE would have rebuilt the array
          // without index 3 — the Mind rift's being would vanish mid-duel,
          // with no error, no toast and no log line. The Tome and Curio
          // rows above are genuinely 3 and 2 and are correct as literals;
          // this one is the battle line and must follow the constant.
          if (f.esclings && !Array.isArray(f.esclings)) f.esclings = riftArray(f.esclings, ESCLING_SLOTS);
        });
      }
      seedRecording(data);   // H6 — no-op after the first full board
      setMatch(data);
      if (data?.players?.[playerIdx]) {
        const p = data.players[playerIdx];
        // ── THE OPENING BOARD, PER SLOT ─────────────────────
        // This used to ask one question — "is slot 0 empty?" — and
        // answer it for all three. That was correct while every
        // Escling was auto-derived and all three were therefore
        // always the same card. THE SETS (2026-08-17) let a player
        // choose the triple, and choosing TWO of the three is a
        // legal, sensible thing to do — so the old shape would
        // have silently overwritten both choices the moment slot 0
        // was left open. Now each rift is filled only if that rift
        // is empty, and a slot the player picked is never touched.
        //
        // The fallback itself is unchanged: an unchosen rift gets
        // the Escling derived from the deck, exactly as before.
        //
        // ⚠ THE GUARD, and why it is not optional. Widening the
        // question from "slot 0" to "any slot" also widens WHEN
        // this can fire: a rift that loses its Escling mid-duel
        // would now be handed a fresh one 500ms later, for free,
        // forever. Nothing nulls an Escling today (transcend and
        // descend both spread the rift and keep the field), so
        // this is a hazard rather than a live bug — but it is one
        // careless write away, and a free Escling is unwinnable to
        // diagnose from a screenshot. So: opening rifts only. A
        // rift that carries an Escendant or a Soul has been PLAYED
        // and is never re-seeded, and the whole pass stops after
        // the first turn regardless.
        if (p.grimoire && (data?.turn || 0) <= 1) {
          const rifts = p.field?.esclings || [];
          // ⚠ R110 — AND SO WAS THIS ONE, in a way that could not be
          // recovered from. The repair pass is gated to `turn <= 1`, so a
          // seat whose fourth rift failed to write on the opening Turn
          // would have stayed three-wide for the entire duel with the
          // repair window already closed behind it.
          const missing = Array.from({ length: ESCLING_SLOTS }, (_, i) => i).filter(i =>
            !rifts[i]?.escling && !rifts[i]?.escendant && !(rifts[i]?.soul || []).length
          );
          if (missing.length) {
            setTimeout(() => {
              const built = buildInitialEsclings(p.grimoire);
              const patch = {};
              missing.forEach(i => { patch[`field/esclings/${i}`] = built[i]; });
              updatePlayer(code, playerIdx, patch);
            }, 500);
          }
        }
      }
      // ── THE PROPHECY CHECK IS NOT HERE ANY MORE (August 2026) ──
      // It used to be: `checkProphecies(code, data)`, right on this
      // line, inside this listener — the only call site in the project,
      // which meant a WIN CONDITION that only existed on clients with
      // this screen mounted. It now lives in src/turnEngine.js ·
      // handOffHalfTurn, which every half-turn transition in both
      // drivers goes through, so a headless duel, the balance harness
      // and a replay all see Prophecies fire.
      //
      // ⚠ AND IT MUST NOT COME BACK HERE ALONGSIDE. The hand-off's own
      // writes produce a snapshot that lands in this very listener; a
      // second check here would re-evaluate the same board the engine
      // just judged and fire a second time on it.
      // Auto-activate Curios once at duel start — for both players.
      // ⚠ KNOWN DEFECT — CURIOS ARE ACTIVATED FROM A RENDERER LISTENER.
      // These bonuses only exist where THIS screen is mounted. Any other
      // driver of a duel — headless, harness, replay, a future server
      // authority — forfeits every Curio for BOTH seats, silently: no
      // error, just a seat that quietly started the duel poorer than the
      // rules say it did. The NPC half of this hole was closed separately
      // inside NPC.js; the human half is still structural and is left
      // alone here deliberately (moving it is an engine change, not a
      // surgical one). `curiosActivated` is the idempotence guard and is
      // the only reason two mounted clients don't double-pay.
      const activateCuriosForPlayer = (pData, pIdx, silent = false) => {
        if (!pData || pData.curiosActivated || !pData.field?.curios) return;
        const curios = pData.field.curios.filter(Boolean);
        let animaBonus = 0, khaoBonus = 0;
        // R75 — the numbers live ONCE, in rules.CURIO_GIFTS (2026-09-15);
        // this switch was the second of two hand-typed copies.
        curios.forEach(id => {
          const gift = CURIO_GIFTS[id];
          if (!gift) return;
          animaBonus += gift.anima || 0;
          khaoBonus  += gift.khao  || 0;
        });
        const updates = { curiosActivated: true };
        if (animaBonus > 0) {
          updates.anima = sealAnimaGain((pData.anima || STARTING_ANIMA), animaBonus);
          Object.assign(updates, buildAnimaGainUpdate(pData, animaBonus));
        }
        if (khaoBonus > 0) {
          updates.maxKhao = Math.min(MAX_KHAO_ABS, (pData.maxKhao || STARTING_KHAO) + khaoBonus);
          updates.khao    = Math.min(MAX_KHAO_ABS, (pData.khao    || STARTING_KHAO) + khaoBonus);
        }
        updatePlayer(code, pIdx, updates).then(() => {
          if (!silent && curios.length > 0) {
            const names = curios.map(id => getCard(id)?.name).filter(Boolean).join(", ");
            showToast(`✦ Curios activated: ${names}`, SD.gold);
          }
        });
      };
      // Activate my curios
      activateCuriosForPlayer(data?.players?.[playerIdx], playerIdx, false);
      // Only activate opponent curios if this is an NPC match —
      // in multiplayer the opponent's own client handles it, preventing double-fire
      if (isNPC) {
        const oppIdx2 = playerIdx === 0 ? 1 : 0;
        activateCuriosForPlayer(data?.players?.[oppIdx2], oppIdx2, true);
      }
    });
    return unsub;
  }, [code, playerIdx]);

  // ⏳ THE MATCH CLOCK. Ticks locally for smoothness, but RE-ANCHORS to
  // the shared `duelStartedAt` on every tick when one exists — so a
  // refresh resumes where the duel actually is instead of at zero, and
  // both PvP seats read the same number. Falls back to a plain count-up
  // for any match written before this field existed.
  useEffect(() => {
    if (!duelLive) return; // match clock is frozen until both strike TERROR

    // ⏳ SEED ONCE FROM THE SHARED STAMP, THEN TICK LOCALLY.
    //
    // The first cut re-derived the whole value from `duelStartedAt` on
    // EVERY tick, and that traded one bug for a worse one: the anchor
    // is a server millisecond and `Date.now()` is the device's, so a
    // client whose system clock is twenty minutes fast would compute a
    // finished duel at move one and — if it were seat 0 — call the
    // Solrend before the first card was played. The old pure count-up
    // was at least skew-immune.
    //
    // Seeding once keeps both properties: a refresh resumes where the
    // duel actually is (the bug this exists to fix), and every second
    // after that is measured by the same device that measured the last
    // one (skew-immune). The seed is REFUSED if it claims the duel is
    // already over, because the safe failure is a fresh clock, never a
    // duel called on arrival.
    const anchor = matchRef.current?.duelStartedAt;
    if (typeof anchor === "number" && anchor > 0) {
      const seed = Math.floor((Date.now() - anchor) / 1000);
      if (seed > 0 && seed <= DUEL_TIME_LIMIT) setGlobalTimer(seed);
    }

    globalRef.current = setInterval(() => setGlobalTimer((t) => t + 1), 1000);
    return () => clearInterval(globalRef.current);
  }, [duelLive]);

  // ══════════════════════════════════════════════════════════
  // 🖐 R10 · THE HAND CAP AT THE HAND-OFF — BOTH DOORS
  // ══════════════════════════════════════════════════════════
  // R10 is draw-THEN-discard and the cap is HAND_LIMIT (8). This board
  // enforced it in exactly one place: the forced-discard MODAL, which a
  // player drives. NEITHER hand-off asked. So a seat whose Turn ran out
  // on the TIMER while the modal was still open crossed the boundary
  // holding nine cards, and the archived snapshot — which is what a
  // replay renders and what the referee audits — was over the cap.
  //
  // The brain has never had this hole: npcEnforceHandCap runs at the
  // bottom of all three runners and on every abort. This is the board's
  // half of the same law, and the two doors get DIFFERENT answers on
  // purpose:
  //
  //   • THE BUTTON is a choice, so it is REFUSED. The player is told
  //     why and the modal comes up — they pick the card, which is what
  //     R10 means for a human.
  //   • THE TIMER is not a choice. Refusing there would stall the table
  //     behind a modal nobody is looking at, so the worst card is
  //     buried automatically and the seat is told what left.
  //
  // ── R133 · THE CLOCK BURIES THE NEWEST CARD ──────────────────
  // caP'n, 2026-09-15, asked which card the timer takes, verbatim:
  // *"the newest card, that's 4xS right there"* — 4xS being his own
  // simplicity filter. So the pick is the last card in the hand and
  // nothing else is consulted: no card data, no sort, no tie-break.
  //
  // ⚠ WHICH END IS "NEWEST": THE TAIL. Every path in this project that
  // puts a card into a hand APPENDS to it — gameManager's
  // drawAndDepleteCards returns `[...currentHand, ...drawn]`, this
  // file's draws, tutors and Luxmoire pulls all spread
  // `[...(me?.hand || []), …]`, and NPC.js's runners `hand.push(drawn)`.
  // There is not one `unshift` into a hand anywhere in src/. So the
  // newest card is `hand[hand.length - 1]`.
  //
  // ⚠ THIS BANNER USED TO CLAIM A MIRROR AND THE CLAIM IS NOW FALSE. It
  // read: "`forcedDiscardPick` MIRRORS THE BRAIN'S npcPickDiscardIdx
  // (NPC.js) so the two drivers bury the same KIND of card: the cheapest
  // Tome, else the second copy of something, else the last card." The
  // two are SUPPOSED to differ now, because they answer two different
  // questions — the same split as the two doors above:
  //
  //   · npcPickDiscardIdx IS A BRAIN CHOOSING, which is the equivalent
  //     of a human picking from the forced-discard modal, not of a
  //     clock running out. A brain has no clock. That function also
  //     carries a MEASURED result — preferring to bury the Tomes the
  //     dispatcher cannot fire cost FIVE POINTS of clock-call rate
  //     (7.5% → 12.5%, seed 7) — so touching it is a balance change
  //     nobody asked for. LEAVE IT AS IT IS.
  //   · THIS IS A FORFEIT. Nobody chose it: the Turn ran out with the
  //     hand over the cap and the board takes a card. "The newest" is
  //     an answer to a forfeit, not to a play.
  //
  // THE MIRROR MUST NOT BE RESTORED. A future reader will find the two
  // functions disagreeing; that disagreement IS the ruling, and putting
  // the Tome-first pick back here would quietly re-decide a rule the
  // designer has already decided.
  //
  // ⚠ DETERMINISM IS PRESERVED, AND IN FACT STRENGTHENED — it was the
  // original reason this pick was hand-written instead of left random.
  // Several clients can legitimately reach the same grace-expiry
  // hand-off in the same second (see handOffChair's banner), and two
  // clients burying two DIFFERENT cards is a board that can be observed
  // wrong. "The newest card in hand" is a pure function of the hand's
  // LENGTH — it cannot vary with card data, sort stability or tie-break
  // order — so every client that reads the same hand reaches the same
  // card. R10 says discard one; it has never said which.
  const forcedDiscardPick = (hand) => hand.length - 1;

  // Bury down to the cap on a seat we are about to hand off FROM.
  // Returns the keys to fold into the caller's own write, or null when
  // the hand is already legal — so an ordinary hand-off pays one length
  // comparison for this and writes nothing extra.
  //
  // Under R133 the loop below is a plain TRIM OF THE TAIL: with the pick
  // fixed at `hand.length - 1`, `splice(…, 1)` is `pop()`. It is left as
  // a loop on purpose so that "which card" is named in exactly ONE place
  // — forcedDiscardPick — rather than implied by a slice index here.
  // `buried` therefore comes out newest-first, which is the order the
  // cards actually left, which is the order the caller's toast names
  // them in.
  const burnToHandCap = (seat) => {
    const hand = [...((seat?.hand) || [])];
    if (hand.length <= HAND_LIMIT) return null;
    const nec = [...((seat?.necropolis) || [])];
    const buried = [];
    while (hand.length > HAND_LIMIT) {
      const [gone] = hand.splice(forcedDiscardPick(hand), 1);
      nec.push(gone);
      buried.push(gone);
    }
    return { hand, necropolis: nec, buried };
  };

  const doEndTurn = useCallback(async () => {
    if (endingTurn.current) return;
    endingTurn.current = true;
    const currentMatch = matchRef.current;
    if (!currentMatch || currentMatch.status === "finished") { endingTurn.current = false; return; }
    const activeIdx = currentMatch.activePlayer;
    const activePlayer = currentMatch.players?.[activeIdx];
    if (!activePlayer) { endingTurn.current = false; return; }
    // ⭐ PHASE 5 LIVES IN THE SEAM (2026-09-15). R28's Lyf reset, the latch
    // clear and the incoming seat's Fallen rise are done by
    // turnEngine.handOffHalfTurn for EVERY driver; this path carried its
    // own copy (the button's twin) until tonight.
    // TURN ORDER, not targeting — "who plays next" and "who I'm
    // attacking" stop being the same seat once there are 3+ of them.
    // getNextActiveSeat walks the match's turnOrder (falling back to a
    // straight 0..seatCount-1 sweep), skipping any eliminated seat.
    const nextPlayer = getNextActiveSeat(currentMatch, activeIdx);
    // ── FALLEN HALF-TURN: the incoming player's turn starts now — their
    // Fallen beings rise immediately (canon: "the moment it's your turn
    // again, the Fallen state goes away").
    // ⚠ NO `khao:` HERE ANY MORE. The pool refills at the START of a
    // seat's Turn (caP'n, 2026-09-13), and handOffHalfTurn writes it
    // onto the INCOMING seat inside the chair transaction. This write
    // used to refill the seat that was leaving, which is what let every
    // seat answer through its adversary's half-turn on a full pool.
    // `maxKhao` is still written because it is this seat's own ceiling.
    const currentMax = activePlayer.maxKhao || STARTING_KHAO;
    // R10 — the timer is not a choice. Bury the worst card(s) and say so.
    // ⚠ READ OFF `currentMatch` (matchRef.current), NOT the render
    // closure, and folded into the write below rather than sent as its
    // own updatePlayer: THE REBASE LAW — a second write built from a
    // draft taken before this one would put the buried card back.
    const capT = burnToHandCap(activePlayer);
    try {
      await updatePlayer(code, activeIdx, {
        maxKhao: currentMax,
        ...(capT ? { hand: capT.hand, necropolis: capT.necropolis } : {}),
      });
      // ⚠ THE TOAST IS IN ITS OWN try, AND THAT IS NOT SUPERSTITION.
      // This whole block sits inside one `try { … } catch (e) {}` whose
      // last statement is the HAND-OFF. Anything that throws between the
      // write above and the hand-off below would be swallowed silently
      // and the chair would never move — the table stalls with nobody
      // able to act. A cosmetic line must not be able to do that.
      if (capT) {
        try {
          const names = capT.buried.map(id => getCard(id)?.name || id).join(", ");
          showToast(`◆ Time — the hand may not end a Turn above ${HAND_LIMIT}. ${names} to the Necropolis.`, GM.vellumFaint);
        } catch (_) { /* no screen, or no toaster yet — the discard still happened */ }
      }
      // ── THE HAND-OFF ─────────────────────────────────────────────
      // This is the TIMER's hand-off — the second of the game's three
      // half-turn transitions. It used to carry its own copy of the R93
      // clock check and NOTHING ELSE: it never rotated the outgoing
      // seat's per-half-turn Anima ledger (so a turn that ran out of
      // time left `animaGainedThisTurn` standing) and it never asked
      // whether a Prophecy had come true. Both now come free with
      // handOffHalfTurn, which owns the whole sequence for all three
      // transitions. See src/turnEngine.js.
      //
      // `currentMatch` is matchRef.current, NOT the React `match` state
      // — this function was already correct about that and must stay
      // that way: the engine reads the board it is handed.
      // `nextIdx` is passed so the written activePlayer is exactly the
      // seat this function already computed.
      //
      // NO TOAST AND NO EARLY RETURN, deliberately. The timer path is
      // silent by design (it fires with nobody necessarily looking at
      // the screen) and there is no trailing work below to skip.
      await handOffHalfTurn(code, currentMatch, activeIdx, { nextIdx: nextPlayer });
    } catch (e) {}
    endingTurn.current = false;
  }, [code]);

  useEffect(() => {
    clearInterval(timerRef.current);
    // H23 — a hold belongs to the turn that bought it. Releasing here
    // means a chord still finishing as the chair changes can never
    // spend the NEXT player's clock, and a finished match can never be
    // left frozen behind an animation that outlived it.
    releaseHold();
    if (isFinished) return;
    // TERROR gate — hold the turn clock at full until both strike. When
    // duelLive flips true this effect re-runs (duelLive is in the deps) and
    // the countdown begins fresh from PLAYER_TIMER, so the active player
    // always gets their whole turn no matter when they mounted.
    if (!duelLive) { setSharedTimer(PLAYER_TIMER); return; }
    endingTurn.current = false;
    setSharedTimer(PLAYER_TIMER);
    if (myTurn) {
      setAscendedThisTurn(false); setTrainedThisTurn(false); setDrawnThisTurn(false); setResurrectedThisTurn(false);
      // ── R108 · A NEW TURN OPENS IN THE MAIN PHASE ──────────────
      // The phase is a per-Turn commitment, so it rewinds with every
      // other per-Turn latch above. Written to the SEAT (not to local
      // state) because that is where the enforcer and the adversary's
      // client read it; guarded on the value so a remount inside a Turn
      // that has ALREADY declared Attack does not quietly reopen it.
      // ⚠ THIS RESET IS MOVING. The DECLARATION is a seat write and the
      // RESET is this renderer effect, so a seat whose Turn was ended by
      // another client's timer while its own client was unmounted stayed
      // in the Attack phase until it next rendered — a rule living in the
      // renderer (trap 4.1). It belongs in turnEngine.handOffHalfTurn,
      // written onto the INCOMING seat inside the same chair transaction
      // that already writes that seat's Khao. V'ael is landing it there;
      // this effect stays until it does, and then it goes. Do not write
      // the reset in two places at once.
      if ((match?.players?.[playerIdx]?.phase) === PHASE.ATTACK) {
        updatePlayer(code, playerIdx, { phase: PHASE.MAIN });
      }
      // R83 — both Primera limits (word + being) reset at the start of
      // your next Turn; the Beast rider and dim ledger reset with them.
      setPrimeraTurn(EMPTY_PRIMERA_TURN);
      animaDimmedSlotsRef.current = new Set();
      if (!myFirstTurnStarted) {
        setMyFirstTurnStarted(true);
        if (!mulliganUsed) setMulliganAvailable(true);
      }
      // ── ATV: Check opponent's active contracts at start of MY turn ──
      // ⚠ WAS `playerIdx === 0 ? 1 : 0` — so a seat-2 or seat-3 player read
      // SEAT 0's board at the top of their turn and got prompted to pay
      // Devotion on a contract belonging to somebody they may not even be
      // fighting. `oppIdx` is the seat I am actually aimed at.
      const oppField = match?.players?.[oppIdx]?.field;
      const activeContracts = (oppField?.tomes || []).filter(t =>
        t?.id && !t?.faceDown &&
        ["tome_king_of_the_castle", "tome_magma_carta"].includes(t.id)
      );
      if (activeContracts.length > 0) {
        setTimeout(() => setDevotionModal({ contracts: activeContracts }), 500);
      }
      // ── ATV: Restraining Ordeal — the offer, stated correctly ────
      // ⚠ THIS TOAST SAID A THIRD THING: "attack this turn or your
      // opponent gains 1 Anima!" — which had the beneficiary backwards
      // AND the incentive backwards, telling the player to attack in
      // order to avoid a reward that is theirs for NOT attacking. The
      // card is a peace offering the adversary is MAKING: hold your
      // swings and the Anima is yours.
      const myOppTomes = oppField?.tomes || [];
      const hasRestraining = myOppTomes.some(t => t?.id === "tome_restraining_ordeal" && !t?.faceDown);
      if (hasRestraining) {
        setTimeout(() => showToast("◆ Restraining Ordeal — their offer: hold your Lyt Attacks this Turn and you gain 1 Anima.", GM.inkGold), 800);
      }
      // ── R132 · THE MAX KHAO CLIMB IS NOT DECIDED HERE ANY MORE ──
      // This effect used to hold the rule: it read `turn` and
      // `khaoSpentThisCycle` off the React board, granted the +1,
      // refilled the pool to the new ceiling and cleared the flag. Three
      // things were wrong with that and only one of them was the
      // arithmetic:
      //
      //   · IT WAS A RULE LIVING IN THE RENDERER (trap 4.1). A seat
      //     whose Turn opened while its own client was unmounted — or
      //     never mounted, which is every headless duel, every replay
      //     and the whole balancer — did not climb at all.
      //   · IT WAS ONE OF FOUR COPIES. The three NPC runners granted at
      //     END TURN instead, so a brain ran a full Turn behind the
      //     player on Max Khao for an entire duel. See the R132 banner
      //     in turnEngine.js for the trace.
      //   · caP'n's 2026-09-15 ruling puts the +1 at the BEGINNING of a
      //     Turn, and the beginning of a Turn is the chair transaction,
      //     not the first render after it.
      //
      // So the climb, the refill to the climbed ceiling and the flag
      // clear all ride handOffHalfTurn's one atomic write now.
      //
      // ⚠ AND THE FLAG CLEAR WENT WITH IT, WHICH IS NOT INCIDENTAL. The
      // window R12 measures is one sit-down to the next — this seat's
      // half-turn PLUS its adversary's. A second clear here would reopen
      // it half a Turn early and let a counter fired on the adversary's
      // half-turn, which caP'n named explicitly as breaking the climb,
      // escape the rule written to catch it. Do not re-add one.
      //
      // The `✦ Max Khao → N!` toast survives, as an OBSERVER of the
      // seat's ceiling rather than a second opinion about the rule —
      // see THE MAX KHAO TOAST below showToast.
    }
    if (isNPC && !myTurn) {
      setSharedTimer(PLAYER_TIMER);
      return;
    }
    // ⭐ A BRAIN'S TURN IS NOT ON THE CLOCK (2026-09-06). In Gauntlet — Duo
    // and Story Co-op the boss is an AI SEAT inside a human match, so
    // `isNPC` is false and both humans counted the boss down — and at
    // -9 the elected grace-ender force-ended a Turn the brain was still
    // playing (4s coin-flip wait + think time + forty awaited writes).
    // Same answer the 1v1 branch above gives, keyed on the seat's
    // controller instead of the match's kind.
    const activeIsAi = match?.players?.[match?.activePlayer]?.controller === "ai";
    if (activeIsAi && !myTurn) {
      setSharedTimer(PLAYER_TIMER);
      return;
    }
    // MP-FIX: turn-timer authority. Previously BOTH human clients ran the
    // countdown and BOTH called doEndTurn() at zero — clock drift meant one
    // client could end the turn that had JUST passed to the other player,
    // skipping their entire turn (the core multiplayer desync).
    //   • My turn  → I am the authority; I end my own turn at 0.
    //   • Opp turn → I only display the countdown. As a disconnect failsafe
    //     I end their turn after a 10s grace past zero.
    timerRef.current = setInterval(() => {
      setSharedTimer(t => {
        if (responseWindowRef.current) return t; // Response Window open — the clock holds (Rulez v2)
        // C12 — a chain holds the clock through its GAPS too. Between
        // links the window is closed (the adversary is deciding), and
        // a second the player cannot act in is a second the clock has
        // no right to take — the same justification as the two holds
        // below it. Bounded by CHAIN_DEPTH_CAP and both failsafes.
        if (chainActiveRef.current) return t;
        // H23 — THE CINEMATIC HOLD. Same shape as the Response Window
        // above it, and the same justification: a second the player
        // could not act in is a second the clock has no right to take.
        // Both clients compute the hold off the same Firebase diff, so
        // both pause together; the writer caps the per-turn total well
        // under the 10s disconnect grace below, so an animation can
        // never cause a turn to be force-ended. At MOTION: OFF no
        // chord ever mounts and this is permanently false.
        if (holdActive()) return t;
        // Wave 3: a PvP declaration in flight holds BOTH clocks — but only
        // while it's fresh, so a vanished declarer can't freeze the duel.
        const paHold = matchRef.current?.pendingAction;
        if (paHold && Date.now() - (paHold.declaredAt || 0) < 15000) return t;
        if (t <= 1) {
          if (myTurn) { clearInterval(timerRef.current); doEndTurn(); return 0; }
          // ⭐ ONE GRACE-ENDER, NOT ALL OF THEM (2026-09-05). `t <= -9` fired
          // on EVERY client where it is not my turn. In 1v1 that is one
          // client and the banner above is right. In a 3-seat co-op both
          // humans hit it in the same second on the AI seat's turn; in a
          // 4-seat 2v2, three clients do — and `endingTurn.current` is
          // per-client local state that cannot see the others. Each then
          // runs a full hand-off (turn++, R93 clock, Khao refresh, Fallen
          // rise, getNextActiveSeat). Identical values in the common case,
          // but any client whose tick lands after the first write reads
          // turn T+1 and writes T+2 — ADVANCING THE CHAIR TWICE AND SKIPPING
          // A SEAT'S WHOLE TURN. Precisely the desync the MP-FIX banner
          // above claims to have fixed, reopened by the third chair.
          //
          // So exactly one seat holds the grace: the lowest living seat
          // that is not the one currently playing. In 1v1 that is the
          // non-active client — byte-identical to the old behaviour. The
          // second tier at -25 is the failsafe for the failsafe: if the
          // elected grace-ender is itself gone, anyone may close it.
          const gm = matchRef.current;
          const graceEnder = gm ? (livingSeats(gm).find((i) => i !== gm.activePlayer) ?? null) : null;
          if (t <= -9 && (graceEnder == null || playerIdx === graceEnder)) {
            clearInterval(timerRef.current); doEndTurn(); return 0;
          }
          if (t <= -25) { clearInterval(timerRef.current); doEndTurn(); return 0; }
          return t - 1; // grace window — counts into negatives, displays as 0:00
        }
        return t - 1;
      });
    }, 1000);
    return () => { clearInterval(timerRef.current); releaseHold(); };
  }, [match?.activePlayer, match?.turn, isFinished, doEndTurn, myTurn, isNPC, duelLive]);

  const showToast = useCallback((msg, color = SD.purple) => {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 2800);
  }, []);
  // Hand the toaster to the module-scope R08 backstop — see toastFromBackstop.
  useEffect(() => {
    toastFromBackstop = showToast;
    return () => { if (toastFromBackstop === showToast) toastFromBackstop = null; };
  }, [showToast]);

  // ══════════════════════════════════════════════════════════
  // ✦ THE MAX KHAO TOAST — AN OBSERVER, NOT A RULE
  // ══════════════════════════════════════════════════════════
  // The line the player sees when their ceiling rises used to be fired
  // by the start-of-turn effect that DECIDED the climb (R132). The
  // decision moved into turnEngine.handOffHalfTurn, where it belongs,
  // and a renderer must not keep a second copy of a rule alive just to
  // narrate it. So this does not re-decide anything: it compares the
  // seat's Max Khao against what the seat's Max Khao was the last time
  // this seat's Turn opened, and says so if it went up.
  //
  // ⚠ WHY SAMPLING AT THE TURN BOUNDARY IS SOUND RATHER THAN LUCKY. The
  // climb is written in the SAME atomic transaction as `activePlayer`
  // and `turn` (handOffChair), so the very snapshot that makes `myTurn`
  // true is the snapshot that already carries the climbed ceiling.
  // There is no window in which the chair has moved and the ceiling has
  // not.
  //
  // ⚠ AND WHY IT IS KEYED TO THE BOUNDARY AND NOT TO maxKhao ITSELF.
  // Khao Garden and Khao Arcadia raise the ceiling at the END of their
  // owner's Turn and each announces itself BY NAME. An observer watching
  // `maxKhao` directly would fire a second, anonymous toast a moment
  // later and — the toaster holding one slot — overwrite the card's own
  // line with a worse version of it. Watching the boundary instead
  // folds any such rise into the next Turn-open, a full half-turn away,
  // where re-stating the ceiling is a reminder rather than a stomp.
  // `maxKhao` is therefore READ in this effect and deliberately NOT in
  // its deps. That is the whole design; do not "fix" the dep array.
  //
  // It reports a rise WHATEVER raised it, and cannot do otherwise
  // without holding the rule again — which is the trade this exists to
  // make. A null ref (first Turn-open, or a mid-duel remount) seeds and
  // stays silent, so the worst case is one missed cosmetic line.
  const prevMaxKhaoRef = useRef(null);
  useEffect(() => {
    if (!myTurn) return;
    const nowMax = match?.players?.[playerIdx]?.maxKhao;
    if (typeof nowMax !== "number") return;
    const wasMax = prevMaxKhaoRef.current;
    prevMaxKhaoRef.current = nowMax;
    // 300ms, as before: the other Turn-open toasts sit at 500 and 800,
    // and this one is meant to land first.
    if (wasMax != null && nowMax > wasMax) {
      setTimeout(() => showToast(`✦ Max Khao → ${nowMax}!`, SD.gold), 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myTurn, match?.turn, playerIdx, showToast]);

  // ═══════════════════════════════════════════════════════════
  // ⏳ THE CLOCK RUNS OUT — R87
  // ═══════════════════════════════════════════════════════════
  // caP'n, 2026-08-20: *"let's enforce the time limit ... 20 minutes,
  // so that should stop the Anima freeze for humans."* The number and
  // the verdict both live in rules.js; this is only the trigger. See
  // THE CLOCK there for what the freeze was and why it is real.
  //
  // ── WHY IT IS AN EFFECT ON `globalTimer` AND NOT A BRANCH INSIDE
  // THE INTERVAL. The interval's callback is a setState updater, and
  // an updater that also fires a network write is a bug waiting for
  // StrictMode to double-invoke it. Watching the value instead means
  // React decides when this runs, the latch below is read and written
  // in the same synchronous pass, and the interval stays one line.
  //
  // ── ⚠ IT ENDS THROUGH turnEngine's endOnClock, NOT THE WRAPPED
  // endMatch, and endOnClock itself calls gameManager's raw endMatch.
  // The wrapper
  // above is THE SECOND WIND: handed a boss seat as the loser it does
  // not end the duel, it refreshes that boss's Anima and Khao and
  // moves to the next phase. A twenty-minute clock expiring during
  // phase 1 of Sir H'osh Vrown would therefore have handed him a full
  // heal instead of ending anything — the exact opposite of a time
  // limit. So a timeout ends the duel outright, and a two-phase boss
  // whose clock runs out in phase 1 loses on the evidence like anyone
  // else. That is a consequence of having a clock at all and it is
  // the honest one.
  //
  // ── ONE WRITER, WITH A GRACE WINDOW. In PvP both seats run a clock
  // anchored to the same shared `duelStartedAt`, so both reach the
  // limit within a tick of each other. Seat 0 closes the book; seat 1
  // stays quiet and keeps watching, and only takes over if the match is
  // still alive fifteen seconds later — which means seat 0's client is
  // gone. Both seats agree on the verdict regardless, because
  // `timeoutWinner` is pure and reads the same board.
  useEffect(() => {
    if (!duelLive || isFinished) return;
    if (globalTimer < DUEL_TIME_LIMIT) return;
    if (timeUpRef.current) return;
    timeUpRef.current = true;

    const m = matchRef.current;
    const verdict = timeoutWinner(m);
    // N-SEAT: verdict.winner is a seat idx (1v1/FFA) or a team id
    // (2v2) — didSeatWin already knows how to read both shapes, so
    // route through it instead of a raw `=== playerIdx` that would
    // never recognize a teammate's win.
    //
    // ⚠ AND IT IS ASKED OF `winners`, NOT OF `winner`. R98 says a
    // Solrend can end with more than one winner; this seat may be one
    // of them without being the board winner, and `{ winner: … }` alone
    // would tell a co-winner they lost. The list comes back from
    // endOnClock — the SAME function R93's bell rings — so both hands
    // of the clock now resolve co-winners identically. See its banner.
    // ⚠ SEAT 1 USED TO ANNOUNCE AND THEN DO NOTHING. The `playerIdx !== 0`
    // return sat AFTER the toast and after the latch, so in PvP — where
    // the two clocks can be a minute apart — seat 1 would be told "TIME.
    // The Solrend is called" while the duel carried on around them, with
    // their own clock stuck at 0:00 and their latch spent. Now the
    // non-writing seat says nothing and keeps watching, and if the
    // writing seat has gone dark it takes over after a grace window
    // rather than waiting forever on a book nobody will close.
    // THE GRACE WINDOW. Seat 0 writes at the limit; seat 1 writes only
    // if the match is somehow still alive fifteen seconds later, which
    // means seat 0's client is gone or wedged. Both seats end up
    // agreeing because `timeoutWinner` is pure and reads the same board.
    // ⚠ WAS `playerIdx !== 0`. With four seats, seats 1, 2 AND 3 all
    // satisfied that and all fell through on the same tick at +15 — three
    // simultaneous endMatchRaw writes plus up to three R98 stamp writes.
    // `timeoutWinner` is pure so they agreed on the verdict, but the writes
    // were pure waste and the co-winner stamp is not idempotent by
    // construction. Now the LOWEST LIVING seat closes the book (seat 0 in
    // every ordinary duel, so nothing changes there) and the rest wait
    // their own staggered grace — five seconds apart by rank, so a dead
    // writer is covered without three clients racing the same second.
    if (playerIdx !== clockWriterSeat && globalTimer < DUEL_TIME_LIMIT + 15 + (clockRank * 5)) {
      timeUpRef.current = false;   // not our turn to close it — keep watching
      return;
    }
    const why = verdict.by === "anima"    ? "on Anima"
              : verdict.by === "beings"   ? "on the board"
              : verdict.by === "grimoire" ? "on what was left to draw"
              : "by the count of last resort";
    (async () => {
      let winners = [verdict.winner];
      try { winners = await endOnClock(code, m, verdict); }
      catch (e) { console.warn("[R87 timeout] endMatch failed:", e?.message); }
      const iWon = didSeatWin({ ...m, winner: verdict.winner, winners }, playerIdx);
      const shared = winners.length > 1 && iWon;
      showToast(`⏳ TIME. The Solrend is called ${why} — ${shared ? "shared" : iWon ? "yours" : "theirs"}.`,
                iWon ? GM.inkGold : SD.red);
    })();
  }, [duelLive, isFinished, globalTimer, playerIdx, clockWriterSeat, clockRank, code, showToast]);

  // ── H15 — a mulligan is a public act (2026-08-18) ─────────────
  // The seat-level mulliganUsed flag is the announcement channel:
  // NPC brain or PvP adversary, the same flag flips, this watcher
  // toasts once. Seeded on first observation so a mid-duel
  // reconnect never re-announces an old mulligan.
  const oppMullSeen = useRef(null);
  useEffect(() => {
    const used = !!opponent?.mulliganUsed;
    if (oppMullSeen.current === null) { oppMullSeen.current = used; return; }
    if (used && !oppMullSeen.current) {
      oppMullSeen.current = true;
      showToast("🔀 The adversary mulligans — a new hand of five.", GM.inkGold);
    }
  }, [opponent?.mulliganUsed, showToast]);

  const closeRadial = () => setRadial(null);

  // ── R103 · A FULL TURN IS TWO HALF-TURNS ────────────────────
  // caP'n, 2026-08-26: *"If I make a general statement like once per
  // turn, especially the 'OPT or OP2' etc, it is referring to
  // combination of the user who played the card (1/2 turn) ending then
  // the turn of their opponent ending, thus creating 1 full turn. But
  // if I say at the end of the user's turn, that is the half turn."*
  //
  // `currentTurn` and `lastUsedTurn` are both HALF-turn counters, so
  // the delta is in half-turns and the ternary that used to live here
  // (OPT→1, OP2→2, OP3→3) was comparing full-turn tags against
  // half-turn arithmetic. rules.cooldownHalfTurns is the one place the
  // cadence is defined now, and it carries the seat count so a 3-4p
  // table measures "a full turn" as a full lap of the table.
  const isTomeOffCooldown = (tomeSlot, currentTurn) => {
    if (!tomeSlot?.lastUsedTurn && tomeSlot?.lastUsedTurn !== 0) return true;
    const card = getCard(tomeSlot.id);
    const cooldown = cooldownHalfTurns(card?.effectTag, match?.seatCount || 2);
    return (currentTurn - tomeSlot.lastUsedTurn) >= cooldown;
  };

  // ── PROPHECY ACTIVATION ─────────────────────────────────────
  const handleActivateProphecy = async () => {
    setMulliganAvailable(false);
    // ── R? · A PROPHECY INTERRUPTS THE DUEL ──────────────────────
    // caP'n, 2026-09-14: a Prophecy may be cashed INSTANTLY — it does
    // not wait for your Turn. `!myTurn` stood here and made the board
    // the only side that did not know that: the tutorial teaches the
    // interrupt, `prophecyEngine.checkProphecies` fires it at EVERY
    // half-turn hand-off including the adversary's, and this door
    // refused. A seat whose Prophecy came true on the adversary's Turn
    // had to wait a full half-turn to cash it, during which the
    // adversary could win, or ruin the very being that completed it.
    //
    // ⚠ AND DROPPING IT WAS NOT THE WHOLE FIX — THE LOCK WAS IN THE
    // RENDERER. This handler never checked the Prophecy's CONDITION at
    // all. It checked the turn, the spend stamp and the fare, and then
    // ended the Solrend. The only thing standing between an unfulfilled
    // Prophecy and a win was `ProphecyButton` declining to render a
    // clickable button — a rule that lives in a renderer, which is to
    // say a rule that does not exist: any other caller of this handler
    // (the mobile mount, a tutorial script, a replay) won the game.
    // `!myTurn` had been HIDING that, because the turn check is the one
    // condition an adversary-turn exploit needs anyway. So the lock is
    // put where the money is spent, and it is the same function the
    // automatic door asks.
    if (!me?.prophecy) return;
    const prophecyId = me.prophecy;
    {
      const liveP = matchRef.current?.players?.[playerIdx] || me;
      const liveO = matchRef.current?.players?.[oppIdx] || opponent;
      if (!prophecyConditionMet(prophecyId, liveP, liveO)) {
        showToast("◇ That Prophecy's condition is not fulfilled.", SD.red);
        return;
      }
    }

    // ── A PROPHECY IS SPENT WHEN IT RESOLVES ─────────────────────
    // The manual twin of prophecyEngine's guard — read that file's
    // ruling banner for the why. Without BOTH doors checking the same
    // stamp the two could ALTERNATE: this button knocked a seat out of
    // an FFA without recording anything, and the very next hand-off
    // fired the identical Prophecy automatically and charged for it
    // again.
    if (me.prophecySpent === prophecyId) {
      showToast("◇ That Prophecy has already been fulfilled this Solrend.", SD.red);
      return;
    }

    // ⚠ ONE SOURCE FOR THE FARE. This was a hand-typed
    // `{ proph_ace_of_shades: 13 } ?? 15` map — the third of FOUR
    // places the number lived, alongside the card face in cards.js,
    // prophecyEngine's automatic charge, and ProphecyButton's gate.
    // They agreed, which is the only reason nobody had been billed a
    // price their card did not print. getProphecyKhaoCost reads the
    // card row, and now everything does.
    const cost = getProphecyKhaoCost(prophecyId);
    if (cost > 0) {
      const paid = await spendKhao(cost);
      if (!paid) { showToast(`Need ${cost} Khao to activate Prophecy!`, SD.red); return; }
    }
    // N-SEAT: a Prophecy win eliminates whoever it was fulfilled
    // against, same as any other win condition — it only ends the
    // WHOLE match once that's the last standing side. (This used to
    // dynamically re-import gameManager's raw endMatch specifically to
    // skip the Second-Wind boss-escalation check; finishOrEliminate
    // skips that check too, since it's a plain fallthrough, not a
    // boss-only path — so the skip is preserved and FFA/2v2 now knock
    // out correctly instead of ending the table.)
    // ── R97 · THE PROPHET'S TOLL, ON THE MANUAL DOOR TOO ──────────
    // caP'n, 2026-08-25: *"any game mode that isn't 1v1: if a prophecy
    // is fulfilled, dim all of your adversary's Anima by 15, if their
    // Anima is currently greater than 30, halve it, round up."*
    //
    // ⚠ BOTH DOORS OR NEITHER. prophecyEngine's automatic path and this
    // button are the two ways a Prophecy resolves, and every previous
    // divergence between them (the fare, the spend stamp, the knockout
    // resolver) was a real defect found later. So the toll is applied
    // here in exactly the same shape rather than left to the automatic
    // path to pick up on the next hand-off — by which time this seat's
    // Prophecy is already stamped spent and would never fire again.
    //
    // ⚠ IT IS NOW LITERALLY THE SAME CODE, not the same shape. The two
    // loops had drifted into opposite behaviour: this one read
    // getEffectiveAnima and wrote through the shadow (which redirects
    // to teamAnima), the engine's read the raw per-seat field and wrote
    // it back — so in a shared-Anima 2v2 the automatic door was a
    // complete no-op while this one worked, and both tolled a pooled
    // team once per SEAT. applyProphetsToll is the one implementation
    // and it holds the ruling that A POOLED TEAM IS ONE ADVERSARY.
    let outcome;
    let tollRows = [];
    const liveM = matchRef.current || match;
    if (!isOneVsOne(liveM)) {
      tollRows = await applyProphetsToll(code, liveM, playerIdx);
      const emptied = tollRows.filter((t) => t.to <= 0);
      outcome = emptied.length
        ? await collectEmptiedSides(code, boardAfterToll(liveM, tollRows), playerIdx, emptied)
        : { ended: false, winner: null, winners: [], eliminatedSeats: [] };
    } else {
      outcome = await finishOrEliminate(code, playerIdx);
    }

    // ── THE STAMPS THIS DOOR NEVER WROTE ─────────────────────────
    // ⚠ THIS PATH WROTE NOTHING AT ALL, and it reached
    // finishOrEliminateSeat, which ALWAYS writes a `runnerUp`. So a
    // manual Prophecy win read back from the archive as
    // `wonByProphecy` absent + `runnerUp` present — and the balancer's
    // `runnerUp != null` fallback, which exists precisely to recognise
    // a clock call written by an older build, filed every manual
    // Prophecy win as an R93 CLOCK CALL. The fallback was broken for
    // exactly the case it was written for.
    //
    // Same two-field semantics as the automatic door, and for the same
    // reason: `prophecyResolved` says a Prophecy CAME TRUE (which in a
    // 3-4p FFA may be a knockout and nothing more), `wonByProphecy`
    // says it ENDED THE SOLREND. `prophecySpent` on the seat is what
    // makes the ruling above enforceable across both doors.
    const stamp = { prophecyResolved: prophecyId, prophecyResolvedBy: playerIdx };
    if (outcome && outcome.ended) { stamp.wonByProphecy = prophecyId; stamp.prophecyWinner = playerIdx; }
    if (tollRows.length) stamp.prophetsToll = tollStamp(tollRows);
    try {
      await updatePlayerRaw(code, playerIdx, { prophecySpent: prophecyId });
      await updateMatch(code, stamp);
    } catch (e) { console.warn("[Prophecy] stamp failed:", e?.message); }

    showToast(
      outcome && outcome.ended
        ? `◆ Prophecy fulfilled — ${me.name} wins the Solrend!`
        : tollRows.length
          ? `◆ Prophecy fulfilled — THE PROPHET'S TOLL: ${tollRows.map((t) => `${t.dimmed} Anima`).join(" · ")} taken from the table.`
          : "◆ Prophecy fulfilled — a side falls, the match continues.",
      SD.gold
    );
  };

  const handleEndTurn = async () => {
    if (!myTurn || !match) return;
    // ── CAPTURED BEFORE THE RESET, FOR RESTRAINING ORDEAL ────────
    // The ATV block at the bottom of this function needs to know
    // whether I swung THIS Turn, and the field reset forty lines below
    // clears `attackedThisTurn` on every one of my rifts before it gets
    // there. Read once, here, off the live board.
    // ── R10 · THE BUTTON IS A CHOICE, SO IT IS REFUSED ───────────
    // See THE HAND CAP AT THE HAND-OFF above doEndTurn. A human over
    // the cap picks the card — that is what draw-then-discard means for
    // a player — so this door raises the modal and does not hand off.
    // Read off the LIVE seat, not the render closure: the draw that
    // pushed the hand over may be a write newer than this paint.
    {
      const liveMe = matchRef.current?.players?.[playerIdx] || me;
      if (((liveMe?.hand) || []).length > HAND_LIMIT) {
        setForcedDiscardModal(true);
        showToast(`◆ The hand holds ${(liveMe.hand || []).length}. Discard to ${HAND_LIMIT} before the Turn ends.`, SD.red);
        return;
      }
    }
    clearInterval(timerRef.current);
    const liveForHandOff = matchRef.current || match;
    const nextActiveIdx = getNextActiveSeat(liveForHandOff, playerIdx);
    // ⭐ PHASE 5 LIVES IN THE SEAM (2026-09-15). Everything this button used
    // to do by hand before handing off — Chief Calidum's loan coming home
    // and its buffs being revoked, R28's Lyf reset and the latch clear, the
    // incoming seat's Fallen rise, Khao Garden / Arcadia's end-of-Turn
    // Max Khao, Restraining Ordeal's offering — is done by
    // turnEngine.handOffHalfTurn for EVERY driver. The timer path and the
    // brain had their own partial copies; Khao Garden paid at two different
    // moments depending on who held the card. One Phase 5 now.
    {
      const res = await handOffHalfTurn(code, liveForHandOff, playerIdx,
                                        { nextIdx: nextActiveIdx });
      if (res.calledOnClock) {
        // R98 — the hand-off returns `winners`, which on the clock path
        // is the verdict's winner plus anyone whose Prophecy came true
        // on that same half-turn. didSeatWin reads the array.
        const iWon = didSeatWin({ ...match, winner: res.verdict.winner, winners: res.winners }, playerIdx);
        showToast(`⏳ The Solrend is called on the turn count — ${iWon ? "yours" : "theirs"}.`,
                  iWon ? GM.inkGold : SD.red);
        return;
      }
      if (res.prophecyEnded) {
        const iWon = res.prophecy.seat === playerIdx;
        showToast(iWon ? `◆ Prophecy fulfilled — ${me?.name} wins the Solrend!`
                       : `◆ The adversary's Prophecy comes true.`,
                  iWon ? SD.gold : SD.red);
        if (res.ended) return;
      }
    }
    setSelectedHandIdx(null); setAttackingSlot(null);
    setTargetMode(null); setAscendedThisTurn(false); setTrainedThisTurn(false); setDrawnThisTurn(false); setResurrectedThisTurn(false);
    if (!myFirstTurnOver) { setMyFirstTurnOver(true); setMulliganAvailable(false); }
    // (Khao Garden / Arcadia and Restraining Ordeal are paid in the seam — see above.)
    const myAtvTomes = me?.field?.tomes || [];
    for (const tome of myAtvTomes) {
      if (!tome?.id || tome.faceDown) continue;
      if (tome.id === "tome_king_of_the_castle") {
        const devoted = tome.devoted || 0;
        showToast(`◆ King of the Castle — the adversary must devote 2 Anima this Turn! (${devoted}/8 total)`, SD.gold);
      }
      if (tome.id === "tome_magma_carta") {
        const devoted = tome.devoted || 0;
        showToast(`◆ Magma Carta — the adversary must devote Anima this Turn! (${devoted}/4 total)`, SD.gold);
      }
    }

    showToast("Turn ended.", GM.vellumDim);
  };

  const handleMulligan = async () => {
    if (!myTurn || !me || mulliganUsed || !mulliganAvailable) return;
    // H15 (2026-08-18): the mulligan is the full-hand redraw of the
    // DEALT hand. A hand that is no longer the dealt five — a counter
    // spent from hand in a turn-0 response window, an effect draw —
    // is no longer mulliganable: slicing a flat 5 over a 4-card hand
    // would mint a free card. Seat-level mulliganUsed also guards a
    // reconnect (local state resets; the seat remembers).
    if (me.mulliganUsed || (me.hand || []).length !== 5) { setMulliganAvailable(false); return; }
    // ── SEAL: R90, TIGHTENED (caP'n, 2026-08-25) ─────────────────
    // *"Either player, if they have not drawn yet, they can do a
    // Mulligan on that first turn and no other."*
    //
    // Both of those clauses used to live ONLY in this component's local
    // React state — `myFirstTurnStarted` / `myFirstTurnOver` gate the
    // BUTTON, and `drawnThisTurn` gated nothing at all here. UI state
    // resets on remount, is invisible to the gate, and is invisible to
    // every headless driver. The rule is asked now, so a reconnect, a
    // scripted client or an NPC seat gets the same answer the button
    // gives.
    {
      const v = gateMulligan(gateCtx(), {
        mulligansUsed: me.mulliganUsed ? 1 : 0,
        drawsThisTurn,
        handSize: (me.hand || []).length,
      });
      if (!v.valid) { showToast(v.reason || "No mulligan available.", SD.red); setMulliganAvailable(false); return; }
    }
    // Hand was already removed from grimoire at match start
    // So: combine hand + current grimoire = full available pool
    const grimoire = { ...me.grimoire } || {};
    const oldHand = me.hand || [];
    // Return old hand to pool first
    oldHand.forEach(id => { grimoire[id] = (grimoire[id] || 0) + 1; });
    // Enforce deck limits — cap at deckLimit per card
    Object.keys(grimoire).forEach(id => {
      const card = ALL_CARDS_FLAT.find(c => c.id === id);
      const limit = card?.deckLimit || 3;
      if (grimoire[id] > limit) grimoire[id] = limit;
    });
    // Draw a fresh opening hand — R90 redraws exactly STARTING_HAND.
    const pool = [];
    Object.entries(grimoire).forEach(([id, qty]) => { for (let i = 0; i < qty; i++) pool.push(id); });
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    const newHand = shuffled.slice(0, STARTING_HAND);
    // Remove new hand from grimoire
    const newGrimoire = { ...grimoire };
    newHand.forEach(id => {
      if (newGrimoire[id] > 1) newGrimoire[id]--;
      else delete newGrimoire[id];
    });
    // H15: mulliganUsed rides the seat object — it is the reconnect
    // guard AND the public announcement (the adversary's HUD watches
    // the flag and toasts; a mulligan is a public act in any TCG).
    await updatePlayer(code, playerIdx, { hand: newHand, grimoire: newGrimoire, mulliganUsed: true });
    setMulliganUsed(true); setMulliganAvailable(false);
    showToast("✦ Mulligan! New hand drawn.", GM.inkGold);
  };

  const handleLuxmoireAction = (e) => {
    if (!myTurn) return;
    const pos = smartPos(e?.clientX || window.innerWidth / 2, e?.clientY || window.innerHeight / 2);
    setRadial({ x: pos.x, y: pos.y, actions: [
      // R10 — a full hand never blocks the draw; it only owes a discard.
      { icon: "✦", label: "Draw", color: GM.inkGold, legal: canDraw && !drawnThisTurn, reason: drawnThisTurn ? "Already drew" : !canDraw ? "Not your turn" : null, onSelect: () => handleDraw(true) },
      // R88 fare + R96 floor: the Anima half is `canPayVoluntary` (strictly
      // more than the fare), because a search is a VOLUNTARY payment and may
      // not take you to 0. Khao has no such floor — it is not a life total.
      { icon: "🔍", label: "Search", color: SD.gold, cost: LUX_SEARCH_COST, legal: !drawnThisTurn && ((me?.khao || 0) >= LUX_SEARCH_COST || canPayVoluntary(me?.anima ?? STARTING_ANIMA, LUX_SEARCH_COST)), reason: drawnThisTurn ? "Already drew — search consumes your draw" : `Need ${LUX_SEARCH_COST} Khao or ${animaFare(LUX_SEARCH_COST)}`, onSelect: () => handleLuxmoireSearch() },
    ]});
  };

  const handleLuxmoireSearch = async () => {
    if (!myTurn || !me) return;
    // R108 — once the Attack phase is declared, no more cards may be
    // played. See THE PHASE GATE above gateCtx.
    if (phaseBars("SEARCH")) return;
    // ── SEAL: R08 — NEITHER BOOK MAY BE SEARCHED ON THE OPENING
    // CYCLE (caP'n, 2026-08-25: *"they are also not allowed to search
    // their Luxmoire that first turn, nor Grimoire"*). Asked BEFORE the
    // draw seal and before the fare, so a seat is never told it cannot
    // afford a thing it was never allowed to buy.
    //
    // ⚠ AND THIS IS THE BARRED KIND, STATED RATHER THAN ASSUMED. caP'n
    // narrowed the ban on 2026-08-26 — *"cards are exempt from the
    // first turn search rule"* — so `gateSearch` now means specifically
    // THE PLAYER'S OWN SEARCH ACTION, and this handler is exactly that:
    // it is reached only from the Luxmoire radial's "Search" spoke, it
    // pays the R88 fare, and it spends the Turn's draw. A card effect
    // that searches the Luxmoire is a different call (gateEffectSearch)
    // and does not come through here.
    {
      const v = gateSearch(gateCtx(), "LUXMOIRE");
      if (!v.valid) { showToast(v.reason || "Cannot search the Luxmoire.", SD.red); return; }
    }
    // ── SEAL: R11 — the Luxmoire search consumes the Turn's draw too.
    // (R07 never seals the Luxmoire lane; gateDraw knows that.)
    {
      const v = gateDraw(gateCtx(), "LUXMOIRE", iAmFirstPlayer, drawsThisTurn);
      if (!v.valid) { showToast(v.reason || "Cannot search the Luxmoire.", SD.red); return; }
    }
    // ── GRIM/LUX FIX (July 2026): no upfront charge. ──
    // The old flow paid 5 Khao BEFORE opening the modal — an empty
    // Luxmoire ate the fare with no refund, and the Cancel button was a
    // 5-Khao donation. The fare is now paid at the moment a Primera is
    // actually taken (the modal's pick handler). Browsing your OWN
    // Luxmoire is not hidden information — you built the deck.
    // ── R106 · IT COSTS YOUR DRAW, AND IT FINDS IMPERA ONLY ──────
    // caP'n, 2026-08-26, both clauses in one breath:
    //
    //   *"Yes it costs your draw, it's one or the other (because we
    //   added the U'hlti and Affinity into the Lux we can't have it so
    //   easy). And just to make sure — you can still only search the
    //   Lux for Impera, not the U/A's."*
    //
    // CLAUSE 1 was already enforced here and stays. Rulez v2 ch.3 and
    // ch.11 both say "this does not consume your turn's draw" and BOTH
    // ARE SUPERSEDED.
    //
    // CLAUSE 2 IS NEW AND THIS LINE IS WHERE IT WAS BROKEN. The picker
    // was built from `Object.keys(luxmoire)` — the WHOLE pile. Since
    // the lane ruling put U'hltis and Affinities in the Luxmoire, that
    // meant a 5-Khao tutor straight to the two rarest cards in a deck:
    // the exact "so easy" the ruling names. They live there, they are
    // DRAWN from there, and they are never searched out of it.
    if (drawnThisTurn) { showToast("Already drew this turn — the search consumes your draw.", SD.red); return; }
    if ((me?.khao || 0) < LUX_SEARCH_COST && !canPayVoluntary(me?.anima ?? STARTING_ANIMA, LUX_SEARCH_COST)) { showToast(`Need ${LUX_SEARCH_COST} Khao or ${animaFare(LUX_SEARCH_COST)} to search Luxmoire.`, SD.red); return; }
    const luxmoire = me.luxmoire || {};
    const available = Object.keys(luxmoire)
      .filter(id => luxmoire[id] > 0 && isLuxSearchable(getCard(id))); // R106
    if (available.length === 0) {
      const anyLeft = Object.keys(luxmoire).some(id => luxmoire[id] > 0);
      showToast(anyLeft
        ? "No Impera left in your Luxmoire — a search finds Impera only. U'hltis and Affinities must be drawn."
        : "Luxmoire is empty!", SD.red);
      return;
    }
    setLuxmoireSearchModal(available);
  };

  // ── ATV SYSTEM ───────────────────────────────────────────────
  // Handle feeding Anima/Khao into ATV tomes (Necklaces)
  // `currency` is still in the signature for the modal's sake, but it is
  // IGNORED: the card decides. See ATV_FEED's banner.
  const handleFeedATV = async (tomeSlotIdx, amount, _currencyIgnored) => {
    // ── ONLY THE OWNER MAY FEED IT ────────────────────────────────
    // caP'n: *"you(the player) can only feed it, no one else."* This
    // handler only ever reads MY rift and spends MY pools, so the
    // ownership is structural — but it is asserted rather than assumed,
    // because the next call site is the one that forgets. It is also
    // the seat's own Turn only: feeding is a Main-phase act.
    if (!myTurn) { showToast("You may only feed your own Treasure, on your own Turn.", SD.red); setAtvModal(null); return; }
    const field = JSON.parse(JSON.stringify(me?.field || {}));
    const tomes = [field.tomes?.[0] ?? null, field.tomes?.[1] ?? null, field.tomes?.[2] ?? null];
    const tome = tomes[tomeSlotIdx];
    if (!tome?.id) return;
    const card = getCard(tome.id);
    if (!card) return;
    const spec = ATV_FEED[card.id];
    if (!spec) return;

    const currentTurn = match?.turn || 0;
    // ── "UP TO n PER TURN" — THE ALLOWANCE, NOT ONE FEED ──────────
    // ⚠ THIS REFUSED THE SECOND FEED OF A TURN OUTRIGHT. Both cards
    // print "up to n per turn"; the till read `fedThisTurn === turn` as
    // "already fed, locked", so a seat that fed 1 Khao could not feed
    // the remaining 2. Joyous Courage needs 6 and allows 3 a Turn — two
    // Turns by the card, four by the old till.
    //
    // ⚠ AND IT IS R129's `feedAllowanceLeft`, NOT RE-DERIVED. That helper
    // already folds the allowance, what this Turn has spent, and the
    // store's ceiling into one number. Re-deriving it here is how the
    // board and the Book came to hold two copies of the same arithmetic.
    const spentThisTurn = (tome?.fedThisTurn === currentTurn) ? (tome?.fedAmountThisTurn || 0) : 0;
    const stored = tome.fed || 0;
    const room = feedAllowanceLeft(card, tome, currentTurn);
    if (room <= 0) {
      showToast(stored >= spec.threshold
        ? `${card.name} is full (${stored}/${spec.threshold}).`
        : `${card.name} has taken its ${spec.perTurn} this turn. Feed again next turn.`, SD.red);
      setAtvModal(null);
      return;
    }
    // Never take more than the allowance, whatever the caller asked for.
    const give = Math.max(1, Math.min(amount, room));

    // ── PAY, THROUGH THE GUARDED TILL, IN THE CARD'S OWN CURRENCY ──
    if (spec.currency === "anima") {
      // R96 — the till law binds it; `spendAnima` refuses before writing
      // and carries R33. (This lane used to bypass the till entirely
      // with a pre-R96 `< amount` check and a raw clamping write, so
      // feeding a Treasure could land a seat on exactly 0 with no
      // endMatch anywhere in the path.) R100 — it is a voluntary
      // payment, so `spendAnima` files it on the SELF ledger.
      if (!(await spendAnima(give))) { showToast(`Not enough Anima. Need ${animaFare(give)}.`, SD.red); return; }
    } else {
      if (!(await spendKhao(give))) { showToast(`Not enough Khao. Need ${give}.`, SD.red); return; }
    }

    // ── THE KHAO IS STORED ON THE CARD, AND IT IS AT RISK ─────────
    // caP'n: *"you just give it Khao and it stores it for you, if the
    // card is ruined the Khao is gone."* Nothing in this file refunds
    // `fed` when a Tome leaves its rift, and nothing should: that is the
    // risk the card is selling. Stated here so nobody "fixes" it into a
    // refund later.
    const newFed = stored + give;
    const amount_ = give;   // every line below reads the amount ACTUALLY taken
    tomes[tomeSlotIdx] = {
      ...tome, fed: newFed,
      // `fedThisTurn` keeps its bare-turn shape for npcTomeBook and
      // NPC.js; the per-Turn amount rides beside it.
      fedThisTurn: currentTurn,
      fedAmountThisTurn: spentThisTurn + give,
    };
    field.tomes = tomes;
    // ── THE FOOD IS PAID, SO THE COUNTER MOVES. HERE. ────────────────
    // ⚠ THIS WRITE USED TO BE AT THE BOTTOM OF THE FUNCTION, BELOW TWO
    // RETURNS. Cross a Necklace of Congealed Death past 8 while the
    // adversary has no being standing — or while a D'uhmdumn holds a
    // must-ruin-first — and the threshold branch `return`s having
    // written NOTHING: the Anima (up to 4) had already left, `fed` never
    // moved, and `fedThisTurn` was never stamped, so the seat could pay
    // again the same Turn and again the Turn after, forever, for nothing.
    // That is the same fault shape as a card removed with no destination
    // write: a cost with no recorded consequence.
    //
    // The rule is the ordering, not the payload: RECORD WHAT WAS PAID
    // FOR before branching on what it bought. Each threshold branch
    // below now writes only its OWN consequence (the Necklace's ruin,
    // the Anima gain), on top of a board that already knows it was fed.
    await updatePlayer(code, playerIdx, { field });

    // Check thresholds
    if (card.id === "tome_necklace_death" && newFed >= spec.threshold) {
      // Ruin 1 opponent being
      showToast(`✦ Necklace of Congealed Death — Fed ${newFed}/${spec.threshold}! Choose a being to ruin!`, SD.red);
      const oppAlive = (opponent?.field?.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
      if (oppAlive.length > 0) {
        // ── R-MRF: priority-target enforcement on tome offensive targeting ──
        const mrf = getRequiredTarget(opponent?.field);
        if (mrf) {
          // The food is already recorded (see above), so this refusal
          // costs the Anima and moves the counter — which is the honest
          // outcome: the Treasure was fed, the ruin had no legal target.
          showToast(`${mrf.name} must be ruined first!`, SD.red); setAtvModal(null); return;
        }
        setTargetPicker({ title: "◆ Necklace of Congealed Death — Ruin 1 Being", options: oppAlive.map(({ s, i }) => ({
          id: String(i), label: `${getCard(s.escendant)?.name}`, slotIdx: i, escendantId: s.escendant,
        })), onPick: async (opt) => {
          // ── ONE FUNNEL FOR "RUIN 1 BEING AND ITS SOUL" ──────────────
          // ⚠ THIS WAS HAND-ROLLED, onto this file's own `updatePlayer`,
          // and it therefore missed two things it could not see:
          //   · R123's Tome ward — Shower of Lyf wards BEINGS and Phoenix
          //     Aura wards CARDS, and the ward wrapper now sits at the
          //     HEAD of applyTomeEffect, which this branch is not inside.
          //   · the C13 SOAK — a being holding `nullifyNextAttack` eats
          //     the ruin and clears the ward instead of dying.
          // It also read the stale render closure (`opponent?.field`)
          // rather than the live board.
          //
          // `tomeResolver.ruinBeingAndSoul` is the canonical funnel: it
          // takes the live seat, honours the soak, carries R68's
          // Heretical Tome, cascades the wielded Treasures, fires the
          // Akula listener and toasts with the label. It returns
          // "soaked" | "ruined" | null, and the Necklace is spent either
          // way — the food bought the firing, not the outcome.
          const outcome = await ruinBeingAndSoul(oppIdx, opt.slotIdx, ctx, { label: "Necklace of Congealed Death" });
          // ── IT PAYS OUT, IT CLEARS, AND IT STANDS ────────────────
          // ⚠ THIS RUINED THE NECKLACE INTO ITS OWNER'S NECROPOLIS, and
          // caP'n's ruling makes that wrong twice over: *"yes, it can be
          // fed after use, it needs to be ruined for it to be taken off
          // the shatterfield."* Paying out is not being ruined. The
          // Treasure keeps its 3 Lyf and its rift, the STORE clears, and
          // the owner may fill it again as often as the per-Turn
          // allowance lets them — which is now the only brake there is.
          //
          // Read the live seat: the counter write above this branch
          // landed after the render closure was taken (THE REBASE LAW).
          {
            const meLiveN = matchRef.current?.players?.[playerIdx] || me;
            const fN = JSON.parse(JSON.stringify(meLiveN?.field || field));
            const tN = [fN.tomes?.[0] ?? null, fN.tomes?.[1] ?? null, fN.tomes?.[2] ?? null];
            if (tN[tomeSlotIdx]?.id === card.id) {
              // The store is SPENT, not refunded — the food bought the
              // firing. `fedAmountThisTurn` is left standing so the Turn's
              // allowance cannot be cycled twice by emptying the card.
              tN[tomeSlotIdx] = { ...tN[tomeSlotIdx], fed: 0 };
            }
            fN.tomes = tN;
            await updatePlayer(code, playerIdx, { field: fN });
          }
          if (outcome !== "soaked") showToast(`✦ The Necklace of Congealed Death is empty again — feed it once more.`, GM.vellumDim);
          setTargetPicker(null);
          setAtvModal(null);
        }});
      } else {
        // Fed past 8 with nothing to ruin. The counter and the fed-stamp
        // are already written; the Treasure keeps its charge and waits.
        showToast(`✦ Necklace of Congealed Death — Fed ${newFed}/${spec.threshold}, but no being stands to ruin.`, GM.vellumFaint);
        setAtvModal(null);
      }
      return;
    }
    if (card.id === "tome_necklace_courage" && newFed >= spec.threshold) {
      // ── IT PAYS OUT AND IT STAYS ON THE FIELD ─────────────────────
      // caP'n: *"once its full of 6 of the owner's Khao, the owner gets
      // 6 Anima. That Tome has 3 Lyf, so it remains on the board (most
      // if not all treasures behave this way) until ruined by Lyt attack
      // or other means."*
      //
      // ⚠ THIS VOIDED THE NECKLACE ON PAYOUT — nulled the rift and sent
      // the card to the Necropolis. Nothing in the printed text says it
      // is ruined, and the card carries 3 Lyf precisely BECAUSE it is
      // meant to sit there and be shot at. Voiding it turned a standing
      // Treasure the adversary has to deal with into a one-shot, and
      // handed its owner the 6 Anima with no board presence left to
      // answer for it.
      const meLiveC = matchRef.current?.players?.[playerIdx] || me;
      const newAnima = sealAnimaGain((meLiveC?.anima ?? STARTING_ANIMA), 6, "Joyous Courage");
      // ⚠ THE STORE CLEARS, AND `paidOut` IS GONE. I asked caP'n whether a
      // Necklace that has paid out may be fed again and built the OTHER
      // reading — pays once, then sits there as a 3-Lyf wall, stamped
      // with a one-way `paidOut` latch. He ruled the opposite: *"yes, it
      // can be fed after use, it needs to be ruined for it to be taken
      // off the shatterfield."* So the store empties and the Treasure is
      // refillable for as long as it survives, and the ONLY brake is the
      // per-Turn allowance — which is why `feedAllowanceLeft` is now
      // load-bearing rather than a nicety.
      //
      // `fedAmountThisTurn` is deliberately NOT cleared: clearing it
      // would let a seat empty the card and feed its whole allowance
      // again inside one Turn, which is the brake removing itself.
      tomes[tomeSlotIdx] = { ...tomes[tomeSlotIdx], fed: 0 };
      field.tomes = tomes;
      await updatePlayer(code, playerIdx, {
        anima: newAnima, ...buildAnimaGainUpdate(meLiveC, 6),
        field,
      });
      showToast(`✦ Necklace of Joyous Courage — full at ${newFed}/${spec.threshold}. You gain 6 Anima; the Necklace stands, empty, ready to be fed again.`, SD.gold);
      setAtvModal(null);
      return;
    }

    // (no field write here — the counter landed the moment the food was
    // paid, above the thresholds. See THE FOOD IS PAID there.)
    showToast(`✦ ${card.name} — Fed ${newFed}/${spec.threshold}${amount_ !== amount ? ` (took ${amount_}, the allowance)` : ""}`, SD.gold);
    setAtvModal(null);
  };

  // Handle devotion — opponent paying Anima to contract tomes
  // ── R96 / R100 · DEVOTION IS A SELF-DIM ────────────────────────
  // A Devotion is Anima you hand to the adversary because their
  // contract Tome (King of the Castle, Magma Carta) demands it. It is
  // COMPELLED by their card, which is an argument for calling it
  // adversarial — but it is your own hand moving your own Anima, and
  // caP'n's ruling is *"dimming your own Anima in any way at all does
  // not satisfy Prophecy requirements"*. It is filed as SELF, and the
  // judgement is DECLARED here rather than buried, because it is the
  // one row a reasonable person could file the other way.
  //
  // The old head guard read `< amount`, the pre-R96 shape: devoting
  // your last Anima was legal and landed you on exactly 0 with no
  // endMatch anywhere in the path.
  const handleDevote = async (amount) => {
    // ══════════════════════════════════════════════════════════
    // ⚠ THIS WAS TWO COPIES OF ONE PAYMENT, AND BOTH RAN.
    // ══════════════════════════════════════════════════════════
    // The old shape was one `tillRefusesSelfDim`, one `canPayVoluntary`,
    // then `if (kingIdx >= 0) { … }` and `if (magmaIdx >= 0) { … }` as
    // two INDEPENDENT ifs. With King of the Castle AND Magma Carta both
    // standing on the adversary's board — which the devotion modal
    // offers, because it lists every active contract — both blocks ran
    // off ONE affordability check and ONE stale `me?.anima`:
    //
    //   · each block wrote `anima: Math.max(0, me.anima - amount)` from
    //     the SAME pre-payment number, so the second write undid the
    //     first: the payer was charged ONCE for TWO contracts;
    //   · each credited the adversary `opponent.anima + amount` off the
    //     same stale figure, so they were credited once too;
    //   · and BOTH `devoted` counters advanced by the full amount.
    //     Two contracts progressed for the price of one.
    //
    // It is a LOOP now, mirroring NPC.npcHonourDevotions, which has
    // always had this right: re-read the live board every iteration,
    // pay through the till, and re-check the Tome is still the Tome it
    // was before writing its counter.
    //
    // ⚠ AND IT PAYS THROUGH `spendAnima`, NOT A RAW WRITE. That is the
    // voluntary-payment law and it is absolute: `spendAnima` carries
    // R96's `canPayVoluntary` (refused BEFORE any write, so a payment
    // that cannot be made never partially happens) and R33's endMatch.
    // The raw `updatePlayer(… Math.max(0, x - amount) …)` these blocks
    // used could clamp a human to exactly 0 with the duel still
    // running — the original R33-at-the-till bug, in the one lane that
    // still had it. A Devotion may never end you.
    //
    // R100 — a Devotion is filed as SELF (see the banner above this
    // function), and `spendAnima` writes the SELF ledger for every
    // payment that reaches it, so that is carried for free.
    const CONTRACTS = [
      { id: "tome_king_of_the_castle", cap: 8, name: "King of the Castle" },
      { id: "tome_magma_carta",        cap: 4, name: "Magma Carta" },
    ];
    for (const contract of CONTRACTS) {
      // ── THE LIVE BOARD, EVERY ITERATION ──────────────────────
      const liveM   = matchRef.current || match;
      const meLiveD = liveM?.players?.[playerIdx] || me;
      const oppLive = liveM?.players?.[oppIdx] || opponent;
      const oppT    = [oppLive?.field?.tomes?.[0], oppLive?.field?.tomes?.[1], oppLive?.field?.tomes?.[2]];
      const idx     = oppT.findIndex(t => t?.id === contract.id && !t?.faceDown);
      if (idx < 0) continue;

      // R96, asked of the live number rather than the render closure.
      if (tillRefusesSelfDim(amount, "Devotion")) return;
      if (!canPayVoluntary(meLiveD?.anima ?? STARTING_ANIMA, amount)) {
        showToast(`Not enough Anima to devote — a payment may never take you to 0. Need ${animaFare(amount)}.`, SD.red);
        break;
      }
      // THE TILL. Refuses before writing; ends the duel if an exempt
      // payment ever zeroes the payer (a Devotion never is exempt, so
      // this one cannot).
      if (!(await spendAnima(amount))) {
        showToast(`Not enough Anima to devote — a payment may never take you to 0. Need ${animaFare(amount)}.`, SD.red);
        break;
      }

      // The Anima goes to THEM — credited off the live figure, so two
      // contracts in one press credit them twice, which is what two
      // contracts cost.
      {
        const oppNow = matchRef.current?.players?.[oppIdx] || oppLive;
        await updatePlayer(code, oppIdx, {
          anima: sealAnimaGain((oppNow?.anima ?? STARTING_ANIMA), amount),
          ...buildAnimaGainUpdate(oppNow, amount),
        });
      }

      // Re-read their field and confirm the Tome is still the one we
      // priced before advancing its counter (npcHonourDevotions' own
      // `if (f.tomes[i]?.id !== t.id) continue;` guard).
      const oppFresh = matchRef.current?.players?.[oppIdx] || oppLive;
      const oppField = JSON.parse(JSON.stringify(oppFresh?.field || {}));
      const oppTomesCopy = [oppField.tomes?.[0] ?? null, oppField.tomes?.[1] ?? null, oppField.tomes?.[2] ?? null];
      if (oppTomesCopy[idx]?.id !== contract.id) continue;
      const devoted = (oppTomesCopy[idx].devoted || 0) + amount;

      if (devoted >= contract.cap) {
        // ── NECROPOLIS LAW — the completed contract rides to ITS OWN
        // OWNER'S Necropolis (it used to vanish), and the owner's
        // draw-1 clause fires there through the shared on-ruin helper.
        oppTomesCopy[idx] = null;
        oppField.tomes = oppTomesCopy;
        // R08 — DECLARED. The payload is byte-identical to a ruin and
        // the meaning is a contract being HONOURED at their own card's
        // instruction. See THE DECLARED EXCEPTION on the updatePlayer
        // shadow above, and THE R08 SEAL in gameManager.js.
        await updatePlayer(code, oppIdx, {
          field: oppField,
          necropolis: [...(oppFresh?.necropolis || []), contract.id],
        }, { notAnAttack: "R08 · a completed Devotion contract rides to its OWNER's Necropolis" });
        await applyTomeRuinClauses(oppIdx, contract.id);
        showToast(`✦ ${contract.name} — Devoted ${devoted}/${contract.cap}! Contract complete, card ruined!`, SD.red);
      } else {
        oppTomesCopy[idx] = { ...oppTomesCopy[idx], devoted };
        oppField.tomes = oppTomesCopy;
        await updatePlayer(code, oppIdx, { field: oppField });
        showToast(`✦ Devoted ${amount} Anima to ${contract.name} (${devoted}/${contract.cap})`, GM.vellumFaint);
      }
    }
    setDevotionModal(null);
  };

  // ── GRIMOIRE SEARCH ─────────────────────────────────────────
  // Cost mirrors Resurrection: L1=2, L2=4, L3=4 (Khao OR Anima)
  // Costs your draw for the turn
  // ── CURIO ACTIVATION — DELETED, AND THAT IS THE FIX ───────────
  // There WAS a `handleActivateCurio` here: a nine-entry `effects` map
  // granting +3 Anima / +1 Max Khao per Curio, with NO idempotence
  // guard of any kind — no `curiosActivated` check, no spent flag, no
  // cadence. It had ZERO call sites, and the R108 `phaseBars` guard at
  // the top of it made it read as wired and reviewed.
  //
  // It was the THIRD copy of one table. The other two are the real
  // ones and they agree row for row:
  //   • activateCuriosForPlayer, in the match listener below — grants
  //     the gift ONCE per seat, guarded on `curiosActivated`.
  //   • NPC.activateNpcCurios (CURIO_GIFTS) — the brain's door onto the
  //     same flag, for a duel with no screen mounted.
  // R05: a Curio is PRE-PLACED and its gift is a once-per-duel gift.
  // The day a radial wedge had been wired to the deleted handler, a
  // human would have pressed Wizard's Cap for +3 Anima every Turn
  // forever while the brain took it once. Deleted rather than guarded,
  // because a fourth door onto a once-per-duel gift is not wanted.


  const handleGrimoireSearch = async (level, payWithAnima) => {
    // R108 — once the Attack phase is declared, no more cards may be
    // played. See THE PHASE GATE above gateCtx.
    if (phaseBars("SEARCH")) return;
    if (!myTurn || !me) return;
    // ── SEAL: R08 — see handleLuxmoireSearch. Same law, other Book,
    // and the same 2026-08-26 narrowing: this is the PLAYER'S search
    // action (the GrimoireSearchModal's own button, paying the by-level
    // R88 fare and spending the Turn's draw), so the opening-Cycle bar
    // applies to it in full. The Machine Primera stamp also pulls a Beb
    // out of this Book and is EXEMPT — it is a card's printed power,
    // not this handler, and it never calls gateSearch.
    {
      const v = gateSearch(gateCtx(), "GRIMOIRE");
      if (!v.valid) { showToast(v.reason || "Cannot search the Grimoire.", SD.red); return; }
    }
    if (drawnThisTurn) { showToast("Already drew this turn.", SD.red); return; }
    // ── SEAL: the search CONSUMES the Turn's draw (R11), so it is a
    // draw as far as the gate is concerned — and R07 seals the Grimoire
    // lane on the first player's Turn 1 whether they draw or search it.
    {
      const v = gateDraw(gateCtx(), "GRIMOIRE", iAmFirstPlayer, drawsThisTurn);
      if (!v.valid) { showToast(v.reason || "Cannot search the Grimoire.", SD.red); return; }
    }
    // ── GRIM/LUX FIX (July 2026): check the pool BEFORE paying. ──
    // The old pay-then-refund flow also reset khaoSpentThisCycle to
    // false on refund — wiping a flag set by EARLIER spends that turn
    // and granting illegal natural Khao growth (R12 exploit). With the
    // pre-check there is nothing to refund, so the exploit path is gone.
    const pool = Object.keys(me.grimoire || {}).filter(id => {
      const c = getCard(id); return c?.type === "Escendant" && c?.level === level;
    });
    if (pool.length === 0) {
      showToast(`No Level ${level} beings in Grimoire.`, GM.vellumFaint); return;
    }
    // R88 — the Grimoire search fare. This file has always charged 2/4 by
    // level; rules.js was corrected to match, so the number now lives in
    // exactly one place instead of two that happened to agree.
    const cost = grimSearchFare(level);
    if (payWithAnima) {
      const paid = await spendAnima(cost);
      if (!paid) { showToast(`Not enough Anima. Need ${animaFare(cost)}.`, SD.red); return; }
    } else {
      const paid = await spendKhao(cost);
      if (!paid) { showToast(`Not enough Khao. Need ${cost}.`, SD.red); return; }
    }
    // Show picker
    setTargetPicker({
      title: `◆ Search Grimoire — Choose a Level ${level} Being`,
      cards: pool,
      count: 1,
      onPick: async (chosen) => {
        // TargetPickerModal passes the item directly (string cardId when using picker.cards)
        const cardId = typeof chosen === "string" ? chosen : chosen?.id || chosen?.[0];
        if (!cardId) return;
        const newGrimoire = { ...me.grimoire };
        if (newGrimoire[cardId] > 1) newGrimoire[cardId]--; else delete newGrimoire[cardId];
        const searchedHand = [...(me.hand || []), cardId];
        await updatePlayer(code, playerIdx, { hand: searchedHand, grimoire: newGrimoire });
        await noteTurnAction("drawnThisTurn", setDrawnThisTurn); // R11 — on the record
        showToast(`◆ ${getCard(cardId)?.name} found!`, GM.inkGold);
        setTargetPicker(null);
        setGrimoireSearchModal(false);
        if (searchedHand.length > HAND_LIMIT) setForcedDiscardModal(true); // R10
      },
    });
  };

  const handleDraw = async (fromLux = false) => {
    // R108 — once the Attack phase is declared, no more cards may be
    // played. See THE PHASE GATE above gateCtx.
    if (phaseBars("DRAW")) return;
    if (!myTurn || !me) return;
    // ── SEAL: universal DRAW gate (R07 + R11) ──────────────────
    // The bespoke checks below are KEPT — they word the refusal in the
    // board's own voice and they fire before the gate can be reached on
    // a stale ctx — but the gate is now the authority, and it is the
    // same one the enforcer and the harness read.
    if (drawnThisTurn) { showToast("Already drew this turn.", SD.red); return; }
    // ── R07 (RULEZ_V2.md, canon; false 'July 2026' cite scrapped 2026-08-27): first player, Turn 1 — the GRIMOIRE
    // is sealed, but the LUXMOIRE is open. Turn 0 is by definition the
    // first player's first turn.
    if (!fromLux && (match?.turn || 0) === 0) {
      showToast("Turn 1 — the Grimoire is sealed for the first player. The Luxmoire is open.", GM.inkGold); return;
    }
    {
      const v = gateDraw(gateCtx(), fromLux ? "LUXMOIRE" : "GRIMOIRE", iAmFirstPlayer, drawsThisTurn);
      if (!v.valid) { showToast(v.reason || "Cannot draw.", SD.red); return; }
    }
    setMulliganAvailable(false);
    const hand = me.hand || [];
    // ── R10 (RULEZ_V2.md, canon; false 'July 2026' cite scrapped 2026-08-27): draw-then-discard. Drawing AT the cap
    // is legal — the draw resolves (hand momentarily 9), then a forced
    // discard of 1 to the Necropolis brings it back to 8.
    const source = fromLux ? me.luxmoire : me.grimoire;
    const pool = [];
    Object.entries(source || {}).forEach(([id, qty]) => { for (let i = 0; i < qty; i++) pool.push(id); });
    if (pool.length === 0) {
      // ── R40–R43: GRAVESHIFT ─────────────────────────────────
      // Luxmoire never Graveshifts. For the Grimoire, an empty draw is
      // not a loss (R43) — it offers a Graveshift: pay 10 Anima to fold
      // hand + Necropolis into a fresh Grimoire. Confirm via the radial.
      if (fromLux) { showToast("Luxmoire empty!", SD.red); return; }
      setGraveshiftModal(true);
      return;
    }
    const drawn = pool[Math.floor(Math.random() * pool.length)];
    const newSource = { ...source };
    if (newSource[drawn] > 1) newSource[drawn]--; else delete newSource[drawn];
    const sourceUpdate = fromLux ? { luxmoire: newSource } : { grimoire: newSource };
    const newHand = [...hand, drawn];
    await updatePlayer(code, playerIdx, { hand: newHand, ...sourceUpdate });
    await noteTurnAction("drawnThisTurn", setDrawnThisTurn); // R11 — on the record
    showToast(fromLux ? "✦ Drawn from the Luxmoire!" : "◆ Card drawn!", GM.inkGold);
    // R10 — over the cap: forced discard, player picks 1 → Necropolis.
    if (newHand.length > HAND_LIMIT) setForcedDiscardModal(true);
  };

  // ── GRAVESHIFT — R40 (empty Grimoire only), R41 (10 Anima),
  //    R42 (hand + Necropolis → new Grimoire), R43 (never a loss) ──
  // Routes through the universal gate. The same rule applies to the NPC.
  const handleGraveshift = async () => {
    if (!myTurn || !me) { setGraveshiftModal(false); return; }
    // R108 — once the Attack phase is declared, no more cards may be
    // played. See THE PHASE GATE above gateCtx.
    if (phaseBars("GRAVESHIFT")) { setGraveshiftModal(false); return; }
    // ── SEAL: universal Graveshift gate ────────────────────────
    {
      const v = enforce("GRAVESHIFT", gateCtx(), {});
      if (!v.valid) { showToast(v.reason || "Cannot Graveshift.", SD.red); setGraveshiftModal(false); return; }
    }
    // ── R42 + R85 — THE SEAL'S GRAVESHIFT (caP'n, July 27 2026) ──
    // sealGraveshift does three things in one pass, and all three matter:
    //   • STAMPS field/soul survivors at the OUTGOING generation — they
    //     did not shift, so they must not be punished as if they had.
    //   • STRIPS seals as hand + Necropolis fold into the Grimoire map,
    //     keeping Firebase keys bare and un-fragmented (3 copies of a Beb
    //     stay `taez_beb: 3`, not three single-count keys).
    //   • TICKS gshift, so everything sealed from here on is gen >= 1 and
    //     barred from the passive Resurrect — card effects still reach it.
    const gs = sealGraveshift(me, isEscendantId);
    // ── R41 + R96's ONE EXEMPTION ────────────────────────────────
    // Pay exactly GRAVESHIFT_COST. This is the single payment in Terror
    // that R96 lets take its payer to 0: R43 says you cannot lose by
    // running out of cards, so R43's own remedy must stay payable by a
    // seat holding exactly the fare. Asked through the rule with
    // `exempt: true` rather than by skipping the check — the exemption
    // is a rule, and it should be visible as one at the only site that
    // has it. (The write stays a single payload, so this does not route
    // through spendAnima; R33 is collected below exactly as spendAnima
    // would collect it.)
    if (!canPayVoluntary(me.anima ?? STARTING_ANIMA, GRAVESHIFT_COST, { exempt: true })) {
      showToast(`Graveshift costs ${GRAVESHIFT_COST} Anima.`, SD.red); setGraveshiftModal(false); return;
    }
    // Spend is delta-based (never overwrite).
    const newAnima = Math.max(0, (me.anima ?? STARTING_ANIMA) - GRAVESHIFT_COST);
    await updatePlayer(code, playerIdx, {
      grimoire: gs.grimoire,
      field: gs.field,
      gshift: gs.gshift,
      hand: [],
      necropolis: [],
      anima: newAnima,
      // R100 — the fare is named by the ruling itself as self-inflicted.
      ...buildSelfDimUpdate(me, GRAVESHIFT_COST),
    });
    setGraveshiftModal(false);
    showToast(`♻ Graveshift! −${GRAVESHIFT_COST} Anima — Grimoire reforged. These cards can no longer be Resurrected by the paid rite — only by card effects.`, SD.gold);
    if (newAnima <= 0) { await endMatch(code, oppIdx, playerIdx) /* AUDIT FIX 2026-08-22: MY anima just hit 0 — the loser is ME, explicitly, never guessed via getTargetIdx(oppIdx) */; }
  };

  const spendKhao = async (amount) => {
    // C12: fresh over rendered — mid-chain, `me` can be a whole link
    // behind, and a false "not enough Khao" would kill a legal answer.
    const current = matchRef.current?.players?.[playerIdx]?.khao ?? me?.khao ?? STARTING_KHAO;
    if (current < amount) return false;
    // ── SEAL: universal KHAO till (R13 the ceiling, R15 pay-before-act,
    // R12/R14 the refill ledger). Every Khao payment in this file funnels
    // through this one function, so gating HERE gates all ~40 of them.
    // Built on the LIVE seat, not the render closure, for the same reason
    // the read above is: mid-chain `me` can be a link behind.
    {
      const liveMe = matchRef.current?.players?.[playerIdx] || me;
      const v = gateKhaoSpend(gateCtx({ me: liveMe }), amount, !!liveMe?.khaoSpentThisCycle);
      if (!v.valid) { showToast(v.reason || "That payment is illegal.", SD.red); return false; }
    }
    const updates = { khao: current - amount };
    if (amount > 0) updates.khaoSpentThisCycle = true;
    await updatePlayer(code, playerIdx, updates);
    // D15 — the ember trail: fuel visibly leaves the vortex for the
    // card it paid for. Only when we know where that was (a radial
    // opened in the last few seconds), and never for free actions.
    if (amount > 0) {
      const pos = lastActionPosRef.current;
      if (pos && Date.now() - pos.at < 8000) khaoEmberTrail(pos, amount);
    }
    return true;
  };

  // ── COUNTER SYSTEM ──────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════
  // RESPONSE WINDOW ENGINE (RULEZ_V2.md, canon; false 'July 2026' cite scrapped 2026-08-27)
  // ═══════════════════════════════════════════════════════════
  // The NPC engine declares every response-worthy action through the
  // ResponseBridge BEFORE resolving it. This impl decides whether a
  // window MATERIALIZES: only when the defender holds a legal,
  // affordable reaction right now — option C in the rulebook, zero
  // dead prompts in practice. Reads matchRef (never stale closures)
  // because it is invoked from outside the React render flow.
  responderImplRef.current = (action) => {
    const m = matchRef.current;
    const meNow = m?.players?.[playerIdx];
    if (!meNow || m?.status === "finished") return Promise.resolve({ responded: false });
    const turnKey = `${m?.turn || 0}-${m?.activePlayer ?? 0}`;
    if (snoozeTurnRef.current === turnKey) return Promise.resolve({ responded: false });
    if (responseWindowRef.current) return Promise.resolve({ responded: false }); // never stack windows

    const khaoNow = meNow.khao || 0;
    // ── THE CHAIN (C12, August 2026) — a REBUTTAL is a declaration ──
    // An adversary's true counter is itself an activation, and it can
    // arrive here as a declaration in its own right (PvP chains travel
    // the same handshake as everything else). Answering one obeys the
    // CHAIN LAW, not the counter hierarchy: same-or-higher level only,
    // never late, and NO generic interrupts — only a true counter
    // answers a true counter. ChainLaw owns that law for all three
    // surfaces; this branch just renders it.
    if (action?.kind === "REBUTTAL") {
      const answered = getCard(action.cardId);
      if (!answered) return Promise.resolve({ responded: false });
      const chainOpts = trueCounterOptions({
        hand: meNow.hand || [],
        tomes: (meNow.field?.tomes) || [],
        khao: khaoNow, getCard,
        declaredLevel: action.level || answered.level || 2,
        isRebuttal: true,
      }).map(o => ({ ...o, counter: o.ilk }));
      if (chainOpts.length === 0) return Promise.resolve({ responded: false });
      return new Promise((resolve) => {
        setResponseWindow({
          action, options: chainOpts, resolve, turnKey,
          actingName: answered.name || null,
          rebuttal: true,                 // this window's own fire never re-chains
          chainDepth: action.chainDepth || 2,
        });
      });
    }
    const isActivation = action?.kind === "ACTIVATION";
    // ── COUNTER HIERARCHY (caP'n ruling, July 2026) ──
    // Only L3+ activations are shielded from lesser counters: a L1/L2
    // counter vs a L3+ activation is LATE — the effect resolves first,
    // THEN the activated card must leave the field. L1 stops L2 fine.
    const activatedLevel = isActivation ? (getCard(action.cardId)?.level || 1) : 0;
    const options = [];
    // L3 Tomes from hand — the true instants (canon).
    (meNow.hand || []).forEach((id, i) => {
      const c = getCard(id);
      if (c?.type === "Tome" && c.level === 3 && (c.khaoCost || 0) <= khaoNow) {
        options.push({ source: "hand", idx: i, cardId: id,
          counter: isActivation && (c.ilk === "Nihil" || c.ilk === "Omnia") ? c.ilk : null,
          late: false }); // hand counters are L3 by law — never late
      }
    });
    // Face-down L2/L3 on field — the ambushes (canon).
    ((meNow.field?.tomes) || []).forEach((t, i) => {
      if (!t || typeof t !== "object" || !t.faceDown) return;
      const c = getCard(t.id);
      if (c?.type === "Tome" && c.level >= 2 && (c.khaoCost || 0) <= khaoNow) {
        const isCtr = isActivation && (c.ilk === "Nihil" || c.ilk === "Omnia");
        options.push({ source: "field", idx: i, cardId: t.id,
          counter: isCtr ? c.ilk : null,
          late: isCtr && activatedLevel >= 3 && (c.level || 1) < 3 });
      }
    });
    if (options.length === 0) return Promise.resolve({ responded: false });

    // Names for the headline — resolved NOW, from the same fresh state.
    const actingName = isActivation ? (getCard(action.cardId)?.name || null) : null;
    // R136 — a bystander answering a swing at somebody else's board has
    // no "target" of its own to name; the index belongs to another seat.
    const targetName = action?.kind === "LYT_ATTACK" && action.aimedAtMe !== false
      ? (getCard(meNow.field?.esclings?.[action.targetIdx]?.escendant)?.name || null)
      : null;

    return new Promise((resolve) => {
      setResponseWindow({ action, options, resolve, turnKey, actingName, targetName });
    });
  };

  // Stable wrapper registered ONCE per mount (NPC matches only) — the
  // ref indirection keeps every render's fresh state reachable without
  // ever re-registering. PvP matches never register: zero pauses.
  // ⭐ …AND ONCE PER MOUNT WHEN A BRAIN SITS AT THE TABLE (2026-09-06).
  // In Gauntlet — Duo / Story Co-op the boss is an AI seat inside a
  // human match: `isNPC` is false, the responder was never registered,
  // and the brain's requestReaction on the host's client resolved
  // {responded:false} — the humans could never answer the boss.
  const hasAiSeat = useMemo(() => allSeatIdxs(match).some((i) => match?.players?.[i]?.controller === "ai"), [match]);
  // ── R136 IN CO-OP · THE PARTNER CAN ANSWER THE BOSS (2026-09-15) ────
  // The brain's requestWindow is IN-PROCESS on the host, so until today
  // only the host's client could answer a boss declaration: the partner
  // seat had no window at all — a declared gap. Now the host's responder
  // FANS OUT: its own in-process window, plus the same Firebase handshake
  // a human declaration uses, written ON THE BRAIN'S BEHALF (`as: aiSeat`)
  // so every other living adversary of the boss gets R136's window. The
  // first legal answer wins the link; the loser's window is withdrawn by
  // the effect below. 1v1-vs-NPC has no other seats and is byte-identical.
  useEffect(() => {
    if (!isNPC && !hasAiSeat) return;
    registerResponder((action) => {
      const mine = responderImplRef.current ? Promise.resolve(responderImplRef.current(action)) : Promise.resolve({ responded: false });
      const m = matchRef.current;
      const aiSeat = allSeatIdxs(m).find((i) => m?.players?.[i]?.controller === "ai");
      const others = aiSeat == null ? [] : livingEnemySeatsOf(m, aiSeat).filter((i) => i !== playerIdx);
      if (isNPC || !others.length) return mine;
      const theirs = declarePvPRef.current
        ? declarePvPRef.current(action, { as: aiSeat, targetSeat: playerIdx })
        : Promise.resolve({ responded: false });
      return new Promise((resolve) => {
        let done = false, left = 2;
        const win = (v, from) => {
          if (done) return; done = true; resolve(v);
          if (from === "mine") settlePvPDeclareRef.current?.(pvpAwaitRef.current?.seq, v);   // withdraw the partner's window
          else resolveResponseWindowRef.current?.({ responded: false });                     // withdraw my own
        };
        const lose = () => { if (!done && --left === 0) { done = true; resolve({ responded: false }); } };
        mine.then((v) => (v?.responded ? win(v, "mine") : lose())).catch(lose);
        theirs.then((v) => (v?.responded ? win(v, "theirs") : lose())).catch(lose);
      });
    });
    return () => unregisterResponder();
  }, [isNPC, hasAiSeat, playerIdx]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── R136 · A WINDOW IS WITHDRAWN WHEN THE DECLARATION IS SETTLED ────
  // With every adversary holding a window, the first to answer closes the
  // link for the rest: the declarer clears pendingAction, and any other
  // seat still looking at a window for that seq resolves it as no answer.
  useEffect(() => {
    const rw = responseWindowRef.current;
    const openSeq = rw?.action?.seq;
    if (openSeq == null) return;
    if (match?.pendingAction?.seq === openSeq) return;
    resolveResponseWindowRef.current?.({ responded: false });
    showToast("⌛ The window closed — another Aenarchist answered first.", GM.vellumFaint);
  }, [match?.pendingAction]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── Wave 3: PvP response lane — BOTH handshake roles live here ──
  // DECLARER: my pendingAction got its pendingResponse → settle the
  //   awaited promise (declarePvP is parked on pvpAwaitRef) and clear
  //   the handshake nodes.
  // DEFENDER: the adversary declared → answer through my OWN responder
  //   impl — the very same function that answers the NPC engine; the
  //   window only materializes when I hold a legal answer — then write
  //   the verdict back as pendingResponse. Every ref used here is a
  //   ref on purpose: this effect stays dependency-clean under CI.
  useEffect(() => {
    if (isNPC) return;
    const pa = match?.pendingAction;
    // ── R136 · ANYONE MAY ANSWER (caP'n, 2026-09-15: "anyone can answer
    // it, even in a free for all, that adds a lot of drama") ────────
    // Every living ADVERSARY of the declarer holds a response window on
    // every declaration — not only the seat it was aimed at. So the
    // handshake grew per-seat slots: each answerer writes
    // pendingResponses/{seat} (one writer per node, never a collision)
    // and the DECLARER settles from the set. The legacy single
    // `pendingResponse` slot is still read, as seat pa.targetSeat's, so a
    // client one version behind cannot stall a duel.
    const answerers = answerersFor(match, pa);
    const responses = pa ? collectPvPResponses(match, pa, answerers) : null;
    // DECLARER — settle when a real answer is in, or when every answerer
    // has declined. The 12 s timeout still guards a vanished seat.
    if (pa && pvpAwaitRef.current && pa.seq === pvpAwaitRef.current.seq && (pa.by === playerIdx || pa.proxyBy === playerIdx) && responses) {
      const first = responses.answered[0];
      if (first) {
        settlePvPDeclareRef.current?.(pa.seq, {
          responded: true,
          counter: first.counter || null,
          late: !!first.late,
          counterName: first.counterName || null,
          // C12 — the chain needs to know WHICH card answered and at what
          // level, or it cannot ask the next question over this handshake.
          counterCardId: first.counterCardId || null,
          counterLevel: first.counterLevel || null,
          answeredBy: first.seat,
        });
        return;
      }
      if (responses.allDeclined) {
        settlePvPDeclareRef.current?.(pa.seq, { responded: false });
        return;
      }
    }
    // DEFENDER — a fresh declaration awaits my answer if I am one of the
    // declarer's living adversaries (R136). A missing seat list (only
    // possible from a pre-N-seat pendingAction shape) falls back to
    // "anyone else in a 2-seat match must mean me," same as the old check.
    const mayAnswer = answerers.includes(playerIdx);
    // ⭐ THE HOST ANSWERS FOR THE BRAIN (2026-09-06). A declaration an AI
    // seat may answer has no client behind it, so nobody wrote its
    // response and every attack on the boss resolved only on the 12s
    // declare timeout — and the boss could never counter. The host (the
    // client running the brain) answers on its behalf through the same
    // requestReaction the 1v1 path uses. R136 widens this from "aimed at
    // the AI seat" to "the AI seat is one of the answerers".
    const aiSeat = answerers.find((i) => match?.players?.[i]?.controller === "ai");
    const answerForAi = aiSeat != null && playerIdx === hostSeat && pa.by !== aiSeat;
    // A declaration I wrote ON THE BRAIN'S BEHALF (co-op, see the
    // responder registration) already has my own in-process window open;
    // the Firebase lane is for the OTHER humans at the table.
    const myOwnProxy = pa?.proxyBy === playerIdx;
    if (pa && pa.by !== playerIdx && !myOwnProxy && (mayAnswer || answerForAi) && pa.seq !== pvpAnsweredSeqRef.current && !pvpAnsweringRef.current) {
      pvpAnsweredSeqRef.current = pa.seq;
      pvpAnsweringRef.current = true;
      const action = { kind: pa.kind, seq: pa.seq };   // seq: so a window can be WITHDRAWN when someone else answers first
      // R136 — tell the responder whose board the declaration is aimed
      // at, so a bystander's in-window ward never lands on its own
      // being at a stranger's index.
      action.aimedAtMe = pa.targetSeat == null ? seatCountOf(match) <= 2 : pa.targetSeat === playerIdx;
      if (pa.cardId !== undefined) action.cardId = pa.cardId;
      if (pa.targetIdx !== undefined) action.targetIdx = pa.targetIdx;
      if (pa.lyt !== undefined) action.lyt = pa.lyt;
      // C12 — a REBUTTAL declaration carries the level it must be
      // answered AT (chain law) and how deep the stack already is.
      if (pa.level !== undefined) action.level = pa.level;
      if (pa.chainDepth !== undefined) action.chainDepth = pa.chainDepth;
      // My own answer first; if I also host a brain seat that may answer
      // and I hold nothing, the brain gets its window (one slot per seat).
      const mine = mayAnswer
        ? Promise.resolve(responderImplRef.current ? responderImplRef.current(action) : { responded: false })
        : Promise.resolve(null);
      const writeSlot = (seat, verdict) => updateMatch(code, {
        [`pendingResponses/${seat}`]: {
          seq: pa.seq, seat, at: Date.now(),
          responded: !!verdict?.responded,
          ...(verdict?.counter ? { counter: verdict.counter } : {}),
          ...(verdict?.late ? { late: true } : {}),
          ...(verdict?.counterName ? { counterName: verdict.counterName } : {}),
          ...(verdict?.counterCardId ? { counterCardId: verdict.counterCardId } : {}),
          ...(verdict?.counterLevel ? { counterLevel: verdict.counterLevel } : {}),
        },
      });
      mine
        .then(async (verdict) => {
          if (mayAnswer) await writeSlot(playerIdx, verdict);
          if (answerForAi) {
            const v = await Promise.resolve(requestReaction(action)).catch(() => ({ responded: false }));
            await writeSlot(aiSeat, v);
          }
        })
        .catch(() => {
          const fallback = {};
          if (mayAnswer) fallback[`pendingResponses/${playerIdx}`] = { seq: pa.seq, seat: playerIdx, at: Date.now(), responded: false };
          if (answerForAi) fallback[`pendingResponses/${aiSeat}`] = { seq: pa.seq, seat: aiSeat, at: Date.now(), responded: false };
          return updateMatch(code, fallback).catch(() => {});
        })
        .finally(() => { pvpAnsweringRef.current = false; });
    }
  }, [match?.pendingAction, match?.pendingResponse, match?.pendingResponses, isNPC, oppIdx, code, hostSeat]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── C12: THE CHAIN'S RECEIPT (PvP only) ──────────────────────
  // When a chain ends, the declarer notes which PERSISTENT counters it
  // ruined — and each client ruins ITS OWN. One writer per seat is the
  // whole reason this note exists instead of a cross-seat write: two
  // clients writing one field is how a board tears. Idempotent by
  // construction (the tome is gone on the second pass, so nothing is
  // pushed to the Necropolis twice) and seq-guarded on top of that.
  useEffect(() => {
    if (isNPC) return;
    const cr = match?.chainResult;
    if (!cr || cr.by === playerIdx || !Array.isArray(cr.ruined)) return;
    if (cr.seq === chainRuinSeqRef.current) return;
    chainRuinSeqRef.current = cr.seq;
    const meNow = match?.players?.[playerIdx];
    const tArr = (meNow?.field?.tomes) || [];
    (async () => {
      for (const cardId of cr.ruined) {
        const ti = tArr.findIndex(t => t && typeof t === "object" && t.id === cardId && t.persistent);
        if (ti < 0) continue;
        await updatePlayer(code, playerIdx, {
          [`field/tomes/${ti}`]: null,
          necropolis: [...(meNow?.necropolis || []), cardId],
        }).catch(() => {});
        showToast(`✕ ${getCard(cardId)?.name || "Your counter"} was answered and ruined.`, SD.red);
      }
    })();
  }, [match?.chainResult, isNPC, playerIdx, code]);

  // Resolve + clear — the bridge promise must NEVER be left hanging.
  const resolveResponseWindow = (result) => {
    const rw = responseWindowRef.current;
    if (!rw) return;
    setResponseWindow(null);
    try { rw.resolve(result); } catch {}
  };
  resolveResponseWindowRef.current = resolveResponseWindow;
  const handleWindowPass = () => resolveResponseWindow({ responded: false });
  const handleWindowSnooze = () => {
    snoozeTurnRef.current = responseWindowRef.current?.turnKey || null;
    resolveResponseWindow({ responded: false });
  };

  // ═══════════════════════════════════════════════════════════
  // THE CHAIN (C12, August 2026) — LIFO, unbounded, both lanes
  // ═══════════════════════════════════════════════════════════
  // A true counter is itself an activation, so it can be answered —
  // and that answer can be answered, until one side has no legal reply
  // or declines. ChainLaw.js owns WHICH cards may answer WHAT and HOW a
  // finished stack resolves; this section owns the WHO-IS-ASKED-NEXT.
  //
  // A LINK, the one shape the whole chain speaks in:
  //   { mine, cardId, ilk: "nihil"|"omnia", name, late, level, persistent }
  // Every link is already PAID FOR and MOVED by the time it lands in the
  // stack — fares stay paid at every depth, forever (canon). The runner
  // decides nothing about cards; it only alternates who is asked.
  //
  // Transport is irrelevant to it: the adversary is the NPC's reactor
  // in an NPC match and the Firebase handshake in PvP, and both answer
  // the identical REBUTTAL declaration.
  const isPersistentCounter = (c) =>
    !!c && (c.effectTag === "OPT" || c.effectTag === "OP2" || c.effectTag === "OP3");

  const linkFromOption = (opt, card) => ({
    mine: true, cardId: opt.cardId,
    ilk: opt.counter === "Nihil" ? "nihil" : "omnia",
    name: card?.name || "a counter",
    late: !!opt.late, level: card?.level || 3,
    persistent: isPersistentCounter(card),
  });

  const linkFromVerdict = (verdict, mine) => {
    const c = getCard(verdict.counterCardId);
    return {
      mine, cardId: verdict.counterCardId || null,
      ilk: verdict.counter,
      name: verdict.counterName || c?.name || "a counter",
      late: !!verdict.late,
      level: verdict.counterLevel || c?.level || 3,
      persistent: isPersistentCounter(c),
    };
  };

  const counterVerdict = (link) => ({
    responded: true, counter: link.ilk, late: !!link.late,
    counterName: link.name, counterCardId: link.cardId, counterLevel: link.level,
  });

  // Close the window UI but KEEP its promise — the chain is still
  // running and the declarer is still waiting on that resolve. The ref
  // is nulled by hand because it is assigned during render: the next
  // link's window must not see a window that is already gone.
  const closeWindowKeepingPromise = () => {
    const rw = responseWindowRef.current;
    if (!rw) return null;
    responseWindowRef.current = null;
    setResponseWindow(null);
    let done = false;
    return (result) => { if (done) return; done = true; try { rw.resolve(result); } catch {} };
  };

  // MY answer to the top link — the same Response Window UI, wearing
  // the chain's own headline. Materializes only when I hold a legal,
  // affordable, same-or-higher-level true counter RIGHT NOW (the
  // anti-prompt-fest law, chain edition) and honors a Turn snooze.
  const offerChainWindow = (top, depth) => {
    const answered = getCard(top.cardId);
    if (!answered) return Promise.resolve(null);
    const m = matchRef.current;
    const meNow = m?.players?.[playerIdx];
    if (!meNow || m?.status === "finished" || responseWindowRef.current) return Promise.resolve(null);
    const turnKey = `${m?.turn || 0}-${m?.activePlayer ?? 0}`;
    if (snoozeTurnRef.current === turnKey) return Promise.resolve(null);
    const options = trueCounterOptions({
      hand: meNow.hand || [],
      tomes: (meNow.field?.tomes) || [],
      khao: meNow.khao || 0, getCard,
      declaredLevel: top.level || answered.level || 2,
      isRebuttal: true,
    }).map(o => ({ ...o, counter: o.ilk }));
    if (options.length === 0) return Promise.resolve(null);
    return new Promise((resolve) => {
      setResponseWindow({
        action: { kind: "REBUTTAL", cardId: top.cardId },
        options, turnKey,
        actingName: answered.name,
        rebuttal: true,          // a link's own window never re-chains
        chainDepth: depth + 1,
        resolve: (r) => resolve(r?.responded && r?.counter ? linkFromVerdict(r, true) : null),
      });
    });
  };

  // THEIR answer to the top link — one declaration, either transport.
  const askAdversaryToRebut = async (top, depth) => {
    const action = { kind: "REBUTTAL", cardId: top.cardId, level: top.level, chainDepth: depth + 1 };
    const verdict = isNPC ? await requestReaction(action) : await declarePvP(action);
    if (!verdict?.responded || !verdict?.counter) return null;
    return linkFromVerdict(verdict, false);
  };

  // The runner. Ends when a side has no answer, declines, or the depth
  // dial trips (CHAIN_DEPTH_CAP is a runaway guard, not a rule — cards
  // and Khao are what really end a chain).
  const runChain = async (chain) => {
    chainActiveRef.current = true;
    try {
      while (chain.length < CHAIN_DEPTH_CAP) {
        // Tell the bridge the chain is alive — its failsafe measures
        // SILENCE, not elapsed time, so a long honest chain is never
        // yanked out from under either side (see ResponseBridge).
        noteWindowActivity();
        const top = chain[chain.length - 1];
        const link = top.mine
          ? await askAdversaryToRebut(top, chain.length)
          : await offerChainWindow(top, chain.length);
        noteWindowActivity();
        if (!link) break;
        chain.push(link);
      }
    } finally {
      chainActiveRef.current = false;
    }
    return chain;
  };

  // A link answered by an EFFECTIVE Omnia is ruined off the field —
  // but only a PERSISTENT counter has a field to fall from; one-shots
  // already rode to a Necropolis on their own lifecycle.
  // PvP never writes the adversary's node (two writers, one seat is
  // how a board tears): the declarer leaves a chainResult note and
  // each client ruins its own — see the chainResult effect.
  const settleChainRuins = async (chain) => {
    for (const link of ruinedLinks(chain)) {
      if (!link.persistent || !link.cardId) continue;
      if (!link.mine && !isNPC) continue;
      const idx = link.mine ? playerIdx : oppIdx;
      const seat = matchRef.current?.players?.[idx];
      const tArr = (seat?.field?.tomes) || [];
      const ti = tArr.findIndex(t => t && typeof t === "object" && t.id === link.cardId && t.persistent);
      if (ti >= 0) {
        await updatePlayer(code, idx, {
          [`field/tomes/${ti}`]: null,
          necropolis: [...(seat?.necropolis || []), link.cardId],
        });
      }
    }
  };

  // PvP only: tell the adversary which of THEIR persistent counters the
  // chain ruined. One writer (me, the declarer), one actor per card
  // (its owner) — see the chainResult effect below.
  const noteChainRuinsForPvP = async (chain) => {
    if (isNPC) return;
    const theirs = ruinedLinks(chain)
      .filter(l => !l.mine && l.persistent && l.cardId)
      .map(l => l.cardId);
    if (theirs.length === 0) return;
    await updateMatch(code, {
      chainResult: { by: playerIdx, seq: pvpSeqRef.current, ruined: theirs },
    }).catch(() => {});
  };

  // ── TRUE COUNTER (Nihil/Omnia vs a declared ACTIVATION) ──────
  // The counter's effect IS the counter — applyTomeEffect must NOT
  // run; the NPC engine consumes the verdict instead. Lifecycle per
  // canon: fare paid, then the counter ruins after use unless
  // persistent (OPT/OP2/OP3). Stale-index guards protect against any
  // state shift between window-open and click.
  const fireCounterFromWindow = async (opt) => {
    const card = getCard(opt.cardId);
    if (!card) return false;
    // ── C12: READ THE BOARD, NOT THE RENDER ───────────────────
    // At depth 3 of a chain this seat has ALREADY spent a card this
    // declaration, and the render prop that fed the first link is a
    // whole counter out of date. matchRef is the same fresh-read law
    // the responder window and the NPC's brain live by; `me` stays as
    // the fallback for the very first frame of a mount.
    const meNow = matchRef.current?.players?.[playerIdx] || me;
    const cost = card.khaoCost || 0;
    if (cost > 0 && !(await spendKhao(cost))) {
      showToast(`Not enough Khao for ${card.name}.`, SD.red);
      return false;
    }
    const field = JSON.parse(JSON.stringify(meNow?.field || {}));
    const tomes = [field.tomes?.[0] ?? null, field.tomes?.[1] ?? null, field.tomes?.[2] ?? null];
    const persistent = card.effectTag === "OPT" || card.effectTag === "OP2" || card.effectTag === "OP3";
    if (opt.source === "hand") {
      const hand = [...(meNow?.hand || [])];
      if (hand[opt.idx] !== opt.cardId) return false; // stale index — abort clean
      hand.splice(opt.idx, 1);
      const slotIdx = tomes.findIndex(t => t === null);
      if (persistent && slotIdx >= 0) {
        tomes[slotIdx] = { id: opt.cardId, faceDown: false, persistent: true };
        field.tomes = tomes;
        await updatePlayer(code, playerIdx, { hand, field });
      } else {
        // One-shot counter — fires straight from hand to the Necropolis.
        await updatePlayer(code, playerIdx, { hand, necropolis: [...(meNow?.necropolis || []), opt.cardId] });
      }
    } else {
      const t = tomes[opt.idx];
      if (!t || t.id !== opt.cardId) return false; // stale index — abort clean
      if (persistent) {
        tomes[opt.idx] = { ...t, faceDown: false, persistent: true };
        field.tomes = tomes;
        await updatePlayer(code, playerIdx, { field });
      } else {
        tomes[opt.idx] = null;
        field.tomes = tomes;
        await updatePlayer(code, playerIdx, { field, necropolis: [...(meNow?.necropolis || []), opt.cardId] });
      }
    }
    const isNihil = card.ilk === "Nihil";
    showToast(isNihil
      ? `◈ ${card.name} — Activation nullified!`
      : `✕ ${card.name} — Card countered and ruined!`, isNihil ? GM.inkGold : SD.red);
    return true;
  };

  // ── WINDOW FIRE — the player chose a reaction ─────────────────
  // Counters route through fireCounterFromWindow and carry a verdict
  // back to the NPC engine. Generic interrupts route through the
  // existing radial handlers (the whole job: pay, place/flip, apply
  // effect, Firebase writes) — the NPC then re-reads fresh state
  // before resolving its declared action.
  const handleWindowFire = async (opt) => {
    const rw = responseWindowRef.current;
    if (!rw) return;
    const card = getCard(opt.cardId);
    if (!card) { resolveResponseWindow({ responded: false }); return; }
    if (opt.counter) {
      const ok = await fireCounterFromWindow(opt);
      if (!ok) { resolveResponseWindow({ responded: false }); return; }
      const myLink = linkFromOption(opt, card);
      // ── THE ONE-WAY LAW IS LIFTED (C12, August 2026) ──────────
      // Wave 2 ran chains in ONE direction, and said so honestly: the
      // NPC could not answer here because runNPCTurn held stale local
      // hand/field drafts that would resurrect a card its own reactor
      // had just spent (the DUPE hazard). Wave 4 laid the re-base
      // foundation; NPC.js's REBASE LAW finished it this session —
      // every funnel that can hold a window re-bases from fresh
      // Firebase, and the turn's hand draft re-bases with it. The gate
      // condition the roadmap named is met, so the NPC may now answer
      // the player's counter, and be answered in turn, unbounded.
      //
      // TWO windows deliberately do NOT chain from here:
      //   • rw.rebuttal — this window IS a chain link; the runner that
      //     opened it owns what happens next (never recurse).
      //   • PvP — the DECLARER owns the chain (see declareAction).
      //     This seat is the defender: its verdict travels back as
      //     pendingResponse and the chain continues over the handshake.
      if (rw.rebuttal || !isNPC) { resolveResponseWindow(counterVerdict(myLink)); return; }
      const settle = closeWindowKeepingPromise();
      if (!settle) return;
      // The declarer is parked on `settle` — it must be called on EVERY
      // exit from here, including a throw, or the NPC engine waits out
      // its failsafe holding a duel that already happened. The counter
      // is paid for and moved by now, so a failed chain honors it.
      let chain = [myLink];
      try { chain = await runChain(chain); await settleChainRuins(chain); }
      catch (e) {
        console.warn("[Chain] broke mid-stack — honoring the counter:", e?.message);
        settle(counterVerdict(myLink));
        return;
      }
      // chain[0] is MY counter — it stands on an ODD chain (LIFO law).
      // When it was answered the declared action resolves after all,
      // but the board moved on BOTH sides, so responded:true sends the
      // NPC engine through its fresh-state re-read (and the attacker-
      // fizzle law) instead of resolving against a stale clone.
      // ── COUNTER HIERARCHY — late counters (L1/L2 vs a L3+ activation)
      // cannot pre-empt: the verdict tells the NPC engine to resolve the
      // effect FIRST, then force the activated card off the field.
      const stands = chainStands(chain.length);
      if (chain.length > 1) {
        showToast(stands
          ? `✦ The chain runs ${chain.length} deep — your ${card.name} stands!`
          : `✕ ${chain[chain.length - 1].name} answered your ${card.name} — the action resolves.`,
          stands ? SD.gold : SD.red);
      }
      settle(stands
        ? counterVerdict(chain[0])
        : { responded: true, counter: null, chained: chain.length });
      return;
    }
    if (opt.source === "hand") await handleInterrupt(opt.idx);
    else await handleInterruptFromField(opt.idx);
    resolveResponseWindow({ responded: true, counter: null });
  };

  // ═══════════════════════════════════════════════════════════
  // DECLARATION ENGINE (Waves 2 & 3, July 2026) — the mirror.
  // ═══════════════════════════════════════════════════════════
  // "No declared action resolves while your adversary still holds an
  // answer to it" now binds BOTH warlocks. Every response-worthy
  // PLAYER action calls declareAction() before resolving:
  //   • NPC matches (Wave 2): the NPC's reactive brain answers through
  //     the ResponseBridge reactor lane (NPC.js makeNpcReactor) —
  //     skill-gated counters, rebuttal chains, survival flips.
  //   • PvP matches (Wave 3): the declaration travels through Firebase
  //     (pendingAction / pendingResponse on the match node) and the
  //     adversary's client answers through the SAME responder impl
  //     that answers the NPC engine. One window system, two transports.
  // Verdict payload is identical to requestWindow's (ResponseBridge
  // header is the contract of record). Callers obey the same laws the
  // NPC engine obeys: nihil = effect voided (fare stays paid, card
  // lifecycle continues), omnia = the declared card is ruined, late =
  // effect first THEN eviction, generic = re-read fresh state before
  // resolving.
  const PVP_DECLARE_TIMEOUT_MS = 12000; // > the 5s window + real latency

  const declareAction = async (action) => {
    if (isFinished) return { responded: false };
    try {
      setAwaitingResponse({ kind: action?.kind });
      const verdict = isNPC ? await requestReaction(action) : await declarePvP(action);
      // ── THE CHAIN (C12, August 2026) — their counter is itself an
      // activation, so I may answer it, and they may answer that, all
      // the way down. Both transports run the identical stack; only
      // askAdversaryToRebut knows which one is in play. A REBUTTAL
      // declaration never opens a chain of its own — the runner that
      // sent it is already holding one.
      if (verdict?.responded && verdict?.counter && action?.kind !== "REBUTTAL") {
        const chain = await runChain([linkFromVerdict(verdict, false)]);
        if (chain.length > 1) {
          await settleChainRuins(chain);
          await noteChainRuinsForPvP(chain);
          // LIFO law: their depth-1 counter stands only on an ODD chain.
          if (!chainStands(chain.length)) {
            showToast(`✦ Rebutted${chain.length > 2 ? ` — ${chain.length} deep` : ""}! ${verdict.counterName || "The counter"} is voided — your action resolves.`, SD.gold);
            // Their counter is void, but cards moved on BOTH fields —
            // responded:true so the caller re-reads before resolving
            // (the same law every generic interrupt lives by).
            return { responded: true, counter: null, rebutted: true, chained: chain.length };
          }
          showToast(`✕ The chain runs ${chain.length} deep — ${verdict.counterName || "their counter"} stands.`, SD.red);
        }
      }
      return verdict || { responded: false };
    } finally {
      setAwaitingResponse(null);
    }
  };

  // ── The Wave 2 single-shot rebuttal window RETIRED here (C12) ──
  // It did one job — offer the player ONE answer to the NPC's counter —
  // and it did it with its own hand-rolled copy of the legality law.
  // offerChainWindow above does the same job at any depth, on either
  // transport, reading the law from ChainLaw. One law, one window.

  // ── Wave 3: PvP declaration — the Firebase handshake ──
  // Declarer writes matches/{code}/pendingAction, awaits a matching
  // matches/{code}/pendingResponse (delivered by the response-lane
  // effect below), then clears both nodes. A hard timeout guarantees a
  // vanished adversary can never hold the Solrend hostage. Seq stamps
  // are seat-salted so the two clients can never collide.
  //
  // ⚠ FIREBASE (roadmap C3): `pendingAction` and `pendingResponse`
  // are NEW match-node fields — whitelist them in the hardening pass.
  //
  // C12 (August 2026): PvP is no longer single-level. A REBUTTAL is
  // just another declaration over this same handshake — the declarer
  // holds the stack (declareAction's runChain), the defender answers
  // each link through its own responderImpl, and the round trips
  // alternate until someone has no answer. Nothing about the transport
  // changed; the payload just learned two fields (level, chainDepth).
  const settlePvPDeclare = (seq, verdict) => {
    const w = pvpAwaitRef.current;
    if (!w || w.seq !== seq) return;
    clearTimeout(w.timer);
    pvpAwaitRef.current = null;
    // The declarer owns the cleanup of every handshake node.
    updateMatch(code, { pendingAction: null, pendingResponse: null, pendingResponses: null }).catch(() => {});
    try { w.resolve(verdict || { responded: false }); } catch {}
  };
  const settlePvPDeclareRef = useRef(null);
  settlePvPDeclareRef.current = settlePvPDeclare;

  // `opts.as` — declare ON BEHALF OF another seat (the co-op boss, whose
  // brain runs inside this client and has no client of its own). The
  // payload then carries `by: thatSeat` so R136's answerer set is computed
  // for the BRAIN, and `proxyBy: playerIdx` so this client does not also
  // pick up the window through the Firebase lane (its own window is the
  // in-process one). `opts.targetSeat` overrides oppIdx for the same reason.
  const declarePvP = async (action, opts = {}) => {
    const seq = (++pvpSeqRef.current) + (playerIdx + 1) * 100000;
    // N-SEAT: stamp WHICH SEAT this declaration is aimed at. `targetSeat`
    // is whose board the action lands on; since R136 (anyone may answer)
    // it no longer decides WHO may answer — matchSeats.answerersFor does,
    // from `by` — but the responder still needs it to know whether the
    // swing is aimed at ITS board (aimedAtMe).
    const by = opts.as ?? playerIdx;
    const payload = { seq, by, targetSeat: opts.targetSeat ?? oppIdx, kind: action.kind, declaredAt: Date.now() };
    if (opts.as != null && opts.as !== playerIdx) payload.proxyBy = playerIdx;
    if (action.cardId !== undefined) payload.cardId = action.cardId;
    if (action.targetIdx !== undefined) payload.targetIdx = action.targetIdx;
    if (action.lyt !== undefined) payload.lyt = action.lyt;
    if (action.level !== undefined) payload.level = action.level;             // C12 chain law
    if (action.chainDepth !== undefined) payload.chainDepth = action.chainDepth;
    const p = new Promise((resolve) => {
      pvpAwaitRef.current = {
        seq, resolve,
        timer: setTimeout(() => settlePvPDeclareRef.current?.(seq, { responded: false }), PVP_DECLARE_TIMEOUT_MS),
      };
    });
    try {
      await updateMatch(code, { pendingResponse: null, pendingResponses: null, pendingAction: payload });
    } catch {
      settlePvPDeclare(seq, { responded: false });
    }
    return p;
  };
  declarePvPRef.current = declarePvP;

  // ═══════════════════════════════════════════════════════════
  // C13 — THE SHIELD TOMES (canon ruling, caP'n, July 10 2026)
  // ═══════════════════════════════════════════════════════════
  // Lyt Footed (beings), Radiant Aura (cards), Sword & Shield of
  // Nihil (cards, L3) are "hold one attack" wards:
  //   • Activation picks ONE of my cards; the target gains a ward
  //     that nullifies the FIRST attack against it — NO expiry, it
  //     holds across turns until consumed ("that face-up card used
  //     3 turns ago still works if nothing attacked until now").
  //   • The tome itself is a one-shot: activated → Necropolis (the
  //     caller's lifecycle handles that — Necropolis law).
  //   • Being wards stamp the EXISTING `nullifyNextAttack` slot flag —
  //     already consumed by both player attack paths, the NPC's
  //     strikeBeing, and (new, this session) targeted being-ruins.
  //   • Card wards stamp the EXISTING `nullifyFirstRuin` tome flag —
  //     already consumed by attemptRuinOppTome, the tome-attack path,
  //     and the NPC's ruinPlayerTomeSlot. One mechanic, one costume
  //     closet, ZERO new consumer code on the strike paths.
  //   • ATTACKER-RUIN CLAUSE ("if the attacking card is an Omnia Tome
  //     [LF/RA] / a Tome [SSoN], ruin it"): ARMED, NOT STAMPED. Every
  //     current attacker Tome is a one-shot that rides to the
  //     Necropolis on firing anyway — the clause only bites when a
  //     PERSISTENT (OPT/ATV) attacker Tome exists (caP'n: "prevents
  //     it from sticking around"). Consumer-before-setter law: we
  //     wire it the day such a card is born. Ledgered in C13's note.
  //   • IN-WINDOW AUTO-TARGET: fired inside a Response Window that
  //     answers a declared attack, the ward lands on the card UNDER
  //     ATTACK — no picker, no race with the declarer's fresh-state
  //     re-read. That's the whole play: flip it at the exact moment.
  //     (The NPC's tome strikes always hunt the MRF-required tome, so
  //     a tome-attack window without an index auto-derives its target
  //     from getRequiredTarget on my own field.)
  const ctx = {
    code, playerIdx, oppIdx, match, matchRef,
    factionStatsRef, responseWindowRef, me, opponent,
    showToast, setTargetPicker, setSgModal, updatePlayer,
  };
  const fireShieldTome = (...args) => resolver_fireShieldTome(...args, ctx);

  

  // ⭐ THE TILL — R33, THEN R96, AND WHAT R96 LEFT STANDING.
  //
  // HISTORY, kept because the shape of the bug explains the shape of the
  // law. balance.mjs found `if (current < amount) return false` on both
  // seats in August 2026: it let an EXACT payment through — pay 10 with
  // 10, land on 0, and nothing ended the Solrend, because every endMatch
  // in this file hangs off an attack or an effect RESOLVING and paying a
  // cost is neither. The seat kept playing at zero Anima.
  //
  // R96 · THE TILL LAW answered it at the source instead of at the exit:
  // a VOLUNTARY payment may not take you to 0 at all. `canPayVoluntary`
  // is that law, and it is why the ordinary path can no longer reach the
  // R33 branch below — `current > amount` guarantees `next >= 1`.
  //
  // ⚠ AND THEN R96 GREW ITS ONE EXEMPTION, which is the only reason the
  // R33 branch is still live code and not dead code:
  //   THE GRAVESHIFT FARE (R41) IS EXEMPT. R41 makes the fare exactly 10
  //   and R43 says you cannot lose by running out of cards, so R43's own
  //   remedy has to stay payable by a seat holding exactly 10 — even
  //   though paying it zeroes them. `canPayVoluntary(c, a, {exempt:true})`
  //   is how that is asked, and it is asked NOWHERE ELSE. Everything
  //   else must clear the till.
  // So: exempt payment → `next` can be 0 → the R33 branch fires and the
  // Solrend ends, correctly, on the payer. Non-exempt payment → refused
  // before it happens, and the branch is unreachable by construction.
  //
  // ⚠ NO CONFIRM PROMPT, DELIBERATELY. A "this will kill you, continue?"
  // dialog is a design decision and this is a rule. The low-Anima
  // warning already lights at 10 and below, the cost is printed on every
  // card, and R33 is in the Rulebook. If caP'n wants the prompt it goes
  // here and it is a small change.
  const spendAnima = async (amount, { exempt = false } = {}) => {
    if (amount <= 0) return true;
    // ⚠ THE LIVE SEAT, NOT THE RENDER CLOSURE. `me` is a snapshot taken
    // at the last paint, and this till is called in sequences — two
    // Devotion contracts standing on one board, a fare followed by a
    // fare. Computing `next` off one stale `me.anima` twice writes the
    // SAME result twice and the second payment silently refunds the
    // first: the seat pays once for two things. THE REBASE LAW, at the
    // one place in this file that decides whether a seat can afford
    // something. `livePlayer` falls back to `me` when nothing is
    // mounted, so a headless caller behaves exactly as before.
    const meNow = livePlayer(playerIdx, me);
    const current = meNow?.anima ?? STARTING_ANIMA;
    // R96 — THE TILL LAW, with its single R41 exemption threaded through.
    // Refuses BEFORE any write, so a payment that cannot be made never
    // partially happens.
    if (!canPayVoluntary(current, amount, { exempt })) return false;
    const next = current - amount;
    // R100 — EVERY payment that reaches this till is voluntary by
    // definition; that is what the till IS. So every one of them is
    // SELF, and none of them may ever feed a Prophecy.
    await updatePlayer(code, playerIdx, { anima: next, ...buildSelfDimUpdate(meNow, amount) });
    // R33 — reachable ONLY on an exempt (Graveshift) payment; see the
    // banner. Not dead code, and not a lie about non-exempt payments
    // either: those cannot get here.
    if (next <= 0) { await endMatch(code, oppIdx, playerIdx) /* AUDIT FIX 2026-08-22: MY anima just hit 0 — the loser is ME, explicitly, never guessed via getTargetIdx(oppIdx) */; }
    return true;
  };

  // ═══════════════════════════════════════════════════════════
  // 🩸 R96 · THE ONE DOOR FOR A CARD'S SELF-DIM
  // ═══════════════════════════════════════════════════════════
  // caP'n, 2026-08-26, verbatim:
  //
  //   *"It is a payment and yes you cannot end yourself that way."*
  //
  // `spendAnima` above is the till for FARES — a cost you choose to
  // pay at a price list. This is the door for the other shape: a card
  // whose printed effect dims its OWN controller's Anima (Eternal
  // Nightmare −4, Relentless −5, Apocalypse −8, Law and Order −1,
  // Restless −5, Equivalent Exchange −4, Pay Day −1, Lyt Hearted −1,
  // Superque −1, Nothing Left −1, Radiant Armor −1, Magma Carta −4)
  // and the Devotion legs, which hand Anima to the adversary.
  //
  // ⚠ WHY THIS EXISTS AT ALL. Eleven of those twelve did a raw
  // `Math.max(0, anima - n)` straight into updatePlayer. Two things
  // were wrong with that and only one of them was obvious:
  //   · The seat could pay itself to exactly 0, and NOTHING ENDED THE
  //     DUEL — every endMatch in this file hangs off an attack
  //     resolving, and a self-dim is not an attack. The board simply
  //     sat there at 0 Anima until something else happened to it.
  //   · R96 was already the law and already had an implementation.
  //     Magma Carta called it. The other eleven did not, so the rule
  //     was true of one card.
  // The ruling closes both: the ACTIVATION IS REFUSED when it would
  // take the controller to 0 or below, which means the unhandled zero
  // is now unreachable rather than merely unhandled.
  //
  // Reads the LIVE seat, not the render closure — a self-dim can fire
  // mid-chain after a response has already moved the Anima, and
  // refusing on a stale total would decline a legal activation.
  const tillRefusesSelfDim = (...args) => resolver_tillRefusesSelfDim(...args, ctx);

  // Hope (Impera p_dg0_1) — "Gain +1 Anima, whenever a new Time Being enters
  // combat." Called after a Time Being lands on MY side via Transcend, Ascend,
  // or Resurrection. Checks if I have Hope attached to any of my beings;
  // if yes, grants me +1 Anima. Must be called with the UPDATED field already
  // reflecting the newly-placed being so the soul check is current.
  // Shadow Gambling streak tracker — called from every SG Tome onResolve.
  // Win → streak + 1. Loss → streak = 0. Also:
  // - Resolves any pendingBet (Underdog's Howl): win → add bet card to hand,
  //   loss → send bet card to Necropolis. Bet is cleared either way.
  // - Ace of Shades prophecy check triggers automatically on next state update.
  // ── AUDIT FIX (July 19 2026): Casino Royale's fortune. Reads the
  // live nextSGAutoWin flag; if armed, clears it and returns true so
  // the NEXT Shadow Gambling ("except Casino Royale", per card text)
  // resolves as an automatic win. Consumed exactly once.
  const consumeSGAutoWin = (...args) => resolver_consumeSGAutoWin(...args, ctx);

  const recordShadowGamblingOutcome = (...args) => resolver_recordShadowGamblingOutcome(...args, ctx);

  const triggerHopeIfTimeEntry = (...args) => resolver_triggerHopeIfTimeEntry(...args, ctx);

  // ═══════════════════════════════════════════════════════════
  // PRIMERAS — THE STAMPS PRESSED (H22, July 2026)
  // ═══════════════════════════════════════════════════════════
  // This used to compensate for an axis inversion in cards.js — the
  // Family-A word was stored under `ilk` and the Family-B word under
  // `school`, opposite to PRIMERAS. Fixed in the data on 2026-08-25, so
  // the axis now simply names the field.
  const primeraWordFor = (card, axis) => card?.[axis];

  // ══════════════════════════════════════════════════════════
  // R79 + R107 · THE STAMP FARE — ONE POOL, NEVER BOTH
  // ══════════════════════════════════════════════════════════
  // caP'n, 2026-08-26, asked whether a fare could be part-Khao and
  // part-Anima:
  //
  //   *"no, that was something you must've added hehe — no mixing for
  //   any reason at all unless the card tells you, like U'hltis etc."*
  //
  // ⚠ THIS FUNCTION WAS THE MIXING, AND IT IS A LIVE BALANCE CHANGE.
  // The Primera stamp was the ONLY fare in Terror that could be split;
  // `payMix` above drained Khao and topped the remainder up out of
  // Anima, rules.js said "mixable" in as many words, and NPC.js grew a
  // whole private till (npcPayPrimeraFare) because the shared either/or
  // helper "would decline a fare the rules say is payable". All three
  // are wrong as of this ruling. A seat on 2 Khao and 40 Anima can
  // still press a Level 3 stamp — out of Anima. A seat on 2 Khao and 2
  // Anima can no longer press it at all, and that used to be free.
  //
  // WHAT SURVIVED. The CHOICE survives, and it is now the whole
  // mechanic rather than a special case: when both pools can carry the
  // fare the player picks, because Anima is a life total and spending
  // it must be deliberate. When only one can, it is taken silently.
  // When neither can, the press is refused — that outcome did not
  // exist before this ruling.
  //
  // `farePoolsFor` is rules.js's ONE answer to "which pools can carry
  // this fare", and it carries R96's till law on the Anima leg, so the
  // strictly-more-than-the-fare rule is not re-derived here.
  const payPrimeraCost = (cost, word, then) => {
    if (cost <= 0) { then(); return; }
    const pools = farePoolsFor(cost, {
      khao: me?.khao ?? 0,
      anima: me?.anima ?? STARTING_ANIMA,
    });
    if (pools.length === 0) {
      showToast(`${word} costs ${cost} — pay it ENTIRELY in Khao or ENTIRELY in Anima, never split. Neither pool covers it.`, SD.red);
      return;
    }
    if (pools.length === 1) {
      (async () => {
        const ok = pools[0] === "anima" ? await spendAnima(cost) : await spendKhao(cost);
        if (ok) then(); else showToast("Payment failed.", SD.red);
      })();
      return;
    }
    setTargetPicker({
      title: `◈ ${word} Primera — pay ${cost} with…`,
      options: [
        { id: "khao", label: `${cost} Khao` },
        { id: "anima", label: `${cost} Anima` },
      ],
      onPick: async (pick) => {
        setTargetPicker(null);
        const ok = pick.id === "anima" ? await spendAnima(cost) : await spendKhao(cost);
        if (ok) then(); else showToast("Payment failed.", SD.red);
      },
    });
  };

  // R81 + R82 bookkeeping — the word is spent for the whole field, the
  // being is spent for the Turn. Dragon is ONE word on both axes: one
  // key in `words` covers Ilk-Dragon and School-Dragon alike.
  const markPrimeraSpent = (word, slotIdx, extra = {}) => {
    const prev = primeraTurnRef.current || EMPTY_PRIMERA_TURN;
    const next = {
      ...prev,
      words: { ...prev.words, [word]: true },
      slots: { ...prev.slots, [slotIdx]: true },
      ...(extra.armBeast ? { beastArmed: { ...prev.beastArmed, [slotIdx]: true } } : {}),
    };
    // The ref is normally refreshed on render; assign it HERE too so two
    // presses inside one tick cannot both read a pre-press ledger.
    primeraTurnRef.current = next;
    setPrimeraTurn(next);
    // ── THE LEDGER GOES ON THE RECORD (R79/R80/R81/R83) ──────────
    // GameStateValidator listed R81 as unwatchable because "the per-BEING
    // stamp ledger is a Turn-local object in the Turn runner, never
    // written to the match" — which was true of BOTH drivers, so nothing
    // in Terror checked the stamp gates on either seat. This is the write
    // that makes them observable. One shared contract:
    //   primeraLedger: { turn, words: { "<Word>": true }, slots: { "<i>": true } }
    //
    // ⭐ THE TURN IS STAMPED, NOT CLEARED. A ledger is only binding for the
    // half-turn named in its own `turn` field; a ledger whose turn ≠ the
    // match's is STALE and reads as empty to a consumer — but it is still
    // THERE, so "this seat pressed nothing this Turn" and "this seat's
    // ledger was never written" stay distinguishable. Clearing it on turn
    // change would have destroyed exactly that evidence.
    //
    // ⚠ FOR WHOEVER WRITES THE READER: `slots` is keyed by slot INDEX, so
    // Firebase RTDB will store {"0":true,"1":true} as an ARRAY. Read it as
    // `ledger.slots?.[i]`, which is correct against both shapes, and never
    // as Object.keys().length.
    // beastArmed stays local on purpose — it is a rider's armed flag, not
    // an R81/R82 allowance, and does not belong in a shared contract.
    updatePlayer(code, playerIdx, {
      primeraLedger: { turn: match?.turn || 0, words: next.words, slots: next.slots },
    }).catch(e => console.warn("[R81 ledger] write failed:", e?.message));
  };

  // Direct Anima dim from a stamp (Phantom, and the Beast rider's
  // retroactive bite). Fresh read, wound-crack trackers, win check.
  const primeraDimOppAnima = async (amount, sourceLabel) => {
    const oppFresh = matchRef.current?.players?.[oppIdx] || opponent;
    const newAnima = Math.max(0, (oppFresh?.anima ?? STARTING_ANIMA) - amount);
    await updatePlayer(code, oppIdx, { anima: newAnima, ...buildAnimaLossUpdate(oppFresh, amount) });
    showToast(`${sourceLabel} — adversary's Anima dimmed by ${amount}!`, SD.red);
    if (newAnima <= 0) { await endMatch(code, playerIdx); return true; }
    return false;
  };

  // THE PRESS. slotIdx = the user's slot; axis = canon "ilk" | "school".
  // Sequence: gate (R78–R82, one chokepoint, R-UNIVERSAL) → effect
  // resolvability pre-check (a press that cannot resolve spends NOTHING
  // — dial) → payment → mark word+being spent → resolve.
  const handlePressPrimera = (slotIdx, axis) => {
    if (!me) return;
    const slot = me.field?.esclings?.[slotIdx];
    const card = getCard(slot?.escendant);
    if (!card || card.type !== "Escendant") return;
    const word = primeraWordFor(card, axis);
    const stampDef = PRIMERAS?.[axis]?.[word];
    if (!word || !stampDef) { showToast(`${word || "This word"} bears no ${axis === "ilk" ? "Ilk" : "School"} stamp.`, GM.vellumFaint); return; }
    const pt = primeraTurnRef.current;
    // R81/R82 SURVIVE A REFRESH TOO. Same hole as the four per-Turn
    // allowances: `primeraTurnRef` is local, so an F5 mid-Turn handed the
    // player every stamp back. The ledger this file already writes is the
    // durable half — read it, and OR the two together so the local latch
    // still covers the frame before the write lands.
    const ledgerNow = meRecord?.primeraLedger;
    const ledgerLive = ledgerNow?.turn === (match?.turn || 0) ? ledgerNow : null;

    // ── SEAL: universal Primera gate (R78–R82) — the illegal press
    // explains itself through the verdict reason, nothing bespoke here.
    {
      const v = enforce("PRIMERA", gateCtx(), {
        word, axis, level: card.level || 1,
        wordUsedThisTurn: !!pt.words[word] || !!ledgerLive?.words?.[word],
        beingUsedThisTurn: !!pt.slots[slotIdx] || !!ledgerLive?.slots?.[slotIdx],
        isOwnTurn: myTurn,
        inResponseWindow: !!responseWindowRef.current,
      });
      if (!v.valid) { showToast(v.reason || "Cannot fire that Primera.", SD.red); return; }
    }

    const cost = PRIMERA_COST[`L${card.level || 1}`] || (card.level || 1);
    const axisLabel = axis === "ilk" ? "Ilk" : "School";

    // ── THE EIGHT ─────────────────────────────────────────────
    // Canon Ilk axis — Dragon / Beast / Machine / Humanoid
    if (axis === "ilk" && word === "Dragon") {
      // "Reduce the user's Lyf by 1; add 1 to its Lyt for the Turn."
      payPrimeraCost(cost, word, async () => {
        markPrimeraSpent(word, slotIdx);
        const field = JSON.parse(JSON.stringify(me?.field || {}));
        const s = field.esclings[slotIdx] || {};
        const curLyf = s.currentLyf !== null && s.currentLyf !== undefined ? s.currentLyf : (card.lyf ?? 0);
        field.esclings[slotIdx] = { ...s, currentLyf: Math.max(0, curLyf - 1), primeraLytBuff: (s.primeraLytBuff || 0) + 1 };
        await updatePlayer(code, playerIdx, { field });
        showToast(`◆ Dragon (${axisLabel}) — ${card.name} trades 1 Lyf for +1 Lyt this Turn!`, STAMP_COLORS.Dragon);
      });
      return;
    }
    if (axis === "school" && word === "Dragon") {
      // "Reduce the user's Lyt by 1; add 1 to its Lyf for the Turn."
      payPrimeraCost(cost, word, async () => {
        markPrimeraSpent(word, slotIdx);
        const field = JSON.parse(JSON.stringify(me?.field || {}));
        const s = field.esclings[slotIdx] || {};
        field.esclings[slotIdx] = { ...s, primeraLytBuff: (s.primeraLytBuff || 0) - 1, primeraLyfBuff: (s.primeraLyfBuff || 0) + 1 };
        await updatePlayer(code, playerIdx, { field });
        showToast(`◆ Dragon (${axisLabel}) — ${card.name} trades 1 Lyt for +1 Lyf this Turn!`, STAMP_COLORS.Dragon);
      });
      return;
    }
    if (word === "Beast") {
      // "If the user dims the adversary's Anima this Turn, dim it by 1
      // more." Covers BOTH orders (dial): already dimmed → the extra bite
      // lands NOW; not yet → the rider arms and the next Anima dim by
      // this being this Turn carries +1 (consumed once).
      payPrimeraCost(cost, word, async () => {
        if (animaDimmedSlotsRef.current.has(slotIdx)) {
          markPrimeraSpent(word, slotIdx);
          await primeraDimOppAnima(1, `◆ Beast — ${card.name} bites deeper`);
        } else {
          markPrimeraSpent(word, slotIdx, { armBeast: true });
          showToast(`◆ Beast armed — ${card.name}'s next Anima dim this Turn bites 1 deeper!`, STAMP_COLORS.Beast);
        }
      });
      return;
    }
    if (word === "Machine") {
      // "Search your Grimoire for 1 Beb and add it to your hand, free.
      // Shuffle after. Does not consume your Turn's draw."
      //
      // ── R08 · A CARD EFFECT MAY SEARCH ON THE OPENING CYCLE ──
      // caP'n, 2026-08-26: *"brilliant distinction yes, cards are
      // exempt from the first turn search rule"*. This stamp is the
      // card's own printed power, not the seat reaching for the Books,
      // so it resolves on the opening Cycle exactly as R09 already said
      // activation must. gateEffectSearch is asked out loud rather than
      // the exemption being left as an ABSENCE of a gate call — an
      // absence is indistinguishable from an oversight, and the next
      // person to widen R08 needs to find this line.
      //
      // ⚠ THE SEARCH BAN AND THE ATTACK BAN ARE DIFFERENT BANS. Machine
      // is exempt from the first; the Phantom and Beast stamps are
      // still refused by the second, up at the PRIMERA gate above, and
      // nothing here touches that.
      {
        const v = gateEffectSearch(gateCtx(), "GRIMOIRE");
        if (!v.valid) { showToast(v.reason || "Cannot search the Grimoire.", SD.red); return; }
      }
      const pool = Object.keys(me?.grimoire || {}).filter(id => {
        const c = getCard(id); return c?.type === "Escendant" && c?.level === 1;
      });
      if (pool.length === 0) { showToast("No Bebs in your Grimoire — the Machine finds nothing.", GM.vellumFaint); return; }
      payPrimeraCost(cost, word, () => {
        markPrimeraSpent(word, slotIdx);
        setTargetPicker({
          title: "◆ Machine Primera — Choose a Beb from your Grimoire",
          cards: pool,
          onPick: async (chosen) => {
            const pickId = typeof chosen === "string" ? chosen : chosen?.id;
            if (!pickId) return;
            const newGrimoire = { ...(me?.grimoire || {}) };
            if (newGrimoire[pickId] > 1) newGrimoire[pickId]--; else delete newGrimoire[pickId];
            const newHand = [...(me?.hand || []), pickId];
            await updatePlayer(code, playerIdx, { hand: newHand, grimoire: newGrimoire });
            // NOT setDrawnThisTurn — canon dial: free searches don't consume the draw
            showToast(`◆ Machine — ${getCard(pickId)?.name} found, free!`, STAMP_COLORS.Machine);
            setTargetPicker(null);
            if (newHand.length > HAND_LIMIT) setForcedDiscardModal(true); // R10
          },
        });
      });
      return;
    }
    if (word === "Humanoid") {
      // "Search your Luxmoire for 1 Level 1 Impera and Train it onto the
      // user, free. Matching laws still apply. Does not consume your
      // Turn's draw." (R71: one Impera per level — L1 slot must be open.)
      //
      // ── R08 · A CARD EFFECT MAY SEARCH — see the Machine branch.
      // Same ruling (caP'n, 2026-08-26), other Book: this stamp digs
      // through the Luxmoire and is exempt from the opening-Cycle
      // search ban because a card is doing it, not the player.
      {
        const v = gateEffectSearch(gateCtx(), "LUXMOIRE");
        if (!v.valid) { showToast(v.reason || "Cannot search the Luxmoire.", SD.red); return; }
      }
      const soul = slot?.soul || [];
      const hasL1Impera = soul.some(id => { const sc = getCard(id); return sc?.type === "Impera" && sc?.level === 1; });
      if (hasL1Impera) { showToast(`${card.name} already holds a Level 1 Impera — one of each level, never two.`, SD.red); return; }
      const pool = Object.keys(me?.luxmoire || {}).filter(id => {
        const c = getCard(id);
        return c?.type === "Impera" && c?.level === 1 && c?.school === card.school && c?.ilk === card.ilk;
      });
      if (pool.length === 0) { showToast("No matching Level 1 Impera in your Luxmoire.", GM.vellumFaint); return; }
      payPrimeraCost(cost, word, () => {
        markPrimeraSpent(word, slotIdx);
        setTargetPicker({
          title: `◆ Humanoid Primera — Train a Level 1 Impera onto ${card.name}`,
          cards: pool,
          onPick: async (chosen) => {
            const pickId = typeof chosen === "string" ? chosen : chosen?.id;
            if (!pickId) return;
            const newLuxmoire = { ...(me?.luxmoire || {}) };
            if (newLuxmoire[pickId] > 1) newLuxmoire[pickId]--; else delete newLuxmoire[pickId];
            const field = JSON.parse(JSON.stringify(me?.field || {}));
            const s = field.esclings[slotIdx] || {};
            field.esclings[slotIdx] = { ...s, soul: [...(s.soul || []), pickId] };
            await updatePlayer(code, playerIdx, { field, luxmoire: newLuxmoire });
            showToast(`◆ Humanoid — ${getCard(pickId)?.name} Trained onto ${card.name}, free!`, STAMP_COLORS.Humanoid);
            setTargetPicker(null);
          },
        });
      });
      return;
    }
    // Canon School axis — Phantom / Time / Space
    if (word === "Phantom") {
      // "Dim the adversary's Anima directly by 1, bypassing all beings."
      payPrimeraCost(cost, word, async () => {
        markPrimeraSpent(word, slotIdx);
        await primeraDimOppAnima(1, `◈ Phantom — ${card.name} reaches through`);
      });
      return;
    }
    if (word === "Time") {
      // "Resurrect 1 Beb from your Necropolis at no cost. Still counts
      // toward that being's Resurrection tally." (R44 every-method-counts;
      // dial: does NOT consume the once-per-Turn paid Resurrection.)
      // R85 EXEMPT — this is a CARD EFFECT, not the paid rite, so a
      // Graveshifted Beb is still reachable here. That exemption is the
      // whole shape of caP'n's rule: after a Graveshift the passive door
      // closes and the card-effect door stays open.
      // Dedupe by BASE so two sealed Taez read as one row, offering the
      // copy with the most returns left.
      const resurrectedCards = me?.resurrectedCards || [];
      const _timeBest = new Map();
      (me?.necropolis || []).forEach(id => {
        const c = getCard(id);
        if (c?.type !== "Escendant" || c?.level !== 1) return;
        const used = resTally(resurrectedCards, id);
        if (used >= (RESURRECT_LIMIT.L1 ?? 3)) return;
        const b = baseId(id);
        const prev = _timeBest.get(b);
        if (!prev || used < prev.used) _timeBest.set(b, { id, used });
      });
      const candidates = [..._timeBest.values()].map(v => v.id);
      if (candidates.length === 0) { showToast("No Beb in your Necropolis can still return (tally or none there).", GM.vellumFaint); return; }
      const emptySlots = (me?.field?.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => !s?.escendant && s?.escling);
      if (emptySlots.length === 0) { showToast("No empty Escling slots to resurrect onto.", SD.red); return; }
      payPrimeraCost(cost, word, () => {
        markPrimeraSpent(word, slotIdx);
        setTargetPicker({
          title: "◈ Time Primera — Resurrect a Beb, free",
          cards: candidates,
          onPick: async (chosen) => {
            const pickId = typeof chosen === "string" ? chosen : chosen?.id;
            const target = getCard(pickId);
            if (!target) return;
            // ── SEAL: same RESURRECT gate every method passes (R44 tally, free fare)
            const v = enforce("RESURRECT", gateCtx(), {
              fromLevel: 0, toLevel: 1,
              resurrectionsThisSolrend: resTally(resurrectedCards, pickId),
              cardName: target.name, free: true,
            });
            if (!v.valid) { showToast(v.reason || "Cannot resurrect.", SD.red); setTargetPicker(null); return; }
            const placeBeb = async (destIdx) => {
              const field = JSON.parse(JSON.stringify(me?.field || {}));
              field.esclings[destIdx] = {
                ...(field.esclings[destIdx] || {}),
                escendant: pickId, soul: [], soulUsed: {}, heretical: null, fallen: false, fallenTurn: null,
                currentLyf: target.lyf ?? null, attackedThisTurn: false, intangible: false,
              };
              const newNecro = [...(me?.necropolis || [])];
              const ni = newNecro.indexOf(pickId);
              if (ni >= 0) newNecro.splice(ni, 1);
              const meLiveP = matchRef.current?.players?.[playerIdx] || me;
              const liveAnimaP = meLiveP?.anima ?? STARTING_ANIMA;
              const hopeAnima = triggerHopeIfTimeEntry(target, field, liveAnimaP);
              await updatePlayer(code, playerIdx, {
                field, necropolis: newNecro,
                ...(hopeAnima !== liveAnimaP ? { anima: hopeAnima } : {}),
                resurrectedCards: [...resurrectedCards, pickId], // R44 — EVERY method marks the tally
              });
              showToast(`◈ Time — ${target.name} Resurrected, free!`, STAMP_COLORS.Time);
              setTargetPicker(null);
            };
            if (emptySlots.length === 1) { await placeBeb(emptySlots[0].i); return; }
            setTargetPicker({
              title: `◈ Resurrect ${target.name} — Choose slot`,
              options: emptySlots.map(({ s, i }) => ({ id: i, label: `Slot ${i + 1} (${getCard(s.escling)?.name || "Escling"})` })),
              onPick: async (slotPick) => { await placeBeb(slotPick.id); },
            });
          },
        });
      });
      return;
    }
    if (word === "Space") {
      // "Ascend 1 Level 2 being from your Grimoire over a matching Level 1
      // you control, free. Does not consume your Turn's one Ascend."
      // "Matching" = Natural (School + Ilk both shared) — dial.
      const l1Slots = (me?.field?.esclings || []).map((s, i) => ({ s, i }))
        .filter(({ s }) => { const c = getCard(s?.escendant); return c?.type === "Escendant" && c?.level === 1; });
      const pool = Object.keys(me?.grimoire || {}).filter(id => {
        const c = getCard(id);
        if (c?.type !== "Escendant" || c?.level !== 2) return false;
        return l1Slots.some(({ s }) => sharedTraits(c, getCard(s.escendant)) === 2);
      });
      if (pool.length === 0) { showToast("No Level 2 in your Grimoire matches a Level 1 you control.", GM.vellumFaint); return; }
      payPrimeraCost(cost, word, () => {
        markPrimeraSpent(word, slotIdx);
        setTargetPicker({
          title: "◈ Space Primera — Ascend a Level 2 from your Grimoire, free",
          cards: pool,
          onPick: async (chosen) => {
            const pickId = typeof chosen === "string" ? chosen : chosen?.id;
            const l2 = getCard(pickId);
            if (!l2) return;
            const matches = l1Slots.filter(({ s }) => sharedTraits(l2, getCard(s.escendant)) === 2);
            const placeL2 = async (destIdx) => {
              const field = JSON.parse(JSON.stringify(me?.field || {}));
              const s = field.esclings[destIdx] || {};
              // CHAIN — soul the outgoing L1 so descents can find it (handleAscend law)
              const prevSoul = s.soul || [];
              const newSoul = s.escendant && !prevSoul.includes(s.escendant) ? [...prevSoul, s.escendant] : prevSoul;
              field.esclings[destIdx] = { ...s, escendant: pickId, soul: newSoul, currentLyf: l2.lyf ?? null, deviant: s.deviant || false, natural: !s.deviant };
              const newGrimoire = { ...(me?.grimoire || {}) };
              if (newGrimoire[pickId] > 1) newGrimoire[pickId]--; else delete newGrimoire[pickId];
              const meLiveS = matchRef.current?.players?.[playerIdx] || me;
              const liveAnimaS = meLiveS?.anima ?? STARTING_ANIMA;
              const hopeAnima = triggerHopeIfTimeEntry(l2, field, liveAnimaS);
              await updatePlayer(code, playerIdx, { field, grimoire: newGrimoire, ...(hopeAnima !== liveAnimaS ? { anima: hopeAnima } : {}) });
              // NOT setAscendedThisTurn — canon dial: doesn't consume the one-Ascend
              showToast(`◈ Space — ${l2.name} Ascends from the Grimoire, free!`, STAMP_COLORS.Space);
              setTargetPicker(null);
            };
            if (matches.length === 1) { await placeL2(matches[0].i); return; }
            setTargetPicker({
              title: `◈ Ascend ${l2.name} — over which Level 1?`,
              options: matches.map(({ s, i }) => ({ id: i, label: `${getCard(s.escendant)?.name} (Slot ${i + 1})` })),
              onPick: async (slotPick) => { await placeL2(slotPick.id); },
            });
          },
        });
      });
      return;
    }
    showToast(`${word} has no engine effect yet — tell V'ael.`, GM.vellumFaint);
  };

  // Builds the CardFace `stamps` prop for one of MY beings — the live
  // legality snapshot the seals render from. Spent = pressed-in grey;
  // locked = dim (not my Turn / can't afford) but STILL tappable so the
  // illegal press can explain itself through the gate's reason.
  const stampsFor = (slot, slotIdx) => {
    const card = getCard(slot?.escendant);
    if (!card || card.type !== "Escendant") return null;
    const cost = PRIMERA_COST[`L${card.level || 1}`] || (card.level || 1);
    // R79 + R107 — ONE POOL OR THE OTHER, never the sum. This line read
    // `khao + anima >= cost` and was the affordability half of the same
    // mixing bug payPrimeraCost carried: it lit a stamp the till would
    // then refuse to pay for out of either pool alone.
    const afford = farePoolsFor(cost, { khao: me?.khao ?? 0, anima: me?.anima ?? STARTING_ANIMA }).length > 0;
    const build = (axis) => {
      const word = primeraWordFor(card, axis);
      const def = PRIMERAS?.[axis]?.[word];
      if (!word || !def) return null;
      const spent = !!primeraTurn.words[word] || !!primeraTurn.slots[slotIdx];
      const state = spent ? "spent" : (!myTurn || !afford || !!responseWindowRef.current) ? "locked" : "ready";
      return {
        word,
        color: STAMP_COLORS[word] || GM.inkGoldBright,
        state,
        onPress: () => handlePressPrimera(slotIdx, axis),
      };
    };
    return { ilk: build("ilk"), school: build("school") };
  };

  const handleTranscend = async (slotIdx, cardIdx, deviant = false) => {
    // R108 — once the Attack phase is declared, no more cards may be
    // played. See THE PHASE GATE above gateCtx.
    if (phaseBars("TRANSCEND")) return;
    setMulliganAvailable(false);
    const hand = [...(me?.hand || [])];
    const card = getCard(hand[cardIdx]);
    const field = JSON.parse(JSON.stringify(me?.field || {}));
    const slot = field.esclings?.[slotIdx];
    if (!card || !slot) return;
    const escling = getCard(slot.escling);
    // ── SEAL: universal Transcend gate (R16 — School MUST match, both paths) ──
    // Rulebook ch.7/15: Natural = School+Ilk match; Deviant = School matches,
    // Ilk differs. A pure school mismatch is illegal entirely.
    {
      const v = enforce("TRANSCEND", gateCtx(), { escling, escendantL1: card });
      if (!v.valid) { showToast(v.reason || "Transcend requires a matching School.", SD.red); return; }
    }
    const isNatural = sharesSchoolAndIlk(card, escling);
    field.esclings[slotIdx] = { ...slot, escendant: hand[cardIdx], currentLyf: card.lyf ?? null, deviant: !isNatural || deviant, natural: isNatural && !deviant };
    hand.splice(cardIdx, 1);
    // Live read + conditional write — an unconditional `anima:` off a stale
    // snapshot can clobber a concurrent damage/payment write even with no spend here.
    const meLiveT = matchRef.current?.players?.[playerIdx] || me;
    const liveAnimaT = meLiveT?.anima ?? STARTING_ANIMA;
    const hopeAnima = triggerHopeIfTimeEntry(card, field, liveAnimaT);
    await updatePlayer(code, playerIdx, { hand, field, ...(hopeAnima !== liveAnimaT ? { anima: hopeAnima } : {}) });
    showToast(`${card.name} ${isNatural ? "Transcended!" : "Deviant Transcended!"}`, GM.inkGold);
    setSelectedHandIdx(null);
  };

  const handleAscend = async (slotIdx, cardIdx, deviant = false) => {
    // R108 — once the Attack phase is declared, no more cards may be
    // played. See THE PHASE GATE above gateCtx.
    if (phaseBars("ASCEND")) return;
    setMulliganAvailable(false);
    if (ascendedThisTurn) { showToast("Already Ascended this turn.", SD.red); return; }
    const hand = [...(me?.hand || [])];
    const card = getCard(hand[cardIdx]);
    const field = JSON.parse(JSON.stringify(me?.field || {}));
    const slot = field.esclings?.[slotIdx];
    if (!card || !slot?.escendant) return;
    const current = getCard(slot.escendant);
    if (!current || card.level !== current.level + 1) { showToast("Must Ascend to next level only.", SD.red); return; }
    const traits = sharedTraits(current, card);
    const isNatural = traits === 2;
    // ── SEAL: universal Ascend gate (R18 one-per-turn, R19 level/school/ilk) ──
    {
      const v = enforce("ASCEND", gateCtx(), {
        fromLevel: current.level, toLevel: card.level,
        fromCard: current, toCard: card,
        isNatural: isNatural && !deviant,
      });
      if (!v.valid) { showToast(v.reason || "Illegal Ascend.", SD.red); return; }
    }
    if (!isNatural || deviant) {
      // ── R101 · THE DEVIANT FARE, AND WHICH LEVEL IT IS CHARGED AT ──
      // `card.level` is the DESTINATION — the level being ascended TO.
      // That is what this file has always used and it is what the
      // ruling settled: RulesEnforcer.validateAscend was charging by
      // `fromLevel`, so a 0-trait L1→L2 climb cost 3 here and 2 at the
      // gate, and which number you paid depended on which door you came
      // through. The enforcer now reads the same table with the same
      // level.
      //
      // ⚠ TWO NUMBERS, NOT ONE. Rulez v2 ch.15's L3 / 0-trait cell is
      // 5 KHAO OR 4 ANIMA — the only fare in the game where Anima is
      // the cheaper pool. The old single-number helper could not say
      // that, so this branch overcharged the Anima leg by 1 on the most
      // expensive climb in the game.
      const fare = deviantFare(traits, card.level);
      // ── CANON (caP'n ruling, July 2026): Deviant fares are payable in
      // Khao OR Anima — Khao first, Anima fallback (the exact payment
      // pattern handleResurrect already uses). The NPC's ascendOne pays
      // through the identical npcPayDeviantFare gate — full parity.
      // ── STALE-ANIMA FIX (July 2026, same bug as handleResurrect) ──
      // The raw `anima: newAnima` write bypassed buildAnimaLossUpdate (no
      // wound cracks) AND the stale `me.anima` read below refunded it a
      // heartbeat later. spendAnima handles both correctly.
      if (!(await spendKhao(fare.khao))) {
        if (!(await spendAnima(fare.anima))) { showToast(`Not enough Khao or Anima. Need ${fare.khao} Khao or ${fare.anima} Anima.`, SD.red); return; }
      }
    }
    // ── DESCENT FIX (Apr 2026) ──
    // Push the outgoing lower-level Escendant into the soul so that when
    // the higher-level being is ruined, descendBeing() can find it and
    // the being descends properly instead of fully ruining back to escling.
    // Also dedupe: if for any reason the previous-level Escendant is
    // already in soul, don't stack duplicates.
    const prevSoul = slot.soul || [];
    const newSoul = prevSoul.includes(slot.escendant) ? prevSoul : [...prevSoul, slot.escendant];
    field.esclings[slotIdx] = { ...slot, escendant: hand[cardIdx], currentLyf: card.lyf ?? null, soul: newSoul, deviant: slot.deviant || !isNatural, natural: isNatural && !slot.deviant };
    hand.splice(cardIdx, 1);
    // Live post-spend Anima — never the stale `me` snapshot. Only write
    // `anima` if Hope actually fired, so a no-op can't clobber the payment.
    const meLiveA = matchRef.current?.players?.[playerIdx] || me;
    const liveAnimaA = meLiveA?.anima ?? STARTING_ANIMA;
    const hopeAnima = triggerHopeIfTimeEntry(card, field, liveAnimaA);
    await updatePlayer(code, playerIdx, { hand, field, ...(hopeAnima !== liveAnimaA ? { anima: hopeAnima } : {}) });
    await noteTurnAction("ascendedThisTurn", setAscendedThisTurn); // R18 — on the record
    showToast(`${card.name} Ascended!`, GM.inkGold);
    setSelectedHandIdx(null);
  };

  // ═══════════════════════════════════════════════════════════
  // ⛧ TRAINING — NATURAL AND DEVIANT (built 2026-08-20)
  // ═══════════════════════════════════════════════════════════
  // Rulebook ch.17 THE DEVIANT PATHS lists three Deviant actions.
  // Transcending and Ascending were built. TRAINING WAS NOT — and it
  // was not "half built", it was ACTIVELY REFUSED: the mismatch branch
  // below used to toast "doesn't match this being's School/Ilk" and
  // return, which is the exact case the Deviant path exists to allow.
  // A local `deviantCost` was the cost table for this action and it
  // had ZERO CALLERS; it sat in the file since the July wave waiting
  // for the function that reads it. ⚠ IT IS GONE AS OF 2026-08-26 —
  // R101 collapsed all three copies of the Deviant fare onto
  // rules.deviantFare, which is what this branch calls now.
  //
  //   *"Deviant Training — Attach an Impera to a being whose School/Ilk
  //    does not match. Once per turn. Cost by shared traits."*
  //
  // ── THE FARE IS KHAO **OR** ANIMA, and that is a ruling, not the
  // table. The Rulebook's cost column reads "1 Khao / 1 Anima" and
  // caP'n ruled in July that Deviant fares are payable either way —
  // Khao first, Anima fallback. handleAscend and handleResurrect both
  // already pay exactly like this, and a third rite inventing its own
  // payment shape is how three become four. (HERETICAL Training is the
  // one deliberate exception; see below. Its Rulebook panel says "Not
  // either. Both." in bold, and it means it.)
  //
  // ── ⚠ ITS OWN ONCE-PER-TURN, NOT ASCEND'S. See `trainedThisTurn`.
  //
  // ── THE LEVEL RULE IS NOT A TRAIT RULE. `card.level > escendant.level`
  // still refuses on both paths. Deviance forgives School and Ilk; it has
  // never forgiven a Level 3 Impera on a Beb.
  const handleAttachImpera = async (slotIdx, cardIdx) => {
    // R108 — once the Attack phase is declared, no more cards may be
    // played. See THE PHASE GATE above gateCtx.
    if (phaseBars("ATTACH")) return;
    setMulliganAvailable(false);
    const hand = [...(me?.hand || [])];
    const cardId = hand[cardIdx];
    const card = getCard(cardId);
    const field = JSON.parse(JSON.stringify(me?.field || {}));
    const slot = field.esclings?.[slotIdx];
    if (!card || !slot?.escendant) return;
    const escendant = getCard(slot.escendant);
    if (!escendant) return;
    if (card.level > escendant.level) { showToast(`A Level ${card.level} Impera cannot attach to a Level ${escendant.level} being.`, SD.red); return; }
    const soul = slot.soul || [];
    const alreadyHasLevel = soul.some(id => { const sc = getCard(id); return sc?.type === "Impera" && sc?.level === card.level; });
    if (alreadyHasLevel) { showToast(`Being already has a Level ${card.level} Impera.`, SD.red); return; }

    // ── SEAL: the universal attachment gate ────────────────────
    // ⚠ THIS HANDLER WAS THE ONE ATTACHMENT PATH THAT NEVER CONSULTED
    // THE GATE. handleAttachSoulCard does, handleHereticalTraining does,
    // and R71's one-Impera-per-level was enforced here by an inline
    // check alone — so the rule the NPC obeys and the rule the player
    // obeys came from two different places, which is the exact drift
    // the Seal exists to prevent.
    {
      const v = enforce("ATTACH", gateCtx(), { being: slot, attachment: cardId });
      if (!v.valid) { showToast(v.reason || "Cannot attach that.", SD.red); return; }
    }

    const traits = sharedTraits(card, escendant);
    const isNatural = traits === 2;
    // ══════════════════════════════════════════════════════════
    // 💰 R128 · A TRAINING PAYS, AND A NATURAL ONE NEVER DID
    // ══════════════════════════════════════════════════════════
    // caP'n, 2026-09-14, verbatim: *"To keep consistency, we are going to
    // make the user pay to use an Impera if it has OPT, OP3, etc type
    // acronyms. If it doesn't have them, then you only pay once, if it
    // does, you must pay to activate again."*
    //
    // ⚠ THE ENTIRE FARE BLOCK USED TO SIT INSIDE `if (!isNatural)`, and
    // the radial's own wedge label said so out loud — "NATURAL (School
    // and Ilk both match, free)". It was never free. RULEZ_V2 ch.10 has
    // said the whole time that a Natural Training pays the card's own
    // fare; the BOARD was the half that did not charge it. So a full
    // Impera set went on for nothing — 6 + 7 + 10 Khao by the tutorial's
    // own script — and an UNTAGGED Impera such as Good Morning was free
    // FOREVER: never charged here because this branch skipped it, and
    // never charged at the soul-card till because a passive is never
    // activated.
    //
    // THE TWO FARES ARE ALTERNATIVES, NEVER A STACK. A Natural Training
    // pays the card's PRINTED fare; a Deviant Training pays the R101
    // Deviant table INSTEAD. The book offers them as alternatives and so
    // does this — `if / else`, not two ifs.
    //
    // ⚠ AND IT IS CHECKED BEFORE THE CARD LEAVES THE HAND. The splice
    // and the single `updatePlayer` are ~40 lines below, after every
    // spend, so a Training that cannot be paid refuses without ever
    // having touched the hand. That ordering is the rule here, not an
    // accident of layout: a Training that put the Impera on the Soul and
    // then failed the charge would be a brand-new pay-for-nothing.
    if (isNatural) {
      const tFare = trainingFare(card);
      if (!tFare.free) {
        // BOTH POOLS TESTED BEFORE EITHER IS SPENT — the same shape
        // handleActivateSoulCard uses, and for the same reason: spendKhao
        // and spendAnima are two writes, so charging the Khao and then
        // discovering the Anima half is unaffordable takes the Khao for
        // nothing. `canPayPrintedFare` folds R96's till law into the
        // Anima leg, so a fare that would empty this seat is refused
        // here rather than at the till.
        const liveMeT = matchRef.current?.players?.[playerIdx] || me;
        if (!canPayPrintedFare(card, { khao: liveMeT?.khao ?? 0, anima: liveMeT?.anima ?? STARTING_ANIMA })) {
          showToast(
            tFare.anima > 0
              ? `${card.name} costs ${tFare.khao} Khao and ${animaFare(tFare.anima)} to Train. Not enough.`
              : `${card.name} costs ${tFare.khao} Khao to Train. Not enough.`,
            SD.red);
          return;
        }
        if (tFare.khao > 0 && !(await spendKhao(tFare.khao))) { showToast(`Not enough Khao. Need ${tFare.khao}.`, SD.red); return; }
        if (tFare.anima > 0 && !(await spendAnima(tFare.anima))) { showToast(`Not enough Anima. Need ${animaFare(tFare.anima)}.`, SD.red); return; }
        // A fare a player did not know about is the commonest complaint,
        // so the charge is narrated — and it says whether this is the
        // ONLY payment the card will ever take (untagged: a passive has
        // no activation to charge) or whether every firing costs it
        // again (tagged: R105 at the soul-card till).
        showToast(
          `✦ ${card.name} — Trained for ${tFare.khao}${tFare.anima > 0 ? ` Khao + ${animaFare(tFare.anima)}` : " Khao"}.` +
          (tFare.paysAgain ? ` ${card.effectTag} — it costs the same again each time you fire it.` : " Paid once; it is a passive."),
          GM.inkGold);
      }
    } else {
      if (trainedThisTurn) { showToast("Already performed a Deviant Training this turn.", SD.red); return; }
      // R101 — for a TRAINING the level is unambiguous and always was:
      // the attached Impera's own printed level. Same table as the
      // Ascend above; Rulez v2 confirms the two schedules are identical.
      const fare = deviantFare(traits, card.level);
      // ⚠ THE ANIMA FLOOR. `spendAnima` ends the match at zero (R33), so
      // paying a VOLUNTARY fare with your last Anima is a concession
      // button wearing a cost. Khao is checked first and is safe at
      // zero; the Anima fallback must leave at least one behind.
      const liveKhaoT  = matchRef.current?.players?.[playerIdx]?.khao  ?? me?.khao  ?? 0;
      const liveAnimaT = matchRef.current?.players?.[playerIdx]?.anima ?? me?.anima ?? 0;
      if (liveKhaoT < fare.khao && liveAnimaT <= fare.anima) {
        showToast(`Not enough Khao, and paying ${fare.anima} Anima would end you.`, SD.red); return;
      }
      if (!(await spendKhao(fare.khao))) {
        if (!(await spendAnima(fare.anima))) { showToast(`Not enough Khao or Anima. Need ${fare.khao} Khao or ${fare.anima} Anima.`, SD.red); return; }
      }
    }

    // ⚠ RE-CLONE AFTER THE SPENDS. `field` above was cloned from the
    // RENDERED `me`, and spendKhao/spendAnima are each their own
    // awaited network round-trip. Anything that landed on the field
    // during them — an opponent's Tome resolving, a Curio, Hope — was
    // being silently overwritten by a snapshot taken before the
    // payment. ⚠ AND BOTH PATHS NOW PAY (R128, 2026-09-14) — this note
    // used to read "Natural Training pays nothing and is unaffected",
    // which stopped being true the moment the Natural branch above
    // grew a till. The re-read was already covering both paths; only
    // the reason it matters changed.
    {
      const liveField = matchRef.current?.players?.[playerIdx]?.field;
      if (liveField) {
        const fresh = JSON.parse(JSON.stringify(liveField));
        if (fresh?.esclings?.[slotIdx]) {
          fresh.esclings[slotIdx] = { ...fresh.esclings[slotIdx] };
          field.esclings = fresh.esclings;
          field.tomes = fresh.tomes ?? field.tomes;
          field.curios = fresh.curios ?? field.curios;
        }
      }
    }

    // ⚠ AND THE SLOT IS RE-READ TOO, not just the field around it. The
    // re-clone above replaced `field.esclings`; writing `...slot` (the
    // pre-payment snapshot) back into it would have put the stale slot
    // straight back and made the whole re-read pointless.
    const liveSlot = field.esclings?.[slotIdx] || slot;
    const liveSoul = liveSlot.soul || soul;
    field.esclings[slotIdx] = {
      ...liveSlot,
      soul: [...liveSoul, cardId],
      // ⚠ A NATURAL ATTACH TOUCHES NEITHER FLAG, AND THE FIRST CUT OF
      // THIS TOUCHED BOTH. Writing `natural: isNatural && !deviant` on
      // every attach meant a slot whose `natural` was UNDEFINED — the
      // resurrect paths spread a slot and never set it — got flipped to
      // `true` by attaching one matching Impera, which newly qualified
      // a resurrected being for an U'hlti and an Affinity it had never
      // earned. A rules-surface change riding in on an attachment is
      // exactly the kind of thing nobody would have found.
      //
      // Only a DEVIANT Training changes status, and it only ever
      // changes it one way. "Once Deviant, always Deviant" (Rulebook
      // ch.17) is then true because nothing here can set it back.
      ...(isNatural ? {} : { deviant: true, natural: false }),
    };
    hand.splice(cardIdx, 1);
    await updatePlayer(code, playerIdx, { hand, field });
    if (!isNatural) await noteTurnAction("trainedThisTurn", setTrainedThisTurn); // R67 — on the record
    showToast(
      isNatural ? `${card.name} attached!` : `${card.name} bound by Deviant Training. ${escendant.name} is Deviant now.`,
      isNatural ? GM.inkGold : SD.gold
    );
    setSelectedHandIdx(null);
  };

  // ═══════════════════════════════════════════════════════════
  // 🔥 HERETICAL TRAINING — R67 / R68 (built 2026-08-20)
  // ═══════════════════════════════════════════════════════════
  // Published, priced, chaptered, catalogued — and unimplemented. It
  // had a Rulebook chapter (ch.18), a constant in rules.js with no
  // importers, a gate wrapper in ruleGate.js with no importers, and no
  // engine path anywhere. A player could read the rite and never
  // perform it.
  //
  //   *"An Escendant that has gone Deviant has lost access to U'hltis
  //    and Affinities. There is one exception: Heretical Training, a
  //    rite that bonds a Tome to a Level 3 Deviant being in the slot
  //    where an U'hlti would have been."*
  //
  // ── ⚠ THE RULEBOOK CONTRADICTED ITSELF AND THIS IS THE READING THAT
  // MAKES ALL THREE STATEMENTS TRUE AT ONCE. The prose says "L3
  // Deviant only" and R67 agrees; but the same chapter's own
  // Distinctions panel says the rite "may be performed on Natural
  // beings too ... but marks the being as Deviant, PERMANENTLY", and
  // the glossary says only "a Level 3 being". Read as:
  //
  //     THE TARGET IS ANY LEVEL 3 ESCENDANT WITH AN EMPTY U'HLTI SLOT.
  //     THE RITE MAKES IT DEVIANT.
  //
  // — which satisfies the prose (afterwards it is always a Deviant
  // that carries a Heretical Tome), the Distinctions panel (a Natural
  // may be targeted, and pays for it) and the glossary (L3). The
  // Rulebook has been edited to say this in one voice.
  //
  // ── THE FARE IS BOTH. Six Khao AND six Anima. "Not either. Both. The
  // most expensive action in Terror." This is the one rite that does
  // not follow the Khao-else-Anima pattern, and the Khao is taken
  // first so a failed Anima payment can be refunded rather than
  // leaving the player short on a rite that did not happen.
  //
  // ── ⚠ THE TOME IS NOT IN THE SOUL, AND THAT IS R68. It rides on
  // `slot.heretical`, a field of its own. Everything that reads `soul`
  // — descendBeing's keptSoul filter, every Resurrect path, the soul
  // pip count — therefore ignores it for free, and "if the being is
  // ruined and later Resurrected, the Tome does not return with it"
  // is true because there is nowhere for it to have been kept.
  const handleHereticalTraining = async (slotIdx, cardIdx) => {
    // R108 — once the Attack phase is declared, no more cards may be
    // played. See THE PHASE GATE above gateCtx.
    if (phaseBars("HERETICAL_TRAINING")) return;
    setMulliganAvailable(false);
    const hand = [...(me?.hand || [])];
    const cardId = hand[cardIdx];
    const card = getCard(cardId);
    const field = JSON.parse(JSON.stringify(me?.field || {}));
    const slot = field.esclings?.[slotIdx];
    if (!card || !slot?.escendant) return;
    const escendant = getCard(slot.escendant);
    if (!escendant) return;
    if (card.type !== "Tome") { showToast("Heretical Training bonds a TOME.", SD.red); return; }
    // ── SEAL: the universal gate, finally dispatched ───────────
    // `enforce("HERETICAL_TRAINING", ...)` has been reachable in
    // RulesEnforcer and wrapped in ruleGate since the July wave, and
    // NOTHING HAS EVER CALLED IT — ruleGate's own header notes the same
    // dead-wrapper shape for tome activation. The inline checks below
    // stay as detailed messaging; this is the rule floor, and it is the
    // floor the NPC will obey identically on the day the brain learns
    // the rite.
    {
      const v = enforce("HERETICAL_TRAINING", gateCtx(), { being: slot });
      if (!v.valid) { showToast(v.reason || "The rite is refused.", SD.red); return; }
    }
    if ((escendant.level ?? 0) !== 3) { showToast("Heretical Training requires a Level 3 Escendant.", SD.red); return; }
    if (slot.heretical) { showToast("That slot already holds a Heretical Tome. It cannot be replaced.", SD.red); return; }
    if ((slot.soul || []).some(id => getCard(id)?.type === "U'hlti")) {
      showToast("The U'hlti slot is taken. Ruling 7 — it cannot be replaced.", SD.red); return;
    }

    const need = HERETICAL_TRAINING_COST;
    const liveKhao  = matchRef.current?.players?.[playerIdx]?.khao  ?? me?.khao  ?? 0;
    const liveAnima = matchRef.current?.players?.[playerIdx]?.anima ?? me?.anima ?? 0;
    // ⚠ BOTH ARE CHECKED BEFORE EITHER IS TAKEN. Spending the Khao and
    // then discovering the Anima is short would leave the player six
    // Khao poorer for a rite that never happened, and Terror has no
    // refund path that survives a mid-write disconnect.
    if (liveKhao < need.khao || liveAnima < need.anima) {
      showToast(`Heretical Training costs ${need.khao} Khao AND ${need.anima} Anima. Both.`, SD.red); return;
    }
    // ⚠ THE ANIMA FLOOR, and it is not pedantry. `spendAnima` ends the
    // match at zero (R33). The rite's advertised price is exactly six
    // Anima, so a player at 6 Khao and 6 Anima was shown a LIT wedge
    // that, pressed, paid the Khao, dropped their Anima to zero and
    // HANDED THE DUEL TO THEIR OPPONENT — after which the tome still
    // bound, on a board that had already been called. The most
    // expensive action in Terror was a suicide button at its own price.
    // R96 (canPayVoluntary) is exactly this floor, written down: a
    // VOLUNTARY payment may never take your own Anima to 0. `> need.anima`
    // is the same test the rule makes, so ask the rule instead of
    // re-deriving it here. ⚠ RulesEnforcer.validateHereticalTraining still
    // gates on `>= 6` — the board and the enforcer disagree by one Anima
    // and the enforcer is the lenient (wrong, pre-R96) one. Reported.
    if (!canPayVoluntary(liveAnima, need.anima)) {
      showToast(`You would have nothing left. Heretical Training needs ${need.anima + 1} Anima to survive.`, SD.red); return;
    }
    // ⚠ ANIMA FIRST, KHAO SECOND, and the order is the whole fix.
    // `spendKhao` reads LIVE state (matchRef); `spendAnima` reads the
    // RENDERED `me`, which can be a frame stale and lower. Paying Khao
    // first meant a stale-low Anima read could fail AFTER six Khao had
    // already been written — six Khao gone, no rite, and no refund path
    // that survives a mid-write disconnect. Paying the fragile one
    // first means a failure there costs nothing at all.
    if (!(await spendAnima(need.anima))) { showToast("Not enough Anima.", SD.red); return; }
    if (!(await spendKhao(need.khao)))   { showToast("Not enough Khao.", SD.red); return; }

    // Re-clone after the two awaited spends — see the note in
    // handleAttachImpera. Same trap, same fix.
    {
      const liveField = matchRef.current?.players?.[playerIdx]?.field;
      if (liveField?.esclings?.[slotIdx]) {
        const fresh = JSON.parse(JSON.stringify(liveField));
        field.esclings = fresh.esclings;
        field.tomes = fresh.tomes ?? field.tomes;
        field.curios = fresh.curios ?? field.curios;
      }
    }
    field.esclings[slotIdx] = {
      ...(field.esclings[slotIdx] || slot),
      heretical: cardId,
      // R64 in reverse: the rite is the one way a Deviant gets an
      // attachment, and it makes a Natural into a Deviant to do it.
      deviant: true,
      natural: false,
    };
    hand.splice(cardIdx, 1);
    await updatePlayer(code, playerIdx, { hand, field });
    showToast(`🔥 ${card.name} bound to ${escendant.name}. Heresy is permanent.`, SD.gold);
    setSelectedHandIdx(null);
  };

  // ── HELPERS ────────────────────────────────────────────────────
  // Counts by SCHOOL — see `_countBeingsOfSchool` at module scope.
  const countBeingsOfSchool = (field, cls) =>
    (field?.esclings || []).filter(s => { const c = getCard(s?.escendant); return c && c.school === cls; }).length;

  const countBeingsOfName = (field, namePart) =>
    (field?.esclings || []).filter(s => { const c = getCard(s?.escendant); return c && c.name?.toLowerCase().includes(namePart.toLowerCase()); }).length;

  const getEffectiveLyf = (slot, myField) => {
    const card = getCard(slot?.escendant);
    if (!card) return slot?.currentLyf ?? 0;
    let lyf = slot?.currentLyf !== null && slot?.currentLyf !== undefined ? slot.currentLyf : (card.lyf ?? 0);
    // Xulan Wanderer diamond skin — +2 Lyf passive
    const hasDiamondSkin = (slot.soul || []).some(id => id === "p_xulan_1");
    if (hasDiamondSkin) lyf += 2;
    // Cosmic Casbah — TEAM AURA: "Your beings gain +1 Lyf, for every Astro
    // Being you have in combat." Any of my beings with this impera attached
    // grants the bonus to every being on my field.
    const anyCosmicCasbah = (myField?.esclings || []).some(s =>
      (s?.soul || []).some(id => id === "p_h09_1")
    );
    if (anyCosmicCasbah) lyf += countBeingsOfSchool(myField, "Space");
    // Premier Dragon Beb — +1 Lyt AND +1 Lyf per Dragon Being
    const card2 = getCard(slot?.escendant);
    if (card2?.id === "premier_dragon_beb") {
      const hasPremierImpera = (slot.soul || []).some(id => id === "p_premier_dragon_1");
      if (hasPremierImpera) lyf += countBeingsOfSchool(myField, "Dragon");
    }
    // Dragon Primera stat-shift (H22): +1 Lyf from the School stamp for the Turn
    lyf += slot?.primeraLyfBuff || 0;
    return lyf;
  };

  const getEffectiveLyt = (slot, myField, oppField = null) => {
    const card = getCard(slot?.escendant);
    if (!card) return 0;
    let lyt = card.lyt || 0;
    // ── Wave 2: Gauntlets of A'zka bonus via wielder bond ──
    // Treasures live on the Tome rift and bond to a being via the
    // `wieldedBy` pointer. We figure out this slot's index and look
    // for any bonded Gauntlets in myField.tomes.
    const mySlotIdx = (myField?.esclings || []).findIndex(s => s === slot);
    if (mySlotIdx >= 0) {
      const hasGauntletsBonded = (myField?.tomes || []).some(t =>
        t?.id === "tome_gauntlets_azka" && t?.wieldedBy === mySlotIdx && !t?.faceDown
      );
      if (hasGauntletsBonded) lyt += 2;
    }
    // Legacy fallback — Gauntlets in soul (old attachment system pre-Wave 1).
    // Keeping for backwards compatibility with saves made before wielder
    // bonds existed; can be removed once confirmed no stale state remains.
    const hasGauntletsLegacy = (slot.soul || []).some(id => id === "tome_gauntlets_azka");
    if (hasGauntletsLegacy) lyt += 2;
    // Premier Dragon Beb Good Morning — +1 Lyt per Dragon Being YOU HAVE in combat (own field only)
    if (card.id === "premier_dragon_beb") {
      const hasGoodMorning = (slot.soul || []).some(id => id === "p_premier_dragon_1");
      if (hasGoodMorning) lyt += countBeingsOfSchool(myField, "Dragon");
    }
    // Premier Dragon Champion Social Dragonfly — +1 Lyt per Dragon Being in combat (BOTH fields)
    if (card.id === "premier_dragon_champion") {
      const hasSocialDragonfly = (slot.soul || []).some(id => id === "p_premier_dragon_2");
      if (hasSocialDragonfly) {
        lyt += countBeingsOfSchool(myField, "Dragon");
        lyt += countBeingsOfSchool(oppField, "Dragon");
      }
    }
    // Z4Z Lord U'hlti Apocalypse Now — +1 Lyt per Time Being in combat (BOTH fields)
    const hasZ4zUhlti = (slot.soul || []).some(id => id === "u_z4z_lord");
    if (hasZ4zUhlti) {
      lyt += countBeingsOfSchool(myField, "Time");
      lyt += countBeingsOfSchool(oppField, "Time");
    }
    // ⚠ KING OF THE ANTHILL WAS HERE, AND IT WAS A COMMENT.
    // This block counted the other Antdroids into a local and then said
    // the total was "tracked as extraAttackCount on the slot (used in
    // attack flow)". `extraAttackCount` existed NOWHERE ELSE in the
    // codebase. The count was computed, discarded, and the card did
    // nothing for the whole life of the game.
    //
    // It is a swing allowance, not a Lyt modifier, so it does not belong
    // in `getEffectiveLyt` at all — it lives in `rules.lytAttacksAllowed`
    // (R124), which both attack paths and the gate now read.
    // Dragon Primera stat-shift (H22): +1 Lyt from the Ilk stamp, −1 from
    // the School stamp — signed, lasts the Turn (cleared at end-of-turn).
    lyt += slot?.primeraLytBuff || 0;
    return Math.max(0, lyt);
  };

  // ── Wave 2: Lightning Blade breakthrough dim ──────────────
  // Card text: "when the wielder Lyt Attacks, dim the Lyf of your
  // adversary's cards by 1." Fires AFTER the attack resolves (per
  // designer intent). Dims EVERY opp card with Lyf — beings AND
  // tomes. Not the swing itself; a separate post-resolution effect.
  // R28 resets Lyf each turn so the dim is soft pressure unless
  // followup attacks convert the -1 into a real kill.
  const fireLightningBladeIfBonded = async (attackerSlotIdx) => {
    const myTomes = me?.field?.tomes || [];
    const bondedBlade = myTomes.some(t =>
      t?.id === "tome_lightning_blade" && t?.wieldedBy === attackerSlotIdx && !t?.faceDown
    );
    if (!bondedBlade) return;
    const oppField = JSON.parse(JSON.stringify(opponent?.field || {}));
    let changed = false;
    // Dim all opp beings with Lyf > 0
    (oppField.esclings || []).forEach((s, i) => {
      if (!s?.escendant) return;
      const c = getCard(s.escendant);
      const cur = s.currentLyf !== null && s.currentLyf !== undefined ? s.currentLyf : (c?.lyf ?? 0);
      if (cur > 0) {
        oppField.esclings[i] = { ...s, currentLyf: Math.max(0, cur - 1) };
        changed = true;
      }
    });
    // Dim all opp tomes with Lyf > 0 (Treasures, Khao D'uhmdumn, etc.)
    (oppField.tomes || []).forEach((t, i) => {
      if (!t?.id) return;
      const c = getCard(t.id);
      const baseLyf = c?.lyf ?? 0;
      if (baseLyf <= 0) return;
      const cur = t.currentLyf !== null && t.currentLyf !== undefined ? t.currentLyf : baseLyf;
      if (cur > 0) {
        oppField.tomes[i] = { ...t, currentLyf: Math.max(0, cur - 1) };
        changed = true;
      }
    });
    if (changed) {
      await updatePlayer(code, oppIdx, { field: oppField });
      showToast(`⚡ Lightning Blade — all adversary cards dimmed by 1 Lyf!`, SD.gold);
    }
  };

  // ── Wave 2: Necklace of Akula listener ─────────────────────
  // Card text: "You gain +1 Anima whenever a being is ruined."
  // Triggers on ANY ruin event, either side. Effect is ALWAYS on —
  // even during the card's 3-turn Intangible window, the listener
  // never stops (the Intangible window only protects the card
  // itself from being attacked/targeted).
  //
  // Checks BOTH players' Tome rifts so whoever owns Akula benefits
  // when any being ruins on the field.
  const fireAkulaListenerOnRuin = (...args) => resolver_fireAkulaListenerOnRuin(...args, ctx);

  const dimAllOppLyf = async (amount) => {
    const oppField = JSON.parse(JSON.stringify(opponent?.field || {}));
    let changed = false;
    (oppField.esclings || []).forEach((s, i) => {
      if (s?.escendant) {
        // ── AUDIT FIX (July 19 2026): every caller of this sweep is an
        // Impera or U'hltima effect — Premier Dragon Lord's "Destined
        // for Greatness" (Intangible to Impera and U'hltimas) is immune.
        // BOTH KEYS on the read: the flag shipped as `intangibleToPrimeras`
        // and is written as `intangibleToImpera` from the Impera rename on.
        // A match already in flight can carry either. Drop the old key here
        // next release, once no live field object can still hold it.
        if (s?.intangibleToImpera || s?.intangibleToPrimeras) return;
        const card = getCard(s.escendant);
        const cur = s.currentLyf !== null && s.currentLyf !== undefined ? s.currentLyf : (card?.lyf ?? 0);
        oppField.esclings[i] = { ...s, currentLyf: Math.max(0, cur - amount) };
        changed = true;
      }
    });
    if (changed) await updatePlayer(code, oppIdx, { field: oppField });
  };

  // ⚰ DEAD HELPER (C14 audit, July 10 2026): zero callers found —
  // every live ruin path routes through descendBeing / attemptRuinOppTome
  // / the effect handlers directly. Kept per the retirement convention;
  // made lawful anyway (Necropolis push) so a future revival can't
  // reintroduce a vanish. Prefer the shared helpers over this.
  // eslint-disable-next-line no-unused-vars
  const ruinCard = async (targetCode, targetPlayerIdx, slotIdx, tomeSlotIdx, curiSlotIdx) => {
    // Ruin a being — descend if L2/L3
    if (slotIdx !== undefined && slotIdx !== null) {
      await descendBeing(targetPlayerIdx, slotIdx);
    }
    if (tomeSlotIdx !== undefined && tomeSlotIdx !== null) {
      const pData = targetPlayerIdx === playerIdx ? me : opponent;
      const field = JSON.parse(JSON.stringify(pData?.field || {}));
      const tomes = [field.tomes?.[0] ?? null, field.tomes?.[1] ?? null, field.tomes?.[2] ?? null];
      const goneId = typeof tomes[tomeSlotIdx] === "object" ? tomes[tomeSlotIdx]?.id : tomes[tomeSlotIdx];
      tomes[tomeSlotIdx] = null;
      field.tomes = tomes;
      // NECROPOLIS LAW: recorded.
      await updatePlayer(targetCode, targetPlayerIdx, { field, necropolis: goneId ? [...(pData?.necropolis || []), goneId] : (pData?.necropolis || []) });
    }
  };

  // ── AUDIT FIX (July 19 2026): dual-source tutor — "from your
  // Grimoire or Necropolis" (Morning Star, Binary Star). Pool spans
  // both zones; when a picked id exists in both, the Grimoire copy is
  // depleted first, then the Necropolis instance. Auto-adds when the
  // pool fits within `count`, mirroring searchAndAdd.
  const searchAndAddGrimNec = async (filter, count, label) => {
    const grimIds = Object.keys(me?.grimoire || {}).filter(id => filter(getCard(id)));
    const necIds = (me?.necropolis || []).filter(id => filter(getCard(id)));
    const pool = [...new Set([...grimIds, ...necIds])];
    if (pool.length === 0) { showToast(`No ${label} available in Grimoire or Necropolis.`, GM.vellumFaint); return false; }
    const takeCards = async (chosenArr) => {
      const newG = { ...(me?.grimoire || {}) };
      const newNec = [...(me?.necropolis || [])];
      chosenArr.forEach(id => {
        if (newG[id] > 0) { if (newG[id] > 1) newG[id]--; else delete newG[id]; return; }
        const ni = newNec.indexOf(id);
        if (ni !== -1) newNec.splice(ni, 1);
      });
      await updatePlayer(code, playerIdx, { hand: [...(me?.hand || []), ...chosenArr], grimoire: newG, necropolis: newNec });
      chosenArr.forEach(id => showToast(`✦ ${getCard(id)?.name} added!`, GM.inkGold));
    };
    if (pool.length <= count) { await takeCards(pool); return true; }
    setTargetPicker({
      title: `Choose ${count} — ${label}`,
      cards: pool,
      count,
      onPick: async (chosen) => {
        const chosenArr = Array.isArray(chosen) ? chosen : [typeof chosen === "string" ? chosen : chosen?.id];
        await takeCards(chosenArr);
        setTargetPicker(null);
      },
    });
    return true;
  };

  const searchAndAdd = (...args) => resolver_searchAndAdd(...args, ctx);


  // ── DESCEND: when a being is ruined, descend to previous level
  // Only the top-level escendant + its level-specific soul cards go to Necropolis
  // Lower levels and their soul cards remain on the slot
  const descendBeing = (...args) => resolver_descendBeing(...args, ctx);

  

  // ── FULL SOUL CARD ACTIVATION ───────────────────────────────
  const handleActivateSoulCard = async (slotIdx, soulCardId) => {
    setMulliganAvailable(false);
    const soulCard = getCard(soulCardId);
    if (!soulCard || !myTurn) return;
    // ── NULLIFICATION CANON: a nullified soul card stays attached but
    // can do NOTHING — refuse before any fare is paid.
    if (((me?.field?.esclings?.[slotIdx]?.nullifiedSouls) || []).includes(soulCardId)) {
      showToast(`◈ ${soulCard.name} has been nullified — it stays attached, but its power is gone.`, GM.vellumFaint);
      return;
    }
    // ── R122 · THE CADENCE TAG IS REAL ON A SOUL CARD TOO ────────
    // caP'n, 2026-09-13: *"every card, acronyms like OPT OP3 etc are
    // real, they are not just there to look cool."* Enforced BEFORE the
    // fare, like every other refusal in this file — a card refused for
    // cadence must not have been charged for the privilege. The stamp
    // is per-card (slot.soulUsed) because a Supreme Being wears five
    // activatable cards with three different cadences.
    // Through the GATE, not past it: `gateSoulActivation` has existed
    // in ruleGate.js with no caller since the phase gate was built, so
    // this is also the line that stops it being a wrapper that watches
    // nothing. It carries R108's phase door and R122's cadence at once.
    {
      const slotNow = me?.field?.esclings?.[slotIdx];
      const v = gateSoulActivation(gateCtx(), slotNow, soulCardId);
      if (!v.valid) { showToast(v.reason || "That card cannot act right now.", GM.vellumFaint); return; }
    }

    // ── R105 · THE PRINTED FARE, BOTH POOLS, EVERY ACTIVATION ────
    // ⚠ THE ANIMA HALF USED TO BE GATED ON `type === "U'hlti"`, so an
    // Impera or Affinity printing an Anima number activated free.
    // ⚠ AND IT USED TO SPEND ANIMA FIRST AND CHECK KHAO AFTER, which
    // took the Anima for nothing when the Khao half failed. Both pools
    // are tested together now, before either is written.
    const { khao: cost, anima: animaCost } = printedFare(soulCard);
    if (!canPayPrintedFare(soulCard, { khao: me?.khao ?? 0, anima: me?.anima ?? STARTING_ANIMA })) {
      showToast(cost > (me?.khao ?? 0) ? `Not enough Khao. Need ${cost}.` : `Not enough Anima. Need ${animaFare(animaCost)}.`, SD.red);
      return;
    }
    if (cost > 0) {
      const paid = await spendKhao(cost);
      if (!paid) { showToast(`Not enough Khao. Need ${cost}.`, SD.red); return; }
    }
    if (animaCost > 0) {
      const paidAnima = await spendAnima(animaCost);
      if (!paidAnima) { showToast(`Not enough Anima. Need ${animaFare(animaCost)}.`, SD.red); return; }
    }
    // R122 — the stamp lands with the fare, not with the effect: a
    // firing that is nullified downstream still spent the Turn's use,
    // exactly as a countered Tome's cooldown anchor does.
    {
      const fStamp = JSON.parse(JSON.stringify(matchRef.current?.players?.[playerIdx]?.field || me?.field || {}));
      const sStamp = fStamp?.esclings?.[slotIdx];
      if (sStamp) {
        sStamp.soulUsed = stampSoulUsed(sStamp, soulCardId, match?.turn ?? 0);
        await updatePlayer(code, playerIdx, { field: fStamp });
      }
    }
    // ── WAVE 2/3: DECLARED — a soul-card effect is a card activation
    // (canon: "any card effect in use" — Sword & Shield semantics).
    // Nihil voids this firing, fares stay spent. Omnia splices the soul
    // card OFF the being → Necropolis, effect never resolves. LATE
    // (Counter Hierarchy): the effect resolves first; the soul card is
    // evicted after the dust settles (delayed splice below — reads
    // FRESH state so the dispatcher's own writes land first; a picker-
    // driven soul effect held open past the splice is a known micro-
    // race, accepted for beta and logged in the roadmap).
    const rwS = await declareAction({ kind: "ACTIVATION", cardId: soulCard.id });
    if (rwS?.counter && !rwS?.late) {
      if (rwS.counter === "omnia") {
        const meFS = matchRef.current?.players?.[playerIdx] || me;
        const fS = JSON.parse(JSON.stringify(meFS?.field || {}));
        const sS = fS.esclings?.[slotIdx];
        if (sS && (sS.soul || []).includes(soulCard.id)) {
          // R70 on the way out — an Omnia'd U'hlti takes its Affinity with it.
          const stripS = stripSoulCard(sS.soul, soulCard.id);
          sS.soul = stripS.soul;
          await updatePlayer(code, playerIdx, { field: fS, necropolis: [...(meFS?.necropolis || []), ...stripS.gone] });
        }
        showToast(`✕ ${rwS.counterName || "A counter"} — ${soulCard.name} countered and ruined!`, SD.red);
      } else {
        showToast(`◈ ${rwS.counterName || "A counter"} — ${soulCard.name}'s effect nullified!`, GM.inkGold);
      }
      return;
    }
    if (rwS?.counter && rwS?.late) {
      // ── WAVE 4 (C12 d): the splice VERIFIES AND RE-EVICTS ──────
      // The single 1600ms timer lost to any picker-driven soul effect
      // whose onPick wrote a pre-eviction field clone after the splice
      // — the evicted card silently re-attached to the being. The
      // eviction now re-checks fresh state at 1.6s / 4s / 9s and
      // re-splices if a late write resurrected the card. First success
      // rides the card to the Necropolis; retries repair the FIELD
      // ONLY (idempotent — no duplicate Necropolis entries even if a
      // stale write also mangled the pile). A picker held open past
      // ~9s remains the documented — now far narrower — beta edge.
      let lateEvicted = false;
      const evictLateSoul = async () => {
        const meFL = matchRef.current?.players?.[playerIdx];
        if (!meFL || matchRef.current?.status === "finished") return false;
        const fL = JSON.parse(JSON.stringify(meFL.field || {}));
        const sL = fL.esclings?.[slotIdx];
        if (!(sL && (sL.soul || []).includes(soulCard.id))) return false;
        // R70 on the way out — the late eviction cascades identically.
        const stripL = stripSoulCard(sL.soul, soulCard.id);
        sL.soul = stripL.soul;
        const upd = { field: fL };
        if (!lateEvicted) upd.necropolis = [...(meFL?.necropolis || []), ...stripL.gone];
        await updatePlayer(code, playerIdx, upd);
        lateEvicted = true;
        return true;
      };
      setTimeout(async () => {
        try {
          const did = await evictLateSoul();
          if (did) showToast(`${rwS.counterName || "A late counter"} — ${soulCard.name} evicted after resolving.`, GM.vellumDim);
        } catch {}
        setTimeout(async () => { try { await evictLateSoul(); } catch {} }, 2400);
        setTimeout(async () => { try { await evictLateSoul(); } catch {} }, 7400);
      }, 1600);
    }
    // Wave 4 (C12 c): the dispatcher base reads POST-WINDOW truth — the
    // declareAction above holds for seconds, and the render closure
    // predates it. Cloning `me?.field` here baked pre-window state into
    // every soul-effect write below (the soul edition of the tome
    // funnel's shadow fix).
    const meSoulLive = matchRef.current?.players?.[playerIdx] || me;
    const field = JSON.parse(JSON.stringify(meSoulLive?.field || {}));
    const slot = field.esclings?.[slotIdx];
    if (!slot) return;
    const id = soulCard.id;
    const beingName = slot.escendant ? getCard(slot.escendant)?.name : "Being";

    // ── IMPERA ──────────────────────────────────────────────────

    // H-01 Blue Giant — OP3: Intangible
    if (id === "p_h01_1") {
      field.esclings[slotIdx] = { ...slot, intangible: true };
      await updatePlayer(code, playerIdx, { field });
      showToast(`✦ Natural Glow — ${beingName} is Intangible!`, GM.inkGold); return;
    }
    // H-01 Quasar — OP2: Must be ruined first
    if (id === "p_h01_2") {
      field.esclings[slotIdx] = { ...slot, mustBeRuinedFirst: true };
      await updatePlayer(code, playerIdx, { field });
      showToast(`✦ Natural Lyt — ${beingName} must be ruined before others can be attacked.`, GM.inkGold); return;
    }
    // H-01 Super Nova — OP3: Add 1 Astro Being from Grimoire or Necropolis
    // ── AUDIT FIX (July 19 2026): the Necropolis half of the card text
    // was ignored — dual-source tutor now honors it.
    if (id === "p_h01_3") {
      await searchAndAddGrimNec(c => c?.school === "Space" && c?.type === "Escendant", 1, "Astro Being");
      return;
    }
    // H-09 Pulsar — passive: +1 Lyf per Astro Being (shown as info)
    if (id === "p_h09_1") {
      showToast(`◆ Cosmic Casbah — passive. ${beingName} gains +1 Lyf per Astro Being in combat.`, GM.vellumFaint); return;
    }
    // H-09 Neutron Star — OP3: Add 2 Astro Beings up to L2
    // ── AUDIT FIX (July 19 2026): "Grimoire or Necropolis" — the
    // Necropolis half was ignored.
    if (id === "p_h09_2") {
      await searchAndAddGrimNec(c => c?.school === "Space" && c?.type === "Escendant" && c?.level <= 2, 2, "Astro Being (up to L2)");
      return;
    }
    // H-09 Hyper Nova — OP2: Nullify 1 Lyt Attack
    if (id === "p_h09_3") {
      field.esclings[slotIdx] = { ...slot, nullifyNextAttack: true };
      await updatePlayer(code, playerIdx, { field });
      showToast(`✦ Radiant Anima — ${beingName} will nullify the next Lyt Attack!`, GM.inkGold); return;
    }
    // Xulan Wanderer — passive: +2 Lyf
    if (id === "p_xulan_1") {
      showToast(`◆ Diamond Skin — passive. ${beingName} has +2 Lyf permanently.`, GM.vellumFaint); return;
    }
    // Xulan Surgeon — OP2: When this being Lyt Attacks, dim opp Anima by 1
    if (id === "p_xulan_2") {
      field.esclings[slotIdx] = { ...slot, dimAnimaOnAttack: 1 };
      await updatePlayer(code, playerIdx, { field });
      showToast(`✦ Natural Scalpel — ${beingName} will dim Anima by 1 on each attack!`, GM.inkGold); return;
    }
    // Xulan Ghost — OPT: Dim own Anima by 4, nullify 1 attack
    if (id === "p_xulan_3") {
      // R96 · THE TILL LAW — see tillRefusesSelfDim. R100 — the dim is
      // SELF-inflicted and feeds no Prophecy.
      if (tillRefusesSelfDim(4, "Eternal Nightmare")) return;
      const newAnima = Math.max(0, (me?.anima || STARTING_ANIMA) - 4);
      await updatePlayer(code, playerIdx, { anima: newAnima, ...buildSelfDimUpdate(me, 4) });
      field.esclings[slotIdx] = { ...slot, nullifyNextAttack: true };
      await updatePlayer(code, playerIdx, { field });
      showToast(`✦ Eternal Nightmare — Dimmed 4 Anima, next attack on ${beingName} nullified!`, GM.inkGold); return;
    }
    // Ghost beasts — passive Anima redirect
    if (["p_shadow_fox_1","p_spectral_tiger_1","p_inverted_serpent_1"].includes(id)) {
      field.esclings[slotIdx] = { ...slot, canDirectAnima: true };
      await updatePlayer(code, playerIdx, { field });
      showToast(`✦ Phantom Paws — ${beingName} can now direct Lyt Attacks at Anima!`, GM.inkGold); return;
    }
    // Shadow Fox champion — OP3: Intangible
    if (id === "p_shadow_fox_2") {
      field.esclings[slotIdx] = { ...slot, intangible: true };
      await updatePlayer(code, playerIdx, { field });
      showToast(`✦ Shadow Fur — ${beingName} is Intangible!`, GM.inkGold); return;
    }
    // Shadow Fox lord — OP2: Intangible
    if (id === "p_shadow_fox_3") {
      field.esclings[slotIdx] = { ...slot, intangible: true };
      await updatePlayer(code, playerIdx, { field });
      showToast(`✦ Lord of Shadows — ${beingName} is Intangible!`, GM.inkGold); return;
    }
    // Spectral Tiger champion — if Lyt Attack dimmed Anima, dim 1 more (set flag)
    if (id === "p_spectral_tiger_2") {
      field.esclings[slotIdx] = { ...slot, spectralClaws: true };
      await updatePlayer(code, playerIdx, { field });
      showToast(`✦ Spectral Claws — ${beingName} will dim 1 extra Anima after attacks!`, GM.inkGold); return;
    }
    // Spectral Tiger lord — OP2: Dim Lyf of ALL opp cards by 2
    if (id === "p_spectral_tiger_3") {
      await dimAllOppLyf(2);
      showToast(`✦ Haunting Presents — All opponent cards' Lyf dimmed by 2!`, SD.red); return;
    }
    // Inverted Serpent champion — passive: dim Anima by 1 per attack
    if (id === "p_inverted_serpent_2") {
      field.esclings[slotIdx] = { ...slot, dimAnimaOnAttack: 1 };
      await updatePlayer(code, playerIdx, { field });
      showToast(`✦ Supernatural Bite — ${beingName} dims Anima by 1 on each attack!`, GM.inkGold); return;
    }
    // Inverted Serpent lord — OPT: Dim Anima x3 per intangible opp being
    if (id === "p_inverted_serpent_3") {
      const oppField = opponent?.field || {};
      const intangibleCount = (oppField.esclings || []).filter(s => s?.intangible).length;
      if (intangibleCount === 0) { showToast("No Intangible beings on opponent's field.", GM.vellumFaint); return; }
      const dmg = intangibleCount * 3;
      const newAnima = Math.max(0, (opponent?.anima || STARTING_ANIMA) - dmg);
      await updatePlayer(code, oppIdx, { anima: newAnima, ...buildAnimaLossUpdate(opponent, dmg) });
      showToast(`✦ Inverted Scales — ${intangibleCount} Intangible × 3 = ${dmg} Anima dimmed!`, SD.red);
      if (newAnima <= 0) await endMatch(code, playerIdx);
      return;
    }
    // Z4Z Demon — OP2: Dim own Anima 5, extra Lyt Attack
    if (id === "p_z4z_1") {
      if (tillRefusesSelfDim(5, "Relentless")) return;   // R96
      const newAnima = Math.max(0, (me?.anima || STARTING_ANIMA) - 5);
      await updatePlayer(code, playerIdx, { anima: newAnima, ...buildSelfDimUpdate(me, 5) }); // R100
      field.esclings[slotIdx] = { ...slot, extraAttack: true };
      await updatePlayer(code, playerIdx, { field });
      showToast(`✦ Relentless — ${beingName} can attack again!`, SD.red); return;
    }
    // Z4Z Wizard — OP2: Extra attack if another L2 Time Being in combat
    if (id === "p_z4z_2") {
      const otherL2Time = me?.field?.esclings?.some((s, i) => {
        if (i === slotIdx) return false;
        const c = getCard(s?.escendant); return c && c.school === "Time" && c.level === 2;
      });
      if (!otherL2Time) { showToast("Need another Level 2 Time Being in combat.", GM.vellumFaint); return; }
      field.esclings[slotIdx] = { ...slot, extraAttack: true };
      await updatePlayer(code, playerIdx, { field });
      showToast(`✦ Catastrophe — ${beingName} can attack again!`, SD.red); return;
    }
    // Z4Z Knights — OP3: Dim own Anima 8, ALL beings get extra attack
    if (id === "p_z4z_3") {
      if (tillRefusesSelfDim(8, "Apocalypse")) return;   // R96
      const newAnima = Math.max(0, (me?.anima || STARTING_ANIMA) - 8);
      await updatePlayer(code, playerIdx, { anima: newAnima, ...buildSelfDimUpdate(me, 8) }); // R100
      const newField = JSON.parse(JSON.stringify(me?.field || {}));
      newField.esclings = newField.esclings.map(s => s?.escendant ? { ...s, extraAttack: true } : s);
      await updatePlayer(code, playerIdx, { anima: newAnima, field: newField });
      showToast(`✦ Apocalypse — All beings can attack again!`, SD.red); return;
    }
    // DG-0 Angel — passive
    if (id === "p_dg0_1") {
      showToast(`◆ Hope — passive. Gain +1 Anima whenever a new Time Being enters combat.`, GM.vellumFaint); return;
    }
    // DG-0 Law and Order — OP3: Dim own Anima 1, draw 1 card
    if (id === "p_dg0_2") {
      if (tillRefusesSelfDim(1, "Law and Order")) return;   // R96
      const newAnima = Math.max(0, (me?.anima || STARTING_ANIMA) - 1);
      const { drawn: [drawn], newGrimoire: ng1 } = drawFromGrimoire(me?.grimoire, 1);
      if (!drawn) { showToast("Grimoire empty!", GM.vellumFaint); return; }
      await updatePlayer(code, playerIdx, { anima: newAnima, hand: [...(me?.hand || []), drawn], grimoire: ng1, ...buildSelfDimUpdate(me, 1) }); // R100
      showToast(`✦ Law and Order — Drew ${getCard(drawn)?.name}!`, GM.inkGold); return;
    }
    // DG-0 I'nfini — OPT: Draw 1 card if gained Anima this turn
    if (id === "p_dg0_3") {
      if (!(me?.animaGainedThisTurn > 0)) { showToast("You haven't gained Anima this turn.", GM.vellumFaint); return; }
      const { drawn: [drawn2], newGrimoire: ng2 } = drawFromGrimoire(me?.grimoire, 1);
      if (!drawn2) { showToast("Grimoire empty!", GM.vellumFaint); return; }
      await updatePlayer(code, playerIdx, { hand: [...(me?.hand || []), drawn2], grimoire: ng2 });
      showToast(`✦ Justice Prevails — Drew ${getCard(drawn2)?.name}!`, GM.inkGold); return;
    }
    // Antdroid Restless — OP3: Dim own Anima 5, extra attack (Anima only)
    if (id === "p_antdroid_1") {
      if (tillRefusesSelfDim(5, "Restless")) return;   // R96
      const newAnima = Math.max(0, (me?.anima || STARTING_ANIMA) - 5);
      await updatePlayer(code, playerIdx, { anima: newAnima, ...buildSelfDimUpdate(me, 5) }); // R100
      field.esclings[slotIdx] = { ...slot, extraAttackAnimaOnly: true };
      await updatePlayer(code, playerIdx, { field });
      showToast(`✦ Restless — ${beingName} can Lyt Attack Anima again!`, SD.red); return;
    }
    // Antdroid Equivalent Exchange — OP2: Dim own Anima 4, draw 2
    if (id === "p_antdroid_2") {
      if (tillRefusesSelfDim(4, "Equivalent Exchange")) return;   // R96
      const newAnima = Math.max(0, (me?.anima || STARTING_ANIMA) - 4);
      const { drawn: drawnEx, newGrimoire: ngEx } = drawFromGrimoire(me?.grimoire, 2);
      await updatePlayer(code, playerIdx, { anima: newAnima, hand: [...(me?.hand || []), ...drawnEx], grimoire: ngEx, ...buildSelfDimUpdate(me, 4) }); // R100
      showToast(`✦ Equivalent Exchange — Drew ${drawnEx.length} cards!`, GM.inkGold); return;
    }
    // Antdroid Lord — passive: extra attack per other Antdroid
    if (id === "p_antdroid_3") {
      showToast(`◆ King of the Anthill — passive. ${beingName} gets extra attacks per other Antdroid in combat.`, GM.vellumFaint); return;
    }
    // Lucky Dragon Beb — OP2: coin flip, draw 1 (SSG)
    if (id === "p_lucky_dragon_1") {
      // ── AUDIT FIX (July 19 2026): the card's "Dim your own Anima by 1"
      // cost was never paid. Pay it first (tracked for Bloody Hell).
      // ⚠ THE BRIEF CALLS THIS CARD "Premier Dragon −1". IT IS NOT.
      // p_lucky_dragon_1 is the LUCKY Dragon Beb (Pay Day); the Premier
      // Dragon Beb (p_premier_dragon_1) is a passive Lyt/Lyf aura and
      // dims nobody. Named here so the next reader does not go looking
      // for a self-dim on a card that has never had one.
      if (tillRefusesSelfDim(1, "Pay Day")) return;   // R96
      const pdAnima = Math.max(0, (me?.anima || STARTING_ANIMA) - 1);
      await updatePlayer(code, playerIdx, { anima: pdAnima, ...buildSelfDimUpdate(me, 1) }); // R100
      // UNREACHABLE BY CONSTRUCTION since R96 was widened — the till
      // above refuses any payment that would land on 0. Kept, not
      // deleted: it is the correct handling if the guard is ever
      // loosened, and deleting it is how the hole came back last time.
      if (pdAnima <= 0) { await endMatch(code, oppIdx, playerIdx) /* AUDIT FIX 2026-08-22: MY anima just hit 0 — the loser is ME, explicitly, never guessed via getTargetIdx(oppIdx) */; return; }
      const result = Math.random() < 0.5;
      // ── GRIM/LUX FIX (July 2026): the coin's draw must DEPLETE the
      // drawing side's Grimoire — both branches were duplicating cards.
      // ── CANON (caP'n, July 19 2026): "your adversary draws 1 as well"
      // — on a LOSS both Warlocks draw 1; on a WIN only the owner draws.
      const pdDrawOne = async (who, whoIdx) => {
        const newG = { ...(who?.grimoire || {}) };
        const pool = Object.entries(newG).flatMap(([gid, qty]) => Array(qty).fill(gid));
        if (pool.length === 0) return;
        // R10 — draw-then-discard: the draw RESOLVES at the cap; the
        // over-cap seat discards after. No block here (was a hard cap).
        const drawn = pool[Math.floor(Math.random() * pool.length)];
        if (newG[drawn] > 1) newG[drawn]--; else delete newG[drawn];
        await updatePlayer(code, whoIdx, { hand: [...(who?.hand || []), drawn], grimoire: newG });
      };
      if (result) {
        await pdDrawOne(me, playerIdx);
        showToast(`✦ Pay Day — Your side! Dimmed 1 Anima, drew a card!`, SD.gold);
      } else {
        await pdDrawOne(me, playerIdx);
        await pdDrawOne(opponent, oppIdx);
        showToast(`✦ Pay Day — Not your side. Both Aenarchists draw a card.`, GM.vellumFaint);
      }
      return;
    }
    // Lucky Dragon Champion — OP3: coin flip, draw 2 (SSG)
    if (id === "p_lucky_dragon_2") {
      const result = Math.random() < 0.5;
      // ── GRIM/LUX FIX (July 2026): deplete the drawing side's Grimoire.
      // ── CANON (caP'n, July 19 2026): "your adversary draws 2 as well"
      // — on a LOSS both Warlocks draw 2; on a WIN only the owner draws.
      const bcDrawTwo = async (who, whoIdx) => {
        const newG = { ...(who?.grimoire || {}) };
        const pool = Object.entries(newG).flatMap(([gid, qty]) => Array(qty).fill(gid));
        // R10 — draw-then-discard: BOTH Warlocks draw 2 regardless of hand
        // size; the over-cap seat discards after. The old `room` clamp was
        // the pre-Rulez-v2 hard cap and quietly shorted the adversary's draw.
        const take = pool.sort(() => Math.random() - 0.5).slice(0, 2);
        take.forEach(gid => { if (newG[gid] > 1) newG[gid]--; else delete newG[gid]; });
        if (take.length > 0) await updatePlayer(code, whoIdx, { hand: [...(who?.hand || []), ...take], grimoire: newG });
      };
      if (result) {
        await bcDrawTwo(me, playerIdx);
        showToast(`✦ Black Cat — Your side! Drew 2 cards!`, SD.gold);
      } else {
        await bcDrawTwo(me, playerIdx);
        await bcDrawTwo(opponent, oppIdx);
        showToast(`✦ Black Cat — Not your side. Both Aenarchists draw 2.`, GM.vellumFaint);
      }
      return;
    }
    // Lucky Dragon Lord — OP3: coin flip, reflect Lyt Attack
    if (id === "p_lucky_dragon_3") {
      field.esclings[slotIdx] = { ...slot, reflectNextAttack: true };
      await updatePlayer(code, playerIdx, { field });
      showToast(`✦ Fate Maker — Next Lyt Attack on this being will be reflected to opponent's Anima!`, SD.gold); return;
    }
    // Premier Dragon Beb — passive
    if (id === "p_premier_dragon_1") {
      showToast(`◆ Good Morning — passive. ${beingName} gains +1 Lyt and +1 Lyf per Dragon Being in combat.`, GM.vellumFaint); return;
    }
    // Premier Dragon Champion — passive
    if (id === "p_premier_dragon_2") {
      showToast(`◆ Social Dragonfly — passive. ${beingName} gains +1 Lyt per Dragon Being in combat.`, GM.vellumFaint); return;
    }
    // Premier Dragon Lord — Intangible to Impera/U'hltis (set flag)
    // BOTH KEYS on the write, for ONE release: a client still running the
    // pre-rename build reads `intangibleToPrimeras` and would otherwise
    // see this being lose its intangibility mid-Solrend. Drop the old key
    // next release, together with the both-key read in dimAllOppLyf.
    if (id === "p_premier_dragon_3") {
      field.esclings[slotIdx] = { ...slot, intangibleToImpera: true, intangibleToPrimeras: true };
      await updatePlayer(code, playerIdx, { field });
      showToast(`✦ Destined for Greatness — ${beingName} is Intangible to Impera and U'hltimas!`, GM.inkGold); return;
    }
    // Chaos Dragon Natural Disaster — OP2: Ruin 1 Non-Being (target picker)
    if (id === "p_chaos_dragon_1") {
      const oppTomes = (opponent?.field?.tomes || []).map((t, i) => ({ t, i })).filter(({ t }) => t?.id);
      const oppCurios = (opponent?.field?.curios || []).map((c, i) => ({ c, i })).filter(({ c }) => c);
      if (oppTomes.length === 0 && oppCurios.length === 0) { showToast("No Non-Beings to target.", GM.vellumFaint); return; }
      const options = [
        ...oppTomes.map(({ t, i }) => ({ id: t.id, label: `Tome: ${getCard(t.id)?.name}`, tomeIdx: i })),
        ...oppCurios.map(({ c, i }) => ({ id: c, label: `Curio: ${getCard(c)?.name}`, curioIdx: i })),
      ];
      // ── R-MRF: if opp has a priority tome, force-target it ──
      const mrf = getRequiredTarget(opponent?.field);
      if (mrf?.kind === "tome") {
        const forced = options.find(o => o.tomeIdx === mrf.slotIdx);
        if (forced) {
          showToast(`◆ ${mrf.name} must be ruined first — auto-targeting.`, GM.inkGold);
          const f = JSON.parse(JSON.stringify(opponent?.field || {}));
          f.tomes[forced.tomeIdx] = null;
          await updatePlayer(code, oppIdx, { field: f, necropolis: [...(opponent?.necropolis || []), mrf.id] });
          showToast(`✦ ${mrf.name} ruined!`, SD.red);
          return;
        }
      }
      setTargetPicker({ title: "◆ Natural Disaster — Ruin 1 Non-Being", options, onPick: async (opt) => {
        const f = JSON.parse(JSON.stringify(opponent?.field || {}));
        if (opt.tomeIdx !== undefined) {
          // Soak-aware (the D'uhmdumn Lord's shield applies) + recorded.
          const wasRuinedND = await attemptRuinOppTome(f, opt.tomeIdx);
          if (wasRuinedND) {
            await updatePlayer(code, oppIdx, { field: f, necropolis: [...(opponent?.necropolis || []), opt.id] });
            showToast(`✦ ${getCard(opt.id)?.name} ruined!`, SD.red);
          } else {
            await updatePlayer(code, oppIdx, { field: f });
          }
          setTargetPicker(null);
          return;
        }
        if (opt.curioIdx !== undefined) { f.curios[opt.curioIdx] = null; }
        // NECROPOLIS LAW: ruined curios hit the record.
        await updatePlayer(code, oppIdx, { field: f, necropolis: [...(opponent?.necropolis || []), opt.id] });
        showToast(`✦ ${getCard(opt.id)?.name} ruined!`, SD.red);
        setTargetPicker(null);
      }});
      return;
    }
    // Chaos Dragon Champion — opponent dims Anima by 1 on each Lyt Attack (set flag)
    if (id === "p_chaos_dragon_2") {
      field.esclings[slotIdx] = { ...slot, thrivingInChaos: true };
      await updatePlayer(code, playerIdx, { field });
      showToast(`✦ Thriving in Chaos — Opponent dims their Anima by 1 each time they Lyt Attack!`, SD.red); return;
    }
    // Chaos Dragon Lord — passive
    if (id === "p_chaos_dragon_3") {
      showToast(`◆ None More Than I — passive. Beings with Lyt > 10 are instantly ruined.`, GM.vellumFaint); return;
    }

    // ── U'HLTIS ─────────────────────────────────────────────────

    // H-09 Hyper Nova — OPT: Dim Lyf by 1 OR Anima by 2 (choice)
    if (id === "u_h09_lord") {
      setTargetPicker({ title: "◆ Radiant Shower — Choose effect", options: [
        { id: "lyf", label: "Dim all opponent Lyf by 1" },
        { id: "anima", label: "Dim opponent Anima by 2" },
      ], onPick: async (opt) => {
        if (opt.id === "lyf") { await dimAllOppLyf(1); showToast("✦ Radiant Shower — All Lyf dimmed by 1!", SD.blue); }
        else { const na = Math.max(0, (opponent?.anima || STARTING_ANIMA) - 2); await updatePlayer(code, oppIdx, { anima: na, ...buildAnimaLossUpdate(opponent, 2) }); showToast("✦ Radiant Shower — Anima dimmed by 2!", SD.red); if (na <= 0) await endMatch(code, playerIdx); }
        setTargetPicker(null);
      }});
      return;
    }
    // Xulan Ghost — passive: dim Anima by 1 whenever they Lyt Attack
    if (id === "u_xulan_lord") {
      showToast(`◆ Sweet Dreams — passive. Opponent dims their own Anima by 1 whenever they Lyt Attack.`, GM.vellumFaint); return;
    }
    // H-01 Super Nova — OP2: Dim Anima by 4
    if (id === "u_h01_lord") {
      const na = Math.max(0, (opponent?.anima || STARTING_ANIMA) - 4);
      await updatePlayer(code, oppIdx, { anima: na, ...buildAnimaLossUpdate(opponent, 4) });
      showToast("✦ Super Nova — Opponent Anima dimmed by 4!", SD.red);
      if (na <= 0) await endMatch(code, playerIdx);
      return;
    }
    // Shadow Fox — OP2: Dim Lyf by 2 AND Anima by 1
    if (id === "u_shadow_fox_lord") {
      await dimAllOppLyf(2);
      const na = Math.max(0, (opponent?.anima || STARTING_ANIMA) - 1);
      await updatePlayer(code, oppIdx, { anima: na, ...buildAnimaLossUpdate(opponent, 1) });
      showToast("✦ Royal Darkness — Lyf −2, Anima −1!", SD.red);
      if (na <= 0) await endMatch(code, playerIdx);
      return;
    }
    // Spectral Tiger — OP2: Dim Lyf by 1 AND Anima by 2
    if (id === "u_spectral_tiger_lord") {
      await dimAllOppLyf(1);
      const na = Math.max(0, (opponent?.anima || STARTING_ANIMA) - 2);
      await updatePlayer(code, oppIdx, { anima: na, ...buildAnimaLossUpdate(opponent, 2) });
      showToast("✦ Haunting Presence — Lyf −1, Anima −2!", SD.red);
      if (na <= 0) await endMatch(code, playerIdx);
      return;
    }
    // Inverted Serpent — OP2: dim Anima +2 if dimmed Anima this attack
    if (id === "u_inverted_serpent_lord") {
      field.esclings[slotIdx] = { ...slot, invFangsBuff: true };
      await updatePlayer(code, playerIdx, { field });
      showToast(`✦ Inverted Fangs — If ${beingName} dims Anima next attack, dims 2 more!`, GM.inkGold); return;
    }
    // Antdroid Lord — OP2: ALL beings redirect to Anima
    if (id === "u_antdroid_lord") {
      const newField = JSON.parse(JSON.stringify(me?.field || {}));
      newField.esclings = newField.esclings.map(s => s?.escendant ? { ...s, animaRedirectAll: true } : s);
      await updatePlayer(code, playerIdx, { field: newField });
      showToast("✦ King of the Hill — All beings can attack Anima this turn!", SD.gold); return;
    }
    // DG-0 U'hlti — Justice Reigns — "Send 1 Non-Being to Necropolis, or draw 1 card."
    // BUG FIX: previously ruined MY tomes, but the card targets opp's non-beings
    // and also draws from my own grimoire (was leaving grimoire untouched).
    if (id === "u_dg0_lord") {
      const oppTomes = (opponent?.field?.tomes || []).map((t, i) => ({ t, i })).filter(({ t }) => t?.id);
      const oppCurios = (opponent?.field?.curios || []).map((c, i) => ({ c, i })).filter(({ c }) => c);
      const oppSoulNonBeing = (opponent?.field?.esclings || []).flatMap((s, si) =>
        (s?.soul || []).filter(sid => {
          const c = getCard(sid);
          return c?.type === "Impera" || c?.type === "U'hlti" || c?.type === "Affinity";
        }).map(sid => ({ sid, slotIdx: si }))
      );
      const oppProphecy = opponent?.field?.prophecy;
      const nonBeingOptions = [
        ...oppTomes.map(({ t, i }) => ({ id: t.id, label: `Tome: ${getCard(t.id)?.name}`, tomeIdx: i })),
        ...oppCurios.map(({ c, i }) => ({ id: c, label: `Curio: ${getCard(c)?.name}`, curioIdx: i })),
        ...oppSoulNonBeing.map(({ sid, slotIdx }) => ({ id: sid, label: `${getCard(sid)?.type}: ${getCard(sid)?.name}`, slotIdx, soulId: sid })),
        ...(oppProphecy ? [{ id: oppProphecy, label: `Prophecy: ${getCard(oppProphecy)?.name}`, prophecy: true }] : []),
      ];
      const topOptions = [
        { id: "draw", label: "Draw 1 card from your Grimoire" },
      ];
      if (nonBeingOptions.length > 0) {
        topOptions.push({ id: "ruin", label: "Send 1 opponent Non-Being to Necropolis" });
      }
      setTargetPicker({ title: "◆ Justice Reigns — Choose", options: topOptions, onPick: async (opt) => {
        if (opt.id === "draw") {
          const pool = Object.entries(me?.grimoire || {}).flatMap(([gid, qty]) => Array(qty).fill(gid));
          if (pool.length === 0) { showToast("Grimoire is empty.", GM.vellumFaint); setTargetPicker(null); return; }
          const drawn = pool[Math.floor(Math.random() * pool.length)];
          const newG = { ...(me?.grimoire || {}) };
          newG[drawn] = (newG[drawn] || 1) - 1;
          if (newG[drawn] <= 0) delete newG[drawn];
          await updatePlayer(code, playerIdx, { hand: [...(me?.hand || []), drawn], grimoire: newG });
          showToast(`✦ Drew ${getCard(drawn)?.name}!`, GM.inkGold);
          setTargetPicker(null);
        } else if (opt.id === "ruin") {
          setTargetPicker({ title: "◆ Justice Reigns — Choose Non-Being", options: nonBeingOptions, onPick: async (nbOpt) => {
            const of = JSON.parse(JSON.stringify(opponent?.field || {}));
            if (nbOpt.tomeIdx !== undefined) {
              const wasRuined = await attemptRuinOppTome(of, nbOpt.tomeIdx);
              if (wasRuined) {
                await updatePlayer(code, oppIdx, { field: of, necropolis: [...(opponent?.necropolis || []), nbOpt.id] });
                showToast(`✦ ${getCard(nbOpt.id)?.name} sent to Necropolis!`, SD.red);
              } else {
                await updatePlayer(code, oppIdx, { field: of });
              }
            } else if (nbOpt.curioIdx !== undefined) {
              of.curios[nbOpt.curioIdx] = null;
              await updatePlayer(code, oppIdx, { field: of, necropolis: [...(opponent?.necropolis || []), nbOpt.id] });
              showToast(`✦ ${getCard(nbOpt.id)?.name} sent to Necropolis!`, SD.red);
            } else if (nbOpt.soulId !== undefined) {
              const stripNB = stripSoulCard(of.esclings[nbOpt.slotIdx].soul, nbOpt.soulId); // R70 on the way out
              of.esclings[nbOpt.slotIdx] = { ...of.esclings[nbOpt.slotIdx], soul: stripNB.soul };
              await updatePlayer(code, oppIdx, { field: of, necropolis: [...(opponent?.necropolis || []), ...stripNB.gone] });
              showToast(`✦ ${getCard(nbOpt.id)?.name} sent to Necropolis!`, SD.red);
            } else if (nbOpt.prophecy) {
              // ⭐ THE PROPHECY LIVES ON THE PLAYER ROOT, NOT ON THE
              // FIELD, so nulling the field copy alone removed the art
              // and nothing else. prophecyEngine reads
              // `mePlayer.prophecy` on every state update; the
              // activation button reads `me.field.prophecy || me.prophecy`
              // and handleActivateProphecy reads `me.prophecy`. So a
              // player who spent a card and its fare to remove their
              // adversary's ALTERNATE WIN CONDITION got a toast saying
              // it was sent to the Necropolis, and it kept evaluating,
              // kept rendering its button and could still end the duel.
              // The one counterplay the game offers against an instant
              // win did nothing at all. Both copies now.
              of.prophecy = null;
              await updatePlayer(code, oppIdx, { field: of, prophecy: null, necropolis: [...(opponent?.necropolis || []), nbOpt.id] });
              showToast(`✦ ${getCard(nbOpt.id)?.name} sent to Necropolis!`, SD.red);
            }
            setTargetPicker(null);
          }});
        }
      }});
      return;
    }
    // Z4Z Lord — passive
    if (id === "u_z4z_lord") {
      showToast(`◆ Apocalypse Now — passive. ${beingName} gains +1 Lyt per Time Being in combat.`, GM.vellumFaint); return;
    }
    // Premier Dragon Lord — OP3: Dim Anima by 10
    if (id === "u_premier_dragon_lord") {
      const na = Math.max(0, (opponent?.anima || STARTING_ANIMA) - 10);
      await updatePlayer(code, oppIdx, { anima: na, ...buildAnimaLossUpdate(opponent, 10) });
      showToast("✦ Show Stomper — Opponent Anima dimmed by 10!", SD.red);
      if (na <= 0) await endMatch(code, playerIdx);
      return;
    }
    // Lucky Dragon Lord — Dragon Eyes — "Roll 2 dice, if their sum is equal
    // to 2, then you've just won 2 Shadow Gamblings, and you can activate
    // another U'hlti."
    // IMPLEMENTATION: on success, +2 to shadowGamblingStreak AND set
    // dragonEyesBonus flag so the next U'hlti activation skips the once-per-turn
    // limit. Flag is consumed by the next U'hlti activation.
    //
    // NOTE: U'hltis currently have no once-per-turn gate in the codebase —
    // they can be re-activated freely. The dragonEyesBonus flag is stored
    // in state so that when a U'hlti cooldown system is introduced, Dragon
    // Eyes' bonus-activation promise can be honored. Until then, the SG
    // streak bonus is the meaningful effect.
    if (id === "u_lucky_dragon_lord") {
      const d1 = Math.floor(Math.random() * 6) + 1;
      const d2 = Math.floor(Math.random() * 6) + 1;
      const sum = d1 + d2;
      if (sum === 2) {
        const newStreak = (me?.shadowGamblingStreak || 0) + 2;
        const newField = JSON.parse(JSON.stringify(me?.field || {}));
        newField.dragonEyesBonus = true;
        await updatePlayer(code, playerIdx, {
          shadowGamblingStreak: newStreak,
          field: newField,
        });
        showToast(`✦ Dragon Eyes — Rolled ${d1}+${d2}=2! +2 Shadow Gambling wins (streak: ${newStreak}). You may activate another U'hlti this turn!`, SD.gold);
      } else {
        // Miss breaks a Shadow Gambling (treated as a loss). Reset streak.
        await updatePlayer(code, playerIdx, { shadowGamblingStreak: 0 });
        showToast(`✦ Dragon Eyes — Rolled ${d1}+${d2}=${sum}. Not 2. Shadow Gambling streak broken.`, GM.vellumFaint);
      }
      return;
    }
    // Chaos Dragon Lord — OP3: Aenarchists ruin own beings until 1 left
    if (id === "u_chaos_dragon_lord") {
      // Each player must ruin beings until 1 remains
      const mySlots = (me?.field?.esclings || []);
      const aliveCount = mySlots.filter(s => s?.escendant).length;
      if (aliveCount > 1) {
        const alive = mySlots.map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
        for (const { i } of alive.slice(0, -1)) {
          await descendBeing(playerIdx, i);
        }
      }
      const oppSlots = opponent?.field?.esclings || [];
      const oppAlive = oppSlots.filter(s => s?.escendant).length;
      if (oppAlive > 1) {
        const oppAliveSlots = oppSlots.map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
        for (const { i } of oppAliveSlots.slice(0, -1)) {
          await descendBeing(oppIdx, i);
        }
      }
      showToast("✦ I or None — Both Aenarchists ruined beings until 1 remains!", SD.red); return;
    }

    // ── AFFINITIES ──────────────────────────────────────────────

    // Holy — Y'Tyranuz: "Nullify any 1 attack."
    // Sets a one-shot flag on this being; any incoming attack is nullified.
    if (id === "aff_holy") {
      field.esclings[slotIdx] = { ...slot, nullifyNextAttack: true };
      await updatePlayer(code, playerIdx, { field });
      showToast("✦ Y'Tyranuz — Any 1 attack on this being will be nullified!", SD.gold); return;
    }
    // Awoken — Enlightenment: "Nullify 1 Affinity."
    // Card says ONE Affinity, not all. If opp has multiple, picker. If one,
    // straight ruin. If none, no-op.
    if (id === "aff_awoken") {
      const oppSlots = opponent?.field?.esclings || [];
      const oppAffinities = oppSlots.flatMap((s, si) =>
        (s?.soul || []).filter(sid => getCard(sid)?.type === "Affinity")
          .map(sid => ({ sid, slotIdx: si, name: getCard(sid)?.name }))
      );
      if (oppAffinities.length === 0) { showToast("Opponent has no Affinity to nullify.", GM.vellumFaint); return; }
      // ── NULLIFICATION CANON (caP'n ruling, July 10 2026) ──
      // "Nullified" renders the card USELESS — it is NOT removed, NOT
      // ruined, NOT sent to the Necropolis. Persistent/attached cards
      // (OPT tomes, Affinities, etc.) STAY where they live; they simply
      // can no longer do anything. Only "nullify AND ruin" text (Omnia
      // territory) removes a card. One-shots nullified mid-activation
      // still ride to the Necropolis — that is their lifecycle, not the
      // nullification. Implementation: the slot carries a
      // `nullifiedSouls` list; both activation gates (player
      // handleActivateSoulCard + NPC soulEffectUseful) refuse entries
      // on it. The Affinity remains visibly attached — a dead sigil.
      // ⚠ FIREBASE (roadmap C3): `nullifiedSouls` is a NEW slot field.
      const nullifyOne = async (pick) => {
        const of = JSON.parse(JSON.stringify(opponent?.field || {}));
        const slN = of.esclings[pick.slotIdx];
        of.esclings[pick.slotIdx] = {
          ...slN,
          nullifiedSouls: [...new Set([...(slN.nullifiedSouls || []), pick.sid])],
        };
        await updatePlayer(code, oppIdx, { field: of });
        showToast(`✦ Enlightenment — ${pick.name} nullified! It stays attached, rendered useless.`, SD.gold);
      };
      if (oppAffinities.length === 1) {
        await nullifyOne(oppAffinities[0]);
      } else {
        setTargetPicker({
          title: "◆ Enlightenment — Choose 1 Affinity to nullify",
          options: oppAffinities.map(a => ({ id: a.sid, label: a.name, slotIdx: a.slotIdx, sid: a.sid, name: a.name })),
          onPick: async (opt) => {
            await nullifyOne(opt);
            setTargetPicker(null);
          },
        });
      }
      return;
    }
    // Evil — X'aol: "Send 1 card to Necropolis, except another Affinity."
    // Card text says "1 card" with no location qualifier — interpret broadly
    // as any of opp's cards in play (Tome, Curio, Impera, U'hlti, Prophecy,
    // or Escendant being). Exception: not another Affinity. Player picks.
    if (id === "aff_evil") {
      const oppTomes = (opponent?.field?.tomes || []).map((t, i) => ({ t, i })).filter(({ t }) => t?.id);
      const oppCurios = (opponent?.field?.curios || []).map((c, i) => ({ c, i })).filter(({ c }) => c);
      const oppSoulNonAff = (opponent?.field?.esclings || []).flatMap((s, si) =>
        (s?.soul || []).filter(sid => {
          const c = getCard(sid);
          return c?.type === "Impera" || c?.type === "U'hlti";
        }).map(sid => ({ sid, slotIdx: si }))
      );
      const oppBeings = (opponent?.field?.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
      const oppProphecy = opponent?.field?.prophecy;
      const options = [
        ...oppTomes.map(({ t, i }) => ({ id: t.id, label: `Tome: ${getCard(t.id)?.name}`, tomeIdx: i })),
        ...oppCurios.map(({ c, i }) => ({ id: c, label: `Curio: ${getCard(c)?.name}`, curioIdx: i })),
        ...oppSoulNonAff.map(({ sid, slotIdx }) => ({ id: sid, label: `${getCard(sid)?.type}: ${getCard(sid)?.name}`, slotIdx, soulId: sid })),
        ...oppBeings.map(({ s, i }) => ({ id: s.escendant, label: `Being: ${getCard(s.escendant)?.name}`, beingSlot: i })),
        ...(oppProphecy ? [{ id: oppProphecy, label: `Prophecy: ${getCard(oppProphecy)?.name}`, prophecy: true }] : []),
      ];
      if (options.length === 0) { showToast("No valid cards to send.", GM.vellumFaint); return; }
      const mrfX = getRequiredTarget(opponent?.field);
      if (mrfX) { showToast(`${mrfX.name} must be ruined first!`, SD.red); return; }
      setTargetPicker({
        title: "◆ X'aol — Send 1 card to Necropolis (not an Affinity)",
        options,
        onPick: async (opt) => {
          const of = JSON.parse(JSON.stringify(opponent?.field || {}));
          let sentId = opt.id;
          if (opt.tomeIdx !== undefined) {
            const wasRuined = await attemptRuinOppTome(of, opt.tomeIdx);
            if (wasRuined) {
              await updatePlayer(code, oppIdx, { field: of, necropolis: [...(opponent?.necropolis || []), sentId] });
              showToast(`✦ X'aol — ${getCard(sentId)?.name} sent to Necropolis!`, SD.red);
            } else {
              await updatePlayer(code, oppIdx, { field: of });
            }
          } else if (opt.curioIdx !== undefined) {
            of.curios[opt.curioIdx] = null;
            await updatePlayer(code, oppIdx, { field: of, necropolis: [...(opponent?.necropolis || []), sentId] });
            showToast(`✦ X'aol — ${getCard(sentId)?.name} sent to Necropolis!`, SD.red);
          } else if (opt.soulId !== undefined) {
            const stripX = stripSoulCard(of.esclings[opt.slotIdx].soul, opt.soulId); // R70 on the way out
            of.esclings[opt.slotIdx] = { ...of.esclings[opt.slotIdx], soul: stripX.soul };
            await updatePlayer(code, oppIdx, { field: of, necropolis: [...(opponent?.necropolis || []), ...stripX.gone] });
            showToast(`✦ X'aol — ${getCard(sentId)?.name} sent to Necropolis!`, SD.red);
          } else if (opt.beingSlot !== undefined) {
            // ── THE FIFTH HAND-ROLLED "RUIN 1 BEING AND ITS SOUL" ────
            // Now the same funnel as the other four (resolver handoff 5).
            // This branch had R68's Heretical Tome and the wielded-
            // Treasure cascade — it was the CLOSEST of the five to
            // correct, which is exactly why it survived four sweeps —
            // but it was missing the C13 SOAK. A being holding
            // `nullifyNextAttack` is supposed to eat one ruin and clear
            // the ward; here it simply died. The same warded being
            // survived the Necklace of Congealed Death and did not
            // survive X'aol, off one Affinity, with nothing in either
            // card's printed text to justify the difference.
            //
            // It also wrote through this file's own `updatePlayer` off
            // the stale render closure (`opponent?.field`), so it could
            // not see R123's Tome ward either — that wrapper sits at the
            // head of `applyTomeEffect`, which this branch is not inside.
            //
            // ⚠ THE NAME WAS THE ONLY THING HOLDING THIS UP, and it was
            // checked rather than assumed: X'aol may not appear in STORY
            // PROSE, and this is a `label` on a card branch — a mechanic,
            // not a mention. caP'n cleared it 2026-09-14.
            await ruinBeingAndSoul(oppIdx, opt.beingSlot, ctx, { label: "X'aol" });
          } else if (opt.prophecy) {
            // Same root/field split as the note in the neighbouring
            // targeting branch — write both or write nothing.
            of.prophecy = null;
            await updatePlayer(code, oppIdx, { field: of, prophecy: null, necropolis: [...(opponent?.necropolis || []), sentId] });
            showToast(`✦ X'aol — ${getCard(sentId)?.name} sent to Necropolis!`, SD.red);
          }
          setTargetPicker(null);
        },
      });
      return;
    }
    // J'ynxi — Fiend or Faux: "Add 1 Shadow Gambling from your Grimoire or
    // Necropolis to your hand; cannot play same Shadow Gambling twice in a
    // row using this card."
    // BUG FIX: previous filter used id.includes("shadow_gambling") but the
    // Shadow Gambling Tome ids are things like tome_snake_eyes, tome_nevermore,
    // etc. — none contain "shadow_gambling". Must filter by ilk.
    // Anti-repeat tracked via slot flag `lastJynxiSG` — the last SG ID added
    // via this specific Affinity. The constraint is per-Affinity not global.
    if (id === "aff_jynxi") {
      const isShadowGambling = c => c?.type === "Tome" && c?.ilk === "Shadow Gambling";
      const grimSG = Object.entries(me?.grimoire || {})
        .flatMap(([gid, qty]) => isShadowGambling(getCard(gid)) ? [{ id: gid, src: "grimoire" }] : []);
      const necSG = (me?.necropolis || [])
        .map((nid, idx) => ({ nid, idx }))
        .filter(({ nid }) => isShadowGambling(getCard(nid)))
        .map(({ nid, idx }) => ({ id: nid, src: "necropolis", necroIdx: idx }));
      // Anti-repeat: filter out the last-used SG (per this specific affinity slot)
      const lastSG = slot?.lastJynxiSG;
      let allOptions = [...grimSG, ...necSG];
      if (lastSG) {
        allOptions = allOptions.filter(o => o.id !== lastSG);
      }
      if (allOptions.length === 0) {
        const msg = lastSG
          ? `Only ${getCard(lastSG)?.name} is available, but it was the last SG played via J'ynxi — cannot repeat.`
          : "No Shadow Gambling cards in Grimoire or Necropolis.";
        showToast(msg, GM.vellumFaint);
        return;
      }
      setTargetPicker({
        title: "◆ Fiend or Faux — Choose 1 Shadow Gambling",
        options: allOptions.map(o => ({
          id: o.id,
          label: `${o.src === "grimoire" ? "Grimoire" : "Necropolis"}: ${getCard(o.id)?.name}`,
          src: o.src,
          necroIdx: o.necroIdx,
        })),
        onPick: async (opt) => {
          const updates = { hand: [...(me?.hand || []), opt.id] };
          if (opt.src === "grimoire") {
            const newG = { ...(me?.grimoire || {}) };
            newG[opt.id] = (newG[opt.id] || 1) - 1;
            if (newG[opt.id] <= 0) delete newG[opt.id];
            updates.grimoire = newG;
          } else {
            const newNec = [...(me?.necropolis || [])];
            newNec.splice(opt.necroIdx, 1);
            updates.necropolis = newNec;
          }
          // Track last SG pulled via this slot's affinity (anti-repeat)
          const newField = JSON.parse(JSON.stringify(me?.field || {}));
          if (newField.esclings?.[slotIdx]) {
            newField.esclings[slotIdx] = {
              ...newField.esclings[slotIdx],
              lastJynxiSG: opt.id,
            };
            updates.field = newField;
          }
          await updatePlayer(code, playerIdx, updates);
          showToast(`✦ Fiend or Faux — ${getCard(opt.id)?.name} added to hand!`, SD.gold);
          setTargetPicker(null);
        },
      });
      return;
    }

    showToast(`✦ ${soulCard.name} — ${soulCard.effect?.substring(0, 60) || "activated!"}`, GM.inkGold);
  };

  const applyTomeEffect = (...args) => resolver_applyTomeEffect(...args, ctx);

  // Tomes that remain on the field permanently until ruined
  const FIELD_PERSISTENT_TOMES = new Set([
    // Passive shields
    "tome_khao_duhmdumn_beb", "tome_khao_duhmdumn_champion", "tome_khao_duhmdumn_lord",
    // Treasures that belong to a being (passive buffs)
    "tome_lightning_blade", "tome_gauntlets_azka",
    // Passive listener
    "tome_necklace_akula",
    // ATV — active until voided by opponent
    "tome_necklace_death", "tome_necklace_courage",
    "tome_restraining_ordeal", "tome_magma_carta", "tome_king_of_the_castle",
  ]);

  // ── Wave 1: Treasure wielder infrastructure ─────────────────
  //
  // These Treasures have "This Treasure belongs to 1 being" in their
  // effect text. On activation (from hand or face-down flip), the
  // player must choose an Escendant to be the wielder. The Treasure
  // remains on the Tome rift but its effect is tied to that being.
  //
  // Lineage tracking works by ESCLING SLOT INDEX, not card id, so
  // Transcend/Ascend/Descend all preserve the bond naturally — the
  // slot number doesn't change when a being transforms. The bond
  // breaks only when the slot empties entirely (being fully ruined
  // back to Escling), at which point any bonded Treasures ruin with it.
  // WIELDER_REQUIRED_TREASURES and needsWielder live in tomeResolver since
  // 2026-09-15 (one resolver) — the bond is the resolver's job now.



  const isTomePersistent = (card) => {
    if (!card) return false;
    const tag = card.effectTag;
    // OPT/OP2/OP3 — reactivatable
    if (tag === "OPT" || tag === "OP2" || tag === "OP3") return true;
    // ATV — active until voided
    if (tag === "ATV") return true;
    // Specific permanent tomes
    if (FIELD_PERSISTENT_TOMES.has(card.id)) return true;
    return false;
  };

  const handlePlayTome = async (tomeSlotIdx, cardIdx, faceDown = false) => {
    // R108 — BOTH doors: setting face-down and playing face-up are each
    // "playing a card", so the bar is chosen from the same argument the
    // handler branches on rather than from two call sites remembering.
    if (phaseBars(faceDown ? "PLACE_TOME" : "ACTIVATE_TOME")) return;
    setMulliganAvailable(false);
    const hand = [...(me?.hand || [])];
    const cardId = hand[cardIdx];
    const card = getCard(cardId);
    if (!card || card.type !== "Tome") return;
    const field = JSON.parse(JSON.stringify(me?.field || {}));
    const tomes = [field.tomes?.[0] ?? null, field.tomes?.[1] ?? null, field.tomes?.[2] ?? null];
    if (tomes[tomeSlotIdx] !== null) { showToast(`Tome rift ${tomeSlotIdx + 1} is occupied.`, SD.red); return; }
    // ── SEAL: universal PLACE_TOME gate (the rift count).
    {
      const v = gateTomePlacement(gateCtx());
      if (!v.valid) { showToast(v.reason || "Cannot place that Tome.", SD.red); return; }
    }
    // ── SEAL: universal ACTIVATE_TOME gate (R35/R36/R37 — THE WAIT).
    // A face-UP play IS an activation, on turn 0 of the Tome's life, so
    // The wait is asked here with `turnsOnField: 0` and faceDown FALSE,
    // which is what makes a face-up hand play legal at every level. The wedges at
    // :11676/:11917 already refused a face-up L1 by hand; this is the
    // same refusal, sourced from the catalogue instead of retyped, and
    // it also covers every caller that never went through a wedge.
    if (!faceDown) {
      // R35 — this is a card being played FACE-UP out of hand, not a set
      // Tome being flipped, so it waits for nothing (caP'n, 2026-09-13).
      // Said explicitly rather than left to the validator's default,
      // which is deliberately the strict one.
      const v = gateTomeActivation(gateCtx(), { id: cardId }, 0, false);
      if (!v.valid) { showToast(v.reason || "That Tome cannot be activated yet.", SD.red); return; }
    }
    // ── Wave 1: wielder sanity check ─────────────────────────
    // Wielder-required Treasures can't be played face-up with no
    // beings on the field — there's nothing to bond to. Face-down
    // placement is still allowed (hidden ambush for when you have
    // beings later).
    const aliveBeings = (me?.field?.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
    if (!faceDown && needsWielder(card) && aliveBeings.length === 0) {
      showToast(`${card.name} needs a wielder — place it face-down until you have a being.`, SD.red);
      return;
    }
    if (!faceDown) {
      if (!(await spendKhao(card.khaoCost || 0))) { showToast(`Not enough Khao. Need ${card.khaoCost || 0}.`, SD.red); return; }
    }
    // ── R-MRF: Khao D'uhmdumn — the Lord's consumable soak (nullifyFirstRuin)
    // is stamped by applyTomeEffect since 2026-09-15 (one resolver): every
    // activation passes through it, so the four stamps this file carried
    // (play, interrupt, flip, radial) are gone and the brain shares the write. ──
    // The ruin-first REQUIREMENT is now self-derived from card id in
    // getRequiredTarget, so we no longer rely on mustBeRuinedFirst surviving.
    // But the Lord's `nullifyFirstRuin` is CONSUMABLE state — it must be
    // written deterministically right here, inline, alongside the tome. The
    // old stale-me.field scan in applyTomeEffect could miss it entirely.
    const isKhaoDuhmdumn = cardId.includes("khao_duhmdumn");
    tomes[tomeSlotIdx] = {
      id: cardId, faceDown, persistent: isTomePersistent(card),
      currentLyf: card.lyf ?? null, wieldedBy: null,
      // ⭐ R35 — THE TURN THIS TOME WAS SET. It was never recorded on
      // the player's side, which is why the rule could not be enforced
      // against them. The NPC has always stamped it (NPC.placeOneTome
      // writes `placedTurn: turn`) and has always obeyed it.
      placedTurn: match?.turn ?? 0,
      // ⭐ R122's ANCHOR — AND WITHOUT IT THE CADENCE DOES NOT EXIST.
      // `isTomeOffCooldown` opens with `if (!tomeSlot?.lastUsedTurn &&
      // tomeSlot?.lastUsedTurn !== 0) return true` — absent means "never
      // fired", which is the right default for a Tome that has not. But
      // this write never SET it, so a Tome played face-up out of hand
      // was permanently "never fired": every re-activation gate passed,
      // for the rest of the Solrend, and an OP3 could fire twice in one
      // half-turn. The brain has stamped it since it had a flip path
      // (NPC.js · flipOneTome: `{ ...t, faceDown: false, lastUsedTurn: turn }`).
      //
      // ⚠ ONLY WHEN FACE-UP, and that is the R35 distinction, not a
      // shortcut. Playing face-up IS the activation and starts the
      // cadence; SETTING one face-down is not an activation at all — it
      // buys the wait instead (caP'n, 2026-09-13: "if you place it face
      // down, you must wait 1 full turn to activate it"). Stamping a set
      // Tome would put it on cooldown for a firing it never made.
      ...(faceDown ? {} : { lastUsedTurn: match?.turn ?? 0 }),
      ...(isKhaoDuhmdumn && !faceDown ? { mustBeRuinedFirst: true } : {}),
    };
    field.tomes = tomes;
    hand.splice(cardIdx, 1);
    await updatePlayer(code, playerIdx, { hand, field });
    if (!faceDown) {
      // ── WAVE 2/3: DECLARED — playing a tome face-up is an activation ──
      // The adversary answers before the effect resolves. Nihil voids the
      // effect (fare stays paid, lifecycle continues); Omnia ruins the tome
      // outright; LATE (Counter Hierarchy) = effect first, THEN eviction.
      const rwT = await declareAction({ kind: "ACTIVATION", cardId });
      if (rwT?.counter && !rwT?.late) {
        const meFT = matchRef.current?.players?.[playerIdx] || me;
        if (rwT.counter === "omnia") {
          await updatePlayer(code, playerIdx, { [`field/tomes/${tomeSlotIdx}`]: null, necropolis: [...(meFT?.necropolis || []), cardId] });
          showToast(`✕ ${rwT.counterName || "A counter"} — ${card.name} countered and ruined!`, SD.red);
        } else {
          if (!isTomePersistent(card)) {
            await updatePlayer(code, playerIdx, { [`field/tomes/${tomeSlotIdx}`]: null, necropolis: [...(meFT?.necropolis || []), cardId] });
          }
          showToast(`◈ ${rwT.counterName || "A counter"} — ${card.name}'s effect nullified!`, GM.inkGold);
        }
        setSelectedHandIdx(null);
        return;
      }
      const aonV = await applyTomeEffect(card);
      if (aonV === "AON_RUINED") { setSelectedHandIdx(null); return; }
      if (rwT?.counter && rwT?.late) {
        // Late counter — the effect resolved; the card leaves regardless,
        // persistence be damned (Counter Hierarchy, caP'n ruling).
        const meFT2 = matchRef.current?.players?.[playerIdx] || me;
        await updatePlayer(code, playerIdx, { [`field/tomes/${tomeSlotIdx}`]: null, necropolis: [...(meFT2?.necropolis || []), cardId] });
        showToast(`${rwT.counterName || "A late counter"} — effect resolved, ${card.name} evicted.`, GM.vellumDim);
      } else if (!isTomePersistent(card)) {
        // ── NECROPOLIS LAW (caP'n ruling, July 10 2026): every fired
        // one-shot rides to the Necropolis — this path used to vanish
        // the card from the game entirely. Direct path update for the
        // slot (avoids stale field reads); fresh read for the record.
        const meNP = matchRef.current?.players?.[playerIdx] || me;
        await updatePlayer(code, playerIdx, { [`field/tomes/${tomeSlotIdx}`]: null, necropolis: [...(meNP?.necropolis || []), cardId] });
      }
      // (The wielder bond for Lightning Blade / Gauntlets / Sword of Shadows
      // is opened by applyTomeEffect itself since 2026-09-15 — one resolver,
      // one picker, shared with the brain. The picker this branch carried
      // is gone.)
    } else {
      showToast(`${card.name} placed face down.`, GM.vellumDim);
    }
    setSelectedHandIdx(null);
  };

  // ── INTERRUPT — from hand (L3 only per rules) ──
  // Place face-up into an empty tome rift, pay Khao, fire effect.
  const handleInterrupt = async (cardIdx) => {
    const hand = [...(me?.hand || [])];
    const cardId2 = hand[cardIdx];
    const card = getCard(cardId2);
    if (!card || card.type !== "Tome" || card.level !== 3) return;
    if (!(await spendKhao(card.khaoCost || 0))) { showToast(`Not enough Khao. Need ${card.khaoCost || 0}.`, SD.red); return; }
    const field = JSON.parse(JSON.stringify(me?.field || {}));
    const tomes = [field.tomes?.[0] ?? null, field.tomes?.[1] ?? null, field.tomes?.[2] ?? null];
    const emptySlot = tomes.findIndex(t => t === null);
    if (emptySlot === -1) { showToast("No empty Tome rift.", SD.red); return; }
    // ── R-MRF: Khao D'uhmdumn Lord interrupt — set consumable shield inline ──
    const isKhaoDuhmdumnInt = cardId2.includes("khao_duhmdumn");
    tomes[emptySlot] = {
      id: cardId2, faceDown: false, persistent: isTomePersistent(card),
      currentLyf: card.lyf ?? null, wieldedBy: null,
      ...(isKhaoDuhmdumnInt ? { mustBeRuinedFirst: true } : {}),
    };
    field.tomes = tomes;
    hand.splice(cardIdx, 1);
    await updatePlayer(code, playerIdx, { hand, field });
    const aonI = await applyTomeEffect(card);
    if (aonI === "AON_RUINED") return;
    // ── NECROPOLIS LAW: interrupts are activations too — a spent
    // one-shot rides to the Necropolis instead of squatting the rift.
    if (!isTomePersistent(card)) {
      const meNI = matchRef.current?.players?.[playerIdx] || me;
      await updatePlayer(code, playerIdx, { [`field/tomes/${emptySlot}`]: null, necropolis: [...(meNI?.necropolis || []), cardId2] });
    }
    showToast(`⚡ ${card.name} activated as interrupt!`, SD.gold);
  };

  // ── INTERRUPT — from field (L2/L3 face-down flip) ──
  // Flip the face-down tome face-up, pay Khao, fire effect.
  const handleInterruptFromField = async (tomeSlotIdx) => {
    const field = JSON.parse(JSON.stringify(me?.field || {}));
    const tomes = [field.tomes?.[0] ?? null, field.tomes?.[1] ?? null, field.tomes?.[2] ?? null];
    const tome = tomes[tomeSlotIdx];
    if (!tome || typeof tome !== "object" || !tome.faceDown) return;
    const card = getCard(tome.id);
    if (!card || card.type !== "Tome" || card.level < 2) return;
    if (!(await spendKhao(card.khaoCost || 0))) { showToast(`Not enough Khao. Need ${card.khaoCost || 0}.`, SD.red); return; }
    // ── R-MRF: flipping a D'uhmdumn face-up activates its block ──
    // The ruin-first requirement self-derives from id + faceDown:false, so
    // simply clearing faceDown is enough to make getRequiredTarget see it.
    // The Lord additionally needs its consumable nullify shield set here —
    // THIS path previously set neither flag (root of the flip-then-attack bug).
    tomes[tomeSlotIdx] = {
      ...tome, faceDown: false, persistent: isTomePersistent(card),
      currentLyf: card.lyf ?? null,
    };
    field.tomes = tomes;
    await updatePlayer(code, playerIdx, { field });
    const aonF = await applyTomeEffect(card);
    if (aonF === "AON_RUINED") return;
    // ── NECROPOLIS LAW: a flipped one-shot fires and rides to the
    // Necropolis — same lifecycle as every other activation.
    if (!isTomePersistent(card)) {
      const meNF = matchRef.current?.players?.[playerIdx] || me;
      await updatePlayer(code, playerIdx, { [`field/tomes/${tomeSlotIdx}`]: null, necropolis: [...(meNF?.necropolis || []), tome.id] });
    }
    showToast(`⚡ ${card.name} flipped — interrupt active!`, SD.gold);
  };

  const handleOpenInterruptRadial = (e) => {
    // ── GILDED PICKER (July 2026 canon): card-list choices use
    // TargetPickerModal, never a text radial. No 6-item cap; both
    // interrupt handlers self-gate Khao via spendKhao.
    const options = [
      ...handInterrupts.map(({ card, idx }) => ({
        id: card.id, src: "hand", idx,
        label: `⚡ ${card.name} (hand) — ${card.khaoCost || 0} Khao`,
      })),
      ...fieldInterrupts.map(({ card, idx }) => ({
        id: card.id, src: "flip", idx,
        label: `⚡ ${card.name} (flip) — ${card.khaoCost || 0} Khao`,
      })),
    ];
    if (options.length === 0) return;
    setTargetPicker({
      title: "⚡ Interrupt — Choose a Tome",
      options,
      onPick: (opt) => {
        setTargetPicker(null);
        if (opt.src === "hand") handleInterrupt(opt.idx); else handleInterruptFromField(opt.idx);
      },
    });
  };

  const handleStartAttack = (slotIdx) => {
    const slot = me?.field?.esclings?.[slotIdx];
    if (!slot?.escendant) return;
    if (slot.attackedThisTurn) { showToast("Being already attacked this turn.", SD.red); return; }
    if (isFirstTurn) { showToast("No Attacks of any kind on the opening Turn.", SD.red); return; }
    setAttackingSlot(slotIdx); setTargetMode("select");
    showToast("Select a target — tap opponent being or ⚔ Hit Anima.", SD.red);
  };

  const handleStartAttackEscling = (slotIdx) => {
    const slot = me?.field?.esclings?.[slotIdx];
    if (!slot?.escling) return;
    if (slot.attackedThisTurn) { showToast("Escling already attacked this turn.", SD.red); return; }
    if (isFirstTurn) { showToast("No Attacks of any kind on the opening Turn.", SD.red); return; }
    // Esclings can Lyt Attack beings (Vulnerable/Ruin) but CANNOT dim Anima
    setAttackingSlot(slotIdx);
    setTargetMode("escling"); // special mode — can only target beings, not Anima
    showToast("Select a target — Esclings cannot dim Anima.", SD.red);
  };

  // ⚠ RULING 7 IS ENFORCED HERE TOO, not only in the radial. The radial
  // is a convenience; the handler is the rule. A Heretical Tome fills
  // the U'hlti slot and nothing may follow it in.
  const handleAttachSoulCard = async (slotIdx, cardIdx) => {
    // R108 — ATTACHING a card from HAND is playing a card and is barred.
    // ⚠ NOT TO BE CONFUSED WITH handleActivateSoulCard, which USES a
    // card that is ALREADY on a Soul and is expressly still legal:
    // caP'n, *"yes you can use cards attached to a being's Soul here"*.
    // The two names are one letter apart and mean opposite things here.
    if (phaseBars("ATTACH")) return;
    setMulliganAvailable(false);
    const hand = [...(me?.hand || [])];
    const cardId = hand[cardIdx];
    const card = getCard(cardId);
    const field = JSON.parse(JSON.stringify(me?.field || {}));
    const slot = field.esclings?.[slotIdx];
    if (!card || !slot?.escendant) return;
    const escendant = getCard(slot.escendant);
    if (!escendant) return;
    // ── SEAL: universal attachment gate ────────────────────────
    // R72/R73 single-attachment, R64 deviant restriction, R70 affinity
    // requires U'hlti. Inline checks below stay as detailed messaging;
    // the gate is the rule floor that the NPC obeys identically.
    {
      const v = card.type === "Affinity"
        ? enforce("ATTACH_AFFINITY", gateCtx(), { being: slot })
        : enforce("ATTACH", gateCtx(), { being: slot, attachment: cardId });
      if (!v.valid) { showToast(v.reason || "Cannot attach that.", SD.red); return; }
    }
    if (card.type === "U'hlti") {
      if (card.owner !== baseId(slot.escendant)) { showToast("This U'hlti belongs to a different being.", SD.red); return; }
      if (!slot.natural) { showToast("Only Natural Escendants can have U'hltis.", SD.red); return; }
      if (escendant.level < 3) { showToast("U'hltis can only attach to Level 3 beings.", SD.red); return; }
      if (slot.heretical) { showToast("A Heretical Tome holds that slot. Ruling 7 — it cannot be replaced.", SD.red); return; }
      const hasUhlti = (slot.soul || []).some(id => getCard(id)?.type === "U'hlti");
      if (hasUhlti) { showToast("Being already has a U'hlti.", SD.red); return; }
    }
    // Affinity — natural, L3, must have U'hlti, no existing Affinity
    if (card.type === "Affinity") {
      if (!slot.natural) { showToast("Only Natural Escendants can have Affinities.", SD.red); return; }
      if (escendant.level < 3) { showToast("Affinities can only attach to Level 3 beings.", SD.red); return; }
      const hasUhlti = (slot.soul || []).some(id => getCard(id)?.type === "U'hlti");
      if (!hasUhlti) { showToast("Being needs a U'hlti before attaching an Affinity.", SD.red); return; }
      const hasAffinity = (slot.soul || []).some(id => getCard(id)?.type === "Affinity");
      if (hasAffinity) { showToast("Being already has an Affinity.", SD.red); return; }
    }
    field.esclings[slotIdx] = { ...slot, soul: [...(slot.soul || []), cardId] };
    hand.splice(cardIdx, 1);
    await updatePlayer(code, playerIdx, { hand, field });
    showToast(`${card.name} attached!`, SD.gold);
    setSelectedHandIdx(null);
  };

  
  

  // ── attemptRuinOppTome — centralize tome-ruin with nullifyFirstRuin check ──
  // Handles Khao D'uhmdumn Lord's "first attack on this card is negated."
  // If the opponent tome at tomeIdx has nullifyFirstRuin, consume the flag
  // and return false (ruin did NOT happen). Otherwise null the slot, push
  // to necropolis, return true.
  //
  // Caller pattern:
  //   const f = JSON.parse(JSON.stringify(opponent?.field || {}));
  //   const wasRuined = await attemptRuinOppTome(f, tomeIdx);
  //   // f is mutated; caller does its own updatePlayer with f
  // ── AUDIT FIX (July 19 2026): on-ruin clauses, honored centrally ──
  // Restraining Ordeal — "You gain 2 Anima when this card is ruined."
  //   (Was granted NOWHERE before this fix.)
  // King of the Castle — "Draw 1 card when this card is sent to
  //   Necropolis." (Previously only fired on the devote-to-8 path.)
  // ownerIdx is the seat that OWNED the ruined tome; grants land there.
  // Reads live state via matchRef so grants stack safely mid-chain.
  const applyTomeRuinClauses = (...args) => resolver_applyTomeRuinClauses(...args, ctx);

  const attemptRuinOppTome = (...args) => resolver_attemptRuinOppTome(...args, ctx);

  // ── WAVE 4 (C12) — post-window refresh, BOTH sides ─────────────
  // A response window is seconds long; whatever the adversary fired may
  // have rewritten EITHER field. Every attack handler re-bases its local
  // clones from matchRef after a responded window — writing a pre-window
  // clone back is how a ruined attacker climbs out of the Necropolis.
  // Mutates the clone IN PLACE so every later reference sees fresh state.
  const refreshFieldInPlace = (fieldObj, side) => {
    const fresh = matchRef.current?.players?.[side]?.field;
    if (!fresh) return false;
    const copy = JSON.parse(JSON.stringify(fresh));
    Object.keys(fieldObj).forEach(k => delete fieldObj[k]);
    Object.assign(fieldObj, copy);
    return true;
  };

  // ── handleAttackOppTome — Lyt Attack directed at the taunt tome ──
  // The must-ruin-first path for attackers (canon, July 2026): while a
  // face-up Khao D'uhmdumn stands, Lyt attacks are DIRECTED AT IT. Only
  // the required priority tome is a legal tome target — this is not a
  // general "attack any tome" mechanic. Works for both Escendant
  // (targetMode "select") and Escling (targetMode "escling") attackers.
  // Resolution mirrors being combat against its Lyf:
  //   • Lord's nullifyFirstRuin → first hit of any kind soaked, flag consumed
  //   • Lyt >= current Lyf → tome ruined to opponent's Necropolis
  //   • Lyt <  current Lyf → chip the tome's currentLyf
  // The attacker spends its attack either way (attackedThisTurn).
  const handleAttackOppTome = async (tomeIdx) => {
    setMulliganAvailable(false);
    if (attackingSlot === null || !me || !opponent || !myTurn) return;
    const required = getRequiredTarget(opponent?.field);
    if (!required || required.kind !== "tome" || required.slotIdx !== tomeIdx) return;
    const myField = JSON.parse(JSON.stringify(me.field || {}));
    const atkSlot = myField.esclings?.[attackingSlot];
    if (!atkSlot || atkSlot.attackedThisTurn) return;
    // Attacker Lyt — being uses effective Lyt; escling uses its base Lyt.
    const isEsclingAtk = targetMode === "escling";
    const atkLyt = isEsclingAtk
      ? (getCard(atkSlot.escling)?.lyt || 1)
      : getEffectiveLyt(atkSlot, myField, opponent?.field || {});
    if (!isEsclingAtk && !atkSlot.escendant) return;

    const oppField2 = JSON.parse(JSON.stringify(opponent.field || {}));
    let tome = oppField2.tomes?.[tomeIdx];
    if (!tome?.id) return;
    // R125 — an Intangible Treasure cannot be ruined, and the swing is
    // not spent trying. The third door onto a Tome ruin; the other two
    // (tomeResolver.attemptRuinOppTome and NPC.ruinPlayerTomeSlot) ask
    // the same predicate in the same order.
    if (tomeIsIntangible(tome, matchRef.current?.turn ?? match?.turn ?? 0, seatCountOf(match))) {
      showToast(`🫧 ${getCard(tome.id)?.name || "That Treasure"} is Intangible — it cannot be ruined.`, GM.inkGold);
      setAttackingSlot(null); setTargetMode(null); return;
    }
    let tomeCard = getCard(tome.id);
    // ── WAVE 2/3: DECLARED — the strike at the Tome rift is announced ──
    {
      const rwA = await declareAction({ kind: "LYT_ATTACK_TOME", targetIdx: tomeIdx, lyt: atkLyt });
      if (rwA?.responded) {
        // Wave 4: BOTH sides re-base — the response may have rewritten
        // either field, and a pre-window clone written back resurrects
        // whatever the interrupt removed.
        refreshFieldInPlace(oppField2, oppIdx);
        tome = oppField2.tomes?.[tomeIdx];
        if (!tome?.id) { setAttackingSlot(null); setTargetMode(null); return; }
        tomeCard = getCard(tome.id);
        refreshFieldInPlace(myField, playerIdx);
        const atkNow = myField.esclings?.[attackingSlot];
        if (!atkNow || (isEsclingAtk ? !atkNow.escling : !atkNow.escendant)) {
          showToast("Your attacker was ruined by the response — the strike fizzles.", SD.red);
          setAttackingSlot(null); setTargetMode(null); return;
        }
        if (rwA.reactionName) showToast(`⚡ Adversary answers with ${rwA.reactionName}!`, GM.inkGold);
      }
    }

    if (tome.nullifyFirstRuin) {
      // Lord soaks the first hit — of ANY kind. Flag consumed, tome stands.
      oppField2.tomes[tomeIdx] = { ...tome, nullifyFirstRuin: false };
      await updatePlayer(code, oppIdx, { field: oppField2 });
      showToast(`◈ ${tomeCard?.name || "Tome"} — First attack nullified!`, GM.inkGold);
    } else {
      const tomeLyf = (tome.currentLyf !== null && tome.currentLyf !== undefined) ? tome.currentLyf : (tomeCard?.lyf ?? 0);
      if (atkLyt >= tomeLyf) {
        oppField2.tomes[tomeIdx] = null;
        // Wave 4: necropolis reads FRESH — the response window may have
        // ruined cards; a stale-prop spread would drop them from the pile.
        await updatePlayer(code, oppIdx, { field: oppField2, necropolis: [...((matchRef.current?.players?.[oppIdx]?.necropolis) || opponent?.necropolis || []), tome.id] });
        showToast(`✦ ${tomeCard?.name || "Tome"} ruined! Attacks are free again.`, SD.red);
        await applyTomeRuinClauses(oppIdx, tome.id);
      } else {
        oppField2.tomes[tomeIdx] = { ...tome, currentLyf: Math.max(0, tomeLyf - atkLyt) };
        await updatePlayer(code, oppIdx, { field: oppField2 });
        showToast(`${tomeCard?.name || "Tome"} hit! Lyf ${tomeLyf} → ${Math.max(0, tomeLyf - atkLyt)}`, GM.vellumFaint);
      }
    }
    // Wave 4: deref the LIVE clone (post-refresh), never the pre-window
    // atkSlot binding — that object may predate the response.
    myField.esclings[attackingSlot] = { ...myField.esclings[attackingSlot], attackedThisTurn: true };
    await updatePlayer(code, playerIdx, { field: myField });
    setAttackingSlot(null); setTargetMode(null);
  };

  // ── Unified attacker passive triggers — fires after ANY Lyt Attack ──
  // Handles: Natural Scalpel/Supernatural Bite (dim Anima on attack),
  // Spectral Claws (dim +1 Anima if Anima was dimmed), Inverted Fangs
  // U'hlti (dim +2 Anima if Anima was dimmed).
  //
  // Args:
  //   attackerSlot  — the attacker's slot data (with flags)
  //   dimmedAnima   — boolean: did THIS Lyt Attack dim the opponent's Anima?
  //                   true for Anima attacks; false for being-vs-being.
  //   startingOppAnima — the opp's CURRENT anima value (e.g. post-Anima-hit).
  //                     Defaults to opponent?.anima if not provided.
  // Returns: the new opponent anima after all triggers, for caller to check
  //          lethal. Also handles the updatePlayer calls internally.
  const runAttackerPassiveTriggers = async (attackerSlot, dimmedAnima, startingOppAnima = null) => {
    if (!attackerSlot) return startingOppAnima ?? (opponent?.anima ?? STARTING_ANIMA);
    let oppAnima = startingOppAnima ?? (opponent?.anima ?? STARTING_ANIMA);
    // Natural Scalpel / Supernatural Bite — "When this being Lyt Attacks,
    // dim adversary's Anima by 1" — fires on ALL Lyt Attacks, not just Anima.
    if (attackerSlot.dimAnimaOnAttack) {
      const amt = attackerSlot.dimAnimaOnAttack;
      oppAnima = Math.max(0, oppAnima - amt);
      await updatePlayer(code, oppIdx, { anima: oppAnima, ...buildAnimaLossUpdate(opponent, amt) });
      showToast(`◆ Passive — ${amt} additional Anima dimmed!`, SD.red);
      if (oppAnima <= 0) return oppAnima;
    }
    // Spectral Claws — "If this being's Lyt Attack dimmed Anima this turn,
    // dim their Anima by an additional 1." Fires only when Anima was dimmed
    // (either by this being attacking Anima, or by Natural Scalpel-style
    // passive above dimming Anima during a being attack).
    if (attackerSlot.spectralClaws && (dimmedAnima || attackerSlot.dimAnimaOnAttack)) {
      oppAnima = Math.max(0, oppAnima - 1);
      await updatePlayer(code, oppIdx, { anima: oppAnima, ...buildAnimaLossUpdate(opponent, 1) });
      showToast(`◆ Spectral Claws — 1 extra Anima dimmed!`, GM.inkGold);
      if (oppAnima <= 0) return oppAnima;
    }
    // Inverted Fangs (U'hlti) — "If this being's Lyt Attack dimmed Anima
    // this turn, dim their Anima by an additional 2." Same gating as
    // Spectral Claws.
    if (attackerSlot.invFangsBuff && (dimmedAnima || attackerSlot.dimAnimaOnAttack)) {
      oppAnima = Math.max(0, oppAnima - 2);
      await updatePlayer(code, oppIdx, { anima: oppAnima, ...buildAnimaLossUpdate(opponent, 2) });
      showToast(`◆ Inverted Fangs — 2 additional Anima dimmed!`, SD.red);
      if (oppAnima <= 0) return oppAnima;
    }
    return oppAnima;
  };

  const handleAttackTarget = async (slotIdx) => {
    setMulliganAvailable(false);
    if (attackingSlot === null || !me || !opponent) return;
    const myField = JSON.parse(JSON.stringify(me.field || {}));
    const oppField = JSON.parse(JSON.stringify(opponent.field || {}));
    const attackingSlotData = myField.esclings?.[attackingSlot];
    const attacker = getCard(attackingSlotData?.escendant);
    if (!attacker) return;
    // ── R118 · THE TANK ART DULLS THIS SWING, IF IT WAS BOUGHT ──
    // caP'n: *"it lasts for only that one specific Lyt Attack. It can
    // only hit 1 of your Adversary's beings per turn."* So the mark is
    // on the DEFENDER's seat, keyed by the ATTACKER's rift, stamped with
    // the half-turn — and it is read HERE, before anything else looks at
    // the number, so every branch below sees the dulled Lyt and no
    // branch has to remember the Art exists.
    const rawLyt = getEffectiveLyt(attackingSlotData, myField, oppField);
    const tankDull = tankDullFor(opponent, attackingSlot, matchRef.current?.turn ?? match?.turn ?? 0);
    const effectiveLyt = Math.max(0, rawLyt - tankDull);
    if (tankDull) showToast(`🛡 The Tank Art — your swing lands ${tankDull} Lyt weaker (${rawLyt} → ${effectiveLyt}).`, "#60a5fa");
    let targetSlot = oppField.esclings?.[slotIdx];
    // ── SEAL: universal Lyt-attack gate (R25 Fallen-immunity, R30 once-per-turn) ──
    {
      const v = enforce("ATTACK", gateCtx(), {
        attacker: attackingSlotData,
        defender: targetSlot,
        attackerSlotIndex: attackingSlot,
        defenderSlotIndex: slotIdx,
        lytDamage: effectiveLyt,
        // R119/R121 — both are questions about WHERE a being stands, so
        // the gate needs the two battle lines, not just the two beings.
        // Passed explicitly rather than left to the gate's fallback so
        // this call site reads as what it is: a swing that can now be
        // refused for a reason neither card knows about.
        rifts: oppField?.esclings || null,
        attackerRifts: myField?.esclings || null,
      });
      if (!v.valid) { showToast(v.reason || "Illegal attack.", SD.red); setAttackingSlot(null); setTargetMode(null); return; }
    }
    if (!targetSlot?.escendant && targetSlot?.escling) { showToast("Esclings cannot be Lyt Attacked — they are eternal.", GM.vellumFaint); return; }
    // ── R25: Fallen Escendants are IMPERVIOUS to Lyt Attacks ──
    // Fallen is a ONE-TURN state (canon, July 2026): set at descent,
    // cleared at the owner's end-of-turn reset alongside Lyf regeneration.
    // While the flag stands, the being cannot be Lyt-attacked.
    if (targetSlot?.fallen) {
      showToast("Fallen beings are impervious to Lyt Attacks.", GM.inkGold);
      setAttackingSlot(null); setTargetMode(null); return;
    }
    // ── R-MRF: mustBeRuinedFirst — priority-target enforcement ──
    // Covers both H-01 Quasar (escling-slot) AND Khao D'uhmdumn (tome-slot).
    // A Khao D'uhmdumn on the field can't be "Lyt-attacked" — it must be
    // ruined by a tome-ruining effect. So when a tome-type protection is
    // active, ALL escendant Lyt attacks are illegal until the tome is gone.
    const required = getRequiredTarget(oppField);
    if (required) {
      if (required.kind === "escling" && required.slotIdx !== slotIdx) {
        showToast(`${required.name} must be ruined first!`, SD.red); return;
      }
      if (required.kind === "tome") {
        showToast(`${required.name} must be ruined first — tap it to direct your attack!`, SD.red); return;
      }
    }
    // ── WAVE 2/3: DECLARED — the Lyt Attack announces itself ──
    // All legality gates have passed; the adversary answers before the
    // strike lands. If anything fired, re-read their FRESH field —
    // defensive flags (intangible, nullifyNextAttack…) may have just
    // been raised, and every check below must see them.
    {
      const rwA = await declareAction({ kind: "LYT_ATTACK", targetIdx: slotIdx, lyt: effectiveLyt });
      if (rwA?.responded) {
        // Wave 4: BOTH sides re-base. The old code refreshed only the
        // defender — if the response ruined MY attacker, the stale
        // myField write at the bottom resurrected it whole.
        refreshFieldInPlace(oppField, oppIdx);
        targetSlot = oppField.esclings?.[slotIdx];
        if (!targetSlot?.escendant) { setAttackingSlot(null); setTargetMode(null); return; }
        refreshFieldInPlace(myField, playerIdx);
        if (!myField.esclings?.[attackingSlot]?.escendant) {
          showToast("Your attacker was ruined by the response — the strike fizzles.", SD.red);
          setAttackingSlot(null); setTargetMode(null); return;
        }
        if (rwA.reactionName) showToast(`⚡ Adversary answers with ${rwA.reactionName}!`, GM.inkGold);
      }
    }
    // Chaos Dragon Lord — beings with Lyt > 10 instantly ruined
    // (Wave 4: read the refreshed clone, not the render prop.)
    const oppChaosLord = oppField.esclings?.some(s => {
      const c = getCard(s?.escendant);
      return c?.id === "chaos_dragon_lord" && (s?.soul || []).some(id => id === "p_chaos_dragon_3");
    });
    if (oppChaosLord && effectiveLyt > 10) {
      // BUG #2 FIX: "ruined" follows universal ruin semantics — an L2/L3
      // attacker DESCENDS (top level to Necropolis, lower levels persist),
      // and on a full ruin its soul cards go to Necropolis + bonded
      // Treasures cascade. The old inline null dropped the soul entirely
      // and never descended. descendBeing handles both cases correctly.
      await descendBeing(playerIdx, attackingSlot);
      showToast(`✦ None More Than I — ${attacker.name} had Lyt > 10 and was instantly ruined!`, SD.red);
      setAttackingSlot(null); setTargetMode(null); return;
    }
    // Wave 4: the defensive branches below used to RE-CLONE from the
    // stale render props (opponent.field / me.field) — immediately after
    // the window refresh above, quietly writing pre-window state back
    // over whatever the response changed. They now build on the live
    // in-scope clones (oppField / myField), which are post-window truth.
    if (targetSlot?.intangible) {
      oppField.esclings[slotIdx] = { ...targetSlot, intangible: false };
      await updatePlayer(code, oppIdx, { field: oppField });
      showToast("⚡ Intangible! The attack passes through — being unharmed.", GM.inkGold);
      myField.esclings[attackingSlot] = { ...myField.esclings[attackingSlot], attackedThisTurn: true };
      await updatePlayer(code, playerIdx, { field: myField });
      setAttackingSlot(null); setTargetMode(null); return;
    }
    // nullifyNextAttack — Holy Affinity, H-09 Hyper Nova, Xulan Ghost
    if (targetSlot?.nullifyNextAttack) {
      oppField.esclings[slotIdx] = { ...targetSlot, nullifyNextAttack: false };
      await updatePlayer(code, oppIdx, { field: oppField });
      showToast("✦ Attack Nullified! The being was protected.", SD.gold);
      myField.esclings[attackingSlot] = { ...myField.esclings[attackingSlot], attackedThisTurn: true };
      await updatePlayer(code, playerIdx, { field: myField });
      setAttackingSlot(null); setTargetMode(null); return;
    }
    // reflectNextAttack — Lucky Dragon Lord (Fate Maker)
    // "Reflect the Lyt Attack to your adversary's Anima" — the card
    // belongs to the DEFENDER; their adversary is ME, the attacker.
    // DIRECTION FIX (July 2026): this used to dim the DEFENDER's own
    // Anima — their protective Impera was nuking themselves.
    if (targetSlot?.reflectNextAttack) {
      oppField.esclings[slotIdx] = { ...targetSlot, reflectNextAttack: false };
      await updatePlayer(code, oppIdx, { field: oppField });
      // R100 — ADVERSARIAL, and it is worth saying why: the Anima
      // leaves MY pool, but the card that took it is THEIRS. Fate Maker
      // belongs to the defender. This is damage done to me by an
      // adversary's Impera, not a payment I chose to make, so it counts
      // toward every Prophecy exactly as a Lyt Attack would. `me` is
      // the classification default; the note is the audit trail.
      const reflected = effectiveLyt;
      // Wave 4: my anima reads FRESH — the response may have moved it.
      const myAnimaNow = matchRef.current?.players?.[playerIdx]?.anima ?? me?.anima ?? STARTING_ANIMA;
      const myNewAnima = Math.max(0, myAnimaNow - reflected);
      await updatePlayer(code, playerIdx, { anima: myNewAnima, ...buildAnimaLossUpdate(me, reflected) });
      showToast(`✦ Fate Maker — Attack reflected! ${reflected} of YOUR Anima dimmed!`, SD.gold);
      myField.esclings[attackingSlot] = { ...myField.esclings[attackingSlot], attackedThisTurn: true };
      await updatePlayer(code, playerIdx, { field: myField });
      if (myNewAnima <= 0) await endMatch(code, oppIdx, playerIdx) /* AUDIT FIX 2026-08-22: MY anima just hit 0 — the loser is ME, explicitly, never guessed via getTargetIdx(oppIdx) */;
      setAttackingSlot(null); setTargetMode(null); return;
    }
    if (targetSlot?.escendant) {
      const defender = getCard(targetSlot.escendant);
      if (!defender) return;
      // Wave 4: effective Lyf derives from the refreshed clone.
      const defenderCurrentLyf = getEffectiveLyf(targetSlot, oppField);
      const isVulnerable = defenderCurrentLyf === 0;
      if (isVulnerable) {
        // Toast truth: descent only happens if the lower level actually
        // waits in the soul — the old toast claimed "descended!" off the
        // top card's level alone, masking full ruins as descents.
        const willDescend = (defender.level || 1) > 1 && (targetSlot.soul || []).some(sid => {
          const sc = getCard(sid);
          return sc?.type === "Escendant" && sc?.level === (defender.level - 1);
        });
        await descendBeing(oppIdx, slotIdx);
        showToast(willDescend ? `${defender.name} descended to L${defender.level - 1}!` : `${defender.name} ruined!`, SD.red);
        await offerWardenBounty(slotIdx, willDescend);
        // Check if opponent anima should be dimmable (all beings gone)
        // Win is only by dimming Anima to 0 — not by ruining beings
      } else if (terrorOverkill(me, attackingSlot, effectiveLyt, defenderCurrentLyf,
                                matchRef.current?.turn ?? match?.turn ?? 0)) {
        // ── R116 · OVERKILL ──────────────────────────────────────
        // caP'n: *"if its lyt attack exceeds the adversary's Lyf by at
        // least one, ruin it instead of fracturing."* STRICTLY greater,
        // and the predicate lives in rules.js so nobody softens it to
        // `>=` here. EQUAL still Fractures — that is the branch below.
        //
        // ⚠ IT IS TESTED BEFORE THE FRACTURE BRANCH, not inside it,
        // because `effectiveLyt >= lyf` swallows every overkill case: a
        // check written after it can never be reached.
        //
        // R118's passive is the one thing that stops it: a Tank is
        // immune to this ruin and to nothing else.
        if (slotIdx === BODY_RIFT) {
          oppField.esclings[slotIdx] = { ...targetSlot, currentLyf: 0 };
          await updatePlayer(code, oppIdx, { field: oppField });
          showToast(`🛡 ${defender.name} is the Tank — the Terror Art cannot ruin it. Fractured! Lyf → 0.`, SD.red);
        } else {
          await descendBeing(oppIdx, slotIdx);
          showToast(`⚔ The Terror Art — ${defender.name} ruined where it stood.`, SD.red);
        }
      } else if (effectiveLyt >= defenderCurrentLyf) {
        oppField.esclings[slotIdx] = { ...targetSlot, currentLyf: 0 };
        await updatePlayer(code, oppIdx, { field: oppField });
        showToast(`${defender.name} Fractured! Lyf → 0.`, SD.red);
      } else {
        oppField.esclings[slotIdx] = { ...targetSlot, currentLyf: defenderCurrentLyf - effectiveLyt };
        await updatePlayer(code, oppIdx, { field: oppField });
        showToast(`${defender.name} hit! Lyf ${defenderCurrentLyf} → ${defenderCurrentLyf - effectiveLyt}`, GM.vellumFaint);
      }
    } else { showToast("No being in that slot.", GM.vellumFaint); return; }
    // ── R30 + R124 · THE SWING IS COUNTED, AND THE FLAG IS THE LATCH ──
    // `attacksThisTurn` goes up by one; `attackedThisTurn` goes true only
    // when the allowance is SPENT. A being wearing King of the Anthill
    // beside two other Antdroids has three swings, and the flag is what
    // three auditors and the brain read to know it is finished.
    {
      const sw = myField.esclings[attackingSlot] || {};
      const takenNow = (Number(sw.attacksThisTurn) || 0) + 1;
      const allowedNow = lytAttacksAllowed(sw, myField.esclings || [], oppField.esclings || [], getCard, attackingSlot);
      myField.esclings[attackingSlot] = {
        ...sw,
        attacksThisTurn: takenNow,
        attackedThisTurn: takenNow >= allowedNow,
        // A GRANTED one-shot is spent by the swing it paid for; the
        // PASSIVE half is recomputed from the board every time and must
        // not be cleared here.
        extraAttack: false,
      };
      if (takenNow < allowedNow) {
        showToast(`⚔ ${attacker.name} may swing again (${takenNow} of ${allowedNow}).`, GM.inkGold);
      }
    }
    await updatePlayer(code, playerIdx, { field: myField });

    // ── Wave 2: Lightning Blade breakthrough dim on wielder's attack ──
    await fireLightningBladeIfBonded(attackingSlot);

    // ── PASSIVE AFTER-ATTACK EFFECTS ────────────────────────────
    const attackerSlot = myField.esclings[attackingSlot];
    const attackerSoul = attackerSlot?.soul || [];
    // Being-vs-being attack: no Anima was dimmed by the attack itself.
    // The helper handles Natural Scalpel/Supernatural Bite (which DO dim Anima)
    // and chains Spectral Claws/Inverted Fangs off of them if present.
    const oppAnimaAfter = await runAttackerPassiveTriggers(attackerSlot, false);
    if (oppAnimaAfter <= 0) { await endMatch(code, playerIdx); setAttackingSlot(null); setTargetMode(null); return; }
    // Chaos dragon champion passive — opponent dimmed Anima whenever THEY attack (checked in handleAttackAnima for opponent)
    // Chaos dragon lord passive — Lyt > 10 instantly ruined.
    // GUARD FIX (July 2026): this sweep used to run UNCONDITIONALLY —
    // any opponent being with Lyt > 10 was obliterated after every one
    // of my attacks, Chaos Lord or no Chaos Lord, with its soul deleted
    // from the game. The aura only exists while I have Chaos Dragon Lord
    // with None More Than I (p_chaos_dragon_3) attached. Ruin follows
    // universal semantics (descend if a lower level waits in the soul;
    // full ruin sends the soul to Necropolis), batched into ONE write.
    const iHaveNoneMoreThanI = (me?.field?.esclings || []).some(s => {
      const c = getCard(s?.escendant);
      return c?.id === "chaos_dragon_lord" && (s?.soul || []).some(id => id === "p_chaos_dragon_3");
    });
    const oppField2 = JSON.parse(JSON.stringify(opponent?.field || {}));
    const oppNec2 = [...(opponent?.necropolis || [])];
    let ruinedByNone = false;
    if (iHaveNoneMoreThanI) (oppField2.esclings || []).forEach((s, i) => {
      if (!s?.escendant) return;
      const topC = getCard(s.escendant);
      if (!topC) return;
      const myField2ForOpp = JSON.parse(JSON.stringify(me?.field || {}));
      const oppLyt = getEffectiveLyt(s, oppField2, myField2ForOpp);
      if (oppLyt > 10) {
        // Universal ruin — batched inline (descendBeing writes per call and
        // would clobber itself across multiple slots in one sweep).
        oppNec2.push(s.escendant);
        const soulN = s.soul || [];
        const prevN = soulN.find(id => { const c = getCard(id); return c?.type === "Escendant" && c?.level === topC.level - 1; });
        // 🔥 R68 — THE HERETICAL TOME LEAVES WITH THE LEVEL IT WAS BONDED TO,
        // in BOTH branches, exactly as tomeResolver.descendBeing and
        // NPC.descendPlayerSlot do. It is NOT in `soul`, so nothing above this
        // line touched it: on the DESCENT branch `{...s}` carried it down onto
        // a Champion, where the rite is illegal, and left it there for the rest
        // of the Solrend. One push, above both branches, because both need it.
        if (s.heretical) oppNec2.push(s.heretical);
        if (prevN && topC.level > 1) {
          const keptN = soulN.filter(id => {
            const c = getCard(id);
            if (!c) return false;
            if (c.type === "Escendant") return true;
            // ⚠ A FIFTH COPY OF THE DESCENT FILTER, and it is why the
            // grep that found the other four missed this one: it reads
            // `topC.level` where they read `topCard.level` / `top.level`.
            // R51/R52 — EVERY Impera stays (caP'n, 2026-09-13).
            if (c.type === "Impera") return true;
            if (c.type === "U'hlti" || c.type === "Affinity") return true;
            return false;
          });
          soulN.filter(id => !keptN.includes(id)).forEach(id => oppNec2.push(id));
          const prevCardN = getCard(prevN);
          oppField2.esclings[i] = { ...s, escendant: prevN, heretical: null, currentLyf: prevCardN?.lyf ?? null, soul: keptN.filter(id => id !== prevN), attackedThisTurn: false, intangible: false, mustBeRuinedFirst: false, nullifyNextAttack: false, reflectNextAttack: false, fallen: true, fallenTurn: match?.turn || 0, nullifiedSouls: (s.nullifiedSouls || []).filter(id => keptN.includes(id)) };
        } else {
          soulN.forEach(id => oppNec2.push(id));
          // (the R68 push is hoisted above both branches — see above)
          oppField2.esclings[i] = { ...s, escendant: null, currentLyf: null, soul: [], soulUsed: {}, heretical: null, fallen: false, fallenTurn: null, nullifiedSouls: [] };
          cascadeWieldedTreasures(oppField2, oppNec2, i);
        }
        ruinedByNone = true;
      }
    });
    if (ruinedByNone) {
      await updatePlayer(code, oppIdx, { field: oppField2, necropolis: oppNec2 });
      showToast("◆ None More Than I — Lyt > 10 being instantly ruined!", SD.red);
      await fireAkulaListenerOnRuin();
    }

    // Thriving in Chaos / Sweet Dreams — fire on ANY Lyt Attack (including
    // attacks against beings, not just Anima). Card belongs to opponent;
    // "your adversary" = me (attacker); "they Lyt Attack" = I just did.
    {
      const oppHasThriving = opponent?.field?.esclings?.some(s =>
        // ⚠ THE SOUL LIMB IS GONE (2026-09-08). `|| soul.includes(...)`
        // made the card fire the moment it was ATTACHED, which made its
        // own activation branch — and the 8 Khao its OPT fare charges —
        // purely decorative. Sweet Dreams (u_xulan_lord) is a genuine
        // passive (effectTag null) and correctly keeps its soul read;
        // this one is not.
        s?.thrivingInChaos
      );
      const oppHasSweetDreams = opponent?.field?.esclings?.some(s =>
        (s?.soul || []).some(id => id === "u_xulan_lord")
      );
      // R100 — ADVERSARIAL. The toast reads "You dimmed 1 of your own
      // Anima" and that phrasing is what makes this row worth a note:
      // BOTH cards belong to the ADVERSARY ("Dim your adversary's Anima
      // by 1, whenever they Lyt Attack"). Attacking is not a payment.
      // Their card, their effect, their damage — it counts.
      let myAnimaAfter = me?.anima ?? STARTING_ANIMA;
      if (oppHasThriving) {
        myAnimaAfter = Math.max(0, myAnimaAfter - 1);
        await updatePlayer(code, playerIdx, { anima: myAnimaAfter, ...buildAnimaLossUpdate(me, 1) });
        showToast(`◆ Thriving in Chaos — You dimmed 1 of your own Anima!`, SD.red);
      }
      if (oppHasSweetDreams) {
        myAnimaAfter = Math.max(0, myAnimaAfter - 1);
        await updatePlayer(code, playerIdx, { anima: myAnimaAfter, ...buildAnimaLossUpdate(me, 1) });
        showToast(`◆ Sweet Dreams — You dimmed 1 of your own Anima!`, SD.red);
      }
      // ── AUDIT FIX (July 19 2026): self-dim to 0 ends the duel —
      // matches the Fate Maker reflect precedent (self-zero = defeat).
      if ((oppHasThriving || oppHasSweetDreams) && myAnimaAfter <= 0) {
        await endMatch(code, oppIdx, playerIdx) /* AUDIT FIX 2026-08-22: MY anima just hit 0 — the loser is ME, explicitly, never guessed via getTargetIdx(oppIdx) */;
      }
    }

    setAttackingSlot(null); setTargetMode(null);
  };

  const handleAttackTargetEscling = async (slotIdx) => {
    setMulliganAvailable(false);
    // Same as handleAttackTarget but triggered by an Escling — uses escling's lyt
    if (attackingSlot === null || !me || !opponent) return;
    const myField = JSON.parse(JSON.stringify(me.field || {}));
    const esclingSlot = myField.esclings?.[attackingSlot];
    const esclingCard = getCard(esclingSlot?.escling);
    if (!esclingCard) return;
    const oppField = JSON.parse(JSON.stringify(opponent.field || {}));
    let targetSlot = oppField.esclings?.[slotIdx];
    if (!targetSlot?.escendant) { showToast("No being to attack.", GM.vellumFaint); return; }
    // ── R-MRF: priority-target enforcement on Escling Lyt attacks ──
    const requiredMRF = getRequiredTarget(oppField);
    if (requiredMRF) {
      if (requiredMRF.kind === "escling" && requiredMRF.slotIdx !== slotIdx) {
        showToast(`${requiredMRF.name} must be ruined first!`, SD.red); return;
      }
      if (requiredMRF.kind === "tome") {
        showToast(`${requiredMRF.name} must be ruined first — tap it to direct your attack!`, SD.red); return;
      }
    }
    // ── R25: Fallen beings are impervious to Lyt Attacks — Esclings too ──
    // (This path had NO fallen check — Esclings could hit Fallen beings.)
    if (targetSlot?.fallen) {
      showToast("Fallen beings are impervious to Lyt Attacks.", GM.inkGold);
      setAttackingSlot(null); setTargetMode(null); return;
    }
    // ── WAVE 2/3: DECLARED — Escling strikes announce themselves too ──
    {
      const rwA = await declareAction({ kind: "LYT_ATTACK", targetIdx: slotIdx, lyt: esclingCard.lyt || 1 });
      if (rwA?.responded) {
        // Wave 4: BOTH sides re-base (see handleAttackTarget).
        refreshFieldInPlace(oppField, oppIdx);
        targetSlot = oppField.esclings?.[slotIdx];
        if (!targetSlot?.escendant) { setAttackingSlot(null); setTargetMode(null); return; }
        refreshFieldInPlace(myField, playerIdx);
        if (!myField.esclings?.[attackingSlot]?.escling) {
          showToast("Your Escling's rift was emptied by the response — the strike fizzles.", SD.red);
          setAttackingSlot(null); setTargetMode(null); return;
        }
        if (rwA.reactionName) showToast(`⚡ Adversary answers with ${rwA.reactionName}!`, GM.inkGold);
      }
    }
    // Wave 4: attacker flag writes deref the LIVE clone — the pre-window
    // esclingSlot binding may predate the response.
    if (targetSlot.intangible) {
      oppField.esclings[slotIdx] = { ...targetSlot, intangible: false };
      await updatePlayer(code, oppIdx, { field: oppField });
      showToast("⚡ Intangible! Attack passes through.", GM.inkGold);
      myField.esclings[attackingSlot] = { ...myField.esclings[attackingSlot], attackedThisTurn: true };
      await updatePlayer(code, playerIdx, { field: myField });
      setAttackingSlot(null); setTargetMode(null); return;
    }
    // nullifyNextAttack — protections apply to ANY Lyt Attack, Esclings included
    if (targetSlot?.nullifyNextAttack) {
      oppField.esclings[slotIdx] = { ...targetSlot, nullifyNextAttack: false };
      await updatePlayer(code, oppIdx, { field: oppField });
      showToast("✦ Attack Nullified! The being was protected.", SD.gold);
      myField.esclings[attackingSlot] = { ...myField.esclings[attackingSlot], attackedThisTurn: true };
      await updatePlayer(code, playerIdx, { field: myField });
      setAttackingSlot(null); setTargetMode(null); return;
    }
    // reflectNextAttack — Fate Maker bounces the Escling's Lyt to MY Anima
    if (targetSlot?.reflectNextAttack) {
      oppField.esclings[slotIdx] = { ...targetSlot, reflectNextAttack: false };
      await updatePlayer(code, oppIdx, { field: oppField });
      // R100 — ADVERSARIAL, same reasoning as the Escendant path above.
      const eLytR = getCard(myField.esclings?.[attackingSlot]?.escling)?.lyt || 1;
      // Wave 4: my anima reads FRESH — the response may have moved it.
      const myAnimaNowE = matchRef.current?.players?.[playerIdx]?.anima ?? me?.anima ?? STARTING_ANIMA;
      const myNewAnimaE = Math.max(0, myAnimaNowE - eLytR);
      await updatePlayer(code, playerIdx, { anima: myNewAnimaE, ...buildAnimaLossUpdate(me, eLytR) });
      showToast(`✦ Fate Maker — Attack reflected! ${eLytR} of YOUR Anima dimmed!`, SD.gold);
      myField.esclings[attackingSlot] = { ...myField.esclings[attackingSlot], attackedThisTurn: true };
      await updatePlayer(code, playerIdx, { field: myField });
      if (myNewAnimaE <= 0) await endMatch(code, oppIdx, playerIdx) /* AUDIT FIX 2026-08-22: MY anima just hit 0 — the loser is ME, explicitly, never guessed via getTargetIdx(oppIdx) */;
      setAttackingSlot(null); setTargetMode(null); return;
    }
    const defender = getCard(targetSlot.escendant);
    if (!defender) return;
    // ⭐ EFFECTIVE Lyf, not raw. This read `currentLyf` directly, so the
    // ESCLING attack path — and only that path — was blind to every Lyf
    // buff in the game. A Xulan Wanderer at currentLyf 2 shows
    // effective Lyf 4 on both boards (Diamond Skin); a 2-Lyt Escling
    // read 2, evaluated 2 >= 2, and FRACTURED it. The Escendant attack
    // path fifty lines up has always used the resolver. The same hole
    // voided the School-Dragon Primera's "+1 Lyf for the Turn" against
    // Esclings entirely — the player paid Khao and watched nothing
    // happen, with no message explaining why.
    // `oppField` (the working copy cloned at the top of this handler),
    // not `opponent.field` — this matches the Escendant attack path at
    // ~9864 exactly. Cosmic Casbah is a TEAM AURA counted off the
    // defender's whole field, so if anything earlier in this handler
    // has already removed one of their beings, the raw snapshot would
    // count a being that is no longer standing.
    const defLyf = getEffectiveLyf(targetSlot, oppField);
    const lyt = esclingCard.lyt || 1;
    if (defLyf === 0) {
      // Already vulnerable — descend or ruin (toast truth: descent only if
      // the lower level actually waits in the soul)
      const willDescendE = (defender.level || 1) > 1 && (targetSlot.soul || []).some(sid => {
        const sc = getCard(sid);
        return sc?.type === "Escendant" && sc?.level === (defender.level - 1);
      });
      await descendBeing(oppIdx, slotIdx);
      showToast(willDescendE ? `${defender.name} descended to L${defender.level - 1}!` : `${defender.name} ruined by Escling!`, SD.red);
    } else if (lyt >= defLyf) {
      // Fracture — make vulnerable
      oppField.esclings[slotIdx] = { ...targetSlot, currentLyf: 0 };
      await updatePlayer(code, oppIdx, { field: oppField });
      showToast(`${defender.name} Fractured! Lyf → 0.`, SD.red);
    } else {
      oppField.esclings[slotIdx] = { ...targetSlot, currentLyf: defLyf - lyt };
      await updatePlayer(code, oppIdx, { field: oppField });
      showToast(`${defender.name} hit! Lyf ${defLyf} → ${defLyf - lyt}`, GM.vellumFaint);
    }
    myField.esclings[attackingSlot] = { ...myField.esclings[attackingSlot], attackedThisTurn: true };
    await updatePlayer(code, playerIdx, { field: myField });
    setAttackingSlot(null); setTargetMode(null);
  };

  const handleAttackAnima = async () => {
    setMulliganAvailable(false);
    if (attackingSlot === null || !me || !opponent) return;
    const myField = JSON.parse(JSON.stringify(me.field || {}));
    // ── RULEZ_V2.md, canon; false 'July 2026' cite scrapped 2026-08-27 — Esclings may NEVER attack Anima ──
    // Hard rule beyond the UI dispatch — a bare-Escling attackingSlot
    // is rejected here no matter how the call arrived.
    if (!myField.esclings?.[attackingSlot]?.escendant) {
      showToast("Esclings cannot dim Anima — target a being.", GM.vellumFaint);
      setAttackingSlot(null); setTargetMode(null); return;
    }
    const attacker = getCard(myField.esclings?.[attackingSlot]?.escendant);
    if (!attacker) return;
    // ── SEAL: universal R31/R26 Anima-attack gate ──────────────
    // The gate is the single truth for "can I hit Anima". It honors the
    // Fallen one-turn shield and Phantom Paws identically for player & NPC.
    const _asd = me?.field?.esclings?.[attackingSlot];
    const _hasDirect = !!(_asd?.canDirectAnima || _asd?.animaRedirectAll);
    {
      const v = enforce("ATTACK_ANIMA", gateCtx(), { targetAnima: 0, hasDirectAnima: _hasDirect });
      if (!v.valid) { showToast(v.reason || "Cannot attack Anima yet.", SD.red); return; }
    }
    // ── R-MRF: priority-target enforcement before Anima attacks ──
    // A Khao D'uhmdumn tome (or Quasar-style escling) must be destroyed
    // before any attack — including Anima — is legal. Phantom Paws does
    // NOT bypass this; the protection is absolute until the priority
    // target is gone.
    const requiredMRF = getRequiredTarget(opponent.field);
    if (requiredMRF) {
      showToast(requiredMRF.kind === "tome"
        ? `${requiredMRF.name} must be ruined first — tap it to direct your attack!`
        : `${requiredMRF.name} must be ruined first!`, SD.red); return;
    }
    const effectiveLytAnima = getEffectiveLyt(myField.esclings?.[attackingSlot], myField, opponent?.field || {});
    // Can attack Anima when:
    // 1. No opponent beings with Lyf remaining, OR
    // 2. Attacker has canDirectAnima (Phantom Paws ability)
    const attackingSlotData2 = me?.field?.esclings?.[attackingSlot];
    const hasDirectAnima = attackingSlotData2?.canDirectAnima || attackingSlotData2?.animaRedirectAll;
    const oppSlots = opponent.field?.esclings || [];
    // Premier Dragon Lord — intangibleToImpera blocks soul card activation
    // (enforced in SoulModal canActivate check — being with this flag can't be targeted by Impera)
    //
    // ── R26 Fallen bypass (canon, July 2026) ──────────────────
    // A descended (Fallen) being does NOT block Anima attacks — it is
    // bypassable IMMEDIATELY, the same turn it descends. Ruin the L3,
    // and the L2 left standing can be walked past to dim Anima directly.
    // The only thing that re-blocks after a descent is a new protection
    // (e.g. dropping a D'uhmdumn tome as an interrupt).
    // ── R31 (RULEZ_V2.md, canon; false 'July 2026' cite scrapped 2026-08-27): FRACTURED STANDS, FRACTURED BLOCKS ──
    // Lyf is irrelevant to blocking. A Fractured (0-Lyf) Escendant still
    // guards the Anima until it descends. The ONLY bypass is Fallen.
    // ⚠ R119 — THE RENDERER'S HALF OF THE WALL, AND IT ASKS THE SAME
    // PREDICATE THE GATE DOES. This was a private `.some()` over every
    // rift, which was correct for three rifts and wrong the moment a
    // fourth arrived: a being in the MIND rift never walls Anima, so a
    // seat with a lone Warden up has an open Anima and this would have
    // said otherwise. And because RulesEnforcer.validateAnimaAttack knows
    // that and this did not, the two would have DISAGREED — the button
    // refusing a swing the gate would have allowed, which reads to a
    // player as a dead button with no message and no way to diagnose it.
    // One predicate, both sides, no private copy.
    // R115 — with the clock, because the Warden's hole in the wall sleeps
    // for three full Turns like every other Role passive. Its twin in the
    // gate (RulesEnforcer.validateAnimaAttack) asks the same predicate the
    // same way, and the two must never learn this separately.
    const hasStandingBeing = !animaOpenToLytAttack(oppSlots, matchRef.current?.turn ?? match?.turn ?? 0, seatCountOf(matchRef.current || match));
    if (hasStandingBeing && !hasDirectAnima) { showToast("Must attack a standing being first — unless this being has Phantom Paws!", SD.red); return; }
    // ── WAVE 2/3: DECLARED — the Anima strike announces itself ──
    // The NPC's reactive brain may flip a survival tome here (Wave 2);
    // a PvP defender may fire any legal interrupt (Wave 3). Either way
    // the strike still lands — on the adversary's FRESH total.
    let oppFreshA = opponent;
    {
      const rwA = await declareAction({ kind: "ANIMA_ATTACK", lyt: effectiveLytAnima });
      if (rwA?.responded) {
        oppFreshA = matchRef.current?.players?.[oppIdx] || opponent;
        // Wave 4: my side re-bases too — if the response ruined the
        // attacker, the strike fizzles instead of a ghost dealing it
        // and the stale myField write resurrecting the being.
        refreshFieldInPlace(myField, playerIdx);
        if (!myField.esclings?.[attackingSlot]?.escendant) {
          showToast("Your attacker was ruined by the response — the strike fizzles.", SD.red);
          setAttackingSlot(null); setTargetMode(null); return;
        }
        if (rwA.reactionName) showToast(`⚡ Adversary answers with ${rwA.reactionName}!`, GM.inkGold);
      }
    }
    let dmg = effectiveLytAnima;
    // ── PRIMERA: the Beast rider (H22) — "if the user dims the
    // adversary's Anima this Turn, dim it by 1 more." Armed by the
    // stamp press, consumed exactly once by this being's dim.
    if (primeraTurnRef.current?.beastArmed?.[attackingSlot] && dmg > 0) {
      dmg += 1;
      setPrimeraTurn(p => {
        const b = { ...p.beastArmed }; delete b[attackingSlot];
        return { ...p, beastArmed: b };
      });
      showToast(`◆ Beast — the bite goes 1 deeper!`, STAMP_COLORS.Beast);
    }
    const newAnima = Math.max(0, (oppFreshA.anima || STARTING_ANIMA) - dmg);
    // Dim ledger — the retroactive Beast press consults this
    if (dmg > 0) animaDimmedSlotsRef.current.add(attackingSlot);
    // R30 + R124 — the Anima swing is a Lyt Attack and is counted like
    // one. (The `extraAttack` re-arm further down still works: it sets
    // the latch back to false, and the count it is spending is its own.)
    {
      const swA = myField.esclings[attackingSlot] || {};
      const takenA = (Number(swA.attacksThisTurn) || 0) + 1;
      const allowedA = lytAttacksAllowed(swA, myField.esclings || [], opponent?.field?.esclings || [], getCard, attackingSlot);
      myField.esclings[attackingSlot] = { ...swA, attacksThisTurn: takenA, attackedThisTurn: takenA >= allowedA };
    }
    await updatePlayer(code, playerIdx, { field: myField });
    await updatePlayer(code, oppIdx, { anima: newAnima, ...buildAnimaLossUpdate(oppFreshA, dmg) });
    // Wall of Light — absorb and ADD damage instead
    // Wave 4 self-clobber fix: this branch used to RE-CLONE the stale
    // me.field render prop to clear the flag — a clone that predated
    // the attackedThisTurn write two lines up, so writing it back
    // erased the flag and granted a free second attack every absorb.
    // It now flips the flag on the SAME live clone that carries the
    // attack mark, and checks the flag off that clone too.
    if (myField.wallOfLightActive && myField.wallOfLightTurn === (matchRef.current?.turn ?? match?.turn ?? 0)) {
      const myAnimaWL = matchRef.current?.players?.[playerIdx]?.anima ?? me?.anima ?? STARTING_ANIMA;
      const gained = sealAnimaGain(myAnimaWL, dmg);
      await updatePlayer(code, playerIdx, { anima: gained, ...buildAnimaGainUpdate(me, dmg) });
      // Clear the flag on the live clone (attackedThisTurn survives).
      const wf = JSON.parse(JSON.stringify(myField));
      wf.wallOfLightActive = false;
      await updatePlayer(code, playerIdx, { field: wf });
      showToast(`◈ Wall of Light absorbed ${dmg} damage — +${dmg} Anima instead!`, GM.inkGold);
      setAttackingSlot(null); setTargetMode(null); return;
    }
    showToast(`Direct hit! −${dmg} Anima!`, SD.red);
    // ── Wave 2: Lightning Blade breakthrough dim on wielder's attack ──
    await fireLightningBladeIfBonded(attackingSlot);
    // Attacker passive triggers — Natural Scalpel, Supernatural Bite,
    // Spectral Claws, Inverted Fangs. dimmedAnima=true because this is
    // the Anima-attack path; pass newAnima as starting value since opp's
    // Anima was just reduced by the hit but that update may not be in
    // `opponent` state yet.
    const attackerSlotAnima = myField.esclings?.[attackingSlot];
    const oppAnimaPostTriggers = await runAttackerPassiveTriggers(attackerSlotAnima, true, newAnima);
    if (oppAnimaPostTriggers <= 0) { await endMatch(code, playerIdx); setAttackingSlot(null); setTargetMode(null); return; }
    // Thriving in Chaos (p_chaos_dragon_2) & Sweet Dreams (u_xulan_lord) —
    // "Dim your adversary's Anima by 1, whenever they Lyt Attack."
    // Card belongs to opponent; "your adversary" = attacker = me; "they" (the
    // attacker) = me. So if opp has either card, I lose 1 Anima per card.
    const oppHasThriving = opponent?.field?.esclings?.some(s =>
      // See the twin of this read above: activation only, not attachment.
      s?.thrivingInChaos
    );
    const oppHasSweetDreams = opponent?.field?.esclings?.some(s =>
      (s?.soul || []).some(id => id === "u_xulan_lord")
    );
    // R100 — ADVERSARIAL, same reasoning as the Escendant path.
    let myAnimaAfter = me?.anima ?? STARTING_ANIMA;
    if (oppHasThriving) {
      myAnimaAfter = Math.max(0, myAnimaAfter - 1);
      await updatePlayer(code, playerIdx, { anima: myAnimaAfter, ...buildAnimaLossUpdate(me, 1) });
      showToast(`◆ Thriving in Chaos — You dimmed 1 of your own Anima!`, SD.red);
    }
    if (oppHasSweetDreams) {
      myAnimaAfter = Math.max(0, myAnimaAfter - 1);
      await updatePlayer(code, playerIdx, { anima: myAnimaAfter, ...buildAnimaLossUpdate(me, 1) });
      showToast(`◆ Sweet Dreams — You dimmed 1 of your own Anima!`, SD.red);
    }
    // ── AUDIT FIX (July 19 2026): self-dim to 0 ends the duel (matches
    // the Fate Maker reflect precedent — self-zero = defeat).
    if ((oppHasThriving || oppHasSweetDreams) && myAnimaAfter <= 0) {
      await endMatch(code, oppIdx, playerIdx) /* AUDIT FIX 2026-08-22: MY anima just hit 0 — the loser is ME, explicitly, never guessed via getTargetIdx(oppIdx) */; setAttackingSlot(null); setTargetMode(null); return;
    }
    // extraAttack/extraAttackAnimaOnly — clear after use
    const mySlotAfter = myField.esclings[attackingSlot];
    if (mySlotAfter?.extraAttackAnimaOnly || mySlotAfter?.extraAttack) {
      // ⚠ THE COUNT COMES BACK WITH THE FLAG. Since R124 the gate counts
      // `attacksThisTurn`, so clearing only the latch would leave the
      // re-armed swing refused by a number nobody reset.
      myField.esclings[attackingSlot] = { ...mySlotAfter, extraAttack: false, extraAttackAnimaOnly: false, attackedThisTurn: false, attacksThisTurn: Math.max(0, (Number(mySlotAfter.attacksThisTurn) || 1) - 1) };
      await updatePlayer(code, playerIdx, { field: myField });
      showToast("◆ Extra attack used!", GM.inkGold);
    }
    if (newAnima <= 0) { await endMatch(code, playerIdx); setAttackingSlot(null); setTargetMode(null); return; }
    setAttackingSlot(null); setTargetMode(null);
  };

  const handleResurrect = async (cardId, slotIdx, payWithAnima = false) => {
    const card = getCard(cardId);
    if (!card) return;
    // R108 — once the Attack phase is declared, no more cards may be
    // played. See THE PHASE GATE above gateCtx.
    if (phaseBars("RESURRECT")) return;
    // ── CANON (caP'n ruling, July 2026): one Resurrection per turn ──
    // R89 — THE GATE READS THE PERSISTED TALLY. `resurrectedThisTurn` is
    // now `local || turnLedgerCount(record, match.turn) >= RESURRECT_PER_TURN`,
    // so an F5 mid-Turn no longer buys a second rite. This handler wrote a
    // perfect {turn,count} ledger and then gated on a useState boolean it
    // reset on every remount; the ledger is the gate now.
    if (resurrectedThisTurn) { showToast("Already Resurrected this turn.", SD.red); return; }
    // ── SEAL: R44 (per-instance TALLY — Beb 3× / Champion 2× / Lord 1×,
    // Rulez July 25 2026 wave) + R47 cost gate ──
    // The tally is per-Escendant, not per-slot: rebuilding a chain
    // (L1→L2→L3) on one slot is legal, and a Beb may now return up to
    // three times. resurrectedCards is append-only and EVERY method
    // appends, so counting occurrences IS the tally.
    // R45 (L1→L2→L3 order) is structurally enforced by findResurrectSlot:
    // an L2/L3 can only land where its L(n-1) of the same chain stands.
    const resurrectedCards = me?.resurrectedCards || [];
    {
      const v = enforce("RESURRECT", gateCtx(), {
        fromLevel: (card.level || 1) - 1,
        toLevel: card.level || 1,
        resurrectionsThisSolrend: resTally(resurrectedCards, cardId),
        cardName: card.name,
        // R85 — this is the PASSIVE rite. Card effects call the gate
        // without this flag and are never blocked by the generation.
        passive: true,
        graveshifted: !canPassiveResurrect(cardId),
      });
      if (!v.valid) { showToast(v.reason || "Cannot resurrect.", SD.red); return; }
    }
    const cost = RESURRECT_COST[`L${card.level || 1}`] ?? RESURRECT_COST.L3; // R-catalogue owns the fare
    // ── CURRENCY CHOICE (July 2026, mirrors handleGrimoireSearch) ──
    // Resurrection used to silently drain Khao first and only fall back to
    // Anima when Khao ran dry — the player never got to CHOOSE. Anima is a
    // life total; spending it must be a deliberate act, never a default.
    // spendAnima also routes through buildAnimaLossUpdate, so wound-crack
    // visuals fire correctly — the old raw anima write skipped them.
    if (payWithAnima) {
      if (!(await spendAnima(cost))) { showToast(`Not enough Anima. Need ${animaFare(cost)}.`, SD.red); return; }
    } else {
      if (!(await spendKhao(cost))) { showToast(`Not enough Khao. Need ${cost}.`, SD.red); return; }
    }
    // LIVE READ (C12) — spendKhao/spendAnima above each AWAIT a write, so
    // `me` is now at least one write out of date. Cloning it here and
    // writing it back reverted the board to its pre-payment shape: the
    // Resurrected being's slot came back null and rendered a bare Escling.
    const meLiveRes = livePlayer(playerIdx, me);
    const field = JSON.parse(JSON.stringify(meLiveRes?.field || {}));
    // ── CHAIN FIX (July 2026, mirrors handleAscend) ──
    // Reviving an L2/L3 onto its standing L(n-1) used to OVERWRITE the
    // lower level — it vanished from the game (not in soul, not in
    // Necropolis). Rebuilding the chain must soul the outgoing being so
    // later descents can find it, exactly like Ascending does.
    const resSlot = field.esclings[slotIdx] || {};
    const resPrevSoul = resSlot.soul || [];
    const resSoul = resSlot.escendant && !resPrevSoul.includes(resSlot.escendant)
      ? [...resPrevSoul, resSlot.escendant]
      : resPrevSoul;
    field.esclings[slotIdx] = { ...resSlot, escendant: cardId, soul: resSoul, fallen: false, fallenTurn: null, currentLyf: card.lyf ?? null, attackedThisTurn: false, intangible: false };
    // ── STALE-ANIMA FIX (July 2026) ─────────────────────────
    // `me` is a render-closure snapshot taken BEFORE spendAnima() ran, so
    // `me.anima` still holds the pre-payment total. Passing it here and
    // then writing `anima:` back in the payload REFUNDED the Resurrection
    // instantly — the Anima left, the wound cracked, and a heartbeat later
    // it was all restored. (The Khao path was immune only because `khao`
    // was never in this payload.) Read the LIVE post-spend value off
    // matchRef instead, and only touch `anima` when Hope actually fires.
    const meLive = matchRef.current?.players?.[playerIdx] || me;
    const liveAnima = meLive?.anima ?? STARTING_ANIMA;
    const hopeAnima = triggerHopeIfTimeEntry(card, field, liveAnima);
    // ── R89 ON THE RECORD — one Resurrection per Turn ────────────
    // Same reasoning as primeraLedger: GameStateValidator could not watch
    // R89 because this allowance lived only in React state. It rides in
    // THIS payload, not a second write, so the tally can never disagree
    // with the rite it is counting. The turn is stamped, not cleared — a
    // tally from an older turn is visibly stale rather than silently 0.
    //
    // ⚠ NAME COLLISION, DELIBERATELY ACCEPTED. Debugger.js's simulator
    // already uses `resurrectedThisTurn` as a plain INTEGER on its own
    // synthetic player objects (Legal.resurrect reads
    // `p.resurrectedThisTurn >= RESURRECT_PER_TURN`). Those objects are
    // built by the simulator and never come from a match record, so there
    // is no collision today — but if anyone ever points Legal.resurrect at
    // a REAL player, `{turn,count} >= 1` is `NaN >= 1`, which is false,
    // and the R89 gate silently passes forever. This file reads `.count`
    // through turnLedgerCount (see the gate at the top of this handler);
    // any other consumer must do the same.
    const resLedger = meLive?.resurrectedThisTurn;
    const resTurnNow = match?.turn || 0;
    // ── R44/R85 · TAKE ONE COPY OUT, NOT EVERY COPY ──────────────
    // ⚠ THIS WAS `.filter(id => id !== cardId)`, and necroSeal.js's own
    // banner names that exact expression as the bug the seal was built to
    // kill: "the resurrect write `necropolis.filter(id => id !== cardId)`
    // deleted BOTH copies when you revived one." The seal masks it today —
    // two ruined Bebs are `taez_beb~0~7` and `taez_beb~0~9`, so the filter
    // can only match one — but the tally is PER COPY, so the removal must
    // be a MULTISET removal or it is only correct by accident. The brain's
    // resurrectOne has always spliced one index; this is the same move.
    const resNec = [...(meLive?.necropolis || [])];
    {
      const gone = resNec.indexOf(cardId);
      if (gone !== -1) resNec.splice(gone, 1);
    }
    // R44 record — append-only, per Solrend. Never overwrite the array.
    await updatePlayer(code, playerIdx, {
      field,
      necropolis: resNec,
      ...(hopeAnima !== liveAnima ? { anima: hopeAnima } : {}),
      resurrectedCards: [...resurrectedCards, cardId],
      resurrectedThisTurn: {
        turn: resTurnNow,
        count: (resLedger?.turn === resTurnNow ? (resLedger.count || 0) : 0) + 1,
      },
    });
    setResurrectedThisTurn(true); // local latch only — the persisted {turn,count} rode the payload above
    showToast(`${card.name} Resurrected!`, GM.inkGold);
  };

  const handleSlotAction = (slotIdx, e) => {
    if (!me || targetMode === "select") return;
    const slot = me.field?.esclings?.[slotIdx];

    if (selectedHandIdx !== null) {
      const cardId = (me?.hand || [])[selectedHandIdx];
      const card = getCard(cardId);
      if (card?.type === "Escendant" && card?.level === 1 && !slot?.escendant) { handleTranscend(slotIdx, selectedHandIdx, false); setSelectedHandIdx(null); setRadial(null); return; }
      if (card?.type === "Escendant" && card?.level > 1 && slot?.escendant) { handleAscend(slotIdx, selectedHandIdx, false); setSelectedHandIdx(null); setRadial(null); return; }
      if (card?.type === "Impera" && slot?.escendant) { handleAttachImpera(slotIdx, selectedHandIdx); setSelectedHandIdx(null); setRadial(null); return; }
      if ((card?.type === "U'hlti" || card?.type === "Affinity") && slot?.escendant) { handleAttachSoulCard(slotIdx, selectedHandIdx); setSelectedHandIdx(null); setRadial(null); return; }
    }

    const pos = smartPos(e.clientX, e.clientY);
    const card = slot?.escendant ? getCard(slot.escendant) : null;
    const actions = [];

    if (!slot?.escendant && slot?.escling) {
      actions.push({ icon: "👁", label: "View", color: GM.vellumDim, legal: true, onSelect: () => viewCard(slot.escling, []) });
      const targets = (me?.hand || []).map((id, i) => ({ card: getCard(id), idx: i })).filter(({ card: c }) => c?.type === "Escendant" && c?.level === 1);
      if (targets.length > 0) {
        // ── GILDED PICKER (July 2026 canon): one Transcend action opens
        // the card picker (real art, no 4-target cap). A single candidate
        // still short-circuits straight to handleTranscend.
        actions.push({
          icon: "⬆", label: "Transcend", color: GM.inkGold, legal: myTurn, reason: "Not your turn",
          onSelect: () => {
            const pickTranscend = ({ card: c, idx }) => handleTranscend(slotIdx, idx, !sharesSchoolAndIlk(c, getCard(slot.escling)));
            if (targets.length === 1) { pickTranscend(targets[0]); return; }
            setTargetPicker({
              title: "⬆ Transcend — Choose an L1 Being",
              options: targets.map(({ card: c, idx }) => ({
                id: c.id, idx, cardObj: c,
                label: `${c.name}${sharesSchoolAndIlk(c, getCard(slot.escling)) ? "" : " — Deviant Transcend"}`,
              })),
              onPick: (opt) => { setTargetPicker(null); pickTranscend({ card: opt.cardObj, idx: opt.idx }); },
            });
          },
        });
      } else {
        actions.push({ icon: "⬆", label: "Transcend", legal: false, reason: "No L1 being in hand", onSelect: () => {} });
      }
      // Esclings CAN Lyt Attack beings (Vulnerable/Ruin) but CANNOT dim Anima
      actions.push({ icon: "⚔", label: "Lyt\nAttack", color: SD.red, legal: myTurn && !slot.attackedThisTurn && !isFirstTurn, reason: slot.attackedThisTurn ? "Already attacked" : isFirstTurn ? "Turn 1" : "Not your turn", onSelect: () => handleStartAttackEscling(slotIdx) });
    }

    if (slot?.escendant && card) {
      actions.push({ icon: "👁", label: "View", color: GM.vellumDim, legal: true, onSelect: () => viewCard(slot.escendant, slot.soul || []) });
      actions.push({ icon: "✦", label: "Soul", color: GM.inkGold, legal: true, onSelect: () => setSoulModal({ ...slot, slotIdx: slotIdx }) });
      // ── WHAT THE BEING IS WEARING (July 28, 2026 — caP'n's ask) ──
      // A wedge per attached card type, straight to its face. The Soul
      // modal keeps listing all of them exactly as before — caP'n:
      // "you can leave it in the soul it's not bothering" — so this is a
      // second door, the same call the Primera wedge below makes.
      //
      // A wedge only exists when the being is actually WEARING that type,
      // so a bare being's ring is byte-for-byte what it was. The eye icon
      // and the View colour mark these as INSPECT, which is what keeps
      // them from reading as the gold ATTACH wedges further down the ring.
      //
      // Written as a table rather than three near-identical blocks: the
      // fourth attachment type — a Heretical Tome, or whatever the Rulez
      // grow next — is one row here, not a new branch. (Heretical Tomes
      // are deliberately NOT listed yet: they are not part of the Soul
      // per R68, so they need their own read before they get a door.)
      {
        const WORN_TYPES = [
          { type: "Impera",  label: "Impera",   showLevel: true  },
          { type: "U'hlti",   label: "U'hlti",   showLevel: false },
          { type: "Affinity", label: "Affinity", showLevel: false },
        ];
        WORN_TYPES.forEach(({ type, label, showLevel }) => {
          const worn = (slot.soul || [])
            .map(id => ({ id, c: getCard(id) }))
            .filter(({ c }) => c?.type === type);
          if (worn.length === 0) return;
          // Imperas stack one per level, so sort them so the sub-radial
          // reads L1 → L2 → L3 rather than attachment order.
          worn.sort((a, b) => (a.c.level || 1) - (b.c.level || 1));
          const wedgeLabel = (showLevel && worn.length === 1)
            ? `${label}\nL${worn[0].c.level ?? 1}`
            : label;
          actions.push({
            icon: "👁", label: wedgeLabel, color: GM.vellumDim, legal: true,
            onSelect: () => {
              if (worn.length === 1) { viewCard(worn[0].id, []); return; }
              // More than one Impera on the being — same nested-radial
              // pattern the Primera word picker uses, so the ring never
              // has to grow a wedge per level.
              setRadial({ x: pos.x, y: pos.y, title: `👁 ${card.name} — ${label}`,
                actions: worn.map(({ id, c }) => ({
                  icon: "👁", label: `${label}\nL${c.level ?? 1}`,
                  color: GM.vellumDim, legal: true,
                  onSelect: () => viewCard(id, []),
                })),
              });
            },
          });
        });
      }
      // ── PRIMERA ON THE RADIAL (July 26, 2026 — caP'n's ask) ──────
      // The wax seals on the card face STAY — they're the signature of
      // H22 and the whole press path in 3D. This is the second door,
      // not a replacement: pinching a ~39px seal on a 56px field card
      // is a dexterity test, and the radial is already open under your
      // thumb. Both routes end in the same `handlePressPrimera`, so
      // the gate, the tallies, and the flare are identical either way.
      {
        const stampAxes = ["ilk", "school"]
          .map(axis => ({ axis, word: primeraWordFor(card, axis) }))
          .filter(a => a.word && PRIMERAS?.[a.axis]?.[a.word]);
        if (stampAxes.length > 0) {
          const pCost = PRIMERA_COST[`L${card.level || 1}`] || (card.level || 1);
          // LEGAL BY DESIGN even when unaffordable / off-turn / spent —
          // same law the seals live under: an illegal press must be able
          // to reach the gate so the gate can say why.
          actions.push({
            icon: "◈", label: "Primera", color: GM.inkGoldBright,
            legal: true, dim: !!primeraTurnRef.current?.slots?.[slotIdx],
            onSelect: () => {
              if (stampAxes.length === 1) { handlePressPrimera(slotIdx, stampAxes[0].axis); return; }
              const pt = primeraTurnRef.current;          // read LIVE, not the build-time snapshot
              const beingSpent = !!pt.slots[slotIdx];
              setRadial({ x: pos.x, y: pos.y, title: `◈ ${card.name} — speak a word`,
                actions: stampAxes.map(({ axis, word }) => ({
                  icon: "◈",
                  label: `${word}\n${axis === "ilk" ? "Ilk" : "School"}`,
                  color: STAMP_COLORS[word] || GM.inkGoldBright,
                  cost: pCost, costKind: "mix",
                  legal: true,
                  dim: beingSpent || !!pt.words[word],
                  onSelect: () => handlePressPrimera(slotIdx, axis),
                })),
              });
            },
          });
        }
      }
      const ascTargets = (me?.hand || []).map((id, i) => ({ card: getCard(id), idx: i })).filter(({ card: c }) => c?.type === "Escendant" && c?.level === card.level + 1);
      if (ascTargets.length > 0 && card.level < 3) {
        const { card: c, idx } = ascTargets[0];
        const natural = sharesSchoolAndIlk(c, card);
        const cost = natural ? 0 : deviantFare(sharedTraits(c, card), c.level).khao;  // R101 — display shows the Khao leg
        actions.push({ icon: "⬆⬆", label: natural ? "Ascend" : "Dev.\nAscend", color: natural ? GM.inkGold : SD.gold, cost: cost || null, legal: myTurn && !ascendedThisTurn, reason: !myTurn ? "Not your turn" : "Already Ascended", onSelect: () => handleAscend(slotIdx, idx, !natural) });
      }
      // ── Impera attach — NATURAL (School and Ilk both match) ──────
      // ⚠ THIS LABEL SAID "free" AND THE WEDGE SHOWED NO COST. Under
      // R128 (caP'n, 2026-09-14) a Natural Training pays the card's
      // PRINTED fare, so the wedge must price it and must grey out when
      // the seat cannot pay — the Deviant wedge below has always done
      // both. A wedge that reads as free and then takes 10 Khao for
      // King of the Anthill is worse than one that refuses: the player
      // cannot learn a fare they were never shown.
      const imperaTargets = (me?.hand || []).map((id, i) => ({ card: getCard(id), idx: i })).filter(({ card: c }) => { if (c?.type !== "Impera") return false; return c.school === card.school && c.ilk === card.ilk && c.level <= card.level; });
      imperaTargets.slice(0, 3).forEach(({ card: c, idx }) => {
        const alreadyHasLevel = (slot.soul || []).some(id => { const sc = getCard(id); return sc?.type === "Impera" && sc?.level === c.level; });
        const tf = trainingFare(c);
        const canPayT = tf.free || canPayPrintedFare(c, { khao: me?.khao ?? 0, anima: me?.anima ?? STARTING_ANIMA });
        actions.push({
          icon: "✦", label: `Impera\nL${c.level}`, color: GM.inkGold,
          cost: tf.khao || null,
          legal: myTurn && !alreadyHasLevel && canPayT,
          reason: !myTurn ? "Not your turn"
                : alreadyHasLevel ? "Level already attached"
                : (tf.anima > 0 ? `Need ${tf.khao} Khao and ${animaFare(tf.anima)} to Train` : `Need ${tf.khao} Khao to Train`),
          onSelect: () => handleAttachImpera(slotIdx, idx),
        });
      });

      // ⛧ DEVIANT TRAINING — the Imperas that do NOT match. Built
      // 2026-08-20; before it the radial simply never offered these and
      // dropping one on the being toasted a refusal. Capped at two
      // wedges because the ring is finite and this is the third
      // Impera-shaped entry on it.
      const devTrainTargets = (me?.hand || []).map((id, i) => ({ card: getCard(id), idx: i }))
        .filter(({ card: c }) => c?.type === "Impera" && c.level <= card.level && sharedTraits(c, card) < 2);
      devTrainTargets.slice(0, 2).forEach(({ card: c, idx }) => {
        const alreadyHasLevel = (slot.soul || []).some(id => { const sc = getCard(id); return sc?.type === "Impera" && sc?.level === c.level; });
        const cost = deviantFare(sharedTraits(c, card), c.level).khao;  // R101 — display shows the Khao leg
        actions.push({
          icon: "⛧", label: `Dev.\nTrain L${c.level}`, color: SD.gold, cost,
          legal: myTurn && !alreadyHasLevel && !trainedThisTurn,
          reason: !myTurn ? "Not your turn" : trainedThisTurn ? "Already trained this turn" : "Level already attached",
          onSelect: () => handleAttachImpera(slotIdx, idx),
        });
      });
      // U'hlti attach — only to Natural L3, only if the slot is empty.
      // ⚠ `|| slot.heretical` IS RULING 7. "An Escendant may hold at most
      // one attached card in its U'hlti slot. Once attached, it cannot
      // be replaced — even through Heretical Training." The reverse is
      // just as true and was not enforced anywhere until 2026-08-20: a
      // Heretical Tome OCCUPIES that slot, so an U'hlti may not follow
      // it in. (A being carrying one is Deviant anyway and `slot.natural`
      // would refuse — but relying on that is relying on a side effect
      // to enforce a rule, and the day a Natural keeps a heresy this
      // would have been the hole.)
      const hasUhlti = (slot.soul || []).some(id => getCard(id)?.type === "U'hlti") || !!slot.heretical;
      const uhltiTargets = (me?.hand || []).map((id, i) => ({ card: getCard(id), idx: i })).filter(({ card: c }) => c?.type === "U'hlti" && c?.owner === baseId(slot.escendant) && card.level === 3 && slot.natural);
      uhltiTargets.slice(0, 1).forEach(({ card: c, idx }) => {
        actions.push({ icon: "◈", label: "U'hlti", color: SD.gold, legal: myTurn && !hasUhlti && slot.natural && card.level === 3, reason: !slot.natural ? "Deviant — no U'hlti" : hasUhlti ? "Already has U'hlti" : card.level < 3 ? "Need L3" : "Not your turn", onSelect: () => handleAttachSoulCard(slotIdx, idx) });
      });
      // Affinity attach — only to Natural L3 with U'hlti
      const hasAffinity = (slot.soul || []).some(id => getCard(id)?.type === "Affinity");
      const affinityTargets = (me?.hand || []).map((id, i) => ({ card: getCard(id), idx: i })).filter(({ card: c }) => c?.type === "Affinity");
      affinityTargets.slice(0, 1).forEach(({ card: c, idx }) => {
        actions.push({ icon: "◆", label: "Affinity", color: SD.gold, legal: myTurn && hasUhlti && !hasAffinity && slot.natural && card.level === 3, reason: !slot.natural ? "Deviant — no Affinity" : !hasUhlti ? "Need U'hlti first" : hasAffinity ? "Already has Affinity" : card.level < 3 ? "Need L3" : "Not your turn", onSelect: () => handleAttachSoulCard(slotIdx, idx) });
      });
      // 🔥 HERETICAL TRAINING — a Tome in the U'hlti slot of a Level 3.
      // Built 2026-08-20; it had a chapter, a cost constant, a gate
      // wrapper and no engine. The wedge shows for ANY L3 with an empty
      // U'hlti slot, Natural or Deviant, because the rite is the one
      // door a Deviant has left AND a Natural may kick it down at the
      // price of never wearing an U'hlti again. See the ruling written
      // over handleHereticalTraining for why both are legal targets.
      const hasHeretical = !!slot.heretical;
      if (card.level === 3 && !hasUhlti && !hasHeretical) {
        const tomeTargets = (me?.hand || []).map((id, i) => ({ card: getCard(id), idx: i }))
          .filter(({ card: c }) => c?.type === "Tome");
        tomeTargets.slice(0, 1).forEach(({ card: c, idx }) => {
          // `>` and not `>=` on the Anima — THE ANIMA FLOOR. Paying the
          // rite's exact Anima price ends the duel (R33), so a wedge lit
          // at exactly six was a lit concession button. See the handler.
          const afford = (me?.khao ?? 0) >= HERETICAL_TRAINING_COST.khao
                      && (me?.anima ?? 0) > HERETICAL_TRAINING_COST.anima;
          actions.push({
            icon: "🔥", label: "Heretical\nTraining", color: "#f97316",
            cost: HERETICAL_TRAINING_COST.khao,
            legal: myTurn && afford,
            reason: !myTurn ? "Not your turn" : `Needs ${HERETICAL_TRAINING_COST.khao} Khao AND ${HERETICAL_TRAINING_COST.anima} Anima — and Anima to spare`,
            onSelect: () => handleHereticalTraining(slotIdx, idx),
          });
        });
      }
      actions.push({ icon: "⚔", label: "Lyt\nAttack", color: SD.red, legal: myTurn && !slot.attackedThisTurn && !isFirstTurn, reason: slot.attackedThisTurn ? "Already attacked" : isFirstTurn ? "Turn 1" : "Not your turn", onSelect: () => handleStartAttack(slotIdx) });

      // ── 🜂 THE ROLE ART OF THIS RIFT (R115–R119) ────────────────
      // caP'n, 2026-09-13, asked whether the Exile Wave was meant to be
      // playable at release: **"1) yes"**.
      //
      // ⚠ THIS WEDGE IS THE WHOLE REASON THE WAVE WAS "RULEBOOK-AHEAD-
      // OF-BOARD". The Arts had constants, validators, a chapter and a
      // Rulebook page, and NO BUTTON — a player read ch.12 and went
      // looking for something the Shatterfield did not offer.
      //
      // The rift owns the Art, so it hangs off the RIFT's own ring
      // rather than off a separate rail: the Art of the Body rift is a
      // thing you do at the Body rift, and putting it anywhere else
      // would teach the player the opposite of the rule.
      //
      // ⚠ THE LEGALITY IS ASKED OF `artsRailFor`, NOT RE-DERIVED HERE.
      // A wedge that computes its own `legal` is a second opinion about
      // a rule, and this file has paid for that twice already (the R08
      // toasts and the Khao-fare buttons). One function answers "may I
      // press this", for this board, the 3D board and the brain alike.
      {
        const railRow = (artsRail || []).find((r) => r.rift === slotIdx);
        if (railRow) {
          const glyph = { Tank: "🛡", Terror: "⚔", Healer: "✚", Warden: "👁" }[railRow.role] || "🜂";
          actions.push({
            icon: glyph,
            label: `${railRow.role}\nArt`,
            color: "#c4b5fd",
            cost: railRow.fare ? railRow.fare.khao : null,
            legal: myTurn && railRow.legal,
            reason: !myTurn ? "Not your turn" : (railRow.reason || "That Art cannot be performed"),
            onSelect: () => openRoleArt(slotIdx, railRow),
          });
        }
      }
    }

    if (actions.length > 0) setRadial({ x: pos.x, y: pos.y, actions });
  };

  // ═══════════════════════════════════════════════════════════
  // 🜂 THE EXILE WAVE, PERFORMED (R111 · R113 · R115–R119)
  // ═══════════════════════════════════════════════════════════
  // Every one of these is a thin shell: it collects the choice the Art
  // needs from a picker and hands it to `src/roleArts.js`, which is the
  // ONE resolver the brain will call too. No rule, no fare and no
  // cadence is decided in this file — that is the whole point of the
  // extraction, and it is the lesson tomeResolver was lifted out to
  // learn. If a number appears below, it is a bug.
  const artsCtx = () => ({
    code, playerIdx, oppIdx,
    match: matchRef.current || match,
    matchRef,
    me, opponent,
    updatePlayer, showToast, setTargetPicker,
  });

  const artsRail = useMemo(
    () => artsRailFor(me, opponent, match?.turn ?? 0, seatCountOf(match)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [me, opponent, match?.turn, match?.seatCount]
  );

  const afterArt = async (r) => {
    if (!r?.ok) { showToast(r?.reason || "That Art cannot be performed.", SD.red); return false; }
    setRadial(null);
    return true;
  };

  const openRoleArt = async (slotIdx, railRow) => {
    setRadial(null);
    // ── THE SOL RIFT takes nothing: it arms overkill for the Turn.
    if (slotIdx === SOL_RIFT) {
      await afterArt(await performRoleArt(slotIdx, artsCtx(), {}));
      return;
    }
    // ── THE BODY RIFT names one of the adversary's beings to dull.
    if (slotIdx === BODY_RIFT) {
      const targets = (opponent?.field?.esclings || [])
        .map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
      if (!targets.length) { showToast("No adversary being to dull.", GM.vellumFaint); return; }
      setTargetPicker({
        title: "🛡 The Tank Art — dull one being's next swing",
        options: targets.map(({ s, i }) => ({
          id: String(i), slotIdx: i,
          label: `${riftNameOf(i)}: ${getCard(s.escendant)?.name || "being"}`,
        })),
        onPick: async (opt) => {
          setTargetPicker(null);
          await afterArt(await performRoleArt(slotIdx, artsCtx(), { targetSlotIdx: opt.slotIdx }));
        },
      });
      return;
    }
    // ── THE HEART RIFT returns one being from the Necropolis, and its
    // fare is the ordinary Resurrection schedule for that being's LEVEL
    // — so the level has to be known before the fare is charged, which
    // is why the picker comes first and `level` rides the call.
    if (slotIdx === HEART_RIFT) {
      const beings = (me?.necropolis || []).filter((id) => getCard(id)?.type === "Escendant");
      if (!beings.length) { showToast("No being in your Necropolis to return.", GM.vellumFaint); return; }
      setTargetPicker({
        title: "✚ The Healer Art — return a being (the tally is not spent)",
        cards: beings,
        onPick: async (chosen) => {
          const id = typeof chosen === "string" ? chosen : chosen?.id;
          setTargetPicker(null);
          await afterArt(await performRoleArt(slotIdx, artsCtx(), {
            cardId: id, level: getCard(id)?.level || 1,
          }));
        },
      });
      return;
    }
    // ── THE MIND RIFT picks one of two Arts, and the card it sends to
    // the Necropolis is part of the PRICE rather than a target.
    if (slotIdx === MIND_RIFT) {
      setTargetPicker({
        title: "👁 The Warden Art — one per Aenarchist per Turn",
        options: WARDEN_OPTIONS.map((o) => ({ id: o.id, label: o.text })),
        onPick: (opt) => {
          setTargetPicker(null);
          const o = wardenOption(opt.id);
          const pool = o.pay === "hand"
            ? (me?.hand || [])
            : Object.keys(me?.grimoire || {}).filter((k) => (me.grimoire[k] || 0) > 0);
          if (!pool.length) { showToast(`Nothing in your ${o.pay} to send.`, GM.vellumFaint); return; }
          setTargetPicker({
            title: `👁 Send 1 card from your ${o.pay === "hand" ? "hand" : "Grimoire"} to the Necropolis`,
            cards: pool,
            onPick: async (chosen) => {
              const id = typeof chosen === "string" ? chosen : chosen?.id;
              setTargetPicker(null);
              await afterArt(await performRoleArt(slotIdx, artsCtx(), { optionId: o.id, payCardId: id }));
            },
          });
        },
      });
    }
  };

  // ── R111 · EXILIATION ───────────────────────────────────────
  // Not a card. Nothing to draw, nothing to build into a Book, nothing
  // to attach — so it has no rift of its own and hangs off the Exile
  // tile, which is the only place on the board it has ever been
  // visible. Two pickers: what you give up, then what you take.
  // The option step is its own function so the pill (SHIP 9.1) can
  // enter at a chosen option; the pit's own picker calls the same one.
  const openExiliation = () => {
    setRadial(null);
    setTargetPicker({
      title: "🕳 Exiliation — one of four, once per 2 full Turns",
      options: EXILIATION_OPTIONS.map((o) => ({ id: o.id, label: o.text })),
      onPick: pickExiliationOption,
    });
  };
  const pickExiliationOption = (pick) => {
    setTargetPicker(null);
    const opt = exiliationOption(pick.id);
    const pool = opt.exileFrom === "necropolis" ? (me?.necropolis || [])
      : opt.exileFrom === "hand" ? (me?.hand || [])
      : Object.keys(me?.grimoire || {}).filter((k) => (me.grimoire[k] || 0) > 0);
    if (!pool.length) { showToast(`Nothing in your ${opt.exileFrom} to exile.`, GM.vellumFaint); return; }
    setTargetPicker({
      title: `🕳 Give up 1 card from your ${opt.exileFrom} — it leaves the Solrend forever`,
      cards: pool,
      onPick: (chosen) => {
        const exiledId = typeof chosen === "string" ? chosen : chosen?.id;
        setTargetPicker(null);
        const level = getCard(exiledId)?.level || 1;
        // WHAT YOU MAY TAKE BACK, filtered by the option's own row —
        // types and level both, so R106 (a Luxmoire finds Impera and
        // nothing else) is obeyed by construction rather than by a
        // second check.
        const findPool = (opt.findIn === "necropolis"
          ? (me?.necropolis || []).filter((id) => id !== exiledId)
          : [
              ...Object.keys(me?.grimoire || {}).filter((k) => (me.grimoire[k] || 0) > 0),
              ...Object.keys(me?.luxmoire || {}).filter((k) => (me.luxmoire[k] || 0) > 0),
            ]
        ).filter((id) => {
          const c = getCard(id);
          return c && opt.finds.includes(c.type) && (!opt.sameLevel || (c.level || 1) === level);
        });
        const fire = async (foundId) =>
          afterArt(await performExiliation(artsCtx(), {
            optionId: opt.id, exiledCardId: exiledId, foundCardId: foundId || null,
          }));
        if (!findPool.length) {
          // ⚠ STILL LEGAL, AND DELIBERATELY OFFERED. An Exiliation
          // with nothing to find is a worse trade, not an illegal
          // one, and refusing it here would be this file inventing a
          // rule. The toast says what is being given up for nothing.
          showToast("Nothing to take back at that level — the Exiliation still stands.", GM.vellumFaint);
          fire(null);
          return;
        }
        setTargetPicker({
          title: `🕳 Take 1 Level ${level} ${opt.finds.join(" or ")}`,
          cards: findPool,
          onPick: async (found) => {
            setTargetPicker(null);
            await fire(typeof found === "string" ? found : found?.id);
          },
        });
      },
    });
  };

  // ── SHIP 9.1 · THE PILL'S PLAYS — one door each, no new gate ──
  // `play` is fieldCodex's descriptor. Every branch is the handler the
  // board itself presses; the pill decided nothing.
  const handlePillPlay = (play, at = {}) => {
    if (!play || !me) return;
    const e = { clientX: at.clientX ?? window.innerWidth / 2, clientY: at.clientY ?? window.innerHeight / 2 };
    switch (play.kind) {
      case "art": {
        const row = (artsRail || []).find((r) => r.rift === play.slotIdx);
        if (!row) { showToast("No Art at that rift.", GM.vellumFaint); return; }
        if (!row.legal) { showToast(row.reason || "That Art cannot be performed.", SD.red); return; }
        openRoleArt(play.slotIdx, row);
        return;
      }
      case "stamp": {
        const slot = me.field?.esclings?.[play.slotIdx];
        const st = slot ? stampsFor(slot, play.slotIdx) : null;
        const seal = st?.[play.axis];
        if (!seal) { showToast("No seal there.", GM.vellumFaint); return; }
        if (seal.state !== "ready") { showToast(seal.state === "spent" ? "That stamp was already pressed this Turn." : "That stamp is locked right now.", SD.red); return; }
        seal.onPress();
        return;
      }
      case "tome": {
        // Build the Tome radial, catch it, press its Activate wedge.
        let fed = false;
        radialSinkRef.current = (r) => {
          fed = true;
          const w = (r.actions || []).find((a) => /^Activate/.test(String(a.label || "")));
          if (!w) { showToast("That Tome has nothing to activate.", GM.vellumFaint); return; }
          if (!w.legal) { showToast(w.reason || "That Tome cannot be activated right now.", SD.red); return; }
          w.onSelect();
        };
        try { handleTomeAction("tome", play.slotIdx, e); }
        finally { radialSinkRef.current = null; }
        if (!fed) showToast("That Tome offers no activation from here — press it on the board.", GM.vellumFaint);
        return;
      }
      case "soul": {
        handleActivateSoulCard(play.slotIdx, play.cardId);
        return;
      }
      case "exile": {
        if (!myTurn) { showToast("Not your turn.", SD.red); return; }
        pickExiliationOption({ id: play.optionId });
        return;
      }
      default:
        return;
    }
  };

  // ── R119 · THE BOUNTY ON A RUINED WARDEN ────────────────────
  // caP'n, 2026-09-06, widened 2026-09-07: *"let's just make it a reward
  // for ruining any level Warden, doesn't matter same reward at every
  // level."* The ATTACKER chooses: 3 Anima, or +1 Max Khao.
  //
  // ⚠ A DESCENT IS NOT A RUIN, and that distinction is the whole
  // correctness of this. A Warden that descends to its lower level is
  // still standing in the Mind rift and still shielded; paying a bounty
  // for it would pay for knocking a level off a being that is still
  // there. Only the ruin that empties the rift earns it.
  //
  // ⚠ AND IT IS ASKED, NOT TAKEN. Two halves, a live choice, and a seat
  // that is already at the Anima ceiling or the Khao ceiling wants the
  // other one. A silent default would be this file choosing for the
  // player on the one decision the rule explicitly hands them.
  const offerWardenBounty = async (slotIdx, willDescend) => {
    if (slotIdx !== MIND_RIFT || willDescend) return;
    if (!rolesAwake(matchRef.current?.turn ?? match?.turn ?? 0, seatCountOf(match))) return;
    setTargetPicker({
      title: "👁 You ruined a Warden — take your bounty",
      options: [
        { id: "anima",   label: `Take ${WARDEN_BOUNTY.anima} Anima` },
        { id: "maxKhao", label: `Take +${WARDEN_BOUNTY.maxKhao} Maximum Khao` },
      ],
      onPick: async (opt) => {
        setTargetPicker(null);
        await payWardenBounty(artsCtx(), opt.id);
      },
    });
  };

  // ── THE EXILE TILE'S OWN RING ───────────────────────────────
  // Two Arts, both of them the Aenarchist's rather than a being's, and
  // both legality-checked by the resolver rather than here: a wedge that
  // decides a rule is a second opinion about it.
  const handleExileAction = (e) => {
    const pos = smartPos(e.clientX, e.clientY);
    const riftNow = me?.[EXILE_RIFT_FIELD] || [];
    const spentNow = me?.[EXILE_RETURNED_FIELD] || [];
    const animaNow = me?.anima ?? STARTING_ANIMA;
    const buyable = riftNow.filter((id) => !spentNow.includes(id));
    setRadial({ x: pos.x, y: pos.y, actions: [
      { icon: "🕳", label: "Exiliation", color: "#a78bfa",
        legal: myTurn, reason: "Not your turn", onSelect: openExiliation },
      { icon: "🜃", label: "The\nRitual", color: "#a78bfa",
        cost: canReachExileRift(animaNow) ? exileRetrievalFare(animaNow) : null,
        legal: myTurn && buyable.length > 0 && canReachExileRift(animaNow),
        reason: !myTurn ? "Not your turn"
          : !riftNow.length ? "Nothing has been exiled"
          : !buyable.length ? "Everything in there is Marked"
          : `The Ritual needs at least ${EXILE_RETRIEVAL_ANIMA_FLOOR} Anima to reach at all`,
        onSelect: openRitual },
    ]});
  };

  // ── R113 · THE RITUAL PROPER ────────────────────────────────
  // The door the Exile tile's own banner said was "somewhere else". It
  // is here now. Two numbers that are not the same number — a floor of
  // 10 Anima to reach the rift, and then HALF of what you hold — and
  // what comes back carries THE MARK.
  const openRitual = () => {
    setRadial(null);
    const rift = me?.[EXILE_RIFT_FIELD] || [];
    if (!rift.length) { showToast("Your Exile rift is empty.", GM.vellumFaint); return; }
    const animaNow = me?.anima ?? STARTING_ANIMA;
    if (!canReachExileRift(animaNow)) {
      showToast(`The Ritual needs at least ${EXILE_RETRIEVAL_ANIMA_FLOOR} Anima to reach at all.`, SD.red);
      return;
    }
    const spent = me?.[EXILE_RETURNED_FIELD] || [];
    setTargetPicker({
      title: `🜃 The Ritual — ${exileRetrievalFare(animaNow)} Anima, and what returns is Marked`,
      cards: rift.filter((id) => !spent.includes(id)),
      onPick: async (chosen) => {
        setTargetPicker(null);
        await afterArt(await performRitual(artsCtx(), {
          cardId: typeof chosen === "string" ? chosen : chosen?.id,
        }));
      },
    });
  };

  const handleOppCurioAction = (slotIdx, e) => {
    const oppCurios = [opponent?.field?.curios?.[0] ?? null, opponent?.field?.curios?.[1] ?? null];
    if (!oppCurios[slotIdx]) return;
    const curioId = oppCurios[slotIdx];
    const pos = smartPos(e.clientX, e.clientY);
    setRadial({ x: pos.x, y: pos.y, actions: [
      { icon: "👁", label: "View", color: GM.vellumDim, legal: true, onSelect: () => viewCard(curioId, []) },
    ]});
  };

  const handleOppSlotAction = (slotIdx, e) => {
    if (targetMode !== "select" && targetMode !== "escling") {
      // View opponent cards when not in attack mode
      const slot = opponent?.field?.esclings?.[slotIdx];
      if (slot?.escendant) { viewCard(slot.escendant, slot.soul || []); return; }
      if (slot?.escling && !slot?.escendant) { viewCard(slot.escling, []); return; }
      return;
    }
    if (targetMode === "select" || targetMode === "escling") {
      const targetSlot = opponent?.field?.esclings?.[slotIdx];
      if (targetMode === "escling") {
        // Escling attack — can only target beings, NOT Anima
        if (!targetSlot?.escendant) {
          showToast("Esclings cannot dim Anima — target a being.", GM.vellumFaint);
          return;
        }
        handleAttackTargetEscling(slotIdx);
      } else {
        // Normal Escendant attack
        if (!targetSlot?.escendant && targetSlot?.escling) {
          handleAttackAnima(); // tap opp escling = attack anima
        } else {
          handleAttackTarget(slotIdx);
        }
      }
    }
  };

  const handleTomeAction = (type, slotIdx, e) => {
    const pos = smartPos(e.clientX, e.clientY);
    if (type === "tome") {
      const tomes = [me?.field?.tomes?.[0] ?? null, me?.field?.tomes?.[1] ?? null, me?.field?.tomes?.[2] ?? null];
      const existing = tomes[slotIdx];
      const existingId = typeof existing === "object" ? existing?.id : existing;
      if (existingId) {
        const existingCard = getCard(existingId);
        const isFaceDown = typeof existing === "object" ? existing?.faceDown : false;
        if (isFaceDown) {
          // Owner can view face-down tomes; opponent cannot
          const viewAction = { icon: "👁", label: "View\n(Mine)", color: GM.vellumDim, legal: true, onSelect: () => viewCard(existingId, []) };
          setRadial({ x: pos.x, y: pos.y, actions: [
            viewAction,
            // ═══════════════════════════════════════════════════
            // ⭐ R35 — A LEVEL-1 TOME MUST WAIT ONE FULL TURN.
            // ═══════════════════════════════════════════════════
            // This wedge had no level check and no wait check, so the
            // player could set an L1 Tome face-down and flip it the
            // same turn for full effect — the exact tempo the Rulebook
            // tells them they cannot have, and that the NPC denies
            // itself at every level (NPC.js reads `placedTurn` before
            // it will flip anything).
            //
            // The rule was never missing. rules.js has the wait table
            // ({L1:1, L2:0, L3:0}), RulesEnforcer has
            // validateTomeActivation reading it, and ruleGate has an
            // ACTIVATE_TOME wrapper — and NOTHING IN THE GAME EVER
            // DISPATCHED THAT ACTION TYPE. So ruleGate's own banner,
            // "every rule-bearing action in Shatterfield — player OR
            // NPC — passes through enforce() before it mutates state.
            // R-UNIVERSAL", was false for the entire Tome subsystem.
            //
            // ⚠ THE SIBLING WEDGES ARE GATED TOO. Playing a Tome FACE-UP
            // from hand is placing and activating in one action, so the
            // "Activate" wedge beside "Face Down" carries the same test
            // — otherwise this gate would only have punished the player
            // who did it the slow way. See handlePlayTome's radials.
            //
            // Checked inline here rather than through enforce(),
            // deliberately: this is a `legal` flag on a radial wedge,
            // it must be synchronous, and it must produce a REASON the
            // player reads on the button. tomeWaitHalfTurns is still the only
            // source of the number.
            //
            // A tome with no `placedTurn` — set before this shipped, or
            // by a path that has not been taught to stamp — reads as
            // turn 0 and is therefore ALWAYS activatable. Fails open on
            // purpose: an old save must never contain a Tome the player
            // can no longer use.
            ...(() => {
              const lvl = existingCard?.level || 1;
              // R35 — the wait comes from the FUNCTION now, not from a
              // bare map. ⚠ AND THE WEDGE'S OWN LABEL WAS THE TELL: it
              // read "must wait N full Turns" while N was a HALF-turn
              // count, so a player was told one thing and charged
              // another. `waitNeeded` is half-turns; the sentence says
              // half-turns; the number of full Turns is derived for the
              // prose rather than assumed.
              const waitNeeded = tomeWaitHalfTurns(lvl, { faceDown: true, seatCount: seatCountOf(match) });
              const waitFull = Math.ceil(waitNeeded / Math.max(1, seatCountOf(match)));
              const waited = (match?.turn ?? 0) - (existing?.placedTurn ?? 0);
              const khaoOk = canPayPrintedFare(existingCard, { khao: me?.khao || 0, anima: me?.anima ?? STARTING_ANIMA });
              const waitOk = waited >= waitNeeded;
              return [{ icon: "✦", label: "Activate", color: SD.gold, cost: existingCard?.khaoCost || 0,
                legal: myTurn && khaoOk && waitOk,
                reason: !waitOk ? `A face-down Level ${lvl} Tome must wait ${waitFull} full Turn${waitFull === 1 ? "" : "s"} after it is set` : "Not enough Khao",
                onSelect: async () => {
              // ── SEAL: universal ACTIVATE_TOME gate. The `legal` flag above
              // stays inline — a radial wedge must be synchronous and must
              // carry a REASON the player reads on the button — but the flag
              // decides how the wedge LOOKS and this decides what it DOES.
              // Any caller that reaches onSelect without the wedge (a replay,
              // a keyboard path, a future driver) now meets the same rule.
              {
                const v = gateTomeActivation(gateCtx(), existing, waited, true);
                if (!v.valid) { showToast(v.reason || "That Tome cannot be activated yet.", SD.red); return; }
              }
              if (!(await spendKhao(existingCard?.khaoCost || 0))) { showToast("Not enough Khao.", SD.red); return; }
              // ── Wave 1: wielder sanity check on flip-activation ──
              const alive = (me?.field?.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
              if (needsWielder(existingCard) && alive.length === 0) {
                showToast(`${existingCard.name} needs a wielder — no beings available.`, SD.red);
                return;
              }
              const newField = JSON.parse(JSON.stringify(me?.field || {}));
              const newTomes = [newField.tomes?.[0] ?? null, newField.tomes?.[1] ?? null, newField.tomes?.[2] ?? null];
              // ── R-MRF: D'uhmdumn flip via radial — requirement self-derives ──
              // from id+faceDown; Lord's consumable nullify shield set inline.
              const isKhaoDuhmdumnFlip = existingId.includes("khao_duhmdumn");
              newTomes[slotIdx] = {
                id: existingId, faceDown: false,
                persistent: isTomePersistent(existingCard),
                currentLyf: existingCard?.lyf ?? null,
                wieldedBy: existing?.wieldedBy ?? null,
                // R35's anchor must SURVIVE the flip — this rebuild dropped
                // it, so a flipped persistent Tome read placedTurn 0 and the
                // re-activation gate above measured its wait from the dawn
                // of the Solrend. Carry it (defaulting to the flip turn).
                placedTurn: existing?.placedTurn ?? (match?.turn ?? 0),
                // ⭐ R122's ANCHOR — see handlePlayTome. "Flipping face-up IS
                // the activation" is this branch's own comment four lines
                // down, and it is exactly what starts the cadence. The
                // rebuild recorded the SET turn and never the FIRING turn,
                // so a flipped OP2/OP3 Tome had no cooldown at all.
                // NPC.flipOneTome writes the same key on the same event.
                lastUsedTurn: match?.turn ?? 0,
                ...(isKhaoDuhmdumnFlip ? { mustBeRuinedFirst: true } : {}),
              };
              newField.tomes = newTomes;
              await updatePlayer(code, playerIdx, { field: newField });
              // ── WAVE 2/3: DECLARED — flipping face-up IS the activation ──
              // Mirrors the NPC's flipOneTome verdict semantics precisely.
              {
                const rwF = await declareAction({ kind: "ACTIVATION", cardId: existingId });
                if (rwF?.counter && !rwF?.late) {
                  const meFF = matchRef.current?.players?.[playerIdx] || me;
                  if (rwF.counter === "omnia" || !isTomePersistent(existingCard)) {
                    await updatePlayer(code, playerIdx, { [`field/tomes/${slotIdx}`]: null, necropolis: [...(meFF?.necropolis || []), existingId] });
                  }
                  showToast(rwF.counter === "omnia"
                    ? `✕ ${rwF.counterName || "A counter"} — ${existingCard.name} countered and ruined!`
                    : `◈ ${rwF.counterName || "A counter"} — ${existingCard.name}'s effect nullified!`,
                    rwF.counter === "omnia" ? SD.red : GM.inkGold);
                  return;
                }
                const aonR = await applyTomeEffect(existingCard);
                if (aonR === "AON_RUINED") return;
                if (rwF?.counter && rwF?.late) {
                  const meFF2 = matchRef.current?.players?.[playerIdx] || me;
                  await updatePlayer(code, playerIdx, { [`field/tomes/${slotIdx}`]: null, necropolis: [...(meFF2?.necropolis || []), existingId] });
                  showToast(`${rwF.counterName || "A late counter"} — effect resolved, ${existingCard.name} evicted.`, GM.vellumDim);
                  return;
                }
              }
              // ── NECROPOLIS LAW: a flipped one-shot rides to the
              // Necropolis after firing (previously squatted face-up).
              if (!isTomePersistent(existingCard)) {
                const meNR = matchRef.current?.players?.[playerIdx] || me;
                await updatePlayer(code, playerIdx, { [`field/tomes/${slotIdx}`]: null, necropolis: [...(meNR?.necropolis || []), existingId] });
                return; // gone — nothing left to bond a wielder to
              }
              // ── Wave 1: open wielder picker after first face-up activation ──
              // (The wielder bond is opened by applyTomeEffect itself since
              // 2026-09-15 — one resolver; the picker this branch carried is gone.)
                }}];
            })(),
          ]});
        } else {
          if (selectedHandIdx !== null) { setSelectedHandIdx(null); setRadial(null); }
          // ── R105 · AN ATV FEED IS AN ACTIVATION AND PAYS FOR IT ───
          // ⚠ THE INTERCEPT USED TO BE HERE, ABOVE THE PERSISTENT RADIAL,
          // AND IT RETURNED. So tapping an ATV Necklace went straight to
          // the feed modal and the human paid only the FOOD — while the
          // brain's tome executor pays the activation fare AND the food
          // for the same act. R105 names ATV in the same breath as the
          // other tags: the cadence "buys the right to STAY on the field,
          // not the right to fire for free."
          //
          // It now lives INSIDE the "Activate Again" wedge, below that
          // wedge's canPayPrintedFare/spendKhao block, so the fare is
          // charged and R122's anchor stamped before the modal opens.
          // The wedge's own ATV cadence (2 half-turns — one full Turn)
          // and `fedThisTurn` inside handleFeedATV say the same thing,
          // which is the spec: "you feed, your opponent goes, then you
          // can feed again."
          const ATVFeedTomes = ["tome_necklace_death", "tome_necklace_courage"];
          const isPersistent = existing?.persistent;
          if (isPersistent) {
            setRadial({ x: pos.x, y: pos.y, actions: [
              { icon: "👁", label: "View", color: GM.vellumDim, legal: true, onSelect: () => viewCard(existingId, []) },
              // ── R105 · THE ACTIVATION FARE IS PAID EVERY TIME ────
              // caP'n, 2026-09-13, OVERTURNING the 2026-08-26 "paid
              // once" reading this wedge was rewritten to obey a
              // fortnight ago: *"you must pay its fare again, thats the
              // cost of it being able to stay on the field
              // 'indefinitely' unlike other cards that are typically
              // one use like most Tomes etc."*
              //
              // So the wedge charges the printed fare, and it charges
              // BOTH numbers when a card prints both — four Tomes do
              // (Nothing Left, Radiant Armor, Superque, Lyt-Hearted),
              // and under the old shape this wedge only ever looked at
              // `fare.khao`, so their Anima half was free even on the
              // first firing if it came through here.
              //
              // `activationFare` is rules.js's ONE answer to "what does
              // this firing cost", and it no longer takes an option:
              // there is no cheaper activation to ask for.
              ...(() => {
                const fare = activationFare(existingCard);
                const affordable = canPayPrintedFare(existingCard, { khao: me?.khao || 0, anima: me?.anima ?? STARTING_ANIMA });
                // ── AND A STANDING-LANE TOME HAS NOTHING TO FIRE ───
                // This wedge appeared for all ELEVEN entries of
                // FIELD_PERSISTENT_TOMES. Two of them (the ATV Necklaces)
                // are intercepted into the feed modal above and never
                // reach here; ONE (Magma Carta) is a genuine activated
                // row and keeps the wedge. The other EIGHT are the Tome
                // Book's standing-lane rows — the three D'uhmdumns,
                // Lightning Blade, Gauntlets of A'zka, Necklace of
                // Akula, Restraining Ordeal and King of the Castle —
                // whose entire printed effect is being on the field.
                // There is no activation to buy, so "Activate Again"
                // charged King of the Castle's 10 Khao and produced a
                // toast. That is trap 4.9: a "should I pay for this"
                // door that could not answer no.
                //
                // ⚠ THE LIST IS NOT RETYPED. `tomeIsStanding` reads the
                // Tome Book's own row, and the Book throws at import if
                // its rows and cards.js disagree — which is what makes
                // this structural rather than another list to maintain.
                const standingRow = tomeIsStanding(existingId);
                const onCd = !isTomeOffCooldown(existing, match?.turn);
                return [{ icon: "✦", label: "Activate\nAgain", color: SD.gold, cost: fare.khao, legal: myTurn && affordable && !onCd && !standingRow, reason: standingRow ? "Nothing to activate — its effect is standing on the field" : onCd ? "On cooldown" : (fare.anima > 0 ? "Not enough Khao or Anima" : "Not enough Khao"), onSelect: async () => {
                // ── SEAL: universal ACTIVATE_TOME gate. A persistent Tome
                // firing again is still an activation; `placedTurn` is what
                // R35 measures from, and it has been stamped since :10099.
                {
                  // A persistent Tome firing AGAIN is already face-up —
                  // the wait was paid when it turned over — so R35 has
                  // nothing left to charge here (caP'n, 2026-09-13: the
                  // wait is a price on SETTING). R122's cadence is what
                  // gates this wedge, and it is checked above.
                  const v = gateTomeActivation(gateCtx(), existing, (match?.turn ?? 0) - (existing?.placedTurn ?? 0), false);
                  if (!v.valid) { showToast(v.reason || "That Tome cannot be activated yet.", SD.red); return; }
                }
                // R105 — the printed fare, both pools, every firing.
                // ⚠ ANIMA IS CHECKED BEFORE EITHER IS SPENT. spendKhao
                // and spendAnima are two writes; charging Khao first and
                // then discovering the Anima half is unaffordable would
                // take the Khao for nothing. `canPayPrintedFare` folds
                // R96's till law into the Anima leg, so a fare that
                // would empty this seat is refused here, not there.
                if (!canPayPrintedFare(existingCard, { khao: me?.khao || 0, anima: me?.anima ?? STARTING_ANIMA })) {
                  showToast(fare.anima > 0 ? "Not enough Khao or Anima." : "Not enough Khao.", SD.red); return;
                }
                if (!(await spendKhao(fare.khao))) { showToast("Not enough Khao.", SD.red); return; }
                if (fare.anima > 0 && !(await spendAnima(fare.anima))) { showToast("Not enough Anima.", SD.red); return; }
                const tf = JSON.parse(JSON.stringify(me?.field || {}));
                tf.tomes[slotIdx] = { ...existing, lastUsedTurn: match?.turn || 0 };
                await updatePlayer(code, playerIdx, { field: tf });
                // ── THE ATV FEED, NOW THAT THE FARE IS PAID ───────────
                // Below the fare and below R122's anchor, which is the
                // whole point of the move. It returns BEFORE declareAction
                // deliberately: the player has not yet chosen how much to
                // feed, and declaring an activation whose amount is still
                // unknown would open a response window on a thing that
                // has not happened. handleFeedATV is where the food is
                // paid and where the threshold resolves.
                if (ATVFeedTomes.includes(existingId)) {
                  setAtvModal({ tome: tf.tomes[slotIdx], tomeSlotIdx: slotIdx });
                  return;
                }
                // ── WAVE 2/3: DECLARED — re-activation is still an activation.
                // Omnia ruins even a persistent tome mid-cooldown; Nihil voids
                // this firing (cooldown anchor already stamped — the turn's use
                // is spent, exactly as if the effect fizzled). Mirrors the NPC's
                // activateOneTome semantics precisely.
                const rwR = await declareAction({ kind: "ACTIVATION", cardId: existingId });
                if (rwR?.counter && !rwR?.late) {
                  if (rwR.counter === "omnia") {
                    const meFR = matchRef.current?.players?.[playerIdx] || me;
                    await updatePlayer(code, playerIdx, { [`field/tomes/${slotIdx}`]: null, necropolis: [...(meFR?.necropolis || []), existingId] });
                    showToast(`✕ ${rwR.counterName || "A counter"} — ${existingCard.name} countered and ruined!`, SD.red);
                  } else {
                    showToast(`◈ ${rwR.counterName || "A counter"} — this firing was nullified!`, GM.inkGold);
                  }
                  return;
                }
                const aonRA = await applyTomeEffect(existingCard);
                if (aonRA === "AON_RUINED") return;
                if (rwR?.counter && rwR?.late) {
                  const meFR2 = matchRef.current?.players?.[playerIdx] || me;
                  await updatePlayer(code, playerIdx, { [`field/tomes/${slotIdx}`]: null, necropolis: [...(meFR2?.necropolis || []), existingId] });
                  showToast(`${rwR.counterName || "A late counter"} — effect resolved, ${existingCard.name} evicted.`, GM.vellumDim);
                }
              }}];
              })(),
            ]});
          } else {
            viewCard(existingId, []);
          }
        }
        return;
      }
      if (selectedHandIdx === null) return;
      const cardId = (me?.hand || [])[selectedHandIdx];
      const card = getCard(cardId);
      if (!card || card.type !== "Tome") return;
      setRadial({ x: pos.x, y: pos.y, actions: [
        { icon: "⬇", label: "Face\nDown", color: GM.vellumDim, legal: myTurn, reason: "Not your turn", onSelect: () => handlePlayTome(slotIdx, selectedHandIdx, true) },
        // ⚠ THIS WEDGE USED TO REFUSE A LEVEL 1 TOME, AND THAT WAS
        // WRONG. The reasoning above it — "playing a Tome face-up IS
        // placing it and activating it in one action, so R35's wait
        // applies to both" — was a V'ael reading, and caP'n overturned
        // it on 2026-09-13 when it was finally put to him plainly:
        //
        //   *"Yes we can, but if you place it face down, you must wait
        //   1 full turn to activate it."*
        //
        // The wait is the PRICE OF SETTING, not a property of the card.
        // Pay the fare out of hand on your own turn and an L1 resolves
        // now; set it for free and it is latent for a full Turn. This is
        // also what RULEZ_V2 ch.13 has said since it was written.
        { icon: "✦", label: "Activate", color: SD.gold, cost: card.khaoCost || 0, legal: myTurn && canPayPrintedFare(card, { khao: me?.khao || 0, anima: me?.anima ?? STARTING_ANIMA }), reason: (card.animaCost || 0) > 0 ? "Not enough Khao or Anima" : "Not enough Khao", onSelect: () => handlePlayTome(slotIdx, selectedHandIdx, false) },
      ]});
    } else if (type === "prophecy") {
      // handleTomeAction is always called for MY side
      const propId = me?.field?.prophecy;
      if (propId) viewCard(propId, []);
      return;
    } else if (type === "curio") {
      const curios = [me?.field?.curios?.[0] ?? null, me?.field?.curios?.[1] ?? null];
      if (curios[slotIdx]) {
        const curioId = curios[slotIdx];
        const pos2 = smartPos(e.clientX, e.clientY);
        const actions = [
          { icon: "👁", label: "View", color: GM.vellumDim, legal: true, onSelect: () => viewCard(curioId, []) },
          { icon: "◆", label: "Auto-activated at duel start", color: GM.vellumFaint, legal: false, onSelect: () => {} },
        ];
        setRadial({ x: pos2.x, y: pos2.y, actions });
        return;
      }
      // ⚠ R63 — CURIOS ARE PRE-PLACED, AND THIS WAS THE THIRD ROUTE IN.
      // The hand radial refuses it ("Curios are pre-placed before the
      // duel") and so does the drop path; the TAP placed one anyway. A
      // rule that holds or does not hold depending on which gesture the
      // player used is not a rule, and a Curio arriving mid-duel pays out
      // through the start-of-duel activation that has already run.
      if (selectedHandIdx === null) return;
      const cardId = (me?.hand || [])[selectedHandIdx];
      const card = getCard(cardId);
      if (!card || card.type !== "Curio") return;
      showToast("Curios are pre-placed before the duel — they belong to no Book and are never played from hand.", SD.red);
      setSelectedHandIdx(null);
    }
  };

  const handleNecropolisAction = (e) => {
    const necropolis = me?.necropolis || [];
    if (necropolis.length === 0) { showToast("Necropolis is empty.", GM.vellumFaint); return; }
    // ── PUBLIC RECORD: off-turn taps skip the action radial and go
    // straight to the view picker; Resurrect stays turn-gated below.
    if (!myTurn) {
      setTargetPicker({
        title: `◆ Your Necropolis — ${necropolis.length} card${necropolis.length === 1 ? "" : "s"}`,
        options: necropolis.map(id => ({ id, label: getCard(id)?.name || id })),
        onPick: (opt) => { setTargetPicker(null); viewCard(opt.id, []); },
      });
      return;
    }
    const pos = smartPos(e.clientX, e.clientY);

    // ── Bug 6 fix — slot-finding per rulebook line 434 ──
    //   • L1 resurrects onto any empty rift (no escendant)
    //   • L2 resurrects directly over an L1 of the same chain (sharedTraits === 2)
    //   • L3 resurrects directly over an L2 of the same chain
    //   • "In order" rule: for a given chain, can't resurrect L2 unless an L1
    //     of that chain is already on field (either surviving or a previously-
    //     resurrected one). Same for L3 needing an L2.
    // Natural chain matched via sharedTraits(slot escendant, candidate) === 2
    // (school + ilk both match), mirroring natural ascension logic.
    const findResurrectSlot = (card) => {
      // LIVE READ (C12) — the slot index chosen here is used by the write
      // below, so both must see the SAME board or the being lands in a
      // slot that no longer looks the way the picker thought it did.
      const esclings = livePlayer(playerIdx, me)?.field?.esclings || [];
      if (card.level === 1) {
        const idx = esclings.findIndex(s => !s?.escendant);
        return idx >= 0 ? idx : -1;
      }
      // L2 or L3 — find a slot where the current escendant is (level-1) of same chain
      const idx = esclings.findIndex(s => {
        if (!s?.escendant) return false;
        const current = getCard(s.escendant);
        if (!current) return false;
        if (current.level !== card.level - 1) return false;
        return sharedTraits(current, card) === 2;
      });
      return idx >= 0 ? idx : -1;
    };

    // ── R44 TALLY + R85 GENERATION (rebuilt July 27, 2026) ──────
    // THE BUG THIS REPLACES: the old line was
    //     if (_revivedThisSolrend.includes(id)) return;
    // — the RETIRED once-per-Solrend rule. handleResurrect and the gate
    // had already moved to tallies (Beb 3x / Champion 2x / Lord 1x), but
    // this picker never offered a card a second time, so the extra returns
    // were legal and unreachable. The gate said 3x; the door said 1x.
    //
    // Now: one row per BASE card (two Taez read as one 'Taez' entry), and
    // the copy offered is the one with the LOWEST tally — the freshest,
    // most returns left. A copy at its cap is skipped; its siblings are
    // not. R85 skips anything that has been through a Graveshift.
    const _revivedThisSolrend = me?.resurrectedCards || [];
    const bestByBase = new Map();
    necropolis.forEach(id => {
      const c = getCard(id);
      if (c?.type !== "Escendant") return;
      if (!canPassiveResurrect(id)) return;            // R85 — Graveshifted
      const used = resTally(_revivedThisSolrend, id);
      if (used >= (RESURRECT_LIMIT[`L${c.level}`] ?? 1)) return; // R44 — spent
      const slotIdx = findResurrectSlot(c);
      if (slotIdx < 0) return;                          // no legal slot now
      const b = baseId(id);
      const prev = bestByBase.get(b);
      if (!prev || used < prev.used) bestByBase.set(b, { id, card: c, slotIdx, used });
    });
    const candidates = [...bestByBase.values()];

    // Sort: level ascending, then alphabetical by name for stability
    candidates.sort((a, b) => a.card.level - b.card.level || a.card.name.localeCompare(b.card.name));

    // Step 1 — [View, Resurrect] radial
    const step1Actions = [
      {
        icon: "👁", label: "View", color: GM.vellumDim, legal: true,
        onSelect: () => {
          // View-only picker — taps open the detail modal for that card
          setTargetPicker({
            title: `◆ Necropolis — ${necropolis.length} card${necropolis.length === 1 ? "" : "s"}`,
            options: necropolis.map(id => ({ id, label: getCard(id)?.name || id })),
            onPick: (opt) => { setTargetPicker(null); viewCard(opt.id, []); },
          });
        },
      },
      {
        icon: "✦", label: "Resurrect", color: GM.inkGold,
        legal: candidates.length > 0 && !resurrectedThisTurn,
        reason: resurrectedThisTurn ? "Already Resurrected this turn" : candidates.length === 0 ? "No valid resurrection targets" : null,
        onSelect: () => {
          // Step 2 — GILDED PICKER (July 2026 canon): every card-list choice
          // goes through TargetPickerModal, never a text radial. Real art,
          // real foils, no 6-candidate cap. handleResurrect self-gates cost.
          setTargetPicker({
            title: `✦ Resurrection — Choose a Being (${candidates.length})`,
            options: candidates.map(({ id, card, slotIdx }) => ({
              id, slotIdx, cost: RESURRECT_COST[`L${card.level || 1}`] ?? RESURRECT_COST.L3,
              label: `L${card.level} — ${card.name} — ${RESURRECT_COST[`L${card.level || 1}`] ?? RESURRECT_COST.L3} Khao or Anima`,
            })),
            onPick: (opt) => {
              setTargetPicker(null);
              // Step 3 — CURRENCY CHOICE. The tile always SAID "Khao/Anima"
              // but the old flow drained Khao silently. Now the player declares
              // which vein they open. Both wedges legality-gated independently.
              const cost = opt.cost;
              const haveKhao = (me?.khao || 0) >= cost;
              // R96 — the Anima wedge must ask the same question spendAnima
              // will: strictly more than the fare. It used to light at exactly
              // `cost` and then fail with "Not enough Anima" on the press.
              const haveAnima = canPayVoluntary(me?.anima ?? STARTING_ANIMA, cost);
              setRadial({ x: pos.x, y: pos.y, actions: [
                { icon: "◈", label: `Pay\n${cost} Khao`, color: GM.inkGold, cost,
                  legal: haveKhao, reason: `Need ${cost} Khao`,
                  onSelect: () => handleResurrect(opt.id, opt.slotIdx, false) },
                { icon: "♥", label: `Pay\n${cost} Anima`, color: SD.red,
                  legal: haveAnima, reason: `Need ${animaFare(cost)}`,
                  onSelect: () => handleResurrect(opt.id, opt.slotIdx, true) },
              ] });
            },
          });
        },
      },
    ];
    setRadial({ x: pos.x, y: pos.y, actions: step1Actions });
  };

  // ── PUBLIC RECORD: the adversary's Necropolis is open to inspection.
  // Graveyards are public information — deck-reading your opponent's
  // ruins is legitimate strategy, not espionage.
  const handleOppNecropolisView = () => {
    const oppNec = opponent?.necropolis || [];
    if (oppNec.length === 0) { showToast("Adversary's Necropolis is empty.", GM.vellumFaint); return; }
    setTargetPicker({
      title: `◆ Adversary's Necropolis — ${oppNec.length} card${oppNec.length === 1 ? "" : "s"}`,
      options: oppNec.map(id => ({ id, label: getCard(id)?.name || id })),
      onPick: (opt) => { setTargetPicker(null); viewCard(opt.id, []); },
    });
  };

  const handleHandCardClick = (cardIdx, e) => {
    const cardId = (me?.hand || [])[cardIdx];
    const card = getCard(cardId);
    if (!card) return;
    if (selectedHandIdx === cardIdx) { setSelectedHandIdx(null); return; }
    setSelectedHandIdx(cardIdx);
    const pos = smartPos(e.clientX, e.clientY);
    const actions = [{ icon: "👁", label: "View", color: GM.vellumDim, legal: true, onSelect: () => viewCard(cardId, []) }];
    if (card.type === "Escendant" && card.level === 1) actions.push({ icon: "⬆", label: "Transcend", color: GM.inkGold, legal: myTurn, reason: "Not your turn", onSelect: () => showToast("Tap a field slot to Transcend.", GM.inkGold) });
    if (card.type === "Escendant" && card.level > 1) actions.push({ icon: "⬆⬆", label: "Ascend", color: GM.inkGold, legal: myTurn && !ascendedThisTurn, reason: !myTurn ? "Not your turn" : "Already Ascended", onSelect: () => showToast("Tap your being on the field to Ascend.", GM.inkGold) });
    if (card.type === "Tome") {
      actions.push({ icon: "⬇", label: "Face Down", color: GM.vellumDim, legal: myTurn, reason: "Not your turn", onSelect: () => showToast("Tap a Tome rift to place.", GM.vellumDim) });
      actions.push({ icon: "✦", label: "Activate", color: SD.gold, cost: card.khaoCost || 0, legal: myTurn && (me?.khao || 0) >= (card.khaoCost || 0), reason: !myTurn ? "Not your turn" : "Not enough Khao", onSelect: () => showToast("Tap a Tome rift to activate.", SD.gold) });
    }
    if (card.type === "Impera") actions.push({ icon: "✦", label: "Attach", color: GM.inkGold, legal: myTurn, reason: "Not your turn", onSelect: () => showToast("Tap your being on the field to attach.", GM.inkGold) });
    if (card.type === "U'hlti") actions.push({ icon: "◈", label: "Attach\nU'hlti", color: SD.gold, legal: myTurn, reason: "Not your turn", onSelect: () => showToast("Tap your Level 3 Natural being to attach U'hlti.", SD.gold) });
    if (card.type === "Affinity") actions.push({ icon: "◆", label: "Attach\nAffinity", color: SD.gold, legal: myTurn, reason: "Not your turn", onSelect: () => showToast("Tap your Level 3 Natural being (with U'hlti) to attach Affinity.", SD.gold) });
    if (card.type === "Curio") actions.push({ icon: "💎", label: "Place", color: SD.gold, legal: false, reason: "Curios are pre-placed before the duel (Rulez v2)", onSelect: () => {} });
    setRadial({ x: pos.x, y: pos.y, actions });
  };

  // ── NEW — Drag-to-play drop dispatcher ──
  // Reuses existing handlers, so drag behavior matches tap-select-tap exactly.
  const handleRiftDrop = (kind, idx, clientX, clientY) => {
    if (draggingHandIdx == null) return;
    const cardIdx = draggingHandIdx;
    const cardId = (me?.hand || [])[cardIdx];
    const card = getCard(cardId);
    setDraggingHandIdx(null);
    if (!card || !myTurn) return;

    if (kind === "slot") {
      const slot = me?.field?.esclings?.[idx];
      if (card.type === "Escendant" && card.level === 1 && !slot?.escendant) {
        const isDev = slot?.escling ? !sharesSchoolAndIlk(card, getCard(slot.escling)) : false;
        handleTranscend(idx, cardIdx, isDev);
        setSelectedHandIdx(null);
        return;
      }
      if (card.type === "Escendant" && card.level > 1 && slot?.escendant) {
        if (ascendedThisTurn) { showToast("Already Ascended this turn.", SD.red); return; }
        handleAscend(idx, cardIdx, false);
        setSelectedHandIdx(null);
        return;
      }
      if (card.type === "Impera" && slot?.escendant) {
        handleAttachImpera(idx, cardIdx);
        setSelectedHandIdx(null);
        return;
      }
      if ((card.type === "U'hlti" || card.type === "Affinity") && slot?.escendant) {
        handleAttachSoulCard(idx, cardIdx);
        setSelectedHandIdx(null);
        return;
      }
      return;
    }

    if (kind === "tome") {
      const tomes = [me?.field?.tomes?.[0] ?? null, me?.field?.tomes?.[1] ?? null, me?.field?.tomes?.[2] ?? null];
      if (tomes[idx] || card.type !== "Tome") return;
      const pos = smartPos(clientX, clientY);
      setSelectedHandIdx(null);
      // Open the same radial as tap-select-tap would: Face Down / Activate
      setRadial({ x: pos.x, y: pos.y, actions: [
        { icon: "⬇", label: "Face\nDown", color: GM.vellumDim, legal: myTurn, reason: "Not your turn", onSelect: () => handlePlayTome(idx, cardIdx, true) },
        // R35 — same gate as the drag path above; see the note there.
        // R35 — see the wedge above: a face-up play out of hand waits for
        // nothing (caP'n, 2026-09-13). This is the mobile twin.
        { icon: "✦", label: "Activate", color: SD.gold, cost: card.khaoCost || 0, legal: myTurn && canPayPrintedFare(card, { khao: me?.khao || 0, anima: me?.anima ?? STARTING_ANIMA }), reason: (card.animaCost || 0) > 0 ? "Not enough Khao or Anima" : "Not enough Khao", onSelect: () => handlePlayTome(idx, cardIdx, false) },
      ]});
      return;
    }

    if (kind === "curio") {
      // R05 (caP'n, 2026-08-26): Curios are PRE-PLACED at duel setup and
      // belong to no deck — never played from hand. A legacy Curio drawn
      // from a grandfathered deck is a dead card in hand. (Same false
      // "Rulez v2" citation as the drop-target above; same correction.)
      showToast("Curios are placed before the duel — they cannot be played from hand.", GM.vellumFaint);
      return;
    }
  };

  // ── SOLREND READY-UP SCREEN ───────────────────────────────────
  if (match && !coinFlipDone && bothPresent && coinFlipPhase === "idle") return (
    <div style={{ background: STARFIELD_BG, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: GM.font }}>
      <style>{`
        @keyframes solrendPulse {
          0%,100% { box-shadow: 0 0 20px ${SD.purple}88, inset 0 0 12px ${SD.purple}33; }
          50%      { box-shadow: 0 0 50px ${GM.inkGold}cc, inset 0 0 24px ${GM.inkGold}44; }
        }
        @keyframes solrendGlow {
          0%,100% { text-shadow: 0 0 12px ${GM.inkGold}, 0 0 40px ${SD.purple}88; }
          50%      { text-shadow: 0 0 24px ${SD.gold}, 0 0 60px ${SD.gold}66; }
        }
      `}</style>
      <div style={{ textAlign: "center", padding: "40px 24px" }}>
        <div style={{ fontSize: 11, letterSpacing: 6, color: GM.vellumFaint, marginBottom: 16 }}>THE SHATTERFIELD AWAITS</div>
        <div style={{ fontSize: 28, fontWeight: 900, fontStyle: "italic", color: GM.inkGold, marginBottom: 8, animation: "solrendGlow 2s ease-in-out infinite" }}>
          ◆ {match.players[0]?.name} vs {match.players[1]?.name}
        </div>
        <div style={{ fontSize: 12, color: GM.vellumFaint, marginBottom: 40, fontStyle: "italic" }}>
          {isNPC ? "Your adversary is ready." : `Both Aenarchists have entered the field.`}
        </div>

        {/* Ready status */}
        <div style={{ display: "flex", gap: 32, justifyContent: "center", marginBottom: 40 }}>
          {[0, 1].map(idx => {
            const ready = match?.solrendReady?.[idx] ?? false;
            const isMe = idx === playerIdx;
            return (
              <div key={idx} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", background: ready ? "#22c55e" : GM.pageBorder, boxShadow: ready ? "0 0 12px #22c55e" : "none", transition: "all 0.3s" }} />
                <div style={{ fontSize: 11, color: ready ? "#22c55e" : GM.vellumFaint, fontStyle: "italic", letterSpacing: 1 }}>
                  {isMe ? "You" : (match.players[idx]?.name || "Opponent")}
                </div>
                <div style={{ fontSize: 10, color: ready ? "#22c55e" : GM.pageBorder }}>{ready ? "READY" : "waiting..."}</div>
              </div>
            );
          })}
        </div>

        {/* Solrend button */}
        {!myReady ? (
          <button
            onClick={() => {
              setSolrendPressed(true);
              updateMatch(code, { [`solrendReady/${playerIdx}`]: true });
            }}
            style={{
              padding: "18px 56px", fontSize: 20, fontWeight: 900, fontStyle: "italic",
              fontFamily: GM.font, letterSpacing: 4, cursor: "pointer",
              background: `linear-gradient(135deg, ${GM.pageMid}, ${GM.pageWarm})`,
              border: `2px solid ${GM.inkGold}`, borderRadius: 8,
              color: GM.vellum, animation: "solrendPulse 2s ease-in-out infinite",
              transition: "all 0.2s",
            }}
          >
            ◆ SOLREND
          </button>
        ) : (
          <div style={{ fontSize: 16, color: "#22c55e", fontStyle: "italic", letterSpacing: 2 }}>
            ✓ Ready — waiting for adversary...
          </div>
        )}

        {/* Countdown */}
        <div style={{ marginTop: 24, fontSize: 11, color: GM.pageBorder, letterSpacing: 2 }}>
          Auto-starts in {solrendCountdown}s
        </div>
      </div>
    </div>
  );

  // ── COIN FLIP ANIMATION — D12 THE SOLREND COIN (2026-08-18) ───
  // A real two-faced coin: the Divine Crest against Snake Eyes.
  // The flip is deterministic from createdAt parity (the MP-FIX
  // above), so the render KNOWS the face before the spin starts and
  // the 1.5s spin genuinely lands on it — heads ends at a full turn
  // (front showing), tails at a half-turn past (back showing).
  // Inline <style> is fine here: this screen's keyframes die with
  // the screen; nothing else references them (the keyframe trap is
  // about SHARED keyframes).
  if (coinFlipPhase === "spinning" || (coinFlipPhase === "result" && !coinFlipDone)) {
    const predictedFlip = ((match?.createdAt || 0) % 2 === 0) ? "heads" : "tails";
    const shownFace = effectiveCoinFlipResult || predictedFlip;
    const endDeg = shownFace === "heads" ? 1800 : 1980; // 5 turns / 5.5 turns
    return (
    <div style={{ background: STARFIELD_BG, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: GM.font }}>
      <style>{`
        @keyframes sfCoinSpinH {
          0%   { transform: rotateY(0deg) translateY(0); }
          18%  { transform: rotateY(430deg) translateY(-56px); }
          45%  { transform: rotateY(1030deg) translateY(-84px); }
          78%  { transform: rotateY(1560deg) translateY(-28px); }
          100% { transform: rotateY(1800deg) translateY(0); }
        }
        @keyframes sfCoinSpinT {
          0%   { transform: rotateY(0deg) translateY(0); }
          18%  { transform: rotateY(470deg) translateY(-56px); }
          45%  { transform: rotateY(1130deg) translateY(-84px); }
          78%  { transform: rotateY(1720deg) translateY(-28px); }
          100% { transform: rotateY(1980deg) translateY(0); }
        }
        @keyframes sfCoinSettle {
          0%   { transform: scale(1); }
          35%  { transform: scale(1.16); }
          65%  { transform: scale(0.96); }
          100% { transform: scale(1); }
        }
        @keyframes resultGlow {
          0%,100% { text-shadow: 0 0 20px ${SD.gold}, 0 0 60px ${SD.gold}66; }
          50%      { text-shadow: 0 0 40px ${SD.gold}, 0 0 100px ${SD.gold}88; }
        }
      `}</style>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 11, letterSpacing: 6, color: GM.vellumFaint, marginBottom: 32 }}>
          {coinFlipPhase === "spinning" ? "DETERMINING FIRST AENARCHIST" : "THE COIN HAS SPOKEN"}
        </div>
        <div style={{ perspective: 720, display: "inline-block", marginBottom: 24 }}>
          <div style={{ display: "inline-block", animation: coinFlipPhase === "result" ? "sfCoinSettle 0.5s ease-out" : "none" }}>
            <div style={{
              width: 132, height: 132, position: "relative", transformStyle: "preserve-3d",
              animation: coinFlipPhase === "spinning" ? `${shownFace === "heads" ? "sfCoinSpinH" : "sfCoinSpinT"} 1.5s cubic-bezier(0.34,0.75,0.42,1) forwards` : "none",
              transform: coinFlipPhase === "result" ? `rotateY(${endDeg}deg)` : undefined,
            }}>
              {/* T11 — both faces come out of the skin table. Shatterfield
                  owns the spin; coinSkins.js owns the metal. */}
              <CoinFaces skinId={coinSkinId} side="heads" />
              <CoinFaces skinId={coinSkinId} side="tails" />
            </div>
          </div>
        </div>
        {coinFlipPhase === "result" && effectiveCoinFlipResult && (
          <>
            {/* T11 — the banner names the TRUE side, whatever the coin
                looks like. This is the line that keeps J'ynxi's
                two-identical-faces skin honest. */}
            <div style={{ fontSize: 32, fontWeight: 900, color: bannerFor(coinSkinId, effectiveCoinFlipResult).color, fontStyle: "italic", marginBottom: 12, animation: "resultGlow 1.5s ease-in-out infinite" }}>
              {bannerFor(coinSkinId, effectiveCoinFlipResult).text}
            </div>
            {(() => {
              const firstIdx = effectiveCoinFlipResult === "heads" ? 0 : 1;
              const youFirst = firstIdx === playerIdx;
              const firstName = match?.players?.[firstIdx]?.name || "Opponent";
              const label = youFirst ? "YOU GO FIRST" : `${firstName.toUpperCase()} GOES FIRST`;
              const accentColor = youFirst ? GM.inkGold : SD.red;
              return (
                <div style={{
                  fontSize: 24,
                  fontWeight: 900,
                  color: accentColor,
                  letterSpacing: 3,
                  padding: "14px 24px",
                  border: `2px solid ${accentColor}`,
                  borderRadius: 4,
                  background: `${accentColor}15`,
                  boxShadow: `0 0 24px ${accentColor}55, inset 0 0 12px ${accentColor}22`,
                  textShadow: `0 0 12px ${accentColor}88`,
                  fontFamily: GM.font,
                  fontStyle: "italic",
                  display: "inline-block",
                }}>
                  ◆ {label} ◆
                </div>
              );
            })()}
          </>
        )}
        {coinFlipPhase === "spinning" && (
          <div style={{ fontSize: 14, color: GM.vellumFaint, fontStyle: "italic" }}>Flipping for first turn...</div>
        )}
      </div>
    </div>
    );
  }

  // Waiting for opponent(s) to join (human match, not everyone present yet)
  //
  // N-SEAT: bothPresent already checks every seat, filled AND present —
  // this text used to assume there was only ever one other chair. A
  // 3-4p lobby has as many as three, so the copy now counts how many
  // are still missing instead of naming a single "adversary."
  if (!coinFlipDone && !bothPresent) {
    const seatN = seatCountOf(match);
    const presentCount = seatIdxsAll.filter((i) => !!match?.players?.[i] && !!match?.present?.[i]).length;
    const stillMissing = Math.max(0, seatN - presentCount);
    return (
    <div style={{ background: STARFIELD_BG, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: GM.font }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 20, fontStyle: "italic", color: GM.inkGold, marginBottom: 14, textShadow: `0 0 24px ${SD.purple}` }}>◆ Entering the Shatterfield ◆</div>
        <div style={{ fontSize: 13, color: GM.vellumFaint, marginBottom: 8, letterSpacing: 2 }}>
          {seatN > 2 ? "Share this code with your table:" : "Share this code with your adversary:"}
        </div>
        <div style={{ fontSize: 40, fontWeight: 900, color: SD.gold, letterSpacing: 8, marginBottom: 20, textShadow: `0 0 20px ${SD.gold}88` }}>{code}</div>
        <div style={{ fontSize: 11, color: GM.pageBorder, fontStyle: "italic" }}>
          {seatN > 2
            ? `Waiting for ${stillMissing} more...`
            : "Waiting for opponent..."}
        </div>
      </div>
    </div>
    );
  }

  // N-SEAT: match.winner is a seat idx (1v1, FFA) or a team id (2v2) —
  // show a single name for the former, both teammates' names for the
  // latter, rather than assuming `players[match.winner]` always exists.
  // ── R98 · THE RESULT SCREEN CAN SAY "BOTH" ────────────────────
  // caP'n, 2026-08-25: *"both players win"*. `winnersOfMatch` returns
  // one id for an ordinary duel and two or more when a Prophecy
  // co-won, so this reads the same for every match it ever read before
  // and grows a second name only when there genuinely is one.
  // A TEAM id resolves to its seats' names joined with "&", exactly as
  // it always did; two WINNERS are joined with "AND" so the screen
  // never has to explain the difference between an ally and a rival
  // who both won.
  const nameOfWinnerId = (w) => {
    if (w == null) return null;
    if (match.players?.[w]) return match.players[w]?.name || null;
    const teamSeats = allSeatIdxs(match).filter((i) => teamOf(match, i) === w);
    return teamSeats.length ? teamSeats.map((i) => match.players?.[i]?.name).filter(Boolean).join(" & ") : null;
  };
  const winnerName = (() => {
    if (!isFinished) return null;
    const names = winnersOfMatch(match).map(nameOfWinnerId).filter(Boolean);
    if (!names.length) return null;
    return names.length === 1 ? names[0] : names.join(" AND ");
  })();
  // R10 (Rulez v2): drawing AT the cap is legal (draw-then-discard) —
  // hand size no longer blocks the draw; drawnThisTurn is the only gate.
  const canDraw = myTurn && !drawnThisTurn;
  return (
    <div className={"sf-root" + (animaImpact ? " sf-board-shake" : "")} style={{ height: "100vh", display: "flex", flexDirection: "column", fontFamily: GM.font, overflow: "hidden", position: "relative", background: "transparent" }}>
      <ShatterfieldVeilBG myAnima={me?.anima} oppAnima={opponent?.anima} />
      {!isMobile && <EmberMotes />}
      <HoloStyle />
      <style>{`
        /* ═══ MAGNUM PASS 4 — THE REAL TYPE ARRIVES ════════════
           Cinzel was declared across the app but never actually
           loaded — every screen silently fell back to Palatino.
           @import must be the first rule of this stylesheet. */
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;800;900&family=Cinzel+Decorative:wght@700;900&family=Cormorant+Garamond:ital,wght@0,500;0,600;0,700;1,500;1,600;1,700&display=swap');

        /* (dead @media 480px block removed July 3 — it targeted classes
           applied to NO element: shatterfield-root, player-side,
           stat-value, hand-area, action-bar. Root carries sf-root.) */
        @keyframes mulliganPulse {
          0%, 100% { box-shadow: 0 0 8px ${SD.purple}66, inset 0 0 6px ${SD.purple}33; }
          50% { box-shadow: 0 0 20px ${GM.inkGold}88, inset 0 0 12px ${GM.inkGold}44; }
        }
        @keyframes sf-khaoSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes attackGlow {
          0%, 100% { box-shadow: 0 0 8px ${SD.red}66; }
          50% { box-shadow: 0 0 22px ${SD.red}cc, 0 0 40px ${SD.red}44; }
        }
        /* ═══ NEW — necropolis glow scales with population ═══ */
        @keyframes sf-necroPulseFaint {
          0%, 100% { box-shadow: 0 0 4px #4a5a3a22, inset 0 0 6px #4a5a3a22; }
          50%      { box-shadow: 0 0 10px #5d7a4844, inset 0 0 10px #5d7a4833; }
        }
        @keyframes sf-necroPulseMid {
          0%, 100% { box-shadow: 0 0 8px #5d7a4844, inset 0 0 8px #4a5a3a33; }
          50%      { box-shadow: 0 0 18px #7ea05966, inset 0 0 14px #5d7a4855; }
        }
        @keyframes sf-necroPulseStrong {
          0%, 100% { box-shadow: 0 0 14px #7ea05966, inset 0 0 10px #5d7a4855; }
          50%      { box-shadow: 0 0 28px #9ec47d99, 0 0 50px #5d7a4844, inset 0 0 18px #7ea05977; }
        }
        /* ═══ NEW — overdraw: draw tile pulses red when one more draw fills the cap ═══ */
        @keyframes sf-overdrawPulse {
          0%, 100% { box-shadow: 0 0 8px ${SD.red}77, inset 0 0 6px ${SD.red}44; }
          50%      { box-shadow: 0 0 20px ${SD.red}cc, 0 0 36px ${SD.red}55, inset 0 0 12px ${SD.red}66; }
        }
        /* ═══ NEW — hand card enter/exit (applied INLINE so the holo class animation survives) ═══ */
        @keyframes sf-handCardEnter {
          0%   { transform: translate(60px, -40px) rotate(-8deg) scale(0.7); opacity: 0; }
          60%  { opacity: 1; }
          100% { transform: translate(0, 0) rotate(0deg) scale(1); opacity: 1; }
        }
        @keyframes sf-handCardExit {
          0%   { transform: translate(0, 0) scale(1); opacity: 1; }
          100% { transform: translate(0, -40px) scale(0.7); opacity: 0; }
        }
        /* ═══ NEW — centered hover-peek: quick, light, no bounce — this is a
           glance, not an entrance ═══ */
        @keyframes sf-hoverPeekIn {
          0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.94); }
          100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        /* ═══ NEW — mobile hand fan: scrollable, overlapping, no visible bar ═══ */
        .sf-hand-fan {
          scrollbar-width: none;          /* Firefox */
          -ms-overflow-style: none;       /* old Edge/IE */
        }
        .sf-hand-fan::-webkit-scrollbar { display: none; width: 0; height: 0; } /* WebKit */
        /* ═══ NEW — drop target breathing outline during drag ═══ */
        @keyframes sf-dropTargetBreath {
          0%, 100% { outline-color: ${GM.inkGold}66; box-shadow: 0 0 10px ${GM.inkGold}44; }
          50%      { outline-color: ${SD.gold}ee; box-shadow: 0 0 22px ${SD.gold}88; }
        }
        .sf-drop-ok {
          outline: 2px dashed ${GM.inkGold}cc;
          outline-offset: 3px;
          animation: sf-dropTargetBreath 1.1s ease-in-out infinite;
        }
        /* Pass 0 — context-aware empty rift breathing */
        @keyframes sf-riftBreathe {
          0%, 100% {
            box-shadow: 0 0 14px ${SD.gold}1a, inset 0 0 12px ${SD.gold}08;
            border-color: ${SD.gold}66;
          }
          50% {
            box-shadow: 0 0 28px ${SD.gold}44, inset 0 0 22px ${SD.gold}18;
            border-color: ${SD.gold}cc;
          }
        }
        .sf-rift-breathe {
          animation: sf-riftBreathe 2.4s ease-in-out infinite;
        }
        /* Pass 0 — calm stat shimmer for the HP/Khao numbers */
        @keyframes sf-statPulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.78; }
        }
        /* Pass 1 — HP change flash (red surge + scale up) */
        @keyframes sf-hpFlash {
          0%   { transform: scale(1);    text-shadow: 0 0 10px ${SD.red}55; }
          30%  { transform: scale(1.35); text-shadow: 0 0 28px ${SD.red}, 0 0 48px ${SD.red}99; color: #ffdddd; }
          100% { transform: scale(1);    text-shadow: 0 0 10px ${SD.red}55; }
        }
        .sf-hp-flash { animation: sf-hpFlash 0.6s ease-out; }
        /* Pass 1 — Khao gained flash (gold bloom) */
        @keyframes sf-khaoFlash {
          0%   { transform: scale(1);    text-shadow: 0 0 10px ${SD.gold}55; }
          30%  { transform: scale(1.28); text-shadow: 0 0 24px ${SD.gold}, 0 0 40px ${GM.inkGold}aa; color: #fff6c8; }
          100% { transform: scale(1);    text-shadow: 0 0 10px ${SD.gold}55; }
        }
        .sf-khao-flash { animation: sf-khaoFlash 0.5s ease-out; }
        /* Pass 1 — generic stat flash (hand, necropolis) */
        @keyframes sf-statFlash {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
        .sf-stat-flash { animation: sf-statFlash 0.4s ease-out; }
        /* Pass 1 — active side aura breathing (subtle, continuous) */
        @keyframes sf-sideActive {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1;    }
        }
        .sf-side-active-aura { animation: sf-sideActive 4s ease-in-out infinite; }
        /* Pass 1 — center flourish gentle pulse */
        @keyframes sf-sigilBreathe {
          0%, 100% { opacity: 0.35; filter: drop-shadow(0 0 6px ${GM.inkGold}44); }
          50%      { opacity: 0.65; filter: drop-shadow(0 0 14px ${GM.inkGold}88); }
        }
        .sf-sigil-breathe { animation: sf-sigilBreathe 5s ease-in-out infinite; }

        /* ═══ PASS 2 — REACTIVE MOMENTS ═══════════════════════ */

        /* Card settle — dramatic scale-in with brightness bloom.
           Longer + bigger so it's visible during fast NPC turn sequences. */
        @keyframes sf-cardSettle {
          0%   { transform: scale(0.70) translateY(-8px);  opacity: 0;   filter: brightness(1.8) saturate(1.5); }
          25%  { transform: scale(1.14) translateY(0);     opacity: 1;   filter: brightness(1.5) saturate(1.3); }
          50%  { transform: scale(0.96) translateY(0);     opacity: 1;   filter: brightness(1.2) saturate(1.15); }
          75%  { transform: scale(1.03) translateY(0);     opacity: 1;   filter: brightness(1.08) saturate(1.05); }
          100% { transform: scale(1)    translateY(0);     opacity: 1;   filter: brightness(1)   saturate(1);    }
        }
        .sf-card-settle { animation: sf-cardSettle 0.55s cubic-bezier(0.34, 1.56, 0.64, 1); transform-origin: center; }

        /* Ruin shatter — crack sweep + desaturate + fade */
        @keyframes sf-cardShatter {
          0%   { transform: scale(1) rotate(0deg);      opacity: 1;   filter: saturate(1) brightness(1);           }
          20%  { transform: scale(1.04) rotate(-0.8deg); opacity: 1;   filter: saturate(1.4) brightness(1.3);       }
          35%  { transform: scale(0.98) rotate(1.2deg);  opacity: 0.9; filter: saturate(0.6) brightness(0.8);       }
          60%  { transform: scale(0.95) rotate(-0.4deg); opacity: 0.7; filter: saturate(0.2) brightness(0.55);      }
          100% { transform: scale(0.88) rotate(0deg);    opacity: 0;   filter: saturate(0)   brightness(0.35);     }
        }
        .sf-card-shatter { animation: sf-cardShatter 0.6s ease-out forwards; pointer-events: none; }

        /* Shatter overlay — the red crack lines sweeping across */
        @keyframes sf-shatterCracks {
          0%   { opacity: 0;   transform: scale(0.85); }
          30%  { opacity: 0.9; transform: scale(1);    }
          100% { opacity: 0;   transform: scale(1.12); }
        }
        .sf-card-shatter-overlay { animation: sf-shatterCracks 0.6s ease-out forwards; pointer-events: none; }

        /* Floating damage number — rises and fades. Longer hang time so
           it's visible during fast sequences; bigger initial pop too. */
        @keyframes sf-dmgFloat {
          0%   { transform: translate(-50%, 0)      scale(0.5); opacity: 0;    }
          12%  { transform: translate(-50%, -6px)   scale(1.35); opacity: 1;    }
          30%  { transform: translate(-50%, -14px)  scale(1.05); opacity: 1;    }
          70%  { transform: translate(-50%, -38px)  scale(1);    opacity: 0.95; }
          100% { transform: translate(-50%, -68px)  scale(0.85); opacity: 0;    }
        }
        .sf-dmg-float { animation: sf-dmgFloat 1.4s ease-out forwards; }

        /* ═══ LAST RITES PASS — reaction FX ═══════════════════ */
        /* Struck-card recoil — sharp jolt away from the blow, brief red
           wash, settles fast. The counterpart to sf-cardLunge: the
           attacker lunges, the victim FLINCHES. */
        @keyframes sf-cardRecoil {
          0%   { transform: translate(0,0)      rotate(0deg);    filter: brightness(1); }
          18%  { transform: translate(3px,-2px) rotate(1.6deg);  filter: brightness(1.55) saturate(1.4) sepia(0.25) hue-rotate(-20deg); }
          40%  { transform: translate(-2px,1px) rotate(-1.1deg); filter: brightness(0.85); }
          65%  { transform: translate(1px,0)    rotate(0.5deg);  filter: brightness(1.05); }
          100% { transform: translate(0,0)      rotate(0deg);    filter: brightness(1); }
        }
        .sf-card-recoil { animation: sf-cardRecoil 0.42s cubic-bezier(0.36, 0.07, 0.19, 0.97); transform-origin: center; }

        /* Ilk slash — the crossing streaks sweep through and vanish. */
        @keyframes sf-ilkSlash {
          0%   { opacity: 0;    transform: translate(-14%, 14%) scale(0.9); }
          22%  { opacity: 1;    transform: translate(0, 0)      scale(1.04); }
          55%  { opacity: 0.85; transform: translate(4%, -4%)   scale(1.02); }
          100% { opacity: 0;    transform: translate(12%, -12%) scale(1); }
        }
        .sf-ilk-slash { animation: sf-ilkSlash 0.46s cubic-bezier(0.22, 1, 0.36, 1) forwards; }

        /* Heal float — a breath, not a hit. Smaller, softer, shorter
           than sf-dmgFloat; drifts up gently and dissolves. */
        @keyframes sf-healFloat {
          0%   { transform: translate(-50%, 0)     scale(0.7); opacity: 0;   }
          18%  { transform: translate(-50%, -5px)  scale(1.1); opacity: 0.95;}
          60%  { transform: translate(-50%, -20px) scale(1);   opacity: 0.8; }
          100% { transform: translate(-50%, -38px) scale(0.9); opacity: 0;   }
        }
        .sf-heal-float { animation: sf-healFloat 1.25s ease-out forwards; }

        /* Fallen mist — slow phantom drift across the grayed veil. */
        @keyframes sf-fallenMist {
          0%   { transform: translate(-4%, 2%)  rotate(0deg);   opacity: 0.7; }
          50%  { transform: translate(4%, -3%)  rotate(2deg);   opacity: 1;   }
          100% { transform: translate(-4%, 2%)  rotate(0deg);   opacity: 0.7; }
        }
        .sf-fallen-mist { animation: sf-fallenMist 4.5s ease-in-out infinite; }

        /* Fracture shimmer — the standing-but-broken breath. Hairline
           cracks pulse dim red while Lyf = 0 (Vulnerable). */
        @keyframes sf-fractureShimmer {
          0%, 100% { opacity: 0.28; filter: drop-shadow(0 0 2px ${SD.red}66); }
          50%      { opacity: 0.75; filter: drop-shadow(0 0 6px ${SD.red}); }
        }
        .sf-fracture-shimmer { animation: sf-fractureShimmer 2.2s ease-in-out infinite; }

        /* Tome casting — amethyst rune-burst (J'ynxi's ink). Distinct from
           every being transition: spellwork arrives in narrator-purple. */
        @keyframes sf-glowTome {
          0%   { box-shadow: 0 0 0 0     #a855f7aa, 0 0 0 0     #6d28d9aa, inset 0 0 0 0  #a855f700; }
          25%  { box-shadow: 0 0 30px 8px #c084fcdd, 0 0 58px 18px #7c3aed99, inset 0 0 26px #a855f755; }
          60%  { box-shadow: 0 0 24px 6px #a855f7aa, 0 0 46px 14px #6d28d977, inset 0 0 18px #a855f733; }
          100% { box-shadow: 0 0 0 0     #a855f700, 0 0 0 0     #6d28d900, inset 0 0 0 0  #a855f700; }
        }
        .sf-glow-tome { border-radius: 10px; animation: sf-glowTome 0.9s ease-out forwards; }

        /* Turn breath — the whole board dims briefly when turn changes */
        @keyframes sf-turnBreath {
          0%   { filter: brightness(1)    contrast(1);     }
          40%  { filter: brightness(0.82) contrast(1.06);  }
          100% { filter: brightness(1)    contrast(1);     }
        }
        .sf-turn-breath { animation: sf-turnBreath 0.45s ease-out; }

        /* ═══ MAGNUM PASS 1 — THE FIELD BREATHES ═══════════════ */

        /* Ember motes — ambient sparks rising through the field */
        @keyframes sf-moteRise {
          0%   { transform: translateY(0)      translateX(0);     opacity: 0;    }
          8%   { opacity: 0.5; }
          50%  { transform: translateY(-55vh)  translateX(10px);  opacity: 0.35; }
          92%  { opacity: 0.25; }
          100% { transform: translateY(-108vh) translateX(-6px);  opacity: 0;    }
        }

        /* Turn Herald — center line unfurls, name blooms in, both dissolve */
        @keyframes sf-heraldLine {
          0%   { transform: scaleX(0);    opacity: 0; }
          18%  { transform: scaleX(1);    opacity: 1; }
          78%  { transform: scaleX(1);    opacity: 1; }
          100% { transform: scaleX(1.05); opacity: 0; }
        }
        .sf-herald-line { animation: sf-heraldLine 1.6s cubic-bezier(0.22, 1, 0.36, 1) forwards; }
        @keyframes sf-heraldText {
          0%   { opacity: 0; letter-spacing: 18px; transform: scale(1.06); filter: blur(4px); }
          22%  { opacity: 1; letter-spacing: 6px;  transform: scale(1);    filter: blur(0);   }
          76%  { opacity: 1; letter-spacing: 6px;  transform: scale(1);    filter: blur(0);   }
          100% { opacity: 0; letter-spacing: 8px;  transform: scale(0.98); filter: blur(2px); }
        }
        .sf-herald-text { animation: sf-heraldText 1.6s cubic-bezier(0.22, 1, 0.36, 1) forwards; }

        /* Danger vignette — screen edges breathe red at low Anima */
        @keyframes sf-dangerBreathe {
          0%, 100% { box-shadow: inset 0 0 90px  20px rgba(200, 40, 40, 0.14); }
          50%      { box-shadow: inset 0 0 140px 40px rgba(200, 40, 40, 0.30); }
        }
        .sf-danger { animation: sf-dangerBreathe 3.4s ease-in-out infinite; }
        @keyframes sf-dangerBreatheHard {
          0%, 100% { box-shadow: inset 0 0 110px 30px rgba(220, 40, 40, 0.24); }
          50%      { box-shadow: inset 0 0 180px 60px rgba(220, 40, 40, 0.44); }
        }
        .sf-danger-critical { animation: sf-dangerBreatheHard 1.4s ease-in-out infinite; }

        /* Anima impact — the whole field flinches when your Anima drops */
        @keyframes sf-boardShake {
          0%, 100% { transform: translate(0, 0); }
          15% { transform: translate(-4px, 2px); }
          30% { transform: translate(4px, -2px); }
          45% { transform: translate(-3px, -2px); }
          60% { transform: translate(3px, 2px); }
          75% { transform: translate(-2px, 1px); }
          90% { transform: translate(1px, -1px); }
        }
        .sf-board-shake { animation: sf-boardShake 0.45s cubic-bezier(0.36, 0.07, 0.19, 0.97); }
        @keyframes sf-hitFlash {
          0%   { box-shadow: inset 0 0 160px 60px rgba(220, 50, 50, 0.5); opacity: 1; }
          100% { box-shadow: inset 0 0 90px  20px rgba(220, 50, 50, 0);   opacity: 0; }
        }
        .sf-hit-flash { animation: sf-hitFlash 0.48s ease-out forwards; }

        /* Shatter Seam — a hairline fracture of gold light between the
           halves, broken at the center sigil, with a slow traveling glint */
        @keyframes sf-seamGlint {
          0%   { background-position: -240px 0;              opacity: 0;   }
          8%   { opacity: 0.9; }
          46%  { background-position: calc(100% + 240px) 0;  opacity: 0.9; }
          54%  { background-position: calc(100% + 240px) 0;  opacity: 0;   }
          100% { background-position: calc(100% + 240px) 0;  opacity: 0;   }
        }
        .sf-seam-glint { animation: sf-seamGlint 7s ease-in-out infinite; }

        /* ═══ MAGNUM PASS 3 — CHROME ═══════════════════════════ */

        /* Modal choreography — every popup shares one entrance:
           backdrop fades, panel rises out of a blur. */
        @keyframes sf-shellIn { from { opacity: 0; } to { opacity: 1; } }
        .sf-modal-shell { animation: sf-shellIn 0.2s ease-out; }
        @keyframes sf-modalIn {
          0%   { opacity: 0; transform: scale(0.94) translateY(10px); filter: blur(6px); }
          100% { opacity: 1; transform: scale(1)    translateY(0);    filter: blur(0);   }
        }
        .sf-modal-shell > * { animation: sf-modalIn 0.3s cubic-bezier(0.22, 1, 0.36, 1); }

        /* Button glint — a light sweep crosses chrome buttons on hover */
        .sf-chrome-btn { position: relative; overflow: hidden; }
        .sf-chrome-btn::after {
          content: ""; position: absolute; top: 0; bottom: 0; left: -60%;
          width: 40%; opacity: 0;
          background: linear-gradient(105deg, transparent, rgba(255,244,214,0.28), transparent);
          transform: skewX(-18deg);
          pointer-events: none;
        }
        .sf-chrome-btn:hover:not(:disabled)::after { animation: sf-btnGlint 0.6s ease-out; }
        @keyframes sf-btnGlint { 0% { left: -60%; opacity: 1; } 100% { left: 120%; opacity: 0; } }
        .sf-chrome-btn:hover:not(:disabled)  { filter: brightness(1.12); }
        .sf-chrome-btn:active:not(:disabled) { transform: translateY(1px) scale(0.985); }

        /* Radial menu bloom — petals unfurl from the core with a stagger */
        /* ═══ THE FORGED RING — RadialMenu v2 (July 26, 2026) ═══ */
        @keyframes sf-radialBloom {
          0%   { opacity: 0;                    transform: translate(-50%,-50%) scale(0.3) rotate(-10deg); }
          70%  { opacity: var(--petal-op, 1);   transform: translate(-50%,-50%) scale(1.06) rotate(1.5deg); }
          100% { opacity: var(--petal-op, 1);   transform: translate(-50%,-50%) scale(1) rotate(0deg);      }
        }
        @keyframes sf-radialCore {
          0%   { transform: translate(-50%,-50%) scale(0)    rotate(-90deg); opacity: 0; }
          100% { transform: translate(-50%,-50%) scale(1)    rotate(0deg);   opacity: 1; }
        }
        @keyframes sf-radialScrim {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes sf-radialRing {
          0%   { opacity: 0; transform: translate(-50%,-50%) scale(0.7) rotate(-24deg); }
          100% { opacity: 1; transform: translate(-50%,-50%) scale(1)   rotate(0deg);   }
        }
        /* the orbit crawls — the ring is machinery, not a decal */
        @keyframes sf-radialOrbit {
          0%   { stroke-dashoffset: 0;  }
          100% { stroke-dashoffset: 60; }
        }
        .sf-radial-orbit { animation: sf-radialOrbit 3.4s linear infinite; }
        /* the hub sigil breathes gold */
        @keyframes sf-radialSigil {
          0%, 100% { opacity: 0.72; transform: scale(0.94) rotate(0deg);   }
          50%      { opacity: 1;    transform: scale(1.08) rotate(45deg);  }
        }
        .sf-radial-sigil { display: inline-block; animation: sf-radialSigil 3.2s ease-in-out infinite; }
        /* the engraving speaks */
        @keyframes sf-radialSay {
          0%   { opacity: 0; transform: translateX(-50%) translateY(-5px); }
          100% { opacity: 1; transform: translateX(-50%) translateY(0);    }
        }
        /* a locked petal recoils instead of silently doing nothing */
        @keyframes sf-radialDeny {
          0%,100% { transform: translate(-50%,-50%) scale(1)    rotate(0deg);    }
          18%     { transform: translate(-52%,-50%) scale(0.96) rotate(-3.5deg); }
          42%     { transform: translate(-48%,-50%) scale(0.97) rotate(3.5deg);  }
          68%     { transform: translate(-51%,-50%) scale(0.99) rotate(-1.6deg); }
        }

        /* Toast entrance — message slides out of a blur */
        @keyframes sf-toastIn {
          0%   { opacity: 0; transform: translateX(-12px); filter: blur(3px); }
          100% { opacity: 1; transform: translateX(0);     filter: blur(0);   }
        }
        .sf-toast-in { animation: sf-toastIn 0.25s cubic-bezier(0.22, 1, 0.36, 1); }

        /* Draw tile hover — brief brightening on enabled tiles */
        .sf-tile-hover:hover { filter: brightness(1.15); }

        /* ═══ MAGNUM PASS 5 — THE SHATTERED VEIL ═══════════════ */
        @keyframes sf-veilTwinkle {
          0%, 100% { opacity: 0.12; }
          50%      { opacity: 0.5;  }
        }
        @keyframes sf-veilCorona {
          0%, 100% { opacity: 0.55; transform: scale(1);     }
          50%      { opacity: 0.95; transform: scale(1.045); }
        }
        @keyframes sf-veilSpin { to { transform: rotate(360deg); } }
        @keyframes sf-veilShard {
          0%, 100% { transform: translateY(0)     rotate(0deg);   }
          50%      { transform: translateY(-15px) rotate(3.5deg); }
        }
        @keyframes sf-veilRibbonCold {
          0%, 100% { transform: translateX(-2.5%) rotate(-6deg); }
          50%      { transform: translateX(2.5%)  rotate(-6deg); }
        }
        .sf-veil-ribbon-cold { animation: sf-veilRibbonCold 34s ease-in-out infinite; transform: rotate(-6deg); }
        @keyframes sf-veilRibbonWarm {
          0%, 100% { transform: translateX(2.5%)  rotate(5deg); }
          50%      { transform: translateX(-2.5%) rotate(5deg); }
        }
        .sf-veil-ribbon-warm { animation: sf-veilRibbonWarm 40s ease-in-out infinite; transform: rotate(5deg); }
        @keyframes sf-veilSpinRev { to { transform: rotate(-360deg); } }
        /* MAGNUM PASS 8 — a new fracture flashes bright, then settles */
        @keyframes sf-crackPop {
          0%   { opacity: 0; stroke-width: 3.4; }
          30%  { opacity: 1; stroke-width: 2.6; }
          100% { opacity: 1; stroke-width: 1.7; }
        }

        /* ═══ MAGNUM PASS 7 — GOTHIC PLATES ═════════════════════
           The house silhouette for interactive boxes: corners cut at
           45°, like plates of cut obsidian. Replaces rounded rects. */
        .sf-plate {
          clip-path: polygon(8px 0, calc(100% - 8px) 0, 100% 8px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0 calc(100% - 8px), 0 8px);
          border-radius: 0 !important;
          transition: all 0.2s;
        }
        .sf-plate:hover:not(:disabled) { filter: brightness(1.14); }

        /* ═══ PASS 3 — SOLREND END CINEMATIC ═══════════════════ */

        /* Phase 1 — board collapse. Everything dims, cards tremble & fade. */
        @keyframes sf-solrendDim {
          0%   { filter: brightness(1)    contrast(1)    saturate(1); }
          100% { filter: brightness(0.22) contrast(1.15) saturate(0.5); }
        }
        .sf-solrend-dim { animation: sf-solrendDim 0.8s ease-in forwards; }

        /* Cards tremble during collapse */
        @keyframes sf-solrendTremble {
          0%, 100% { transform: translate(0, 0)     rotate(0deg); }
          15%      { transform: translate(-2px,1px) rotate(-0.5deg); }
          30%      { transform: translate(2px,-1px) rotate(0.6deg); }
          45%      { transform: translate(-1px,2px) rotate(-0.3deg); }
          60%      { transform: translate(2px,1px)  rotate(0.4deg); }
          75%      { transform: translate(-2px,-1px)rotate(-0.2deg); }
        }
        .sf-solrend-tremble { animation: sf-solrendTremble 0.28s linear infinite; }

        /* Phase 2 — singularity bloom from center */
        @keyframes sf-solrendBloom {
          0%   { opacity: 0;   transform: translate(-50%,-50%) scale(0);   filter: blur(0);  }
          30%  { opacity: 1;   transform: translate(-50%,-50%) scale(0.4); filter: blur(2px); }
          70%  { opacity: 1;   transform: translate(-50%,-50%) scale(2.2); filter: blur(6px); }
          100% { opacity: 0.9; transform: translate(-50%,-50%) scale(5);   filter: blur(12px);}
        }
        .sf-solrend-bloom { animation: sf-solrendBloom 1.4s cubic-bezier(0.5, 0, 0.2, 1) forwards; }

        /* Phase 2 — inward pulling void rings */
        @keyframes sf-solrendRing {
          0%   { opacity: 0;   transform: translate(-50%,-50%) scale(2.5); border-width: 1px; }
          40%  { opacity: 0.9; transform: translate(-50%,-50%) scale(1.2); border-width: 2px; }
          100% { opacity: 0;   transform: translate(-50%,-50%) scale(0.05);border-width: 1px; }
        }
        .sf-solrend-ring  { animation: sf-solrendRing  1.1s cubic-bezier(0.4, 0, 0.6, 1) forwards; }

        /* Phase 3 — final white flash */
        @keyframes sf-solrendFlash {
          0%   { opacity: 0; }
          20%  { opacity: 1; }
          60%  { opacity: 0.9; }
          100% { opacity: 0; }
        }
        .sf-solrend-flash { animation: sf-solrendFlash 0.55s ease-out forwards; }

        /* Phase 4 — VictoryScreen rises from the light */
        @keyframes sf-victoryRise {
          0%   { opacity: 0; transform: scale(0.92) translateY(24px); filter: blur(8px); }
          60%  { opacity: 1; transform: scale(1.02) translateY(0);    filter: blur(0); }
          100% { opacity: 1; transform: scale(1)    translateY(0);    filter: blur(0); }
        }
        .sf-victory-rise { animation: sf-victoryRise 0.6s ease-out forwards; }

        /* Red void cracks radiating inward during collapse */
        @keyframes sf-solrendCrack {
          0%   { opacity: 0;   transform: scale(0.6); }
          40%  { opacity: 0.7; transform: scale(1);   }
          100% { opacity: 0;   transform: scale(1.1); }
        }
        .sf-solrend-crack { animation: sf-solrendCrack 0.8s ease-out forwards; }

        /* ═══ PASS 3.5 — TRANSITION GLOWS ══════════════════════ */

        /* Transcendence — Escling → Beb. SILVER halo, crisp and cold. */
        @keyframes sf-glowTranscend {
          0%   { box-shadow: 0 0 0 0    #e8ecf0aa,
                              0 0 0 0    #c0c8d0aa,
                              inset 0 0 0 0 #ffffff00; }
          30%  { box-shadow: 0 0 32px 8px #e8ecf0cc,
                              0 0 60px 16px #c0c8d088,
                              inset 0 0 24px #ffffff44; }
          70%  { box-shadow: 0 0 28px 6px #d0d6de99,
                              0 0 50px 12px #a8b0b866,
                              inset 0 0 18px #ffffff22; }
          100% { box-shadow: 0 0 0 0    #e8ecf000,
                              0 0 0 0    #c0c8d000,
                              inset 0 0 0 0 #ffffff00; }
        }
        .sf-glow-transcend {
          border-radius: 10px;
          animation: sf-glowTranscend 0.9s ease-out forwards;
        }

        /* Ascension — Beb → Champion / Champion → Lord. GOLD halo, warm radiance. */
        @keyframes sf-glowAscend {
          0%   { box-shadow: 0 0 0 0    ${GM.inkGoldBright}aa,
                              0 0 0 0    ${SD.gold}aa,
                              inset 0 0 0 0 ${GM.inkGold}00; }
          25%  { box-shadow: 0 0 36px 10px ${GM.inkGoldBright}dd,
                              0 0 70px 22px ${SD.gold}aa,
                              inset 0 0 32px ${GM.inkGold}66; }
          60%  { box-shadow: 0 0 32px 8px ${GM.inkGold}aa,
                              0 0 60px 18px ${GM.inkGoldDim}77,
                              inset 0 0 24px ${GM.inkGold}33; }
          100% { box-shadow: 0 0 0 0    ${GM.inkGoldBright}00,
                              0 0 0 0    ${SD.gold}00,
                              inset 0 0 0 0 ${GM.inkGold}00; }
        }
        .sf-glow-ascend {
          border-radius: 10px;
          animation: sf-glowAscend 0.9s ease-out forwards;
        }

        /* Revival — pulled from Necropolis. PURPLE → BLACK → GREEN chroma rot. */
        @keyframes sf-glowRevive {
          0%   { box-shadow: 0 0 0 0 #6600ccaa, 0 0 0 0 #000000aa, inset 0 0 0 0 #22ff8800; }
          25%  { box-shadow: 0 0 32px 8px #9933ffcc, 0 0 50px 14px #3b076488, inset 0 0 20px #220033; }
          50%  { box-shadow: 0 0 30px 8px #2a002088, 0 0 56px 18px #000000aa, inset 0 0 20px #000000; }
          75%  { box-shadow: 0 0 34px 10px #22ff88cc, 0 0 60px 20px #00663388, inset 0 0 22px #003322; }
          100% { box-shadow: 0 0 0 0 #6600cc00, 0 0 0 0 #000000 , inset 0 0 0 0 #22ff8800; }
        }
        .sf-glow-revive {
          border-radius: 10px;
          animation: sf-glowRevive 0.9s ease-out forwards;
        }

        /* Ascension to LORD — Champion → Lord. Rainbow cascade, longer duration,
           this is the top of the ascension tree and should feel triumphant. */
        @keyframes sf-glowLord {
          0%   { box-shadow: 0 0 0 0 #ff006600, 0 0 0 0 #00ccff00, inset 0 0 0 0 transparent; }
          15%  { box-shadow: 0 0 30px 8px #ff0066cc, 0 0 55px 14px #ff006688, inset 0 0 18px #ff006644; }
          30%  { box-shadow: 0 0 34px 10px #ff6600dd, 0 0 62px 18px #ff660099, inset 0 0 22px #ff660055; }
          45%  { box-shadow: 0 0 36px 11px #ffcc00ee, 0 0 68px 20px #ffcc00aa, inset 0 0 24px #ffcc0066; }
          60%  { box-shadow: 0 0 34px 10px #00ff88dd, 0 0 62px 18px #00ff8899, inset 0 0 22px #00ff8855; }
          75%  { box-shadow: 0 0 32px 9px  #00ccffdd, 0 0 58px 16px #00ccff88, inset 0 0 20px #00ccff44; }
          90%  { box-shadow: 0 0 28px 7px  #6600ffcc, 0 0 52px 14px #6600ff77, inset 0 0 16px #6600ff33; }
          100% { box-shadow: 0 0 0 0 #ff006600, 0 0 0 0 #6600ff00, inset 0 0 0 0 transparent; }
        }
        .sf-glow-lord {
          border-radius: 10px;
          animation: sf-glowLord 1.3s ease-out forwards;
        }

        /* ═══ PASS 4 — HOLO-AWARE SETTLE OVERRIDES ═════════════
           When a card has a holo tier, its transition glow adopts the
           holo's aesthetic instead of the default silver/gold/rainbow. */

        /* MYTHIC holo — pink-magenta rainbow shift, highest-tier vibe */
        @keyframes sf-glowHoloMythic {
          0%   { box-shadow: 0 0 0 0 #ff66cc00, inset 0 0 0 0 transparent; }
          20%  { box-shadow: 0 0 32px 9px #ff66ccdd, 0 0 60px 18px #ff33aa88, inset 0 0 22px #ff66cc55; }
          50%  { box-shadow: 0 0 38px 12px #ffaaddee, 0 0 70px 22px #ff66ccaa, inset 0 0 28px #ff66cc77; }
          80%  { box-shadow: 0 0 30px 9px #ff66ccaa, 0 0 58px 17px #ff33aa66, inset 0 0 20px #ff66cc44; }
          100% { box-shadow: 0 0 0 0 #ff66cc00, inset 0 0 0 0 transparent; }
        }
        .sf-glow-holo-mythic { border-radius: 10px; animation: sf-glowHoloMythic 1.2s ease-out forwards; }

        /* GOLD holo — warm, champagne-bright */
        @keyframes sf-glowHoloGold {
          0%   { box-shadow: 0 0 0 0 #ffd70000, inset 0 0 0 0 transparent; }
          25%  { box-shadow: 0 0 36px 10px #ffd700ee, 0 0 66px 20px #d4a84baa, inset 0 0 26px #ffd70066; }
          60%  { box-shadow: 0 0 32px 9px #ffdd44dd, 0 0 60px 18px #d4a84b88, inset 0 0 22px #ffd70044; }
          100% { box-shadow: 0 0 0 0 #ffd70000, inset 0 0 0 0 transparent; }
        }
        .sf-glow-holo-gold { border-radius: 10px; animation: sf-glowHoloGold 1.1s ease-out forwards; }

        /* SILVER holo — cooler than default transcend, platinum shimmer */
        @keyframes sf-glowHoloSilver {
          0%   { box-shadow: 0 0 0 0 #ccccdd00, inset 0 0 0 0 transparent; }
          25%  { box-shadow: 0 0 32px 9px #ffffffdd, 0 0 58px 17px #ccccddaa, inset 0 0 22px #ffffff55; }
          60%  { box-shadow: 0 0 30px 8px #e8ecf0cc, 0 0 55px 16px #aaaabb88, inset 0 0 20px #ffffff33; }
          100% { box-shadow: 0 0 0 0 #ccccdd00, inset 0 0 0 0 transparent; }
        }
        .sf-glow-holo-silver { border-radius: 10px; animation: sf-glowHoloSilver 1.1s ease-out forwards; }

        /* VOID holo — deep purple with electric fringe */
        @keyframes sf-glowHoloVoid {
          0%   { box-shadow: 0 0 0 0 #7c3aed00, inset 0 0 0 0 transparent; }
          20%  { box-shadow: 0 0 34px 10px #9333eaee, 0 0 64px 20px #7c3aedaa, inset 0 0 24px #3b076477; }
          55%  { box-shadow: 0 0 40px 13px #cc66ffee, 0 0 72px 24px #9333eaaa, inset 0 0 30px #6600cc66; }
          85%  { box-shadow: 0 0 30px 9px #7c3aedaa, 0 0 56px 16px #3b076477, inset 0 0 20px #6600cc44; }
          100% { box-shadow: 0 0 0 0 #7c3aed00, inset 0 0 0 0 transparent; }
        }
        .sf-glow-holo-void { border-radius: 10px; animation: sf-glowHoloVoid 1.2s ease-out forwards; }

        /* SOLREND holo — blood crimson, pulsing red star */
        @keyframes sf-glowHoloSolrend {
          0%   { box-shadow: 0 0 0 0 #ff226600, inset 0 0 0 0 transparent; }
          20%  { box-shadow: 0 0 34px 10px #ff2266ee, 0 0 62px 20px #cc003399, inset 0 0 24px #cc003366; }
          55%  { box-shadow: 0 0 40px 13px #ff4488ff, 0 0 72px 24px #ff226688, inset 0 0 30px #ff226666; }
          85%  { box-shadow: 0 0 30px 9px #ff226688, 0 0 56px 16px #cc003366, inset 0 0 20px #cc003344; }
          100% { box-shadow: 0 0 0 0 #ff226600, inset 0 0 0 0 transparent; }
        }
        .sf-glow-holo-solrend { border-radius: 10px; animation: sf-glowHoloSolrend 1.2s ease-out forwards; }

        /* AETERNUS holo — pure white, angelic */
        @keyframes sf-glowHoloAeternus {
          0%   { box-shadow: 0 0 0 0 #ffffff00, inset 0 0 0 0 transparent; }
          30%  { box-shadow: 0 0 40px 14px #ffffffff, 0 0 72px 24px #ccccffaa, inset 0 0 30px #ffffff77; }
          70%  { box-shadow: 0 0 34px 10px #f8f8ffdd, 0 0 62px 18px #ccccff88, inset 0 0 24px #ffffff55; }
          100% { box-shadow: 0 0 0 0 #ffffff00, inset 0 0 0 0 transparent; }
        }
        .sf-glow-holo-aeternus { border-radius: 10px; animation: sf-glowHoloAeternus 1.2s ease-out forwards; }

        /* SHADOW holo — dark purple sinking to near-black */
        @keyframes sf-glowHoloShadow {
          0%   { box-shadow: 0 0 0 0 #6600cc00, inset 0 0 0 0 transparent; }
          25%  { box-shadow: 0 0 32px 9px #6600cccc, 0 0 60px 18px #44009988, inset 0 0 24px #2d006677; }
          60%  { box-shadow: 0 0 28px 8px #2d006699, 0 0 52px 15px #000014aa, inset 0 0 22px #000000; }
          100% { box-shadow: 0 0 0 0 #6600cc00, inset 0 0 0 0 transparent; }
        }
        .sf-glow-holo-shadow { border-radius: 10px; animation: sf-glowHoloShadow 1.2s ease-out forwards; }

        /* ═══ LORD SPOTLIGHT ═════════════════════════════════
           Board recognition when a Level-3 Escendant enters play.
           Dim layer fades in around surrounding cards; spotlit slot
           gets a brief gold-rise that lifts it visually. */
        @keyframes sf-lordDim {
          0%   { opacity: 0; }
          30%  { opacity: 1; }
          70%  { opacity: 1; }
          100% { opacity: 0; }
        }
        .sf-lord-spotlight-dim { animation: sf-lordDim 0.9s ease-out forwards; }
        @keyframes sf-lordRise {
          0%   { transform: scale(1)    translateY(0);    filter: brightness(1); }
          30%  { transform: scale(1.06) translateY(-4px); filter: brightness(1.25) drop-shadow(0 8px 22px rgba(232,184,75,0.5)); }
          70%  { transform: scale(1.04) translateY(-3px); filter: brightness(1.18) drop-shadow(0 6px 18px rgba(232,184,75,0.4)); }
          100% { transform: scale(1)    translateY(0);    filter: brightness(1); }
        }
        .sf-lord-slot-rise { animation: sf-lordRise 0.9s ease-out; transform-origin: center; }

        /* ═══ ATTACK LUNGE ════════════════════════════════════
           When a card's attackedThisTurn flag flips true, the card
           briefly lunges toward its target. Because opponent cards
           render with rotate(180deg), the same translateY(-12px)
           visually points toward each side's enemy row — one universal
           animation, two correct directions. */
        @keyframes sf-cardLunge {
          0%   { transform: translateY(0)     scale(1);    filter: brightness(1); }
          22%  { transform: translateY(-14px) scale(1.05); filter: brightness(1.25) drop-shadow(0 6px 14px rgba(139,32,32,0.5)); }
          48%  { transform: translateY(-10px) scale(1.02); filter: brightness(1.15); }
          100% { transform: translateY(0)     scale(1);    filter: brightness(1); }
        }
        .sf-card-lunge { animation: sf-cardLunge 0.36s cubic-bezier(0.5, -0.3, 0.3, 1.3); transform-origin: center; }

        /* DEPTHS holo — deep ocean blue (in case you wire this tier in future) */
        @keyframes sf-glowHoloDepths {
          0%   { box-shadow: 0 0 0 0 #00aaff00, inset 0 0 0 0 transparent; }
          25%  { box-shadow: 0 0 34px 10px #00aaffee, 0 0 62px 20px #004488aa, inset 0 0 24px #001a3377; }
          60%  { box-shadow: 0 0 38px 12px #44ccffff, 0 0 70px 22px #00aaff99, inset 0 0 28px #003366; }
          100% { box-shadow: 0 0 0 0 #00aaff00, inset 0 0 0 0 transparent; }
        }
        .sf-glow-holo-depths { border-radius: 10px; animation: sf-glowHoloDepths 1.2s ease-out forwards; }

        /* J'YNXI holo — trickster's emerald (added with the HoloFoil migration;
           the tier finally exists in-duel, so it gets its placement burst too) */
        @keyframes sf-glowHoloJynxi {
          0%   { box-shadow: 0 0 0 0 #2ee66b00, inset 0 0 0 0 transparent; }
          25%  { box-shadow: 0 0 34px 10px #2ee66bee, 0 0 62px 20px #166534aa, inset 0 0 24px #05291277; }
          60%  { box-shadow: 0 0 38px 12px #4ade80ff, 0 0 70px 22px #2ee66b99, inset 0 0 28px #166534; }
          100% { box-shadow: 0 0 0 0 #2ee66b00, inset 0 0 0 0 transparent; }
        }
        .sf-glow-holo-jynxi { border-radius: 10px; animation: sf-glowHoloJynxi 1.2s ease-out forwards; }
        /* Ornate divider style */
        .terror-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, ${GM.inkBrown}66, ${GM.inkGold}99, ${GM.inkBrown}66, transparent);
        }
        /* Parchment panel */
        .terror-panel {
          background: linear-gradient(145deg, ${GM.pageMid}, ${GM.pageWarm});
          border: 1px solid ${GM.inkBrown};
          border-radius: 4px;
        }
        * { font-family: 'Cinzel', 'Palatino Linotype', serif; }
      `}</style>



      {/* ══ THE SECOND WIND — the boss stands back up ═════════════
          Deliberately NOT a toast. This is the moment the duel you
          thought you had won turns out to be half a duel, and a player
          who misses it thinks the game is broken. It sits over the
          board, says who they became, and leaves on its own. */}
      {secondWind && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 620, pointerEvents: "none",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `radial-gradient(ellipse at 50% 50%, ${secondWind.accent}22, rgba(4,2,8,0.55) 60%, rgba(4,2,8,0.8))`,
          animation: "sfWindIn 520ms cubic-bezier(.16,1,.3,1) both",
        }}>
          <style>{`
            @keyframes sfWindIn { from { opacity:0 } to { opacity:1 } }
            @keyframes sfWindRise { from { opacity:0; transform: translateY(18px) scale(.96) } to { opacity:1; transform:none } }
          `}</style>
          <div style={{ textAlign: "center", animation: "sfWindRise 620ms cubic-bezier(.16,1,.3,1) both", fontFamily: GM.font }}>
            <div style={{ fontSize: 11, letterSpacing: 7, color: GM.vellumFaint, marginBottom: 14 }}>
              IT IS NOT OVER
            </div>
            <div style={{
              fontSize: 40, fontWeight: 900, fontStyle: "italic", color: secondWind.accent,
              textShadow: `0 0 26px ${secondWind.accent}88, 0 0 70px ${secondWind.accent}44`,
              lineHeight: 1.1,
            }}>{secondWind.name}</div>
            {secondWind.title && (
              <div style={{ fontSize: 13, letterSpacing: 4, color: GM.vellumDim, marginTop: 8, fontStyle: "italic" }}>
                {secondWind.title}
              </div>
            )}
            {phaseLabel(match) && (
              <div style={{ fontSize: 10, letterSpacing: 3, color: GM.vellumFaint, marginTop: 16 }}>
                PHASE {phaseLabel(match).current} OF {phaseLabel(match).total}
              </div>
            )}
          </div>
        </div>
      )}

      {/* R98 — `isCoWonMatch` drives the sub-headline; the name line
          already carries both names via winnerName above. */}
      {isFinished && winnerName && (
        <SolrendEndCinematic
          winnerName={winnerName}
          coWon={isCoWonMatch(match)}
          onComplete={() => {}}
          onExit={() => onExit(match.winner, { iWon: didSeatWin(match, playerIdx) })}
        />
      )}
      {detailModal && <CardDetailModal cardId={detailModal.cardId} soul={detailModal.soul} holoTier={detailModal.holoTier || null} rec={detailModal.rec || null} onClose={() => setDetailModal(null)} />}

      {/* ── TERROR GATE ── board is dispersed, first turn not yet begun.
          Every clock is frozen behind this until both Aenarchists strike.
          Keyed off duelLive (the shared duelStarted latch) so it clears for
          both clients in lockstep — never one board live while the other waits. */}
      {coinFlipDone && !duelLive && (
        <div style={{ position: "fixed", inset: 0, zIndex: 460, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(4,2,10,0.82)", backdropFilter: "blur(3px)", fontFamily: GM.font }}>
          <style>{`
            @keyframes terrorGatePulse {
              0%,100% { box-shadow: 0 0 24px ${SD.red}aa, inset 0 0 14px ${SD.red}44; transform: scale(1); }
              50%     { box-shadow: 0 0 60px ${GM.inkGold}dd, inset 0 0 28px ${GM.inkGold}55; transform: scale(1.04); }
            }
            @keyframes terrorGateGlow {
              0%,100% { text-shadow: 0 0 14px ${SD.red}, 0 0 44px ${SD.red}88; }
              50%     { text-shadow: 0 0 28px ${GM.inkGold}, 0 0 70px ${GM.inkGold}66; }
            }
          `}</style>
          <div style={{ textAlign: "center", padding: "40px 24px" }}>
            <div style={{ fontSize: 11, letterSpacing: 6, color: GM.vellumFaint, marginBottom: 14 }}>THE FIELD IS SET</div>
            <div style={{ fontSize: 30, fontWeight: 900, fontStyle: "italic", color: SD.red, marginBottom: 8, animation: "terrorGateGlow 2s ease-in-out infinite" }}>
              ◆ STRIKE TO BEGIN ◆
            </div>
            <div style={{ fontSize: 12, color: GM.vellumFaint, marginBottom: 34, fontStyle: "italic" }}>
              No clock moves until both Aenarchists are ready.
            </div>

            <div style={{ display: "flex", gap: 32, justifyContent: "center", marginBottom: 34 }}>
              {[0, 1].map(idx => {
                const ready = match?.terrorReady?.[idx] ?? false;
                const isMe = idx === playerIdx;
                return (
                  <div key={idx} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 12, height: 12, borderRadius: "50%", background: ready ? "#22c55e" : GM.pageBorder, boxShadow: ready ? "0 0 12px #22c55e" : "none", transition: "all 0.3s" }} />
                    <div style={{ fontSize: 11, color: ready ? "#22c55e" : GM.vellumFaint, fontStyle: "italic", letterSpacing: 1 }}>
                      {isMe ? "You" : (match?.players?.[idx]?.name || "Opponent")}
                    </div>
                    <div style={{ fontSize: 10, color: ready ? "#22c55e" : GM.pageBorder }}>{ready ? "READY" : "waiting..."}</div>
                  </div>
                );
              })}
            </div>

            {!myTerror ? (
              <button
                onClick={() => updateMatch(code, { [`terrorReady/${playerIdx}`]: true })}
                style={{
                  padding: "18px 60px", fontSize: 22, fontWeight: 900, fontStyle: "italic",
                  fontFamily: GM.font, letterSpacing: 5, cursor: "pointer",
                  background: `linear-gradient(135deg, ${GM.pageMid}, ${GM.pageWarm})`,
                  border: `2px solid ${SD.red}`, borderRadius: 8, color: GM.vellum,
                  animation: "terrorGatePulse 1.4s ease-in-out infinite", transition: "all 0.2s",
                }}
              >
                TERROR
              </button>
            ) : (
              <div style={{ fontSize: 16, color: "#22c55e", fontStyle: "italic", letterSpacing: 2 }}>
                ✓ Struck — waiting for adversary...
              </div>
            )}

            <div style={{ marginTop: 22, fontSize: 11, color: GM.pageBorder, letterSpacing: 2 }}>
              Auto-begins in {terrorCountdown}s
            </div>
          </div>
        </div>
      )}
      {forcedDiscardModal && (
        <div className="sf-modal-shell" style={{ position: "fixed", inset: 0, background: "rgba(5,3,0,0.96)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500, backdropFilter: "blur(6px)" }}>
          <div style={{ position: "relative", background: `linear-gradient(145deg, ${GM.pageMid}, ${GM.pageWarm})`, border: `1px solid ${GM.inkGold}`, borderRadius: 4, padding: "26px 24px", width: "min(94vw, 440px)", maxHeight: "80vh", display: "flex", flexDirection: "column", fontFamily: GM.font, boxShadow: `0 0 32px rgba(0,0,0,0.9), inset 0 0 24px ${GM.inkGold}06` }}>
            <TerrorFrame />
            <div style={{ fontSize: 10, color: GM.inkGold, letterSpacing: 4, marginBottom: 4 }}>◆ HAND OVERFLOWING</div>
            <div style={{ fontSize: 20, fontWeight: 900, fontStyle: "italic", color: GM.vellum, marginBottom: 6 }}>Discard to {HAND_LIMIT}</div>
            <div style={{ fontSize: 11, color: GM.vellumFaint, fontStyle: "italic", lineHeight: 1.6, marginBottom: 14 }}>The draw resolved — the hand holds {(me?.hand || []).length}. Choose 1 card for the Necropolis.</div>
            <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
              {(me?.hand || []).map((id, idx) => {
                const c = getCard(id);
                return (
                  <div key={`fd-${idx}`} onClick={async () => {
                    const h = [...(me?.hand || [])];
                    const [gone] = h.splice(idx, 1);
                    // ── SEAL: universal DISCARD gate (R10). The gate reads the
                    // hand AFTER the discard, which is the shape R10 actually
                    // states. A hand still over the cap keeps the modal up.
                    const vD = gateDiscard(gateCtx(), h);
                    if (!vD.valid) { showToast(vD.reason || "That discard leaves an illegal hand.", SD.red); return; }
                    await updatePlayer(code, playerIdx, { hand: h, necropolis: [...(me?.necropolis || []), gone] });
                    showToast(`◆ ${getCard(gone)?.name || "Card"} sent to the Necropolis.`, GM.vellumFaint);
                    if (h.length <= HAND_LIMIT) setForcedDiscardModal(false);
                  }} className="sf-plate" style={{ background: `linear-gradient(135deg, ${GM.pageMid}44, ${GM.page})`, border: `1px solid ${SD.red}44`, borderRadius: 8, padding: "8px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
                    {/* GILDED PICKER SWEEP — see exactly what you're burying */}
                    <div style={{ flexShrink: 0 }}>
                      <FieldCard cardId={id} size="small" holoTier={getHoloTier(id, collection)} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 900, fontStyle: "italic", color: GM.vellum }}>{c?.name || id}</div>
                      <div style={{ fontSize: 9, color: GM.vellumFaint, marginTop: 2 }}>{c?.type}{c?.level ? ` • L${c.level}` : ""}</div>
                    </div>
                    <div style={{ fontSize: 16, color: SD.red }}>⚰</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {luxmoireSearchModal && (
        <div className="sf-modal-shell" style={{ position: "fixed", inset: 0, background: "rgba(0,0,5,0.88)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 120, backdropFilter: "blur(4px)" }} onClick={() => setLuxmoireSearchModal(null)}>
          <div style={{ position: "relative", background: `linear-gradient(145deg, ${GM.pageWarm}, ${GM.page})`, border: `1px solid ${GM.inkGold}`, borderRadius: 16, padding: "24px 20px", width: "min(92vw, 420px)", maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: shimmerBorder(GM.inkGold), fontFamily: GM.font }} onClick={e => e.stopPropagation()}>
            <TerrorFrame />
            <div style={{ fontSize: 14, fontWeight: 900, fontStyle: "italic", color: GM.inkGold, letterSpacing: 3, marginBottom: 4, textAlign: "center" }}>✦ LUXMOIRE SEARCH</div>
            <div style={{ fontSize: 11, color: GM.vellumFaint, textAlign: "center", marginBottom: 16 }}>Select an Impera to add to your hand — a Luxmoire search finds Impera only (R106)</div>
            <div style={{ overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
              {luxmoireSearchModal.map(id => {
                const card = getCard(id);
                const qty = me?.luxmoire?.[id] || 0;
                return (
                  <div key={id} onClick={async () => {
                    // ── GRIM/LUX FIX (July 2026): the search fare is paid
                    // HERE, when a card is actually taken. Closing or
                    // cancelling the modal costs nothing.
                    // ── R88: LUX_SEARCH_COST in Khao, OR the same fare in
                    // Anima subject to R96 (strictly more than the fare —
                    // a voluntary payment may not zero you). Consumes the
                    // Turn's draw either way (R11).
                    if (drawnThisTurn) { showToast("Already drew this turn — the search consumes your draw.", SD.red); setLuxmoireSearchModal(null); return; }
                    // ── R106, AT THE TILL AS WELL AS AT THE LIST ──────
                    // The list above is already filtered; this is the
                    // door that TAKES THE CARD, and it is the one that
                    // matters if a future path ever opens this modal
                    // with its own array. Asked BEFORE the fare, so a
                    // refused pick never costs 5 Khao.
                    if (!isLuxSearchable(card)) {
                      showToast(`A Luxmoire search finds Impera only — ${card?.name || "that card"} must be drawn.`, SD.red);
                      return;
                    }
                    let paid = await spendKhao(LUX_SEARCH_COST);
                    // R96 — the strict `>` was already correct; it is now the
                    // rule itself rather than a hand-rolled copy of it.
                    if (!paid && canPayVoluntary(me?.anima ?? STARTING_ANIMA, LUX_SEARCH_COST)) paid = await spendAnima(LUX_SEARCH_COST);
                    if (!paid) { showToast(`Need ${LUX_SEARCH_COST} Khao or ${animaFare(LUX_SEARCH_COST)} to search Luxmoire.`, SD.red); return; }
                    const luxmoire = { ...me.luxmoire };
                    if (luxmoire[id] > 1) luxmoire[id]--; else delete luxmoire[id];
                    const luxHand = [...(me.hand || []), id];
                    await updatePlayer(code, playerIdx, { hand: luxHand, luxmoire });
                    showToast(`✦ ${card?.name} found!`, GM.inkGold);
                    await noteTurnAction("drawnThisTurn", setDrawnThisTurn); // R11 — on the record
                    setLuxmoireSearchModal(null);
                    if (luxHand.length > HAND_LIMIT) setForcedDiscardModal(true); // R10
                  }} style={{ background: `linear-gradient(135deg, ${GM.pageMid}44, ${GM.page})`, border: `1px solid ${GM.inkGold}44`, borderRadius: 10, padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12, transition: "all 0.2s" }}>
                    {/* GILDED PICKER SWEEP — the actual owned card, foil and all */}
                    <div style={{ flexShrink: 0 }}>
                      <FieldCard cardId={id} size="small" holoTier={getHoloTier(id, collection)} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 900, fontStyle: "italic", color: GM.inkGold }}>{card?.name}</div>
                      <div style={{ fontSize: 10, color: GM.vellumFaint, marginTop: 3 }}>{card?.effectTag && <span style={{ color: SD.gold }}>{card.effectTag}: </span>}{card?.effect?.substring(0, 80)}{(card?.effect?.length || 0) > 80 ? "…" : ""}</div>
                      <div style={{ fontSize: 10, color: SD.gold, marginTop: 2 }}>{card?.khaoCost != null ? `✦ ${card.khaoCost} Khao to activate` : `✦ ${card?.type || "Luxmoire card"}`}{card?.level != null ? ` • L${card.level}` : ""}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                      <div style={{ fontSize: 22, fontWeight: 900, color: GM.inkGold, fontFamily: GM.font }}>{qty}</div>
                      <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 1 }}>LEFT</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <button onClick={() => setLuxmoireSearchModal(null)} style={{ marginTop: 14, padding: "10px", background: "transparent", border: `1px solid ${GM.pageBorder}`, borderRadius: 8, color: GM.vellumFaint, cursor: "pointer", fontFamily: GM.font, fontSize: 12 }}>Cancel</button>
          </div>
        </div>
      )}
      {targetPicker && <TargetPickerModal picker={targetPicker} onClose={() => setTargetPicker(null)} collection={collection} />}
      {sgModal && <ShadowGamblingModal sg={sgModal} onClose={() => setSgModal(null)} />}
      {responseWindow && (
        <ResponseWindow
          rw={responseWindow}
          onFire={handleWindowFire}
          onPass={handleWindowPass}
          onSnooze={handleWindowSnooze}
        />
      )}
      {/* ── WAVE 2/3: declaration-in-flight pill — the player's action is
          announced, the adversary is deciding. Hidden the instant a
          window (theirs or a rebuttal of ours) takes the stage. ── */}
      {awaitingResponse && !responseWindow && (
        <div style={{ position: "fixed", bottom: 200, left: "50%", transform: "translateX(-50%)", zIndex: 449, fontFamily: GM.font, pointerEvents: "none" }}>
          <div style={{ background: `linear-gradient(145deg, ${GM.pageWarm}, ${GM.page})`, border: `1px solid ${GM.pageBorder}`, borderRadius: 999, padding: "7px 16px", fontSize: 10, fontWeight: 900, fontStyle: "italic", letterSpacing: 2, color: GM.vellumFaint, boxShadow: "0 4px 18px #0009" }}>
            ⚡ DECLARED — the adversary may answer…
          </div>
        </div>
      )}
      {atvModal && <ATVFeedModal tome={atvModal.tome} tomeSlotIdx={atvModal.tomeSlotIdx} onFeed={handleFeedATV} onClose={() => setAtvModal(null)} myAnima={me?.anima ?? STARTING_ANIMA} myKhao={me?.khao ?? 0} currentTurn={match?.turn || 0} />}
      {devotionModal && <DevotionModal contracts={devotionModal.contracts} onDevote={handleDevote} onClose={() => setDevotionModal(null)} myAnima={me?.anima ?? STARTING_ANIMA} />}
      {grimoireSearchModal && <GrimoireSearchModal onSearch={handleGrimoireSearch} onDraw={() => { handleDraw(false); setGrimoireSearchModal(false); }} onClose={() => setGrimoireSearchModal(false)} myKhao={me?.khao ?? 0} myAnima={me?.anima ?? STARTING_ANIMA} drawnThisTurn={drawnThisTurn} />}
      {graveshiftModal && (() => {
        const reforged = [...(me?.hand || []), ...(me?.necropolis || [])].filter(Boolean).length;
        // R96's R41 exemption: `>=`, not `>`. A seat on exactly the fare
        // may pay it — that is the whole point of the exemption.
        const afford = canPayVoluntary(me?.anima ?? STARTING_ANIMA, GRAVESHIFT_COST, { exempt: true });
        const canReforge = reforged > 0;
        return (
          <div onClick={() => setGraveshiftModal(false)} className="sf-modal-shell" style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(5,4,2,0.82)", backdropFilter: "blur(4px)" }}>
            <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "min(420px, 90vw)", background: `linear-gradient(160deg, ${GM.pageWarm}, ${GM.page})`, border: `1px solid ${GM.inkGold}66`, borderRadius: 6, padding: "26px 24px", fontFamily: GM.font, boxShadow: `0 0 40px ${GM.inkGold}22, inset 0 0 30px ${GM.page}` }}>
              <TerrorFrame />
              <div style={{ fontSize: 20, color: GM.inkGoldBright, letterSpacing: 3, fontWeight: 900, textAlign: "center", marginBottom: 6 }}>♻ GRAVESHIFT</div>
              <div style={{ height: 1, background: `linear-gradient(90deg, transparent, ${GM.inkGold}66, transparent)`, margin: "0 0 16px" }} />
              <p style={{ fontSize: 13, color: GM.vellum, lineHeight: 1.7, margin: "0 0 14px", textAlign: "center" }}>
                Your Grimoire is empty — but the dead remember. Fold your hand and Necropolis into a reforged Grimoire.
              </p>
              <div style={{ fontSize: 12, color: GM.vellumDim, lineHeight: 1.9, marginBottom: 18, padding: "12px 14px", background: GM.page, borderRadius: 4, border: `1px solid ${GM.pageBorder}` }}>
                <div>Cost: <span style={{ color: afford ? SD.gold : SD.red, fontWeight: 900 }}>−{GRAVESHIFT_COST} Anima</span> &nbsp;(you have {me?.anima ?? STARTING_ANIMA})</div>
                <div>Reforged Grimoire: <span style={{ color: GM.inkGoldBright, fontWeight: 900 }}>{reforged} cards</span> &nbsp;({(me?.hand || []).length} hand + {(me?.necropolis || []).length} Necropolis)</div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setGraveshiftModal(false)} style={{ flex: 1, padding: "10px 0", background: "transparent", border: `1px solid ${GM.pageBorder}`, borderRadius: 3, cursor: "pointer", fontFamily: GM.font, fontSize: 12, color: GM.vellumFaint, letterSpacing: 1 }}>Cancel</button>
                <button onClick={handleGraveshift} disabled={!afford || !canReforge} style={{ flex: 2, padding: "10px 0", background: (afford && canReforge) ? `linear-gradient(135deg, ${GM.inkGold}33, ${GM.pageMid})` : "transparent", border: `1px solid ${(afford && canReforge) ? GM.inkGold : GM.pageBorder}66`, borderRadius: 3, cursor: (afford && canReforge) ? "pointer" : "not-allowed", fontFamily: GM.font, fontSize: 12, fontWeight: 900, color: (afford && canReforge) ? GM.inkGoldBright : GM.vellumFaint, letterSpacing: 2, opacity: (afford && canReforge) ? 1 : 0.5 }}>
                  {!canReforge ? "Nothing to reforge" : !afford ? "Not enough Anima" : "♻ Reforge Grimoire"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {soulModal && <SoulModal slot={soulModal?.slot || soulModal} slotIdx={soulModal?.slotIdx ?? 0} myTurn={myTurn} myKhao={me?.khao ?? 0} myAnima={me?.anima ?? STARTING_ANIMA} turn={match?.turn ?? 0} seatCount={seatCountOf(match)} onClose={() => setSoulModal(null)} onActivate={(escendantId, scId) => { const idx = me?.field?.esclings?.findIndex(s => s?.escendant === escendantId); handleActivateSoulCard(idx >= 0 ? idx : (soulModal?.slotIdx ?? 0), scId); }} collection={collection} />}
      {radial && <RadialMenu actions={radial.actions} onClose={closeRadial} x={radial.x} y={radial.y} title={radial.title || null} />}
      {(targetMode === "select" || targetMode === "escling") && <TargetOverlay message={targetMode === "escling" ? "⚔ Escling attacks — tap a being (cannot dim Anima)" : "⚔ Select target — tap opponent being or ⚔ Hit Anima"} onCancel={() => { setAttackingSlot(null); setTargetMode(null); }} />}

      {/* ── MAGNUM PASS 1 — Danger vignette: edges breathe red at low Anima ── */}
      {!isFinished && typeof me?.anima === "number" && me.anima <= 10 && me.anima > 0 && (
        <div className={me.anima <= 5 ? "sf-danger-critical" : "sf-danger"} style={{ position: "fixed", inset: 0, zIndex: 25, pointerEvents: "none" }} />
      )}

      {/* ── MAGNUM PASS 1 — Anima impact edge flash ── */}
      {animaImpact && (
        <div className="sf-hit-flash" style={{ position: "fixed", inset: 0, zIndex: 26, pointerEvents: "none" }} />
      )}

      {/* ── MAGNUM PASS 1 — Turn Herald banner ── */}
      {turnHerald && (
        <div style={{ position: "fixed", inset: 0, zIndex: 95, pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "relative", width: "min(560px, 86vw)", textAlign: "center" }}>
            <div className="sf-herald-line" style={{
              position: "absolute", left: 0, right: 0, top: "50%", height: 1,
              background: `linear-gradient(90deg, transparent, ${turnHerald === "mine" ? GM.inkGoldBright : GM.vellumFaint}, transparent)`,
              boxShadow: turnHerald === "mine" ? `0 0 12px ${GM.inkGold}88` : "none",
            }} />
            <div className="sf-herald-text" style={{
              position: "relative", display: "inline-block",
              padding: "10px 34px",
              background: "rgba(6,5,2,0.9)",
              fontFamily: FONT_DECO, fontWeight: 900,
              fontSize: isMobile ? 15 : 22,
              color: turnHerald === "mine" ? GM.inkGoldBright : GM.vellumDim,
              textShadow: turnHerald === "mine" ? `0 0 24px ${GM.inkGold}cc, 0 0 60px ${SD.gold}55` : `0 0 12px ${SD.purple}66`,
              border: `1px solid ${turnHerald === "mine" ? GM.inkGold + "88" : GM.pageBorder + "88"}`,
              whiteSpace: "nowrap",
            }}>
              {turnHerald === "mine" ? "◆ YOUR TURN ◆" : `◇ ${(opponent?.name || "OPPONENT").toUpperCase()}'S TURN ◇`}
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position: "fixed", top: "42%", left: isMobile ? "50%" : 60, transform: isMobile ? "translate(-50%, -50%)" : "translateY(-50%)", background: "rgba(4,4,10,0.95)", border: `1px solid ${toast.color}`, color: toast.color, padding: "12px 28px", borderRadius: 4, fontSize: 15, fontStyle: "italic", zIndex: 150, boxShadow: `0 0 0 1px ${toast.color}33, 0 0 20px ${toast.color}44`, pointerEvents: "none", fontFamily: FONT_BODY, maxWidth: isMobile ? "80vw" : "320px", textAlign: "center", letterSpacing: 0.5 }}><span className="sf-toast-in" style={{ display: "block" }}>{toast.msg}</span></div>
      )}

      {/* ── C17: THE EMPTY CHAIR ────────────────────────────────
          Deliberately loud and deliberately not modal. Loud because
          a player who has been staring at a dead board needs the
          answer to "is it me or them" immediately. Not modal because
          if the watchdog is WRONG — a backgrounded phone, a tunnel —
          the duel is still live underneath and must stay playable
          the instant their adversary comes back. Nothing here blocks
          a single input. */}
      {(oppAbandoned || ceremonyStalled) && (
        <div style={{
          position: "fixed", top: "18%", left: "50%", transform: "translateX(-50%)",
          zIndex: 160, maxWidth: "min(420px, 88vw)", textAlign: "center",
          background: "rgba(6,4,10,0.96)", border: "1px solid #dc262688",
          borderRadius: 8, padding: "16px 22px 18px",
          boxShadow: "0 0 0 1px rgba(220,38,38,0.14), 0 12px 40px rgba(0,0,0,0.7)",
          backdropFilter: "blur(10px)", fontFamily: FONT_BODY,
        }}>
          <div style={{ fontSize: 10, letterSpacing: 4, color: "#f87171", marginBottom: 7 }}>
            {ceremonyStalled ? "THE TABLE NEVER FILLED" : "THE CHAIR IS EMPTY"}
          </div>
          <div style={{ fontSize: 14, color: GM.vellum, fontStyle: "italic", lineHeight: 1.6 }}>
            {(absentNames || opponent?.name || "Your adversary")} {absentSeats.length > 1 ? "have" : "has"} not stirred in{" "}
            {absentSilenceSec} seconds.
          </div>
          <div style={{ fontSize: 11, color: GM.vellumFaint, marginTop: 6, lineHeight: 1.6 }}>
            {ceremonyStalled
              ? "The Solrend never opened. Walking away costs nobody anything."
              : "They may yet return. The duel is still yours to play."}
          </div>
          <button onClick={ceremonyStalled ? abandonBeforeStart : claimAbandonedVictory} style={{
            marginTop: 13, padding: "9px 20px", cursor: "pointer",
            background: "linear-gradient(135deg, #8b2020, #5c1414)",
            border: "1px solid #dc2626", borderRadius: 5,
            color: "#ffd7d7", fontFamily: GM.font, fontSize: 12,
            fontWeight: 900, fontStyle: "italic", letterSpacing: 1,
            boxShadow: "inset 0 1px 0 rgba(255,180,180,0.35), 0 0 14px rgba(220,38,38,0.35)",
          }}>{ceremonyStalled ? "Call it off" : "Claim the Solrend"}</button>
        </div>
      )}

      {/* ── FLOATING TOP-LEFT CLUSTER: exit + turn status + room code ── */}
      <div style={{
        position: "absolute", top: 8, left: 12, zIndex: 30,
        display: "flex", alignItems: "center", gap: 10,
        background: `linear-gradient(180deg, rgba(10,8,4,0.85), rgba(10,8,4,0.45))`,
        border: `1px solid ${GM.inkGold}1f`,
        boxShadow: `0 0 0 1px ${GM.inkGold}0a, 0 2px 10px rgba(0,0,0,0.5)`,
        padding: "5px 12px", borderRadius: 6,
        backdropFilter: "blur(10px)",
      }}>
        <button onClick={handleExitClick} style={{
          background: concedeArmed ? "rgba(139,32,32,0.35)" : "transparent",
          border: `1px solid ${concedeArmed ? "#dc2626" : GM.inkGold + "88"}`,
          color: concedeArmed ? "#f87171" : GM.inkGoldBright,
          padding: "3px 10px", borderRadius: 5,
          cursor: "pointer", fontFamily: GM.font, fontSize: 11,
          fontWeight: concedeArmed ? 900 : 400,
          letterSpacing: concedeArmed ? 1 : 0,
          opacity: 0.92, transition: "all 0.2s",
          textShadow: concedeArmed ? "0 0 8px #dc262688" : `0 0 6px ${GM.inkGold}66`,
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = 1}
        onMouseLeave={e => e.currentTarget.style.opacity = 0.92}>{concedeArmed ? "CONCEDE?" : "✕"}</button>
        <span style={{
          fontSize: 11,
          color: myTurn ? GM.inkGoldBright : GM.vellumFaint,
          fontStyle: "italic", fontWeight: 900,
          letterSpacing: 3,
          textShadow: myTurn ? `0 0 12px ${GM.inkGold}aa` : "none",
          opacity: myTurn ? 1 : 0.5,
          transition: "all 0.3s",
        }}>{myTurn ? "◆ YOUR TURN" : "◇ Waiting"}</span>
        <span style={{
          fontSize: isMobile ? 9 : 10,
          color: GM.vellumDim,
          letterSpacing: isMobile ? 1 : 3,
          fontFamily: GM.font, opacity: 0.92,
          textShadow: `0 0 6px ${GM.inkGold}33`,
        }}>{code}</span>
      </div>

      {/* ── FLOATING TOP-RIGHT CLUSTER: turn count + match timer ── */}
      <div style={{
        position: "absolute", top: 8, right: 12, zIndex: 30,
        display: "flex", alignItems: "center", gap: 14,
        background: `linear-gradient(180deg, rgba(10,8,4,0.85), rgba(10,8,4,0.45))`,
        border: `1px solid ${GM.inkGold}1f`,
        boxShadow: `0 0 0 1px ${GM.inkGold}0a, 0 2px 10px rgba(0,0,0,0.5)`,
        padding: "5px 14px", borderRadius: 6,
        backdropFilter: "blur(10px)",
      }}>
        <span style={{
          fontSize: 10, color: GM.inkGoldBright, opacity: 0.95,
          letterSpacing: 2, fontFamily: GM.font, fontWeight: 700,
          textShadow: `0 0 8px ${GM.inkGold}66`,
        }}>T {Math.floor((match.turn || 0) / 2)}</span>
        <span style={{
          fontSize: 13, color: GM.inkGoldBright, opacity: 1, fontWeight: 900,
          letterSpacing: 2, fontFamily: GM.font,
          textShadow: `0 0 10px ${GM.inkGold}88`,
        }}>{/* ⏳ IT COUNTS DOWN NOW. It used to count up forever, which
                is a stopwatch — informative and unactionable. A limit
                the player cannot see is a limit that arrives as a bug
                report. Red inside the last minute, and it floors at
                0:00 by formatTime's own clamp. */}
          {(() => {
            const left = DUEL_TIME_LIMIT - globalTimer;
            const urgent = left <= 60;
            return <span style={{ color: urgent ? SD.red : "inherit",
              textShadow: urgent ? "0 0 10px rgba(255,40,60,.8)" : "inherit" }}>
              {formatTime(left)}
            </span>;
          })()}</span>
        <button onClick={() => setField3D(v => !v)} title="Toggle 3D field" style={{
          padding: "2px 9px", borderRadius: 4, cursor: "pointer",
          background: field3D ? `linear-gradient(135deg, ${GM.inkGold}2a, transparent)` : "transparent",
          border: `1px solid ${field3D ? GM.inkGold : GM.inkGold + "44"}`,
          color: field3D ? GM.inkGoldBright : GM.vellumDim,
          fontFamily: GM.font, fontSize: 10, letterSpacing: 1.5, fontWeight: 700,
        }}>{field3D ? "⬡ 3D" : "▤ 2D"}</button>
      </div>

      <div className={[
        turnBreath ? "sf-turn-breath" : "",
        isFinished ? "sf-solrend-dim" : "",
      ].filter(Boolean).join(" ")} style={{ flex: 1, position: "relative", zIndex: 1, background: "transparent", display: "flex", flexDirection: "column", justifyContent: "flex-start", paddingTop: isMobileLandscape ? 0 : isMobile ? 52 : 4, paddingBottom: isMobileLandscape ? 0 : isMobile ? 56 : 4, marginTop: 0, overflowY: (isMobileLandscape || isMobile) ? "auto" : "hidden", overflowX: "hidden", WebkitOverflowScrolling: "touch" }}>
        {/* ✦ SHIP 9 — THE PILL. caP'n, 2026-09-16: the rules you are
            expected to memorise and every play you can legally make right
            now, behind ONE pill so the screen stays clean. Both boards, 2D
            and 3D, one mount. It reads the SAME `stampsFor` / `gateCtx` the
            radials read; it never fires anything (fieldCodex.js).
            Desktop only, like the rest of the floating chrome — caP'n:
            "don't worry about mobile for now, pc only." */}
        {!isMobile && (
          <FieldPill
            me={me}
            opponent={opponent}
            turn={match?.turn ?? 0}
            seatCount={seatCountOf(match)}
            myTurn={myTurn}
            stampsFor={stampsFor}
            gateCtx={gateCtx}
            responseOpen={!!responseWindowRef.current}
            onPlay={handlePillPlay}
          />
        )}
        {/* ACTION BUTTONS — floating right on desktop only */}
        {!isMobile && (
          <>
            {/* Interrupt + Hit Anima — far right edge */}
            <div style={{ position: "absolute", right: 18, top: "38%", transform: "translateY(-50%)", zIndex: 20, display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-end" }}>
              {/* ⚠ C18 (July 25): on desktop the ProphecyButton lives INSIDE the
                  2D PlayerSide — so the 3D board had NO way to activate a
                  Prophecy on desktop at all (mobile pills carry it in both
                  modes; desktop 3D carried nothing). It joins the floating
                  chrome here, gated on field3D so the 2D path is untouched.
                  Same gate as PlayerSide: isMe && prophecy && myTurn. */}
              {field3D && myTurn && (me?.field?.prophecy || me?.prophecy) && (
                <ProphecyButton prophecyId={me?.field?.prophecy || me?.prophecy} myKhao={me?.khao || 0} match={match} playerIdx={playerIdx} onActivate={handleActivateProphecy} />
              )}
              {canInterrupt && (
                <SFActionButton icon="⚡" label="INTERRUPT" onClick={handleOpenInterruptRadial} accent={SD.gold} />
              )}
              {targetMode === "select" && (
                <SFActionButton icon="⚔" label="HIT ANIMA" onClick={handleAttackAnima} accent={SD.red} />
              )}
            </div>
            {/* End Turn with Mulligan stacked directly beneath it */}
            <div style={{ position: "absolute", right: 230, top: "50%", transform: "translateY(-50%)", zIndex: 20, display: "flex", flexDirection: "column", gap: 12, alignItems: "stretch" }}>
              {/* ── R108 · DECLARE THE ATTACK PHASE ────────────────────
                  caP'n, 2026-08-26: *"now you declare attack phase, you
                  cannot play any more cards."* One-way, so the button
                  disappears once pressed and a standing banner takes its
                  place — a disabled button that looks pressable is how a
                  player learns a rule by being refused by it. */}
              {myTurn && !attackPhaseDeclared && (
                <SFActionButton icon="⚔" label="ATTACK PHASE" onClick={handleDeclareAttackPhase} accent={SD.red} big />
              )}
              {myTurn && attackPhaseDeclared && (
                <div style={{ padding: "8px 12px", border: `1px solid ${SD.red}88`, borderRadius: 2, background: `${SD.red}18`, color: SD.red, fontFamily: GM.font, fontSize: 10, fontWeight: 900, fontStyle: "italic", letterSpacing: 1, textAlign: "center", lineHeight: 1.5 }}>
                  ⚔ ATTACK PHASE<div style={{ fontSize: 8.5, color: GM.vellumFaint, fontWeight: 700, letterSpacing: 0.5, marginTop: 3 }}>No more cards. Souls &amp; stamps only.</div>
                </div>
              )}
              <SFActionButton icon="⟳" label="END TURN" onClick={handleEndTurn} disabled={!myTurn} accent={GM.inkGold} big />
              {myTurn && mulliganAvailable && !mulliganUsed && !me?.mulliganUsed && (me?.hand || []).length === 5 && myFirstTurnStarted && !myFirstTurnOver && (
                <SFActionButton icon="🔀" label="MULLIGAN" onClick={handleMulligan} accent={GM.inkGold} big />
              )}
            </div>
          </>
        )}
        {field3D ? (
          <div style={{ flex: 1, minHeight: 340, position: "relative" }}>
            {/* ⬡ SHIP 6 — the 3D board now renders the REAL duel HUD
                (Anima heart, Khao vortex, ornate name plates, framed
                timers, LUX/GRIM tiles) through <FieldChrome3D>, so the
                two values it cannot derive from `me` / `opponent` alone
                — the shared turn clock and whether the draw is spent —
                are handed down below. Both are optional on the far side:
                without them the board drops the timers and falls back to
                `myTurn && !canDraw`. It degrades; it never breaks.

                ◈ H22c — PRIMERAS ON THE 3D BOARD. `stampsFor` is the SAME
                builder the 2D <PlayerSide> receives below, handed down
                unchanged: one legality snapshot, one `handlePressPrimera`
                chokepoint, both boards. The 3D board mounts the seals on
                <CardFace> through CSS3D, so they ARE the 2D seals — same
                wax, same punch, same breath. Optional on the far side: a
                board without it simply wears no stamps. */}
            <Field3DWithHoverPreview
              busy={actionInProgress}
              me={me}
              opponent={opponent}
              myTurn={myTurn}
              attackingSlot={attackingSlot}
              targetMode={targetMode}
              selectedHandIdx={selectedHandIdx}
              canDraw={canDraw}
              onMySlot={handleSlotAction}
              onOppSlot={handleOppSlotAction}
              onMyTome={handleTomeAction}
              onOppTome={(type, slotIdx, e) => { if (type === "curio") handleOppCurioAction(slotIdx, e); if (type === "prophecy") { const propId = opponent?.field?.prophecy; if (propId) setDetailModal({ cardId: propId, soul: [] }); } if (type === "tome") { const t = opponent?.field?.tomes?.[slotIdx]; const tId = typeof t === "object" ? t?.id : t; const tFaceDown = typeof t === "object" ? (t?.faceDown || false) : false; const reqMRF = (attackingSlot !== null && targetMode) ? getRequiredTarget(opponent?.field) : null; if (reqMRF?.kind === "tome" && reqMRF.slotIdx === slotIdx) { handleAttackOppTome(slotIdx); return; } if (tId && !tFaceDown) setDetailModal({ cardId: tId, soul: [] }); else if (tFaceDown) showToast("That Tome is face down.", GM.vellumFaint); } }}
              onMyNecropolis={handleNecropolisAction}
              onOppNecropolis={handleOppNecropolisView}
              onMyExile={handleExileAction}
              onHandCardClick={handleHandCardClick}
              onDrawGrimoire={() => setGrimoireSearchModal(true)}
              onDrawLuxmoire={handleLuxmoireAction}
              effLyt={getEffectiveLytPure}
              effLyf={getEffectiveLyfPure}
              holoOf={holoOf}
              sharedTimer={sharedTimer}
              drawnThisTurn={drawnThisTurn}
              stampsFor={stampsFor}
              drawCue={drawCue}
            />
          </div>
        ) : (<>
        <div>
          <PlayerSide player={opponent} isMe={false} oppField={me?.field || {}} myTurn={myTurn} onSlotAction={handleOppSlotAction} onTomeAction={(type, slotIdx, e) => { if (type === "curio") handleOppCurioAction(slotIdx, e); if (type === "prophecy") { const propId = opponent?.field?.prophecy; if (propId) setDetailModal({ cardId: propId, soul: [] }); } if (type === "tome") { const t = opponent?.field?.tomes?.[slotIdx]; const tId = typeof t === "object" ? t?.id : t; const tFaceDown = typeof t === "object" ? (t?.faceDown || false) : false; const reqMRF = (attackingSlot !== null && targetMode) ? getRequiredTarget(opponent?.field) : null; if (reqMRF?.kind === "tome" && reqMRF.slotIdx === slotIdx) { handleAttackOppTome(slotIdx); return; } if (tId && !tFaceDown) setDetailModal({ cardId: tId, soul: [] }); else if (tFaceDown) showToast("That Tome is face down.", GM.vellumFaint); } }} onNecropolisAction={handleOppNecropolisView} attackingSlot={attackingSlot} targetMode={targetMode} sharedTimer={sharedTimer} collection={null} />
        </div>
        {/* Void seam with centered sigil — pure empty space between halves,
            anchored by a subtle manuscript flourish. No divider line. */}
        <div style={{
          flex: (isMobileLandscape || isMobile) ? "0 0 auto" : "1 1 auto",
          minHeight: 8, position: "relative",
          display: "flex", alignItems: "center", justifyContent: "center",
          pointerEvents: "none",
        }}>
          {/* MAGNUM PASS 1 — Shatter Seam: hairline gold fracture spanning the
              divide, deliberately BROKEN at center so the sigil owns the middle.
              Not a divider line — a crack of light in the field itself. */}
          <div style={{
            position: "absolute", left: "3%", right: "3%", top: "50%", height: 1,
            background: `linear-gradient(90deg, transparent 0%, ${GM.inkGold}44 15%, ${GM.inkGold}26 42%, transparent 47%, transparent 53%, ${GM.inkGold}26 58%, ${GM.inkGold}44 85%, transparent 100%)`,
            boxShadow: `0 0 8px ${GM.inkGold}1a`,
          }} />
          <div className="sf-seam-glint" style={{
            position: "absolute", left: "3%", right: "3%", top: "50%", height: 1,
            background: `linear-gradient(90deg, transparent 40%, #fff3c4bb 50%, transparent 60%)`,
            backgroundSize: "220px 100%", backgroundRepeat: "no-repeat",
            mixBlendMode: "screen",
          }} />
          <svg
            className="sf-sigil-breathe"
            width="120" height="16" viewBox="0 0 120 16"
            style={{ opacity: 0.5 }}
          >
            {/* Left filigree flourish */}
            <path d="M 2 8 L 38 8" stroke={GM.inkGold} strokeWidth="0.5" fill="none" opacity="0.5" />
            <path d="M 26 4 Q 32 8 26 12" stroke={GM.inkGold} strokeWidth="0.6" fill="none" opacity="0.7" />
            <circle cx="36" cy="8" r="1.2" fill={GM.inkGold} opacity="0.6" />
            {/* Center sigil — a four-point diamond inside a ring */}
            <circle cx="60" cy="8" r="5.5" stroke={GM.inkGoldBright} strokeWidth="0.6" fill="none" opacity="0.85" />
            <path d="M 60 3.5 L 63.5 8 L 60 12.5 L 56.5 8 Z" fill={GM.inkGoldBright} opacity="0.9" />
            <circle cx="60" cy="8" r="1.1" fill="#fff3c4" opacity="0.95" />
            {/* Right filigree flourish (mirror) */}
            <path d="M 82 8 L 118 8" stroke={GM.inkGold} strokeWidth="0.5" fill="none" opacity="0.5" />
            <path d="M 94 4 Q 88 8 94 12" stroke={GM.inkGold} strokeWidth="0.6" fill="none" opacity="0.7" />
            <circle cx="84" cy="8" r="1.2" fill={GM.inkGold} opacity="0.6" />
          </svg>
        </div>
        <PlayerSide
          player={me}
          isMe={true}
          oppField={opponent?.field || {}}
          oppAnima={opponent?.anima ?? STARTING_ANIMA}
          myTurn={myTurn}
          onSlotAction={handleSlotAction}
          onTomeAction={handleTomeAction}
          onNecropolisAction={handleNecropolisAction}
          onExileAction={handleExileAction}
          attackingSlot={attackingSlot}
          targetMode={targetMode}
          sharedTimer={sharedTimer}
          onDrawGrimoire={() => setGrimoireSearchModal(true)}
          onDrawLuxmoire={handleLuxmoireAction}
          canDraw={canDraw}
          drawnThisTurn={drawnThisTurn}
          luxPulse={luxPulse}
          grimPulse={grimPulse}
          luxTone={luxTone}
          grimTone={grimTone}
          grimSealed={grimSealed}
          stampsFor={stampsFor}
          selectedHandIdx={selectedHandIdx}
          onHandCardClick={handleHandCardClick}
          handHint={selectedHandIdx !== null ? (() => { const cid = (me?.hand || [])[selectedHandIdx]; const c = getCard(cid); const hints = { "Escendant": c?.level === 1 ? "⬆ Tap Escling slot to Transcend" : "⬆ Tap your being to Ascend", "Impera": "✦ Tap your being to attach", "U'hlti": "◈ Tap Level 3 Natural being", "Affinity": "◆ Tap Level 3 Natural being (with U'hlti)", "Tome": "⬇ Tap a Tome rift", "Curio": "💎 Curios are pre-placed — never played from hand" }; return hints[c?.type] || "◆ Tap a target"; })() : null}
          collection={collection}
          draggingHandIdx={draggingHandIdx}
          enteringHandIdxs={enteringHandIdxs}
          exitingHandCards={exitingHandCards}
          onHandDragStart={(i) => setDraggingHandIdx(i)}
          onHandDragEnd={() => setDraggingHandIdx(null)}
          onRiftDrop={handleRiftDrop}
          prophecyBtnProps={{ myKhao: me?.khao || 0, match, playerIdx, onActivate: handleActivateProphecy }}
        />
        </>)}
      </div>

      {/* MOBILE ACTION PILLS — portrait + landscape, pinned bottom-right */}
      {isMobile && (
        <div style={{ position: "fixed", bottom: 16, right: 12, zIndex: 100, display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
          {/* ⚠ `myTurn &&` STOOD HERE and made the MOBILE mount the only
              one of the three that hid the Prophecy on the adversary's
              Turn — the desktop mounts (:3913, :13845) never had it. So
              the same fulfilled Prophecy was cashable on one device and
              invisible on another, which is the divergence, not the
              turn gate itself. caP'n ruled a Prophecy INTERRUPTS the
              duel (2026-09-14); all three mounts now agree, and the
              button's own `conditionMet` is what decides if it glows. */}
          {me?.prophecy && <ProphecyButton prophecyId={me.prophecy} myKhao={me?.khao || 0} match={match} playerIdx={playerIdx} onActivate={handleActivateProphecy} />}
          {canInterrupt && <button onClick={handleOpenInterruptRadial} style={{ padding: "6px 14px", background: `linear-gradient(135deg, rgba(212,168,75,0.2), ${GM.pageWarm}ee)`, border: `1px solid ${SD.gold}`, borderRadius: 20, cursor: "pointer", fontFamily: GM.font, fontSize: 10, color: SD.gold, whiteSpace: "nowrap", backdropFilter: "blur(8px)" }}>⚡ Interrupt</button>}
          {targetMode === "select" && <button onClick={handleAttackAnima} style={{ padding: "6px 14px", background: `linear-gradient(135deg, ${SD.redDim}, ${GM.pageWarm}ee)`, border: `1px solid ${SD.red}`, borderRadius: 20, cursor: "pointer", fontFamily: GM.font, fontSize: 10, color: SD.red, whiteSpace: "nowrap", backdropFilter: "blur(8px)", boxShadow: shimmerBorder(SD.red) }}>⚔ Hit Anima</button>}
          {myTurn && mulliganAvailable && !mulliganUsed && !me?.mulliganUsed && (me?.hand || []).length === 5 && myFirstTurnStarted && !myFirstTurnOver && (
            <button onClick={handleMulligan} style={{ padding: "6px 16px", background: `linear-gradient(135deg, ${GM.pageMid}, ${GM.pageWarm}ee)`, border: `1px solid ${GM.inkGold}`, borderRadius: 20, cursor: "pointer", fontFamily: GM.font, fontSize: 10, color: GM.inkGold, whiteSpace: "nowrap", backdropFilter: "blur(8px)", animation: "mulliganPulse 2s ease-in-out infinite" }}>🔀 Mulligan</button>
          )}
          <button onClick={handleEndTurn} disabled={!myTurn} style={{ padding: "8px 20px", background: myTurn ? `linear-gradient(135deg, ${GM.inkGold}22, ${GM.pageMid}cc)` : "transparent", border: `1px solid ${myTurn ? GM.inkGold : GM.pageBorder}`, borderRadius: 2, cursor: myTurn ? "pointer" : "not-allowed", fontFamily: GM.font, fontSize: 12, color: myTurn ? GM.inkGoldBright : GM.vellumFaint, fontWeight: 900, fontStyle: "italic", backdropFilter: "blur(8px)", boxShadow: myTurn ? `0 0 12px ${GM.inkGold}33` : "none", whiteSpace: "nowrap" }}>⟳ End Turn</button>
        </div>
      )}

    </div>
  );
}